import { WeaponCycle, weaponStats, weaponSpread, weaponSummary } from "@hyperlight/shared";
import {WeaponSelector,type WeaponOption} from "./ui/weaponSelector";
import { ENVIRONMENT, VISUAL_STYLES } from "./visual/styleProfiles";
/**
 * Hyperlight client bootstrap and game loop.
 *
 * Loop structure: fixed 60 Hz simulation steps (framerate-independent
 * physics/input) driven by an accumulator inside requestAnimationFrame.
 *
 * Precision: ship/planet positions are system-space float64. The scene is
 * rendered relative to a floating origin that snaps to the ship whenever it
 * strays 30 km from it, so float32 GPU math never sees large coordinates.
 *
 * Modes: intro (title) → fly ⇄ menu (ESC) ⇄ shipyard.
 */
import * as THREE from "three";
import { opaqueFrontToBack } from './engine/opaqueSort';
import { ScenePose } from './engine/scenePose';
import { compileObjectAsync } from './engine/compileObjectAsync';
import { warmSceneViews } from './engine/warmSceneViews';
import { resolveCharacterPose } from "@hyperlight/shared";
import { audio } from "./audio";
import { FootstepSurfaceProbe } from './world/footstepSurface';
import type { FootstepSurface } from './audio/footsteps';
import type { PlaceContext } from './audio/place/context';
import {
  DEFAULT_LAYOUT,
  DEFAULT_STYLE,
  RESIDENT_RANK_XP,
  SETTLEMENT_PROGRESSION_MAX,
  ITEMS,
  facilityUpgradeAfter,
  settlementFoundingStatus,
  SETTLEMENT_FOUNDER,
  SETTLEMENT_SECOND_FOUNDER,
  homesteadPrefabById,
  homesteadPrefabCost,
  addItem,
  validateLayout,
  cellCenterDir,
  regionFrame,
  localToDir,
  settlementSeeds,
  settlementInfluenceAt,
  settlementName,
  PlanetSampler,
  sprawlSeeds,
  parseRegionSettlementId,
  parseCanonicalNpcId,
  resolveHomeCanonicalNpc,
  materializeRegion,
  packCell,
  LEVEL_REGION,
  tierRegistryFor,
  TIER_NAMES,
  TIER_REQUIREMENTS,
  type CellId,
  type CityGraph,
  type SettlementSeed,
  type CharacterStyle,
  type CharacterRecordV1,
  type ShipLayout,
  dirToCell,
  homeOwner, parseSystemRef, homeSystemId,
  isPrologueStage, resolvePrologueSites, PROLOGUE_QUEST_ID,
  CHARACTER_SLOTS_MAX,
  systemDefFor,
  defaultStoryState, sealedSystemStub,
  HOME_ARRIVAL_QUEST_ID,
  SCOUT_SPEEDER,
  VEHICLES,
  PART_VARIANTS,
  ARMOR_SEAL_MAX,
  SHIELD_CHARGE_ENERGY_GATE,
  SHIELD_DRAW_RATIO,
  armorProfile,
  hullLayout,
  layoutHull,
  shipUnlockToken,
  vehicleDef,
  vehicleWeapon,
  compileVehicleDef,
  vehicleDefinitionIssues,
  buildStructures,
  BUILDING_STRIDE,
  BUILDING_ARCHETYPES,
  buildingPlan,
  planDoorSide,
  interiorEligible,
  isSealedFrontier,
} from "@hyperlight/shared";
import { Input } from "./engine/input";
import { perf } from "./engine/perf";
import { makeRenderCensus } from "./engine/renderCensus";
import { StarSystem, type NeighborStar } from "./world/starsystem";
import { DroneSwarm } from "./world/drones";
import { setTerrainQuality, SUNSET_WARM } from "./world/planet";
import { OceanPatch } from "./world/water/oceanPatch";
import { RiverReflection } from "./world/water/riverReflection";
import {setVolcanicReviewTime,volcanicTime} from './world/volcano/effects';
import {createVolcanicExposure,volcanicExposure} from '@hyperlight/shared';
import { setFloraTime, setFloraEye, setFloraFadeScale, floraWind, WIND_TUNING, GRASS_TUNING, updateGrassUniforms } from "./world/flora";
import { WIND_UNIFORMS } from "./world/flora/wind";
import { setWeatherTime } from "./world/weather/cloudShell";
import { PAINT_LIGHT, updateSpeedWarp, updateStylized, updateWeatherGround, weatherSurface } from "./world/stylizedShading";
import { WEATHER, sampleWeather, emptyWeather, setWeatherReviewTime, type WeatherPreset } from "./world/weather/weatherField";
import { weatherSkyTint, nightSkyTint, precipitationAltitude, weatherMistAmount, landscapeHazeDensity } from "./world/weather/atmosphereMood";
import type { AtmosphereFrame } from "./fx/volumetricAtmosphere";
import { cliffHit, waterColumn, seaSurface, surfaceSupportRadius, surfaceSupportNormal, surfaceColumn, createSurfaceColumn, lavaContactRate, type WaterColumn } from "@hyperlight/shared";
import { PainterlyPost, PAINTERLY } from "./fx/painterlyPost";
import { DepthPartition } from "./world/depthPartition";
import { TravelLog } from "./ui/travelLog";
import { ControlsPanel } from "./ui/controls";
import { PlayerMarkers, ShipMarker } from "./ui/playerMarkers";
import { StationVis } from "./world/stationVis";
import { StationPanel } from "./ui/station";
import { TradeWindow } from "./ui/trade";
import { SensorView, type SensorNode } from "./ui/sensorView";
import { GroundMinimap, type GroundMapMarker } from "./ui/groundMinimap";
import { BodyMarkers } from "./ui/bodyMarkers";
import { horizonAnchor } from "./ui/horizon";
import { ResourceNodes } from "./world/resourceNodes";
// CACHES (P6b): the aperture presence layer on a planet, and the dressed instance
// that a `cache:` system id is played inside.
import { CacheApertures, APERTURE_MAP_COLOR } from "./world/cacheApertures";
import { LiveCache, cacheInfoOf, type CacheInstanceInfo } from "./cache/liveCache";
import { HomesteadMarkers } from "./world/homesteadMarkers";
import { HomesteadPieces } from "./world/homesteadPieces";
import { HomesteadResidentLife } from "./world/homesteadResidentLife";
import { HomesteadBuild, BUILD_REACH_M, DEMOLISH_REACH_M } from "./homestead/build";
import { BuildPalette } from "./ui/buildPalette";
import { ClaimPanel } from "./ui/claimPanel";
import { SettlementRenderer } from "./world/settlement/renderer";
import { DoorLeaves } from "./world/settlement/doorLeaves";
import { ObserveSystem } from "./observe";
import { BuildingObserveProvider } from "./world/settlement/observeBuildings";
import { PlaceIndex, type Place } from "./world/settlement/places";
import { planScan } from "./world/settlement/scan";
import { RegionNpcLife } from "./world/regionNpcLife";
import { DialogPanel, type DialogQuestAction } from "./ui/dialog";
import { QuestLog } from "./rpg/quests";
import { JournalPanel } from "./ui/journal";
import { ChatPanel } from "./ui/chat";
import { SocialPanel } from "./ui/socialPanel";
import { SocialModel } from "./rpg/social";
import { PartyModel } from "./rpg/party";
import { GuildModel } from "./rpg/guild";
import { PartyFrames } from "./ui/partyFrames";
import { QuestBoardPanel } from "./ui/questBoard";
import { questEpoch, settlementTierName, type CacheTheme, type QuestIntentMsg } from "@hyperlight/shared";
import { MiningBeam } from "./fx/miningBeam";
import { COMBAT_ACTIONS, HIT_ARCS, SWEPT_MELEE, sweepArcVsCapsule } from "@hyperlight/shared";
import { validateBeacon, validatePlacedPiece, validateSettlementPublicWire, validateSettlementStateWire, settlementPublicState, settlementGrowthStatus, PIECE_CATALOG, pieceCatalogCost, supportedSet, wouldFloat, placementSupported, groundedOn, isFurniturePiece, canClaim, claimTownInfluence, tierDef, systemIdOfPlanetId, type Beacon, type PlacedPiece, type Piece, type SettlementStateV1, type SettlementPublicV1 } from "@hyperlight/shared";
import { type BodyFrame, type MeleeTarget } from "./combat/meleeContact";
import { feedback } from "./combat/feedback";
import { SoftLock } from "./combat/softLock";
import { ActionSystem } from "./combat/actionSystem";
import { makeLockMarker, LOCK_MARKER_LIFT } from "./combat/lockMarker";
import { BladeTrail } from "./fx/bladeTrail";
import { HitSparks } from "./fx/hitSparks";
import { MINE_CYCLE_S, MINE_RANGE_FOOT, MINE_RANGE_SHIP, NODE_KINDS, countItem, countUnbound, consumeItem, consumeAt, validateInventory, validateUpgrades, equipFromSlot, equipSlotFor, styleWithWornGear, equippedStyleTokens, unequipSlot, type EquipSlot, unpackCell, DOCK_RANGE, DOCK_SPEED, stationPose, DRONE_RADIUS, FISTS_MELEE, GUARD_SOAK, MAX_LEVEL, MELEE_HIT_RADIUS, PLAYER_HIT_HEIGHT, PLAYER_HIT_MARGIN, PLAYER_HIT_RADIUS, SHIELD_SOAK, UPGRADE_DEFS, UPGRADE_TRACKS, generateSystem, nextUpgradeCost, weatherGroundState, WORLD_SCALE, SERVER_AUTHORITATIVE_BOLTS, SERVER_AUTHORITATIVE_HP, vehicleBailImpulse, vehicleBailSpeed, type UpgradeTrack, type WeatherColumn, type WeatherGround, type PlayerState, type VehicleSeatDef } from "@hyperlight/shared";
import type { Planet } from "./world/planet";
import { TerrainContacts } from './world/terrainContacts';
import { Ship } from "./ship/ship";
import { Autopilot } from "./ship/autopilot";
import { CharacterController, FootCamera } from "./character/character";
import { DeathPresentation } from "./character/deathPresentation";
import { makePlanetDeathResolver } from "./character/deathCollision";
import { GroundVehicle } from "./vehicles/groundVehicle";
import { GarageModel } from "./rpg/garage";
import { GaragePanel } from "./ui/garagePanel";
import { PlayerPanel } from "./ui/playerPanel";
import { PlanetVehicleGround } from "./vehicles/planetGround";
import type { VehicleView } from "./vehicles/proceduralSpeeder";
import { createVehicleView } from "./vehicles/glbVehicle";
import { VehicleCamera } from "./vehicles/vehicleCamera";
import { riderLeanSignal, vehicleRiderPose, vehicleSeatPose } from "./vehicles/rider";
import {
  vehicleBailPose,
  vehicleBailVelocity,
  vehicleMountTransitionPose,
  type VehicleMountTransition,
} from "./vehicles/mountTransition";
import {
  VEHICLE_BAIL_ANIMATION_URLS,
  VEHICLE_DISMOUNT_ANIMATION_URLS,
  VEHICLE_MOUNT_ANIMATION_URLS,
  vehicleConstraintWorldTarget,
} from "./vehicles/mountAnimation";
import { convergeVehicleWeapon } from "./vehicles/aim";
import { Avatar, BODY_FILL } from "./character/avatar";
import { ConversationLook } from "./character/conversationLook";
import {
  humanoidAnimationEventsBetween,
  loadHumanoidAnimation,
  sampleHumanoidConstraints,
  type LoadedHumanoidAnimation,
} from "./character/humanoidAnimation";
import { getClipsAndReports, getClipProgress } from "./character/anim";
import { LoadScreen, computeLoaderState } from "./ui/loader";
import { IntroStars } from "./ui/introStars";
import { MeridianLine } from "./ui/meridianLine";
import { BODY_MODELS, loadPiece } from "./character/bodyModels";
import { PlayerRpg } from "./rpg/rpg";
import { InventoryPanel } from "./ui/inventory";
import { ShieldHex } from "./fx/shieldHex";
import { GodPanel } from "./ui/godPanel";
import { CharacterCreator } from "./ui/characterCreator";
import { CharacterPortraits } from "./ui/characterPortraits";
import { CutscenePlayer, type CutsceneFrame } from "./cutscene/player";
import { HOMECOMING_CUTSCENE } from "./cutscene/homecoming";
import { SOUL_REVEAL_CUTSCENE } from "./cutscene/soulReveal";
import { LANDFALL_CUTSCENE } from "./cutscene/landfall";
import type { CutsceneEditor } from "./cutscene/editor";
import { DamageNumbers } from "./ui/damageNumbers";
import { buildShipFromLayout, disposeShip, type BuiltShip } from "./ship/parts";
import { WorldWildlife } from './creatures/worldWildlife';
import { OverheadBubble } from "./ship/shipMesh";
import { ThrusterTrails, type TrailAnchor } from "./fx/trails";
import { BoostStreams } from "./fx/boostStreams";
import { FlightSpeedLines } from "./fx/flightSpeedLines";
import { HoverRing } from "./fx/hoverRing";
import { BoltSystem } from "./fx/bolts";
import { WaterFx } from "./fx/waterFx";
import { VehicleFx } from "./fx/vehicleFx";
import { PrecipFx } from "./fx/precipFx";
import { WeatherVeil } from "./fx/weatherVeil";
import { WarpTunnel } from "./fx/warp";
import { WormholeField } from "./fx/wormholes";
import { NetClient } from "./net/net";
import { Hud } from "./ui/hud";
import { PauseMenu } from "./ui/menu";
import { Companion } from "./character/companion";
import { CompanionMenu, type CompanionAction } from "./ui/companionMenu";
import { ActionMenu } from "./ui/actionMenu";
import { NavChart, type ChartPresence, type NavTarget, navTargetId } from "./ui/navChart";
import { Shipyard, loadSavedLayout, saveLocalLayout } from "./builder/shipyard";
import {
  authAvailable,
  authErrorMessage,
  createShipDoc,
  deleteShipDoc,
  getIdToken,
  listShipDocs,
  loadCharacterProfile,
  type Profile,
  refreshUser,
  resendVerification,
  savePosition,
  saveRpg,
  saveUpgrades,
  saveHomeWorld,
  currentUid,
  listDiscovered,
  addDiscovered,
  saveShip,
  saveStyle,
  listOutfitDocs,
  createOutfitDoc,
  updateOutfitDoc,
  deleteOutfitDoc,
  setActiveOutfitId,
  setActiveShipId,
  signIn,
  signOutUser,
  signUp,
  devMintVerifiedUser,
  firebaseEmulatorMode,
  watchAuth,
  createCharacter,
  listCharacters,
  deleteCharacter,
  loadCharacter,
  activateCharacter,
  advanceCharacterStage,
  noteCharacter,
  noteUid,
  CharacterApiError,
  listAssetDocs,
  type AuthUser,
  type ShipDoc,
} from "./auth/firebase";
import { ProceduralAssetVehicleView } from "./assets/proceduralAssetView";
import {
  acceptSystemReveal,
  accessTicketFor,
  hasSystemReveal,
  loadAuthorizedSystemReveal,
} from "./world/universeReveal";

const FIXED_DT = 1 / 60;
const ORIGIN_SNAP = 30000;
const FIRE_COOLDOWN_MS = 160;

/** Daytime sky shaping — `__hl.sky` in the console. 1 restores the raw
 *  atmosphere colour straight from worldgen. */
const SKY = { saturate: 2 };

const canvas = document.getElementById("game") as HTMLCanvasElement;
const renderer = new THREE.WebGLRenderer({
  canvas,
  antialias: true,
  // The scene spans ~0.5 m terrain detail to 800 km starfield shells.
  //
  // OFF, and deliberately. The range 0.1 m .. 10,000 km needs more precision
  // than a 24-bit depth buffer has, but three buys that by writing gl_FragDepth
  // in every fragment shader, which disables early-Z: in a forest the GPU shades
  // every hidden leaf. Measured at 49.2 ms/frame with it against 21.6 ms
  // without. The precision is instead obtained by DEPTH PARTITIONING — drawing
  // the scene in a few disjoint depth slices, each with the whole buffer to
  // itself. See world/depthPartition.ts.
  //
  // `?logdepth=1` restores the old single-pass behaviour, for A/B only.
  logarithmicDepthBuffer:
    typeof location !== "undefined" && location.search.includes("logdepth=1"),
});
renderer.setOpaqueSort(opaqueFrontToBack);
/**
 * Render scale — the single biggest performance lever in this client.
 *
 * Measured 2026-07-27 on an M4 Pro: the frame is GPU-FILL bound and obeys a
 * clean law, render ≈ 2.2 ms + 5.6 ms per megapixel, while every CPU bucket
 * combined is under 1 ms. A retina display asks for four times the pixels of a
 * 1x one, so this slider moves the frame further than any shader change
 * available — 100% → 75% is roughly 38 ms → 22 ms at 1600x1000 retina.
 *
 * Defaults to 1, i.e. exactly the previous behaviour: it is a knob offered, not
 * a quality cut applied. Menu → Audio & Video → Resolution.
 */
const RES_SCALE_MIN = 0.5;
const RES_SCALE_MAX = 1;
let resScale = Math.min(
  RES_SCALE_MAX,
  Math.max(RES_SCALE_MIN, parseFloat(localStorage.getItem("hl-res-scale") ?? "1") || 1),
);
/** Re-apply the drawing-buffer resolution. three's setPixelRatio re-runs setSize
 *  itself, and painterlyPost notices the ratio change on its next frame. */
function applyRenderScale(): void {
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2) * resScale);
}
applyRenderScale();
renderer.setSize(window.innerWidth, window.innerHeight);
// GPU pass timer for the god perf overlay (engine/perf.ts) — inert without
// EXT_disjoint_timer_query_webgl2, and a no-op until the overlay is on.
perf.attachRenderer(renderer.getContext(), renderer.info);
// Real-time sun shadows: a single player-centred shadow map (the sun light carries the
// shadow camera; see StarSystem). Soft PCF to hide the resolution at this world scale.
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;

/** Natural grading over the finished frame — restrained distance softening,
 * filmic roll-off and vignette (paper grain and color fringing disabled) — plus
 * the vehicle speed lens folded in as one of its terms. ON by default; opt out
 * with hl-painterly=0 in localStorage (+ reload) or ?painterly=0, which falls
 * straight through to renderer.render and allocates nothing. */
const painterlyPost = new PainterlyPost();
/** Multi-slice scene draw — the reason logarithmicDepthBuffer is off above.
 *  Bypassed (single pass) when ?logdepth=1 restores the old scheme. */
const depthPartition = new DepthPartition();
const worldRenderPose = new ScenePose();
const partitionedDraw = renderer.capabilities.logarithmicDepthBuffer
  ? undefined
  : (r: THREE.WebGLRenderer, sc: THREE.Scene, cam: THREE.PerspectiveCamera): void => {
    painterlyPost.distantDepth.beginFrame();
    const vents=atmosphereFrame?.planet.volcanicEffects;
    depthPartition.render(r, sc, cam,painterlyPost.enabled&&vents?.group.visible
      ? (near,far)=>painterlyPost.distantDepth.capture(r,near,far):undefined);
  };
painterlyPost.enabled =
  !((typeof localStorage !== "undefined" && localStorage.getItem("hl-painterly") === "0") ||
    (typeof location !== "undefined" && location.search.includes("painterly=0")));
painterlyPost.setSize(window.innerWidth, window.innerHeight);

const scene = new THREE.Scene();
scene.background = new THREE.Color(0x000005);
// Fog exists from the start (density ~0) so every material compiles with fog
// support; entering an atmosphere just raises the density.
const fog = new THREE.FogExp2(0x000005, 1e-9);
scene.fog = fog;

const camera = new THREE.PerspectiveCamera(
  72,
  window.innerWidth / window.innerHeight,
  0.1,
  // Far enough to see across a (WORLD_SCALE'd) system from its outer edge.
  // Safe to push out this far because the renderer uses a logarithmic depth buffer.
  2_000_000 * WORLD_SCALE,
);

/**
 * Perf sentinel — a draw that rasterises nothing, sorted FIRST among opaque
 * objects. three builds the shadow maps at the top of a render() call, before
 * any object's onBeforeRender, so this callback is the earliest point after
 * them: it closes the "shadow" GPU bracket the depth partition opened and opens
 * the slice's own. Armed only for the outermost slice (the one that rebuilds
 * the maps); in every other pass it is an inert triangle.
 */
{
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(new Float32Array(9), 3));
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1);
  const mat = new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false, depthTest: false, fog: false });
  const sentinel = new THREE.Mesh(geo, mat);
  sentinel.name = "perf-sentinel";
  sentinel.frustumCulled = false;
  sentinel.renderOrder = -1e9;
  sentinel.matrixAutoUpdate = false;
  sentinel.onBeforeRender = () => {
    if (depthPartition.shadowArmed) {
      depthPartition.shadowArmed = false;
      perf.gpuBegin(depthPartition.sliceZone);
    }
  };
  scene.add(sentinel);
}
perf.setCensusWalker(makeRenderCensus(scene, camera));

/** Camera-space handoff between foot and vehicle rigs. Both rigs still solve
 * their normal desired pose; this blends from the previously rendered camera
 * so beginning/ending a mount cannot snap sideways or off-centre. */
let cameraHandoff: {
  /** Source camera in system space, so floating-origin changes cannot move it. */
  position: THREE.Vector3;
  /** Optional moving anchor that carries the source pose during the blend. */
  followPosition: THREE.Vector3 | null;
  quaternion: THREE.Quaternion;
  up: THREE.Vector3;
  fov: number;
  elapsed: number;
  duration: number;
} | null = null;
const cameraDesiredPosition = new THREE.Vector3();
const cameraDesiredQuaternion = new THREE.Quaternion();
const cameraDesiredUp = new THREE.Vector3();
const cameraHandoffSourcePosition = new THREE.Vector3();

function beginCameraHandoff(
  renderOrigin: THREE.Vector3,
  followPosition: THREE.Vector3 | null = null,
  duration = 0.3,
): void {
  cameraHandoff = {
    position: camera.position.clone().add(renderOrigin),
    followPosition: followPosition?.clone() ?? null,
    quaternion: camera.quaternion.clone(),
    up: camera.up.clone(),
    fov: camera.fov,
    elapsed: 0,
    duration,
  };
}

function applyCameraHandoff(
  dt: number,
  renderOrigin: THREE.Vector3,
  currentFollowPosition: THREE.Vector3 | null = null,
): void {
  const handoff = cameraHandoff;
  if (!handoff) return;
  handoff.elapsed = Math.min(handoff.duration, handoff.elapsed + dt);
  const x = handoff.elapsed / Math.max(0.001, handoff.duration);
  const k = x * x * (3 - 2 * x);
  cameraDesiredPosition.copy(camera.position);
  cameraDesiredQuaternion.copy(camera.quaternion);
  cameraDesiredUp.copy(camera.up);
  const desiredFov = camera.fov;
  cameraHandoffSourcePosition.copy(handoff.position);
  if (handoff.followPosition && currentFollowPosition) {
    cameraHandoffSourcePosition.add(currentFollowPosition).sub(handoff.followPosition);
  }
  cameraHandoffSourcePosition.sub(renderOrigin);
  camera.position.lerpVectors(cameraHandoffSourcePosition, cameraDesiredPosition, k);
  camera.quaternion.slerpQuaternions(handoff.quaternion, cameraDesiredQuaternion, k);
  camera.up.lerpVectors(handoff.up, cameraDesiredUp, k).normalize();
  camera.fov = THREE.MathUtils.lerp(handoff.fov, desiredFov, k);
  camera.updateProjectionMatrix();
  if (handoff.elapsed >= handoff.duration) cameraHandoff = null;
}

window.addEventListener("resize", () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  // Re-apply the scale first: dragging a window between a retina and a non-retina
  // display changes devicePixelRatio, and setSize alone would keep the old one.
  applyRenderScale();
  renderer.setSize(window.innerWidth, window.innerHeight);
  painterlyPost.setSize(window.innerWidth, window.innerHeight);
  syncReflectionSize();
});

const input = new Input(canvas);
const hud = new Hud(document.getElementById("hud")!);
const loadScreen = new LoadScreen();
// Warm the body-model template cache during boot: the GLB is the ONLY body
// (no procedural placeholder), so the first avatar must not wait on the network.
void loadPiece(BODY_MODELS.male.body);
if (BODY_MODELS.male.hair) void loadPiece(BODY_MODELS.male.hair);
void loadPiece(BODY_MODELS.female.body);
if (BODY_MODELS.female.head) void loadPiece(BODY_MODELS.female.head);
if (BODY_MODELS.female.hair) void loadPiece(BODY_MODELS.female.hair);
let launching = false;
const dmgNums = new DamageNumbers(document.getElementById("hud")!);
/** MY shot landed (Halo-style confirm): flash the reticle hitmarker ticks + a subtle
 *  UI tick. Fires ONLY beside a damage number the LOCAL player earned — never for
 *  spectated hits (`bhit` is broadcast room-wide, so that path gates on `by`). */
function confirmHit(): void {
  hud.hitMarker();
  audio.sfx("hit.marker");
}
/** Reticle bloom: grows per shot, decays — feeds spread + the reticle. */
const weaponCycle=new WeaponCycle();
let cycleWeapon:string|null=null;

// --- World (current galaxy; switched by hyperlight warp) ---
// C4: the system ID STRING is the identity ("x:y:z" lattice or "home:<uid>").
// SYSTEM_COORDS is the derived lattice point — null inside a home system —
// and every piece of coordinate arithmetic (jump reach, nav direction,
// chart plotting) guards on it.
// The unauthenticated title feed opens on a fresh lattice system each page load.
// A restored profile switches to its saved system before launch, and a new pilot
// still starts in their private home system, so this is presentation-only.
// ⚠ Re-roll away from the SEALED FRONTIER: a sealed coordinate's canonical spelling is
// "sealed:x:y:z", so joining its raw lattice spelling fails the server's canonicality
// check — every pre-auth join from such a boot died as "invalid join payload",
// intermittently by roll (found 2026-07-29 chasing "guest joins are broken").
const titleSystemCoords: [number, number, number] = (() => {
  for (;;) {
    const c: [number, number, number] = [
      Math.floor(Math.random() * 41) - 20,
      Math.floor(Math.random() * 41) - 20,
      Math.floor(Math.random() * 41) - 20,
    ];
    if (!isSealedFrontier(...c)) return c;
  }
})();
let SYSTEM_ID = titleSystemCoords.join(":");
let SYSTEM_COORDS: [number, number, number] | null = titleSystemCoords;
/** The last LATTICE system visited — where RETURN leads from the home pocket
 *  (survives relog via the profile, so logging out at home isn't a trap). */
let lastLattice: string | null = "0:0:0";
let system = new StarSystem(scene, SYSTEM_ID);
let introBackgroundFrames = 0;
let introBackgroundStartedAt = performance.now();

function resetIntroBackground(): void {
  if (!document.body.classList.contains("intro-active")) return;
  document.body.classList.remove("intro-background-ready");
  introBackgroundFrames = 0;
  introBackgroundStartedAt = performance.now();
  system.resetTerrainIdle();
}

// DEBUG (temporary): isolate the horizon "line" by toggling sky layers from the
// console. Walks the LIVE scene (robust to HMR / stale instances). Each call RETURNS
// how many meshes it toggled — if that's 0, the layer wasn't found (tell me).
//   __sky.atmo(false)  // atmosphere shell  (ShaderMaterial with a uRo uniform)
//   __sky.sea(false)   // far sea sphere    (transparent MeshPhong SphereGeometry)
//   __sky.fog(false)   // distance fog
let dbgNoFog = false;
let dbgNoWater = false;
const eachMesh = (test: (m: THREE.Mesh) => boolean, v: boolean): number => {
  let n = 0;
  scene.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh && test(m)) {
      o.visible = v;
      n++;
    }
  });
  return n;
};
let dbgAtmoLog = false;
let dbgFrame = 0;
let dbgLogT = 0;
const _dbgFrustum = new THREE.Frustum();
const _dbgMat = new THREE.Matrix4();
const _dbgFwd = new THREE.Vector3();
const _dbgUp = new THREE.Vector3();
(window as unknown as { __sky: unknown }).__sky = {
  atmo: (v = false) => eachMesh((m) => !!(m.material as THREE.ShaderMaterial)?.uniforms?.uRo, v),
  sea: (v = false) =>
    eachMesh(
      (m) =>
        (m.material as THREE.MeshPhongMaterial)?.isMeshPhongMaterial === true &&
        m.geometry?.type === "SphereGeometry",
      v,
    ),
  fog: (v = false) => {
    dbgNoFog = !v;
    return dbgNoFog;
  },
  // Hide ALL water — the animated near patch (via the ocean update) AND every far sea
  // sphere — to test whether the water is what cuts the atmosphere off at the horizon.
  water: (v = false) => {
    dbgNoWater = !v;
    eachMesh(
      (m) =>
        (m.material as THREE.MeshPhongMaterial)?.isMeshPhongMaterial === true &&
        m.geometry?.type === "SphereGeometry",
      v,
    );
    return dbgNoWater;
  },
  // Per-frame console log of the nearest planet's atmosphere state (throttled ~5 Hz).
  log: (v = true) => (dbgAtmoLog = v),
  // Shell visualization: 0 off · 1 flat magenta (coverage/culling) · 2 elevation ·
  // 3 optical depth · 4 alpha. Watch how the magenta coverage changes as you tilt.
  viz: (n = 1) => system.setAtmoDebug(n),
};

// --- Player ship (modular, persisted layout) ---
const ship = new Ship();
const savedShip = loadSavedLayout();
let currentLayout: ShipLayout = validateLayout(savedShip?.layout) ?? DEFAULT_LAYOUT;
/** When the current layout was last saved (epoch ms) — cloud reconciliation. */
let currentLayoutTime = savedShip?.t ?? 0;
let built: BuiltShip = buildShipFromLayout(currentLayout, 0.58);
scene.add(built.group);

const trails = new ThrusterTrails(scene);
const flightSpeedLines = new FlightSpeedLines();
/** Shield mode's only visual: a hexagon flares on the barrier where an absorbed hit came from. */
const shieldHex = new ShieldHex(scene, new THREE.Color(DEFAULT_STYLE.accent));
trails.setTips(built.thrusterTips);
// Player propulsion is rendered by the ten crystal thrusters on the Avatar.
// Ground-vehicle boost trails: one stream per rear exhaust glow (see
// ProceduralSpeederView.rearExhaustPoints). Same planet-anchored ribbon as the
// player streams; intensity is speed baseline + boost spike, tinted to the glow.
const vehicleBoostStreams = new BoostStreams(scene, 3);
vehicleBoostStreams.setColor(new THREE.Color(0x56d9ff));
const vehicleExhaustSrc: THREE.Vector3[] = [];
// Interpolated planet pose for ground-anchored FX (the boost trails). Matches the frame
// the terrain renders at (system.update's lerp), so the streak doesn't shear off the
// fast-orbiting/​spinning ground in atmosphere and read as jitter.
const _fxPose = { center: new THREE.Vector3(), quat: new THREE.Quaternion(), invQuat: new THREE.Quaternion() };
const _fxAxis = new THREE.Vector3();
/** Fill _fxPose with a planet's interpolated render pose (center + spin), the
 * frame ground-anchored FX record and rebuild against. Shared by the on-foot
 * player streams and the ground-vehicle boost trails. */
function updateFxPose(planet: Planet, a: number): void {
  _fxPose.center.lerpVectors(planet.posePrev.center, planet.pose.center, a);
  let dAng = planet.pose.spinAngle - planet.posePrev.spinAngle;
  dAng -= Math.round(dAng / (Math.PI * 2)) * (Math.PI * 2);
  _fxAxis.set(planet.def.spin.axis[0], planet.def.spin.axis[1], planet.def.spin.axis[2]);
  _fxPose.quat.setFromAxisAngle(_fxAxis, planet.posePrev.spinAngle + dAng * a);
  _fxPose.invQuat.copy(_fxPose.quat).invert();
}
// Same, for the WEATHER anchor planet (which can be the nearest planet in FLIGHT, not footPlanet).
const _wxPose = { center: new THREE.Vector3(), quat: new THREE.Quaternion(), invQuat: new THREE.Quaternion() };
/** Scratch for the flora wind field: the player's planet-local position, and the
 *  planet-local tangent the air travels toward. */
const _windLocal = new THREE.Vector3();
const _windPart = new THREE.Vector3();
const _windDir = new THREE.Vector3();
const _wxAxis = new THREE.Vector3();
const _wxCam = new THREE.Vector3(); // camera in WORLD space for the precip view-coverage box
// Ship-trail anchor (fields point at the near planet's live interp group each frame).
const _trailAnchor: TrailAnchor = { gpos: new THREE.Vector3(), gquat: new THREE.Quaternion(), k: 0 };
const bolts = new BoltSystem(scene);

// Ground-effect ring under a low, slow ship (parked hover, recall pickup).
const hoverRing = new HoverRing(scene);
// The charge-arc ring COLORS now live in the SVG (hud.ts): outer cyan = boost-run, inner amber = jump charge.
const arcWorld = new THREE.Vector3(); // scratch for projecting the player's feet to the charge arc
const miningBeam = new MiningBeam(scene);

let resourceNodes: ResourceNodes | null = new ResourceNodes(system);
/** Last authoritative depletion value per node. A secret manifest can arrive
 * after the initial snapshot, so rebuilding the overlay reapplies this map. */
const latestNodeState = new Map<number, number>();

function rebuildRevealedResources(): void {
  resourceNodes?.dispose();
  resourceNodes = new ResourceNodes(system);
  resourceNodes.setLeft([...latestNodeState].map(([id, left]) => ({ id, left })));
}

// Homesteads (player land claims) — ON by default. Opt out with hl-homesteads=0
// in localStorage (+ reload) or ?homesteads=0; the headless harness still passes
// ?homesteads=1, which stays on. Markers ride planet.group; fed by hsState/hsDelta.
const HOMESTEADS_ON =
  !((typeof localStorage !== "undefined" && localStorage.getItem("hl-homesteads") === "0") ||
    (typeof location !== "undefined" && location.search.includes("homesteads=0")));

// Founding replaces the legacy homecoming/crawl for NEW characters. Existing
// character records remain authoritative and resume their persisted stage.
let KICKOFF_ON = true;

// Social (chat, phase 1) — ON by default since Phase 1 verified in-game
// (2026-07-18); hl-social is now a KILL-SWITCH (console: hl-social=0 + reload,
// or ?social=0). ENTER opens the input; the Input typing gate + the panel's
// key swallowing keep gameplay blind while typing.
const SOCIAL_ON = !(
  (typeof localStorage !== "undefined" && localStorage.getItem("hl-social") === "0") ||
  (typeof location !== "undefined" && location.search.includes("social=0"))
);
let homesteadMarkers: HomesteadMarkers | null = HOMESTEADS_ON ? new HomesteadMarkers(system) : null;
let homesteadPieces: HomesteadPieces | null = HOMESTEADS_ON ? new HomesteadPieces(system) : null;
let homesteadResidentLife: HomesteadResidentLife | null = HOMESTEADS_ON ? new HomesteadResidentLife(scene) : null;
const homesteadBuild: HomesteadBuild | null = HOMESTEADS_ON ? new HomesteadBuild() : null;
// E in build mode opens the piece picker; a pick re-arms the hammer and closes.
const buildPalette: BuildPalette | null = HOMESTEADS_ON
  ? new BuildPalette({
    onPick: (t) => {
      if (!homesteadBuild) return;
      homesteadBuild.select(t); // fresh pick starts straight-up on the ground
      audio.sfx("ui.select");
      hud.showToast(PIECE_CATALOG[t]?.name.toUpperCase() ?? "", 1200);
    },
    onClose: () => input.requestPointerLock(),
  })
  : null;
/** Wire the piece-collision hooks onto every planet of the CURRENT system —
 *  the character consumes them like the settlement/scatter colliders. Called
 *  at init and again after each system switch (fresh Planet objects). */
function wireHomesteadColliders(): void {
  if (!homesteadPieces) return;
  for (const planet of system.planets) {
    const id = planet.def.id;
    planet.homesteadStandRadius = (x, y, z) => homesteadPieces?.standRadiusAt(id, x, y, z) ?? null;
    planet.resolveHomesteadCollision = (x, y, z, r, out) => homesteadPieces?.resolveAt(id, x, y, z, r, out) ?? false;
    // Interiors P1: the other two solid-geometry hooks were never wired for
    // player builds — the camera boom clipped through placed walls and bolts
    // flew through them. (Cache pads overwrite all four AFTER switchSystem —
    // liveCache.ts assignment order — so this composes with them unchanged.)
    planet.homesteadOccludes = (x, y, z, r) => homesteadPieces?.occludesAt(id, x, y, z, r) ?? false;
    planet.homesteadSegmentHit = (ax, ay, az, bx, by, bz) =>
      homesteadPieces?.segmentHitAt(id, ax, ay, az, bx, by, bz) ?? -1;
  }
}
if (HOMESTEADS_ON) wireHomesteadColliders();

// Homestead panel (B on foot): place beacon + current-claim management + the
// cross-system "my homesteads" roster with warp targets.
let claimPanelBeaconId: string | null = null;
/** The owner's beacons across ALL systems (hsState.mine, validated). */
let hsMine: Beacon[] = [];
/** HUD-tracked homestead (the ⌂ body marker), toggled from the panel. */
let hsTrackedId: string | null = null;
let hsSnapshotReady = false;
/** Latest owner-only production snapshots. They are discarded on system
 * switches by beacon id naturally and refreshed whenever the panel opens. */
const settlementStates = new Map<string, SettlementStateV1>();
const settlementPending = new Set<string>();
const settlementSeenEvents = new Set<string>();
const settlementSeenReadiness = new Set<string>();
let settlementSeq = 0;
const hsTrackV = new THREE.Vector3();
let lastFoundingStep = "";

function foundingBeaconHereOrCurrent(): Beacon | null {
  if (activeCharacterRecord?.stage !== "prologue:founding") return null;
  return hsMine.find((beacon) => systemIdOfPlanetId(beacon.planetId) === SYSTEM_ID) ?? null;
}

function currentFoundingStatus() {
  const beacon = foundingBeaconHereOrCurrent();
  const site = beacon ? homesteadPieces?.residentSiteOf(beacon.id) ?? null : null;
  if (!beacon || !site) return null;
  return settlementFoundingStatus(site.rooms, site.pieces, settlementStates.get(beacon.id) ?? null);
}

function foundingObjectiveLine(): string | null {
  if (activeCharacterRecord?.stage !== "prologue:founding") return null;
  const beacon = foundingBeaconHereOrCurrent();
  if (!beacon) return "FOUNDING: B — PLACE YOUR HOMESTEAD BEACON";
  if (mode === "foot" && ownBeaconHere()?.id !== beacon.id) return "FOUNDING: FOLLOW ⌂ TO YOUR HOMESTEAD";
  const status = currentFoundingStatus();
  switch (status?.step) {
    case "seal-room": return "FOUNDING: H BUILD · E PIECES — SEAL A ROOM WITH A DOOR";
    case "furnish-dwelling": return "FOUNDING: PLACE A BED + STANDING LAMP";
    case "furnish-biogenerator": return "FOUNDING: PLACE THE BIOGENERATOR FARM PLOT";
    case "add-storage": return "FOUNDING: PLACE A CHEST + SHELF";
    case "connect-home-work": return "FOUNDING: CONNECT THE BED TO THE FARM PLOT";
    case "invite-founder": return `FOUNDING: B — INVITE ${SETTLEMENT_FOUNDER.name.toUpperCase()}`;
    case "furnish-second-dwelling": return `FOUNDING: BUILD ${SETTLEMENT_SECOND_FOUNDER.name.split(" ")[0]!.toUpperCase()} A BED + LIGHT`;
    case "furnish-synthesizer": return "FOUNDING: PLACE THE MATERIA SYNTHESIZER";
    case "connect-synthesizer-home": return "FOUNDING: CONNECT THE SECOND HOME TO THE SYNTHESIZER";
    case "invite-second-founder": return `FOUNDING: B — INVITE ${SETTLEMENT_SECOND_FOUNDER.name.toUpperCase()}`;
    case "complete": return "HOMESTEAD ESTABLISHED";
    default: return "FOUNDING: H — BUILD THE FOUNDER'S CABIN";
  }
}

function foundingRecommendedPieceIds(): string[] {
  return currentFoundingStatus()?.recommendedPieceIds ?? [];
}

function announceFoundingStep(): void {
  if (activeCharacterRecord?.stage !== "prologue:founding") { lastFoundingStep = ""; return; }
  const beacon = foundingBeaconHereOrCurrent();
  const step = beacon ? currentFoundingStatus()?.step ?? "seal-room" : "place-beacon";
  if (step === lastFoundingStep) return;
  lastFoundingStep = step;
  const text: Record<string, string> = {
    "place-beacon": "FOUND A HOMESTEAD — PRESS B TO PLACE YOUR BEACON",
    "seal-room": "CLAIM STAKED — BUILD A SEALED ROOM",
    "furnish-dwelling": "ROOM SEALED — ADD A BED AND LIGHT",
    "furnish-biogenerator": "DWELLING READY — PLACE THE BIOGENERATOR FARM PLOT",
    "add-storage": "BIOGENERATOR READY — ADD A CHEST AND SHELF",
    "connect-home-work": "CONNECT THE BED TO THE FARM PLOT",
    "invite-founder": `CABIN READY — PRESS B TO INVITE ${SETTLEMENT_FOUNDER.name.toUpperCase()}`,
    "furnish-second-dwelling": `${SETTLEMENT_FOUNDER.name.split(" ")[0]!.toUpperCase()} IS SETTLED — BUILD A SECOND BED AND LIGHT`,
    "furnish-synthesizer": "SECOND HOME READY — PLACE THE MATERIA SYNTHESIZER",
    "connect-synthesizer-home": "CONNECT THE SECOND HOME TO THE SYNTHESIZER",
    "invite-second-founder": `SYNTHESIZER READY — PRESS B TO INVITE ${SETTLEMENT_SECOND_FOUNDER.name.toUpperCase()}`,
    complete: "HOMESTEAD ESTABLISHED",
  };
  hud.showSystemBanner(text[step] ?? "FOUNDING UPDATED");
}

function newSettlementProgressionNotices(beaconId: string, state: SettlementStateV1): string[] {
  const notices: string[] = [];
  const rooms = homesteadPieces?.roomsOf(beaconId) ?? [];
  const growth = settlementGrowthStatus(state, rooms);
  if (growth.ready && growth.nextName) {
    const key = `${beaconId}:stage:${growth.nextStage}`;
    if (!settlementSeenReadiness.has(key)) {
      settlementSeenReadiness.add(key);
      notices.push(`${growth.nextName.toUpperCase()} ADVANCEMENT READY`);
    }
  }
  for (const facility of state.facilities) {
    const upgrade = facilityUpgradeAfter(facility.grade);
    if (!upgrade || !upgrade.costs.every((stack) => (state.storage[stack.id] ?? 0) >= stack.n)) continue;
    const room = rooms.find((candidate) =>
      candidate.functionAnchors[facility.roomFunction] === facility.anchorPid);
    if (!room) continue;
    const key = `${beaconId}:${facility.id}:grade:${upgrade.toGrade}`;
    if (!settlementSeenReadiness.has(key)) {
      settlementSeenReadiness.add(key);
      notices.push(`${facility.roomFunction.toUpperCase()} L${upgrade.toGrade} BUILDING UPGRADE READY`);
    }
  }
  for (const resident of state.residents) {
    if (resident.rank >= SETTLEMENT_PROGRESSION_MAX) continue;
    const target = (resident.rank + 1) as keyof typeof RESIDENT_RANK_XP;
    const facility = state.facilities.find((candidate) => {
      const room = rooms.find((entry) => entry.functionAnchors[candidate.roomFunction] === candidate.anchorPid);
      return resident.workplacePid !== null
        && resident.facilityId === candidate.id
        && (room?.functionSlots[candidate.roomFunction].includes(resident.workplacePid) ?? false);
    });
    if (!facility || resident.xp < RESIDENT_RANK_XP[target]) continue;
    const key = `${beaconId}:${resident.id}:rank:${target}`;
    if (!settlementSeenReadiness.has(key)) {
      settlementSeenReadiness.add(key);
      notices.push(`${resident.name.toUpperCase()} READY FOR RANK ${target}`);
    }
  }
  return notices;
}

function announceHomesteadRoomChange(beforeRooms: ReturnType<HomesteadPieces["roomsOf"]>,
  afterRooms: ReturnType<HomesteadPieces["roomsOf"]>): void {
  const beforeFunctions = new Set(beforeRooms.flatMap((room) => room.functions));
  const afterFunctions = new Set(afterRooms.flatMap((room) => room.functions));
  if (beforeRooms.length === 0 && afterRooms.length > 0) hud.showToast("ROOM SEALED", 1600);
  if (beforeRooms.length > 0 && afterRooms.length === 0) hud.showToast("ROOM INVALIDATED — PRODUCTION PAUSED", 2600);
  for (const fn of afterFunctions) if (!beforeFunctions.has(fn)) hud.showToast(`${fn.toUpperCase()} RECOGNIZED`, 1800);
  for (const fn of beforeFunctions) if (!afterFunctions.has(fn)) {
    hud.showToast(`${fn.toUpperCase()} INVALIDATED — PRODUCTION PAUSED`, 2600);
  }
}
const claimPanel: ClaimPanel | null = HOMESTEADS_ON
  ? new ClaimPanel({
    onExpand: (beaconId, tier) => {
      net?.sendExpand({ seq: ++hsSeq, beaconId, tier });
      hud.showToast("UPGRADING CLAIM…", 1200);
    },
    onPermitAdd: (beaconId, playerId) => {
      net?.sendPermit({ seq: ++hsSeq, beaconId, op: "add", player: playerId });
    },
    onPermitRemove: (beaconId, uid) => {
      net?.sendPermit({ seq: ++hsSeq, beaconId, op: "remove", uid });
    },
    onPlaceBeacon: () => enterBeaconMode(),
    onTrack: (beaconId) => {
      // Toggle the HUD ⌂ tracker on an in-system claim.
      if (hsTrackedId === beaconId) {
        hsTrackedId = null;
        hud.showToast("HOMESTEAD TRACKING CLEARED", 1400);
      } else {
        hsTrackedId = beaconId;
        hud.showToast("TRACKING HOMESTEAD ⌂ — FOLLOW THE MARKER", 2200);
      }
    },
    onSettlementRefresh: (beaconId) => requestSettlementState(beaconId),
    onSettlementBootstrap: (beaconId) => {
      net?.sendSettlement({ seq: ++settlementSeq, beaconId, op: "bootstrap" });
      hud.showToast("ESTABLISHING SETTLEMENT…", 1400);
    },
    onSettlementDeposit: (beaconId, item, n) => {
      if (n < 1) return;
      net?.sendSettlement({ seq: ++settlementSeq, beaconId, op: "deposit", item, n });
    },
    onSettlementWithdraw: (beaconId, item, n) => {
      if (n < 1) return;
      net?.sendSettlement({ seq: ++settlementSeq, beaconId, op: "withdraw", item, n });
    },
    onSettlementQueue: (beaconId, facilityId, recipeId, workerIds) => {
      net?.sendSettlement({ seq: ++settlementSeq, beaconId, op: "queue", facilityId, recipeId, workerIds });
      hud.showToast("WORK ORDER QUEUED…", 1200);
    },
    onSettlementTarget: (beaconId, facilityId, recipeId, targetQuantity, workerIds) => {
      net?.sendSettlement({
        seq: ++settlementSeq, beaconId, op: "setTarget", facilityId,
        ...(recipeId === null ? {} : { recipeId }), targetQuantity, workerIds,
      });
      hud.showToast(recipeId === null ? "PRODUCTION TARGET CLEARED" : "PRODUCTION TARGET SET", 1400);
    },
    onSettlementUpgradeFacility: (beaconId, facilityId) => {
      net?.sendSettlement({ seq: ++settlementSeq, beaconId, op: "upgradeFacility", facilityId });
      hud.showToast("INSTALLING FACILITY UPGRADE…", 1400);
    },
    onSettlementStoreBuy: (beaconId, offerId) => {
      net?.sendSettlement({ seq: ++settlementSeq, beaconId, op: "storeBuy", offerId });
      hud.showToast("PURCHASING FROM STOREFRONT…", 1400);
    },
    onSettlementFoundBank: (beaconId, anchorPid) => {
      net?.sendSettlement({ seq: ++settlementSeq, beaconId, op: "foundBank", targetAnchorPid: anchorPid });
      hud.showToast("ESTABLISHING THE BANK…", 1400);
    },
    onSettlementBankTransfer: (beaconId, n) => {
      net?.sendSettlement({ seq: ++settlementSeq, beaconId,
        op: n > 0 ? "bankDeposit" : "bankWithdraw", n: Math.abs(n) });
      hud.showToast(n > 0 ? "DEPOSITING CREDITS…" : "WITHDRAWING CREDITS…", 1400);
    },
    onSettlementGuildOrder: (beaconId, gop, orderId) => {
      net?.sendSettlement({ seq: ++settlementSeq, beaconId, op: gop, offerId: orderId });
      hud.showToast("GUILD ORDER UPDATED…", 1400);
    },
    onSettlementPromoteResident: (beaconId, residentId, facilityId, specialty) => {
      net?.sendSettlement({ seq: ++settlementSeq, beaconId, op: "promoteResident", residentId, facilityId,
        ...(specialty ? { specialty } : {}) });
      hud.showToast("PROMOTING RESIDENT…", 1400);
    },
    onSettlementReassignResident: (beaconId, residentId, homePid, workplacePid, facilityId) => {
      net?.sendSettlement({
        seq: ++settlementSeq, beaconId, op: "reassignResident",
        residentId, homePid, workplacePid, facilityId,
      });
      hud.showToast("UPDATING RESIDENT ROUTINE…", 1400);
    },
    onSettlementRelicenseFacility: (beaconId, facilityId, targetAnchorPid) => {
      net?.sendSettlement({
        seq: ++settlementSeq, beaconId, op: "relicenseFacility", facilityId, targetAnchorPid,
      });
      hud.showToast("MOVING FACILITY LICENSE…", 1400);
    },
    onSettlementRecruit: (beaconId, applicantId, applicantProfession, placement) => {
      net?.sendSettlement({
        seq: ++settlementSeq, beaconId, op: "recruit", applicantId,
        homePid: placement.homePid, workplacePid: placement.workplacePid, facilityId: placement.facilityId,
        applicantProfession,
      });
      hud.showToast("SENDING SETTLEMENT INVITATION…", 1400);
    },
    onSettlementAdvanceStage: (beaconId) => {
      net?.sendSettlement({ seq: ++settlementSeq, beaconId, op: "advanceStage" });
      hud.showToast("ADVANCING SETTLEMENT…", 1400);
    },
    onSettlementDecommission: (beaconId) => {
      net?.sendSettlement({ seq: ++settlementSeq, beaconId, op: "decommission" });
    },
    onWarp: (systemId, label) => {
      // Set the nav DESTINATION — the player still boards, charges the jump and
      // spends the fuel cell (the honest travel flow; never a teleport).
      const ref = parseSystemRef(systemId);
      if (!ref) return;
      // A cache id names a non-lattice INSTANCE (no coords to plot or reach-
      // test); the client can't enter one until the Phase 4 renderer lands.
      if (ref.kind === "cache") return;
      if (ref.kind === "home") {
        // Your own pocket: always jumpable (the C4 affordance). The lattice
        // guard would wrongly test the (0,0,0) placeholder coords.
        forceNavTarget({ sx: 0, sy: 0, sz: 0, name: "HOME", id: systemId });
      } else if (canJumpTo(ref.c[0], ref.c[1], ref.c[2])) {
        forceNavTarget({ sx: ref.c[0], sy: ref.c[1], sz: ref.c[2], name: label });
      } else {
        hud.showToast("OUT OF DIRECT RANGE — ROUTE VIA THE NAV CHART", 2800);
        return;
      }
      hud.showToast(`DESTINATION SET — ${label.toUpperCase()} · BOARD + CHARGE YOUR JUMP`, 3000);
    },
    onClose: () => {
      claimPanelBeaconId = null;
      input.requestPointerLock();
    },
  })
  : null;

/** Open (or refresh) the homestead panel; `current` = the own claim underfoot. */
function requestSettlementState(beaconId: string): void {
  if (!net || settlementPending.has(beaconId)) return;
  settlementPending.add(beaconId);
  net.sendSettlement({ seq: ++settlementSeq, beaconId, op: "state" });
  window.setTimeout(() => {
    if (!settlementPending.delete(beaconId)) return;
    if (claimPanel?.visible && claimPanelBeaconId === beaconId) {
      openClaimPanel(hsMine.find((beacon) => beacon.id === beaconId) ?? null);
    }
  }, 8000);
}

function openClaimPanel(current: Beacon | null, refreshSettlement = false): void {
  if (!claimPanel) return;
  claimPanelBeaconId = current?.id ?? null;
  if (current && refreshSettlement) requestSettlementState(current.id);
  const mine = hsMine.map((b) => {
    const systemId = systemIdOfPlanetId(b.planetId);
    if (!systemId) return null;
    const ref = parseSystemRef(systemId);
    // Cache ids are non-lattice instance ids with no coords for generateSystem;
    // they fall back to the raw id until the Phase 4 renderer lands.
    const label = ref?.kind === "home" ? "HOME SYSTEM"
      : ref && ref.kind !== "cache" ? generateSystem(ref.c[0], ref.c[1], ref.c[2]).name : systemId;
    return {
      beaconId: b.id, systemId, label, tier: b.tier,
      here: systemId === SYSTEM_ID, tracked: b.id === hsTrackedId,
    };
  }).filter((r): r is NonNullable<typeof r> => r !== null);
  claimPanel.open({
    current: current
      ? { beacon: current, pieceCount: homesteadPieces?.piecesOf(current.id).length ?? 0 }
      : null,
    credits: rpg.credits,
    online: [...(net?.remotes.values() ?? [])].map((r) => ({ id: r.id, name: r.name })),
    mine,
    settlement: current ? {
      rooms: homesteadPieces?.roomsOf(current.id) ?? [],
      roomSolution: homesteadPieces?.residentSiteOf(current.id)?.rooms
        ?? { rooms: [], fields: [], tooLargeComponents: [] },
      pieces: homesteadPieces?.piecesOf(current.id) ?? [],
      state: settlementStates.get(current.id) ?? null,
      inventory: Object.fromEntries(Object.keys(ITEMS).map((id) => [id, countItem(rpg.inventory, id)])),
      syncing: settlementPending.has(current.id),
    } : null,
  });
}

// ── Beacon placement ghost (build-mode style: aim, validity tint, LMB plants) ──
let hsBeaconMode = false;
let hsBeaconGhost: THREE.Group | null = null;
let hsBeaconMat: THREE.MeshBasicMaterial | null = null;
let hsBeaconValid = false;
let hsBeaconAnchor: [number, number, number] | null = null;
const _hsBgCam = new THREE.Vector3();
const _hsBgFwd = new THREE.Vector3();
const _hsBgInv = new THREE.Quaternion();
const _hsBgP = new THREE.Vector3();

function enterBeaconMode(): void {
  if (!homesteadMarkers) return;
  if (mode !== "foot" || !footPlanet) {
    hud.showToast("STAND ON A PLANET TO PLACE A BEACON", 1800);
    return;
  }
  hsBeaconMode = true;
  homesteadMarkers.showAllBoundaries = true; // see EVERY claim while siting
  hud.showToast("PLACE BEACON — AIM · LMB PLANT · B CANCEL", 2600);
}

function exitBeaconMode(): void {
  hsBeaconMode = false;
  hsBeaconValid = false;
  hsBeaconAnchor = null;
  if (homesteadMarkers) homesteadMarkers.showAllBoundaries = false;
  if (hsBeaconGhost) {
    hsBeaconGhost.parent?.remove(hsBeaconGhost);
    hsBeaconGhost.traverse((o) => { if (o instanceof THREE.Mesh) o.geometry.dispose(); });
    hsBeaconGhost = null;
  }
}

/** RENDER phase: camera-axis ground aim + validity mirror of the server's
 *  canClaim (overlap vs every claim on the planet, town footprint, caps). */
function updateBeaconGhostVisual(): void {
  if (!hsBeaconMode) return;
  if (mode !== "foot" || !footPlanet || !homesteadMarkers) { exitBeaconMode(); return; }
  const planet = footPlanet;
  const g = planet.group;
  _hsBgInv.copy(g.quaternion).invert();
  camera.getWorldDirection(_hsBgFwd);
  const fwd = _hsBgFwd.applyQuaternion(_hsBgInv).normalize();
  _hsBgCam.copy(camera.position).sub(g.position).applyQuaternion(_hsBgInv);
  _hsBgP.copy(character.pos).sub(origin).sub(g.position).applyQuaternion(_hsBgInv);
  const along = Math.max(0, _hsBgP.sub(_hsBgCam).dot(fwd));
  // March the crosshair ray to the terrain (same contract as piece placement).
  const tFar = along + BUILD_REACH_M * 1.6;
  let anchor: [number, number, number] | null = null;
  for (let i = 0; i <= 14; i++) {
    const t = along * 0.4 + ((tFar - along * 0.4) * i) / 14;
    _hsBgP.copy(_hsBgCam).addScaledVector(fwd, t);
    const l = _hsBgP.length() || 1;
    const surf = surfaceSupportRadius(planet.sampler, _hsBgP.x / l, _hsBgP.y / l, _hsBgP.z / l);
    if (l <= surf) {
      _hsBgP.multiplyScalar(surf / l); // seat exactly on the ground
      anchor = [_hsBgP.x, _hsBgP.y, _hsBgP.z];
      break;
    }
  }
  if (!anchor) {
    // No ground under the crosshair: seat at reach, clamped to the surface.
    _hsBgP.copy(_hsBgCam).addScaledVector(fwd, tFar);
    const l = _hsBgP.length() || 1;
    const surf = surfaceSupportRadius(planet.sampler, _hsBgP.x / l, _hsBgP.y / l, _hsBgP.z / l);
    _hsBgP.multiplyScalar(surf / l);
    anchor = [_hsBgP.x, _hsBgP.y, _hsBgP.z];
  }
  hsBeaconAnchor = anchor;
  // Client mirror of the server's claim rules (the server re-validates).
  const uid = currentUid();
  const radius = tierDef(0).radius;
  hsBeaconValid = uid !== null && canClaim({
    isHome: homeOwner(SYSTEM_ID) === uid,
    ownerUid: uid,
    anchor,
    tier: 0,
    existing: homesteadMarkers.beaconsOn(planet.def.id),
    townInfluence: claimTownInfluence(planet.sampler, anchor, radius),
  }).ok;
  // Ghost: post + claim-boundary cylinder, cyan valid / red floating.
  if (!hsBeaconGhost) {
    hsBeaconMat = new THREE.MeshBasicMaterial({
      color: 0x2fd8ff, transparent: true, opacity: 0.3, depthWrite: false,
      side: THREE.DoubleSide, blending: THREE.AdditiveBlending,
    });
    const group = new THREE.Group();
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.22, 2.4, 0.22), hsBeaconMat);
    post.position.y = 1.2;
    group.add(post);
    const wall = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, 8, 64, 1, true), hsBeaconMat);
    wall.position.y = 2;
    group.add(wall);
    hsBeaconGhost = group;
  }
  if (hsBeaconGhost.parent !== g) g.add(hsBeaconGhost);
  hsBeaconGhost.position.set(anchor[0], anchor[1], anchor[2]);
  _hsBgP.set(anchor[0], anchor[1], anchor[2]).normalize();
  hsBeaconGhost.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), _hsBgP);
  hsBeaconMat?.color.setHex(hsBeaconValid ? 0x2fd8ff : 0xff5a4a);
}

/** SIM tick: LMB plants the aimed beacon (server validates authoritatively). */
function driveBeaconPlace(): void {
  if (!hsBeaconMode || !net) return;
  const now = performance.now();
  if (now - hsActionAt < 280) return;
  if (input.wasButtonPressed(0) && footPlanet && hsBeaconAnchor) {
    hsActionAt = now;
    if (!hsBeaconValid) {
      hud.showToast("CAN'T CLAIM HERE", 1600);
      return;
    }
    net.sendClaim({ seq: ++hsSeq, planetId: footPlanet.def.id, anchor: hsBeaconAnchor, tier: 0 });
    audio.sfx("build.place", { at: character.pos });
    exitBeaconMode();
  }
}

// Camera in a planet's RENDER-local frame (staged-bake distance driver).
// Cached per frame per planet id — several builds usually share one planet.
const _hsFocusCache = new Map<string, [number, number, number] | null>();
const _hsFocusTmp = new THREE.Vector3();
const _hsFocusInv = new THREE.Quaternion();
function hsFocusFor(planetId: string): [number, number, number] | null {
  if (_hsFocusCache.has(planetId)) return _hsFocusCache.get(planetId)!;
  const planet = system.planets.find((pl) => pl.def.id === planetId);
  let out: [number, number, number] | null = null;
  if (planet) {
    _hsFocusInv.copy(planet.group.quaternion).invert();
    _hsFocusTmp.copy(camera.position).sub(planet.group.position).applyQuaternion(_hsFocusInv);
    out = [_hsFocusTmp.x, _hsFocusTmp.y, _hsFocusTmp.z];
  }
  _hsFocusCache.set(planetId, out);
  return out;
}

let hsSeq = 0;
let hsActionAt = 0; // edge-debounce: the render loop sees wasPressed until the next sim tick
const _hsLocal = new THREE.Vector3(); // scratch: player world pos → planet-local anchor
const _hsFwd = new THREE.Vector3();
const _hsFwdLocal = new THREE.Vector3();
const _hsTarget = new THREE.Vector3();

// (The projected build reticle was removed — placement aligns with the MAIN
// centre crosshair instead: the aim ray is CAMERA-centred, so the ghost lands
// exactly where the fixed crosshair points. No moving DOM marker to jitter.)
const _hsCamLocal = new THREE.Vector3();
// Support-graph memo (recomputed when the beacon's piece set changes).
let hsSupportKey = "";
let hsSupported: Set<number> = new Set();
let hsFloatKey = "";
let hsFloatSet: number[] = [];

/**
 * Drive the build-mode ghost + inputs for this frame (foot mode, inside an
 * own/permitted claim). Combat stays untouched: every combat LMB path requires
 * character.aiming, and build placement requires NOT aiming — a clean partition.
 */
// Latest render-phase aim state, consumed by the sim-tick edge handler. All
// coordinates PLANET-LOCAL — frame-independent, so a pose computed against the
// render group is equally valid when the sim tick sends it to the server.
let hsVis: {
  beaconId: string;
  ghost: ReturnType<HomesteadBuild["placement"]>;
  picked: { pid: number; dist: number; centre: [number, number, number] } | null;
  placeable: boolean;
} | null = null;
const _hsGroupInv = new THREE.Quaternion();

/**
 * RENDER phase (runs AFTER system.update wrote this frame's interpolated
 * planet pose): the whole visual aim — camera ray, terrain march, ghost, pick,
 * highlight — computed ENTIRELY in the RENDER group's local frame. The ghost
 * is a child of planet.group, so a pose derived in that same frame renders
 * rock-solid on the crosshair. (Computing it in the sim tick from p.pose made
 * every placement sawtooth AND drift off the reticle — the cape jitter class.)
 */
function updateHomesteadVisual(): void {
  hsVis = null;
  if (!homesteadBuild?.active || !net || !homesteadMarkers || !homesteadPieces) return;
  if (buildPalette?.visible || claimPanel?.visible) return; // a panel owns the screen
  if (mode !== "foot" || !footPlanet) { homesteadBuild.hide(); return; }
  const planet = footPlanet;
  const g = planet.group;
  _hsGroupInv.copy(g.quaternion).invert();
  // Player, camera and aim in the RENDER group's local frame (group.position is
  // origin-rebased render space; the character's render pos = pos − origin).
  _hsLocal.copy(character.pos).sub(origin).sub(g.position).applyQuaternion(_hsGroupInv);
  const beacon = homesteadMarkers.beaconAt(planet.def.id, [_hsLocal.x, _hsLocal.y, _hsLocal.z]);
  const uid = currentUid();
  const permitted = beacon !== null && uid !== null
    && (beacon.ownerUid === uid || beacon.allowlist.includes(uid));
  if (!permitted) {
    homesteadBuild.hide();
    homesteadPieces.setHighlight(null, null);
    return;
  }
  camera.getWorldDirection(_hsFwd);
  _hsFwdLocal.copy(_hsFwd).applyQuaternion(_hsGroupInv).normalize();
  // CAMERA-centred aim ray: every point on it projects onto the fixed centre
  // crosshair, so the ghost lands exactly where the main reticle points.
  _hsCamLocal.copy(camera.position).sub(g.position).applyQuaternion(_hsGroupInv);
  const alongToPlayer = Math.max(0, _hsTarget.copy(_hsLocal).sub(_hsCamLocal).dot(_hsFwdLocal));
  const tNear = alongToPlayer * 0.4; // start ahead of the camera, before the player
  const tFar = alongToPlayer + BUILD_REACH_M;
  // March the ray to the terrain so ground placements stay ON the ray
  // (crosshair-true) instead of sliding off under a radial clamp.
  let tEnd = tFar;
  let hitGround = false;
  for (let i = 0; i <= 12; i++) {
    const t = tNear + ((tFar - tNear) * i) / 12;
    _hsTarget.copy(_hsCamLocal).addScaledVector(_hsFwdLocal, t);
    const l = _hsTarget.length() || 1;
    if (l <= surfaceSupportRadius(planet.sampler, _hsTarget.x / l, _hsTarget.y / l, _hsTarget.z / l)) {
      tEnd = t; hitGround = true; break;
    }
  }
  if (hitGround) {
    let lo = Math.max(tNear, tEnd - (tFar - tNear) / 12);
    let hi = tEnd;
    for (let i = 0; i < 5; i++) { // bisect to a clean surface point
      const mid = (lo + hi) / 2;
      _hsTarget.copy(_hsCamLocal).addScaledVector(_hsFwdLocal, mid);
      const l = _hsTarget.length() || 1;
      if (l <= surfaceSupportRadius(planet.sampler, _hsTarget.x / l, _hsTarget.y / l, _hsTarget.z / l)) hi = mid;
      else lo = mid;
    }
    tEnd = lo; // rest ON the surface, not inside it
  }
  _hsTarget.copy(_hsCamLocal).addScaledVector(_hsFwdLocal, tEnd);
  // FURNITURE aim (settlement-loop P0): the ray also stops on placed-piece
  // surfaces — a bed aimed at an upper floor must land ON that floor, not on
  // the terrain beneath it — then settles onto the highest walkable top there.
  const holdingFurn = homesteadBuild.sel !== null && isFurniturePiece(homesteadBuild.sel);
  if (holdingFurn) {
    const sx = _hsCamLocal.x + _hsFwdLocal.x * tNear;
    const sy = _hsCamLocal.y + _hsFwdLocal.y * tNear;
    const sz = _hsCamLocal.z + _hsFwdLocal.z * tNear;
    const ht = homesteadPieces.segmentHitAt(planet.def.id,
      sx, sy, sz, _hsTarget.x, _hsTarget.y, _hsTarget.z);
    if (ht >= 0) {
      // Pull marginally back to the near side so the stand probe reads the top.
      tEnd = tNear + (tEnd - tNear) * Math.max(0, ht - 0.01);
      _hsTarget.copy(_hsCamLocal).addScaledVector(_hsFwdLocal, tEnd);
    }
  }
  // Numerical safety: never below the surface (nor below a piece floor when
  // placing furniture — standRadiusAt lifts onto walkable piece tops).
  const len = _hsTarget.length() || 1;
  const dirX = _hsTarget.x / len, dirY = _hsTarget.y / len, dirZ = _hsTarget.z / len;
  const surface = surfaceSupportRadius(planet.sampler, dirX, dirY, dirZ);
  const hsStand = holdingFurn
    ? homesteadPieces.standRadiusAt(planet.def.id, _hsTarget.x, _hsTarget.y, _hsTarget.z)
    : null;
  const r = Math.max(len, surface, hsStand ?? 0);
  const target: [number, number, number] = [dirX * r, dirY * r, dirZ * r];
  const holding = homesteadBuild.sel !== null;
  const isGrounded = (pp: Piece): boolean => groundedOn(planet.sampler, pp);
  const pieces = homesteadPieces.piecesOf(beacon.id);
  const piecesKey = `${beacon.id}:${pieces.length}:${pieces.length ? pieces[pieces.length - 1].pid : -1}`;

  let ghost: ReturnType<HomesteadBuild["placement"]> = null;
  let picked: { pid: number; dist: number; centre: [number, number, number] } | null = null;
  let placeable = false;
  if (holding) {
    // HOLDING: drive the ghost, mirror the server's support rule for the tint.
    homesteadPieces.setHighlight(null, null);
    const sockets = homesteadPieces.socketsNear(beacon.id, target, 8);
    ghost = homesteadBuild.update(g, target, [dirX, dirY, dirZ], sockets);
    if (ghost) {
      if (piecesKey !== hsSupportKey) {
        hsSupportKey = piecesKey;
        hsSupported = supportedSet(pieces, isGrounded);
      }
      placeable = placementSupported(
        { t: ghost.t, p: ghost.p, q: ghost.q, mat: ghost.mat }, pieces, hsSupported, isGrounded);
      homesteadBuild.setValid(placeable);
    }
  } else {
    // EMPTY HANDS: the crosshair picks the piece its ray ENTERS FIRST (exact
    // OBB test inside pickPiece — bounding spheres handed you the piece beside
    // the one you meant); highlight it + everything that would float without it
    // (the cascade the server removes on X). Reach is measured from the PLAYER,
    // not the camera, so backing the camera off doesn't extend your hammer.
    homesteadBuild.hide();
    picked = homesteadPieces.pickPiece(beacon.id,
      [_hsCamLocal.x, _hsCamLocal.y, _hsCamLocal.z],
      [_hsFwdLocal.x, _hsFwdLocal.y, _hsFwdLocal.z],
      alongToPlayer + DEMOLISH_REACH_M,
      hsVisPrevPick ?? undefined);
    if (picked) {
      const fKey = `${piecesKey}:${picked.pid}`;
      if (fKey !== hsFloatKey) {
        hsFloatKey = fKey;
        hsFloatSet = wouldFloat(pieces, picked.pid, isGrounded);
      }
      homesteadPieces.setHighlight(beacon.id, [picked.pid, ...hsFloatSet], picked.pid);
    } else {
      homesteadPieces.setHighlight(null, null);
    }
  }
  hsVisPrevPick = picked?.pid ?? null;
  if ((ghost?.snapped ?? false) && !hsPrevSnapped) audio.sfx("build.snap");
  hsPrevSnapped = ghost?.snapped ?? false;
  hsVis = { beaconId: beacon.id, ghost, picked, placeable };
}
let hsPrevSnapped = false;
let hsVisPrevPick: number | null = null;

/** SIM tick (before endTick — edge inputs are cleared there): inputs ONLY,
 *  acting on the latest render-phase aim (hsVis; planet-local = frame-safe). */
function driveHomesteadBuild(): void {
  if (!homesteadBuild?.active || !net) return;
  if (buildPalette?.visible || claimPanel?.visible) return;
  if (mode !== "foot" || !footPlanet) return;
  const holding = homesteadBuild.sel !== null;
  const now = performance.now();
  if (now - hsActionAt < 280) return; // rate guard matching the server limiter
  // E opens the piece picker (inventory-style grid; a pick closes it armed).
  if (input.wasPressed("KeyE") && buildPalette) {
    hsActionAt = now;
    const afford = PIECE_CATALOG.map((_, t) =>
      (pieceCatalogCost(t) ?? []).every((s) => countItem(rpg.inventory, s.id) >= s.n));
    buildPalette.open(homesteadBuild.sel ?? -1, afford, foundingRecommendedPieceIds());
    if (document.pointerLockElement === canvas) document.exitPointerLock();
    return;
  }
  // Q puts the held piece away — empty hands arm the demolish flow.
  if (input.wasPressed("KeyQ")) {
    hsActionAt = now;
    if (holding) {
      homesteadBuild.putDown();
      hud.showToast("HANDS EMPTY — AIM AT A PIECE · DELETE DEMOLISHES IT + WHAT IT SUPPORTS", 2600);
    }
    return;
  }
  if (input.wasPressed("BracketRight")) { hsActionAt = now; hud.showToast(homesteadBuild.cycle(1), 900); return; }
  if (input.wasPressed("BracketLeft")) { hsActionAt = now; hud.showToast(homesteadBuild.cycle(-1), 900); return; }
  // R cycles the SCROLL rotation axis (yaw → pitch → roll); scroll itself
  // rotates via the wheel listener (event-driven, not a sim-tick edge).
  if (input.wasPressed("KeyR")) { hsActionAt = now; hud.showToast(`ROTATE AXIS: ${homesteadBuild.cycleAxis()}`, 1100); return; }
  // V toggles snapping — free placement is the escape hatch for the placement
  // the socket grid can't express (and for when the grid is just in the way).
  if (input.wasPressed("KeyV")) {
    hsActionAt = now;
    const on = homesteadBuild.toggleSnap();
    audio.sfx("ui.select");
    hud.showToast(on ? "SNAP ON" : "FREE PLACEMENT — SNAP OFF", 1400);
    return;
  }
  // T steps to the next weld under the crosshair and PINS it: a snap you don't
  // want costs one keystroke instead of a fight with the mouse.
  if (input.wasPressed("KeyT") && holding) {
    hsActionAt = now;
    const n = homesteadBuild.cycleSnap();
    if (n === 0) hud.showToast(homesteadBuild.snapOn ? "NO SNAP POINT HERE" : "SNAP IS OFF (V)", 1200);
    else { audio.sfx("build.snap"); hud.showToast(`SNAP POINT — ${n} HERE`, 1000); }
    return;
  }
  // LMB places exactly what the ghost showed last frame (client-side support
  // pre-check; the server re-validates authoritatively).
  if (input.wasButtonPressed(0) && holding && hsVis?.ghost) {
    hsActionAt = now;
    if (!hsVis.placeable) {
      hud.showToast("NO SUPPORT — GROUND IT OR WELD TO YOUR BUILD", 1800);
      return;
    }
    const ghost = hsVis.ghost;
    net.sendPlace({ seq: ++hsSeq, beaconId: hsVis.beaconId, piece: { t: ghost.t, p: ghost.p, q: ghost.q, mat: ghost.mat } });
    audio.sfx("build.place", { at: character.pos });
    return;
  }
  // Delete demolishes the crosshair-picked piece — empty hands only.
  if (input.wasPressed("Delete")) {
    hsActionAt = now;
    if (holding) {
      hud.showToast("PRESS Q TO EMPTY YOUR HANDS FIRST", 1600);
      return;
    }
    if (hsVis?.picked) {
      net.sendDemolish({ seq: ++hsSeq, beaconId: hsVis.beaconId, pid: hsVis.picked.pid });
      audio.sfx("build.demolish", { at: character.pos });
    }
  }
}

/** The beacon whose claim sphere contains the player's feet AND is owned by the
 *  signed-in uid (abandon is strictly owner's — allowlist grants build only). */
function ownBeaconHere(): Beacon | null {
  if (!homesteadMarkers || !footPlanet) return null;
  const pose = footPlanet.pose;
  _hsLocal.copy(character.pos).sub(pose.center).applyQuaternion(pose.invQuat);
  const beacon = homesteadMarkers.beaconAt(footPlanet.def.id, [_hsLocal.x, _hsLocal.y, _hsLocal.z]);
  const uid = currentUid();
  return beacon !== null && uid !== null && beacon.ownerUid === uid ? beacon : null;
}

/** Hold-to-abandon duration (a tap plants/hints; only a deliberate hold destroys). */
const ABANDON_HOLD_S = 1.5;
let abandonHoldT = 0;
let abandonSentFor: string | null = null;

/** Frame-driven B-hold tracker: standing in your own claim and holding B for
 *  ABANDON_HOLD_S sends the abandon intent once (release re-arms). The server
 *  still owns the decision (owner-only, piece-empty). */
function driveBeaconAbandon(dt: number): void {
  if (!HOMESTEADS_ON || !net || mode !== "foot" || claimPanel?.visible) { abandonHoldT = 0; abandonSentFor = null; return; }
  if (!input.isDown("KeyB")) { abandonHoldT = 0; abandonSentFor = null; return; }
  const own = ownBeaconHere();
  if (!own) { abandonHoldT = 0; return; }
  abandonHoldT += dt;
  if (abandonHoldT >= ABANDON_HOLD_S && abandonSentFor !== own.id) {
    abandonSentFor = own.id; // once per hold
    net.sendAbandon({ seq: ++hsSeq, beaconId: own.id });
    hud.showToast("ABANDONING CLAIM…", 1400);
  }
}

/** Plant a claim beacon at the player's feet on the current foot planet. The server
 *  re-derives proximity from the same pose, validates ownership/economy/caps, and
 *  either broadcasts the new beacon or replies with a refusal toast (hsState.err). */
function plantBeaconHere(tier = 0): boolean {
  if (!net || !homesteadMarkers || !footPlanet) return false;
  const pose = footPlanet.pose;
  _hsLocal.copy(character.pos).sub(pose.center).applyQuaternion(pose.invQuat);
  net.sendClaim({ seq: ++hsSeq, planetId: footPlanet.def.id, anchor: [_hsLocal.x, _hsLocal.y, _hsLocal.z], tier });
  hud.showToast("PLANTING CLAIM…", 1200);
  return true;
}

// W2b grown settlements: terrain-conforming roads/walls/stairs + buildings,
// built lazily near the camera from each planet's resident RegionService graphs.
let settlementRenderer: SettlementRenderer | null = new SettlementRenderer(system);
// Auto-doors (2026-07-30): proximity-animated sliding leaves on hollow
// buildings' doorways — one pooled system, visual-only.
const doorLeaves = new DoorLeaves();
// OBSERVE (2026-08-02): unarmed RMB look reads what the reticle faces — first
// provider: building "signs" (type + residents/staff). Replaces the floating
// debug house labels. Providers resolve renderer/npcLife lazily (both rebuild
// on system change).
const observe = new ObserveSystem();
observe.addProvider(new BuildingObserveProvider({
  renderer: () => settlementRenderer,
  npcLife: () => npcLife,
}));
// Citizens: name + role when the reticle rests on a person. Registered after
// buildings but selection is nearest-along-ray, so a citizen in front of a
// wall wins over the wall.
observe.addProvider({
  observe: (planet, o, d, m) => {
    const grown = npcLife?.observeHit(planet.def.id, o, d, m) ?? null;
    const homestead = homesteadResidentLife?.observeHit(planet.def.id, o, d, m) ?? null;
    const hit = grown && homestead ? (grown.dist <= homestead.dist ? grown : homestead) : grown ?? homestead;
    const ownResident = homestead && hit === homestead && hsMine.some((beacon) => beacon.id === homestead.beaconId);
    return hit ? {
      dist: hit.dist,
      info: {
        title: hit.title,
        subtitle: hit.subtitle,
        lines: ownResident ? ["Look + F nearby — manage settlement"] : [],
      },
    } : null;
  },
});
// Vehicles: the spawned ground vehicle reads its name + the boarding hint.
observe.addProvider({
  observe: (planet, o, d, m) => {
    if (!vehicleSpawned) return null;
    _obsVeh.copy(groundVehicle.pos).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat);
    // Hull sample: centre lifted ~0.7 m, radius from the collider.
    const ul = 1 / Math.max(1e-9, _obsVeh.length());
    _obsVeh.multiplyScalar(1 + ul * 0.7);
    const ox = _obsVeh.x - o.x, oy = _obsVeh.y - o.y, oz = _obsVeh.z - o.z;
    const t = ox * d.x + oy * d.y + oz * d.z;
    if (t < 0.5 || t > m) return null;
    const px = ox - d.x * t, py = oy - d.y * t, pz = oz - d.z * t;
    const r = groundVehicle.def.collider.radius + 0.6;
    if (px * px + py * py + pz * pz > r * r) return null;
    return {
      dist: t,
      info: {
        title: groundVehicle.def.name,
        subtitle: "vehicle",
        lines: ["F (hold) — board"],
      },
    };
  },
});

// W4 residents — indexed lazily from resident RegionService graphs and posed
// analytically from seeds + world time (no per-resident network streaming).
let npcLife: RegionNpcLife | null = new RegionNpcLife(scene);
npcLife.setRenderer(settlementRenderer); // P-B2: anchor resolver reads retained buildings

// Server-authoritative NPC drones (rendered here, owned by the server).
const drones = new DroneSwarm(scene);
const wildlife = new WorldWildlife(scene);

// Near-field animated ocean (cosmetic; gameplay always uses the flat seaRadius,
// so the surface stays non-authoritative). The far static sea sphere lives on
// each planet group; this rides the scene and re-seats under the viewer.
const oceanPatch = new OceanPatch();
const riverReflection = new RiverReflection();
scene.add(oceanPatch.mesh);
// Reflection target resolution: a fraction of the canvas DRAWING BUFFER (so it tracks
// the display's pixel density — the old innerWidth×0.5 ignored devicePixelRatio and was
// ~1/4 canvas res on HiDPI), capped so the second scene pass stays bounded on big screens.
const REFL_QUALITY = 0.75;
const REFL_MAX_SIDE = 1600;
function syncReflectionSize(): void {
  const dpr = renderer.getPixelRatio();
  let w = window.innerWidth * dpr * REFL_QUALITY;
  let h = window.innerHeight * dpr * REFL_QUALITY;
  const longest = Math.max(w, h);
  if (longest > REFL_MAX_SIDE) {
    const s = REFL_MAX_SIDE / longest;
    w *= s;
    h *= s;
  }
  oceanPatch.setReflectionSize(w, h);
}
syncReflectionSize();
const oceanSunDir = new THREE.Vector3();
const oceanSunCol = new THREE.Color();
/** Sea planet the ocean patch is seated over this frame (for the reflection pass). */
let seaPlanetThisFrame: Planet | null = null;

// Water VFX: splashes on entry, ripple wakes while swimming/wading (local-only).
const waterFx = new WaterFx(scene);
const vehicleFx = new VehicleFx(scene, waterFx);
let prevWaterState: "land" | "wade" | "swim" = "land";

// Precipitation + the shared per-frame weather sample at the player (also drives the sky/fog
// response in updateAtmosphere). Deterministic from weatherAt(), so all clients see the same.
const precipFx = new PrecipFx(scene);
const weatherVeil = new WeatherVeil(scene); // far-field rain/snow curtain across the cell
const playerWeather: WeatherColumn = { cloud: 0, precip: 0, kind: 0, windx: 0, windy: 0, windz: 0, windSpeed: 0 };
const precipWeather = emptyWeather();
const precipLight = new THREE.Color();
const atmosphereAmbient = new THREE.Color();
const atmosphereUp = new THREE.Vector3();
const moonWeatherLight = new THREE.Color();
let atmosphereFrame: AtmosphereFrame | undefined;
let weatherAltitudeK = 1;
const rainWater: WaterColumn = { floorR: 0, floorDepth: 0, surfR: 0, surfDepth: 0, riverK: 0, flowSpeed: 0, fx: 0, fy: 0, fz: 0 };
const rainLocalDir = new THREE.Vector3();
// Deterministic ground aftermath at the player's column. Recomputed a few times a
// second (it changes slowly), with the shader uniforms eased toward it every frame.
const groundWx: WeatherGround = { wet: 0, snow: 0 };
let groundWxWet = 0; // smoothed shader value
let groundWxSnow = 0;
let groundWxTimer = 0; // seconds until next recompute
const _wDir = new THREE.Vector3();
const _wLocal = new THREE.Vector3();
// Interiors P1: inside-a-building signal (SettlementColliders.indoorsAt) —
// drives the precip gate, the interior light dim, and the short camera boom.
const _indoorLocal = new THREE.Vector3();
// Observe reticle ray scratch (planet-local camera origin + forward).
const _obsOrigin = new THREE.Vector3();
const _obsDir = new THREE.Vector3();
const _obsVeh = new THREE.Vector3();
const _obsQ = new THREE.Quaternion();
// Sticky talk-focus state (head-look flicker hysteresis).
let talkSticky: ReturnType<NonNullable<typeof npcLife>["nearest"]> | null = null;
let talkStickyMissS = 0;
let indoorsNow = false;
let indoorsK = 0;
// P2: warm interior lamp pool for the building the player stands in.
// ⚠ ALLOCATED ONCE AT STARTUP AND NEVER REPARENTED — the streetlamp-pool rule
// (renderer.ts): three.js recompiles EVERY material when the scene's light
// count changes, so creating/parenting lights on first house entry froze the
// game for a beat (2026-07-30 play finding). The pool lives in the scene root
// at intensity 0; per frame we set intensity and re-express the current
// building's planet-LOCAL lamp anchors through the interpolated render
// transform (localToWorld on planet.group — the carry ride, done by hand).
const INTERIOR_LIGHT_MAX = 3;
const interiorLights: THREE.PointLight[] = [];
for (let i = 0; i < INTERIOR_LIGHT_MAX; i++) {
  const l = new THREE.PointLight(0xffc987, 0, 11, 1.6);
  scene.add(l);
  interiorLights.push(l);
}
let interiorLightPlanet: import("./world/planet").Planet | null = null;
let interiorVolume = -1;
let interiorLampsLocal: number[] = [];
function updateInteriorLights(planet: import("./world/planet").Planet | null, volume: number, k: number): void {
  if (planet !== interiorLightPlanet || volume !== interiorVolume) {
    interiorLightPlanet = planet;
    interiorVolume = volume;
    interiorLampsLocal = planet !== null && volume >= 0 && planet.settlementColliders
      ? planet.settlementColliders.interiorLampsAt(volume) : [];
  }
  for (let i = 0; i < INTERIOR_LIGHT_MAX; i++) {
    const l = interiorLights[i];
    const has = interiorLightPlanet !== null && i * 3 + 2 < interiorLampsLocal.length;
    l.intensity = has ? 20 * k : 0; // 30 → 20: third softening (2026-07-30 user call)
    if (has && interiorLightPlanet !== null) {
      l.position.set(interiorLampsLocal[i * 3], interiorLampsLocal[i * 3 + 1], interiorLampsLocal[i * 3 + 2]);
      interiorLightPlanet.group.localToWorld(l.position);
    }
  }
}
// Rain dimpling water: a spawn budget + scratch for placing ripple rings on the water surface.
let rainRippleAccum = 0;
const _ripA = new THREE.Vector3();
const _ripB = new THREE.Vector3();
const _ripDir = new THREE.Vector3();
const _ripPt = new THREE.Vector3();
let wakeTimer = 0;
let entityWakeTimer = 0;
const _wfxDir = new THREE.Vector3();
const _wfxPos = new THREE.Vector3();
const _wfxVel = new THREE.Vector3();
/** Collider ids for the local player's own bounds. */
const LOCAL_CHAR = -2;
const LOCAL_SHIP = -3;
const localCharCollider = {
  kind: "char" as const,
  id: LOCAL_CHAR,
  pos: new THREE.Vector3(),
  radius: 1.1,
};
const localShipCollider = {
  kind: "ship" as const,
  id: LOCAL_SHIP,
  pos: new THREE.Vector3(),
  radius: 5,
};
const droneVelZero = new THREE.Vector3();
const fireDir = new THREE.Vector3();
const fireMuzzle = new THREE.Vector3();
/** A shot fired this SIM tick whose VISUAL bolt is spawned in the RENDER pass, from the muzzle as
 *  it is actually DRAWN this frame — see {@link flushFootBolt}. `null` = nothing queued. */
let pendingBolt: {
  speed?: number;
  damage: number;
  range?: number;
  wp: string;
  /** Spread half-angle sampled at the trigger pull (includes this shot's bloom). */
  spread: number;
  /** Convergence reach for the flush-time aim solve (the weapon's range). */
  reach: number;
} | null = null;
/** Pending Z draw/stow: the armed flip lands mid-gesture (null = no swap pending). */
let stowSwapTo: boolean | null = null;
let stowSwapT = 0;
/** Reticle-convergence scratch (tryFootFire): camera ray origin + converged aim point. */
const fireAimFrom = new THREE.Vector3();
const fireAimAt = new THREE.Vector3();
const _rcTo = new THREE.Vector3();

// --- On-foot character (phase 6: disembark & explore) ---
const character = new CharacterController();
const footCam = new FootCamera();
const avatar = new Avatar(scene, { crystalWings: true });
// Player weapons are built from the crystal wings (rifles → the crystal lance,
// the light blade → a crystal edge). NPC avatars keep the modelled weapons.
avatar.setCrystalWeapon("sniper");
// CLOTH SOLVER: the bone-chain cage (character/cloth.ts) is the default; the per-vertex PBD
// path (character/clothPbd.ts) is opt-in via ?pbdcloth=1 or window.__hl.devPbdCloth(). Read
// before the first setStyle, because the mesh split happens when a garment is mounted.
avatar.setPbdCloth(
  new URLSearchParams(location.search).get("pbdcloth") === "1" ||
  (typeof localStorage !== "undefined" && localStorage.getItem("hl-pbdcloth") === "1"),
);
// BVH colliders default ON for the PBD path; ?pbdbvh=0 / hl-pbdbvh=0 falls back to capsules.
avatar.setPbdBvh(!(
  new URLSearchParams(location.search).get("pbdbvh") === "0" ||
  (typeof localStorage !== "undefined" && localStorage.getItem("hl-pbdbvh") === "0")
));
let groundVehicle = new GroundVehicle(SCOUT_SPEEDER);
let groundVehicleView: VehicleView = createVehicleView(SCOUT_SPEEDER);
const groundVehicleCamera = new VehicleCamera();
scene.add(groundVehicleView.group);
groundVehicleView.group.visible = false;
let vehicleGround: PlanetVehicleGround | null = null;
let vehicleSpawned = false;
const vehicleVisPos = new THREE.Vector3();
const vehicleVisQuat = new THREE.Quaternion();
const vehicleRiderPos = new THREE.Vector3();
const vehicleRiderQuat = new THREE.Quaternion();
const vehicleMountRoot = new THREE.Vector3();
const vehicleMountRootQuat = new THREE.Quaternion();
const vehicleMountEntry = new THREE.Vector3();
const vehicleMountStartLocal = new THREE.Vector3();
const vehicleMountUp = new THREE.Vector3();
let vehicleMountTransition: VehicleMountTransition | null = null;
/** DEV only (`__hl.devVehicleExitScrub`): pin an exit at this clip time. */
let devExitScrub: number | null = null;
const loadedVehicleMountAnimations = new Map<string, LoadedHumanoidAnimation>();
const loadedVehicleDismountAnimations = new Map<string, LoadedHumanoidAnimation>();
const loadedVehicleBailAnimations = new Map<string, LoadedHumanoidAnimation>();
const animationSources: readonly [Readonly<Record<string, string>>, Map<string, LoadedHumanoidAnimation>, string][] = [
  [VEHICLE_MOUNT_ANIMATION_URLS, loadedVehicleMountAnimations, "mount"],
  [VEHICLE_DISMOUNT_ANIMATION_URLS, loadedVehicleDismountAnimations, "dismount"],
  [VEHICLE_BAIL_ANIMATION_URLS, loadedVehicleBailAnimations, "bail"],
];
for (const [urls, into, label] of animationSources) {
  for (const [vehicleId, url] of Object.entries(urls)) {
    void loadHumanoidAnimation(url).then((loaded) => {
      into.set(vehicleId, loaded);
    }).catch((error) => {
      console.warn(`Vehicle ${label} animation fallback (${vehicleId})`, error);
    });
  }
}
let vehicleMountPlayback: LoadedHumanoidAnimation | null = null;
let installedVehicleMountClip: LoadedHumanoidAnimation | null = null;
const vehicleMountConstraintTarget = new THREE.Vector3();
const vehicleImpactPoint = new THREE.Vector3();
const vehicleImpactNormal = new THREE.Vector3();
const vehicleSpawnRight = new THREE.Vector3();
const vehicleSpawnForward = new THREE.Vector3();
const vehicleSpawnSample = { point: new THREE.Vector3(), normal: new THREE.Vector3() };
/** Planet being walked on; null while flying. */
let footPlanet: Planet | null = null;
/** Flies the ship while the player is on foot (orbit + recall). */
let autopilot: Autopilot | null = null;
/** Interpolated render pose for the character (the ship's visPos pattern). */
const charVisPos = new THREE.Vector3();
const charVisQuat = new THREE.Quaternion();
/** DEV cloth test bed (window.__hl.devCloak): a standalone floating cape parented under the
 *  foot planet. Null when off. `follow` re-anchors it in front of the camera each frame. */
let devCloak: { avatar: Avatar; follow: boolean } | null = null;
/** How close (m) the character must be to the parked ship to board. */
const BOARD_RANGE = 12;
/** Additional reach beyond the chassis hull. Boarding is radial: no privileged
 * driver side, but the player must still be genuinely near the vehicle. */
const VEHICLE_BOARD_MARGIN = 1.5;

function vehicleBoardRange(): number {
  return groundVehicle.def.collider.radius + VEHICLE_BOARD_MARGIN;
}

function vehicleSpawnClearance(def: import("@hyperlight/shared").VehicleDef): number {
  if (def.probes.length === 0) return def.collider.halfHeight + 0.5;
  let total = 0;
  for (const probe of def.probes) total += probe.rideHeight - probe.position[1];
  return total / def.probes.length;
}

// --- RPG chassis: health, inventory, ammo (phase 6 slice 3) ---
const rpg = new PlayerRpg();
/** Landing harder than this (m/s) starts costing integrity. */
const SAFE_LANDING = 16;
const FALL_DMG_RATE = 1.5; // hp per m/s over the limit — a SMALL sting; the landing animation is the main consequence

/** Latch so a repeated authoritative hp:0 stream doesn't re-trigger the death UX. */
let serverDead = false;
// ── PLAYER DEATH (2026-08-02): ragdoll on the spot, then recover at the nearest town ──
let playerDyingT = 0;
const PLAYER_DEATH_S = 2.8;
const deathPresentation = new DeathPresentation();
const deathTarget = new THREE.Vector3();
// The camera can still belong to the previous origin when a hit arrives
// between the world rebase and the final camera update.
const deathCameraOrigin = new THREE.Vector3();
const deathRootLocal = new THREE.Vector3();
const deathRootRotationLocal = new THREE.Quaternion();
const deathPlanetInverse = new THREE.Quaternion();
const deathContact = makePlanetDeathResolver(() => footPlanet, () => origin, () => footPlanet?.group ?? null);
const deathFlash = document.createElement("div");
deathFlash.id = "death-flash";
deathFlash.setAttribute("aria-hidden", "true");
deathFlash.style.cssText = "position:fixed;inset:0;z-index:4;pointer-events:none;opacity:0;will-change:opacity;";
deathFlash.style.background = matchMedia("(prefers-reduced-motion: reduce)").matches
  ? "radial-gradient(ellipse at center,transparent 35%,#a5001d75 100%)"
  : "radial-gradient(ellipse at center,#c92c3818 12%,#b9142a85 65%,#a5001ddd 100%)";
document.body.appendChild(deathFlash);
import.meta.hot?.dispose(() => deathFlash.remove());
const _deathImp = new THREE.Vector3();
const deathVelocity = new THREE.Vector3();
const lastHitFromV = new THREE.Vector3();
let lastHitValid = false;

function resetPlayerDeath(): void {
  playerDyingT = 0;
  lastHitValid = false;
  deathPresentation.reset();
  deathFlash.style.opacity = "0";
  avatar.endRagdoll();
}

function doDeath(): void {
  /**
   * ⚠⚠ DYING INSIDE A CACHE MUST EJECT YOU FROM IT.
   *
   * The ordinary failsafe puts the body back in its ship — which, inside an
   * instance, leaves the player flying around a 60 km barren sphere with no
   * wormholes, no lattice coordinates, a nav chart that refuses `cache:` ids and
   * an aperture layer that (correctly) marks nothing: a dead end with no door.
   * Found the moment the live probe first entered a tier-1 cache and the garrison
   * killed it. So a death in an instance is the run ENDING — back to the aperture.
   */
  const ejecting = inCacheInstance();
  // ON-FOOT DEATH (2026-08-02, user call): the body RAGDOLLS where it fell (same verlet the
  // enemies use, flung away from the killing hit), holds the death cam ~3s, then the suit
  // recovers the pilot at the NEAREST TOWN — no more emergency recall to the ship.
  if (!ejecting && footPlanet) {
    if (playerDyingT > 0) return; // already mid-death
    playerDyingT = 1e-4;
    avatar.torsoCenterInto(deathTarget);
    deathPresentation.start(camera, deathTarget, deathCameraOrigin);
    cameraHandoff = null;
    // Preserve the displayed body pose and planetary carry, independently of
    // the controller that continues its own limp simulation during death.
    deathPlanetInverse.copy(footPlanet.group.quaternion).invert();
    deathRootLocal.copy(avatar.group.position).sub(footPlanet.group.position).applyQuaternion(deathPlanetInverse);
    deathRootRotationLocal.copy(avatar.group.quaternion).premultiply(deathPlanetInverse);
    deathVelocity.copy(character.vel);
    if (groundVehicle.occupied || vehicleMountTransition) {
      deathVelocity.copy(groundVehicle.vel);
      groundVehicle.vacateSeat();
      vehicleMountTransition = null;
      vehicleMountPlayback = null;
      avatar.setMounted(false);
      mode = returnMode = "foot";
    }
    if (lastHitValid) _deathImp.copy(character.pos).sub(lastHitFromV).normalize();
    else _deathImp.copy(character.heading).negate();
    _deathImp.multiplyScalar(3.4).addScaledVector(character.up, 1.2);
    avatar.startRagdoll(deathVelocity, { impactWorld: _deathImp, resolveContact: deathContact });
    // The single death pulse owns the vignette from here; a late damage echo
    // must not stack another full-strength hurt flash on top of it.
    const hurt = document.getElementById("hurt-vignette");
    if (hurt) { hurt.style.transition = "none"; hurt.style.opacity = "0"; }
    hud.showToast("SUIT INTEGRITY LOST", 2600);
    return; // restore + respawn happen when the ragdoll settles (respawnAtTown)
  }
  resetPlayerDeath();
  hud.showToast(
    ejecting ? "SUIT INTEGRITY LOST — EJECTED FROM THE CACHE" : "SUIT INTEGRITY LOST — EMERGENCY RECALL",
    4200,
  );
  // Come back the way you started: weapon stowed, with a full pool.
  rpg.armed = false;
  character.energy = character.energyMax;
  // Under server HP the server owns respawn: ask for it, then adopt the streamed full HP.
  // Otherwise (legacy) restore locally.
  if (SERVER_AUTHORITATIVE_HP) net?.sendRespawn();
  else rpg.restore();
  // AFTER the respawn request — leaving rebuilds the socket, and a respawn sent
  // down the new one would arrive before the join that gives it a room.
  if (ejecting) leaveCacheInstance();
}
/** Recover the dead pilot at the nearest settlement on the foot planet (worldgen seed roster —
 *  every settlement on the planet is a candidate, resident or not). Falls back to standing
 *  back up in place if the planet somehow has no towns. */
function respawnAtTown(): void {
  resetPlayerDeath();
  const pl = footPlanet;
  if (pl) {
    let bestDir: THREE.Vector3 | null = null;
    let bd = Infinity;
    const localDir = character.pos.clone().sub(pl.pose.center).applyQuaternion(_rsUnspin.copy(pl.group.quaternion).invert()).normalize();
    const reg = tierRegistryFor(pl.sampler);
    for (const packed of reg.byCell.keys()) {
      for (const seed of settlementSeeds(pl.sampler, unpackCell(packed))) {
        _rsDir.set(seed.dx, seed.dy, seed.dz);
        const d = _rsDir.distanceTo(localDir);
        if (d < bd) { bd = d; bestDir = _rsDir.clone(); }
      }
    }
    if (bestDir) {
      const worldDir = bestDir.applyQuaternion(pl.group.quaternion).normalize();
      const pos = pl.pose.center.clone().addScaledVector(worldDir, pl.surfaceRadiusWorld(worldDir) + 1.3);
      character.restoreAt([pos.x, pos.y, pos.z], [0, 0, 0, 1], pl);
    }
  }
  rpg.armed = false;
  character.energy = character.energyMax;
  if (SERVER_AUTHORITATIVE_HP) net?.sendRespawn();
  else rpg.restore();
  hud.showToast("RECOVERED AT THE NEAREST SETTLEMENT", 3600);
}
const _rsDir = new THREE.Vector3();
const _rsUnspin = new THREE.Quaternion();
// devRiver scratch (dev-only path; kept out of the hot loop's allocations anyway).
const _drDir = new THREE.Vector3();
const _drSide = new THREE.Vector3();

// Legacy path: rpg.damage() drove HP to 0 locally. Under SERVER_AUTHORITATIVE_HP the death is
// instead triggered by the authoritative hp stream (see net.onHealth).
rpg.onDeath = () => doDeath();

// Self-inflicted damage (fall/hazard). Under SERVER_AUTHORITATIVE_HP it's PREDICTED locally
// (instant bar drop) AND reported to the server, which owns HP + streams the confirmation;
// hazard is per-frame, so it's accumulated into whole-HP chunks to bound traffic. Otherwise
// applied locally for real.
let hazardAccum = 0;
function takeFall(amount: number): void {
  if (!(amount > 0)) return;
  if (SERVER_AUTHORITATIVE_HP) {
    rpg.predictDamage(amount);
    net?.sendSelfDamage(amount, "fall");
  } else rpg.damage(amount, "fall");
}
/** The worn kit as applied numbers, recomputed on any inventory rev (cheap). */
let armorNow = armorProfile({});
let lastArmorRev = -1;
function refreshArmorProfile(): void {
  // "Shields first" (v50): while the server refills the shield, the one stream
  // is spoken for — action-energy regen runs gated. Cheap; every frame.
  character.regenGate = rpg.shieldMax > 0 && rpg.shield < rpg.shieldMax - 0.5
    ? SHIELD_CHARGE_ENERGY_GATE : 1;
  if (rpg.rev === lastArmorRev) return;
  lastArmorRev = rpg.rev;
  armorNow = armorProfile(rpg.inventory.equipped);
  character.applyArmor(armorNow.burnMul, armorNow.regenMul);
}

// Emergency reserve (v50): ACTION spends short on energy draw from the shield,
// lossy (SHIELD_DRAW_RATIO shield per energy). Deliberate by construction —
// only spendEnergy() calls this, never passive movement burn.
/** Last shield→energy draw (ms) — the authoritative shield stream dips after a
 *  draw exactly like after a hit, and the reconcile bounce made onHealth flare
 *  the shield-impact hex for a conversion the player asked for. */
let lastShieldDrawAt = -1e9;
/** Last DIRECTIONAL shield flare (real contact point) — the stream fallback
 *  must not double-flare with its camera-faked direction right after. */
let lastDirectionalShieldFlareAt = -1e9;
character.shieldDraw = (need: number): number => {
  const drawPoints = Math.min(30, Math.ceil(need * SHIELD_DRAW_RATIO));
  const take = Math.min(Math.floor(rpg.shield), drawPoints);
  if (take < 1) return 0;
  lastShieldDrawAt = performance.now();
  rpg.shield -= take; // optimistic; the hp stream reconciles
  rpg.rev++;
  net?.sendShieldDraw(take);
  hud.showToast("SHIELD → ENERGY", 900);
  return take / SHIELD_DRAW_RATIO;
};

function takeHazard(amount: number): void {
  // SEAL (v49): the worn kit scales planet-hazard drain; a full seal is
  // immunity. Applied HERE so every hazard call site inherits it.
  amount *= armorNow.hazardScale;
  if (!(amount > 0)) return;
  if (!SERVER_AUTHORITATIVE_HP) { rpg.damage(amount, "hazard"); return; }
  hazardAccum += amount;
  if (hazardAccum >= 1) {
    const n = Math.floor(hazardAccum);
    hazardAccum -= n;
    rpg.predictDamage(n);
    net?.sendSelfDamage(n, "hazard");
  }
}

// Character appearance: applied locally at once; the broadcast + cloud
// save trail behind a debounce that respects the server's style cooldown.
let playerStyle: CharacterStyle = { ...DEFAULT_STYLE };
let styleTimer: number | null = null;

function applyStyle(style: CharacterStyle): void {
  playerStyle = style;
  avatar.setStyle(style);
  // The character editor owns its save and publishes the completed draft.
  if (mode === "outfit") return;
  publishStyleAfterCooldown();
}

/** Coalesce rapid edits so the server’s two-second restyle limit cannot drop the final look. */
function publishStyleAfterCooldown(persist = true): void {
  if (styleTimer !== null) clearTimeout(styleTimer);
  styleTimer = window.setTimeout(() => {
    styleTimer = null;
    net?.sendStyle(playerStyle);
    if (persist && authUser?.emailVerified) void saveStyle(playerStyle).catch(() => { });
  }, 2200);
}

/**
 * The WoW rule, re-derived: worn gear SHOWS unless the slot carries a deliberate
 * transmog (any non-default look — an expressed preference always wins).
 * equipGear applies this incrementally at the moment of wearing; this recomputes
 * it from the equipped map so RESTORE paths (login, the server inventory echo)
 * dress the character too — the saved style alone can miss the kit (a style save
 * raced logout, or predates the armor), while `equipped` is the durable truth.
 * Returns the input by reference when nothing changes.
 */
function styleShowingEquipped(style: CharacterStyle): CharacterStyle {
  return styleWithWornGear(style, rpg.inventory.equipped);
}

/** Input stub for "limp" simulation: menus never pause the world — the
 *  body keeps falling, sliding and draining; it just stops listening. */
const limpInput = {
  isDown: () => false,
  wasPressed: () => false,
  isButtonDown: () => false,
  consumeMouse: () => ({ dx: 0, dy: 0 }),
  endTick: () => { },
  requestPointerLock: () => { },
  pointerLocked: false,
} as unknown as Input;


/** The pilot's chosen home WORLD — any planet, set from the nav chart. */
let homeWorld: { system: string; planet: number } | null = null;

function homeWorldName(): string | null {
  if (!homeWorld) return null;
  const def = systemDefFor(homeWorld.system); // C4: bookmark may be a home planet
  return def?.planets[homeWorld.planet]?.name ?? null;
}

// --- Discovery: the galaxy is charted by flying it. The home base is
// always charted; uncharted space is reached by chaining jumps through
// systems you can SEE (the neighbor stars); anywhere you've been is a
// direct warp from then on. ---
const BASE_SYSTEM = "0:0:0";
/** One uncharted hop reaches this far (the visible neighbor-star shell). */
const JUMP_REACH = 3;
/** Charted system id → discovery time (epoch ms; 0 = time unknown). */
const discovered = new Map<string, number>([[BASE_SYSTEM, 0]]);

function recordDiscovery(id: string, announce: boolean): void {
  if (homeOwner(id) !== null) return; // C4: private pockets are never charted
  if (discovered.has(id)) return;
  discovered.set(id, Date.now());
  if (authAvailable) void addDiscovered(id);
  if (announce) hud.showToast(`SYSTEM CHARTED — ${system.def.name.toUpperCase()}`, 3000);
}

function canJumpTo(sx: number, sy: number, sz: number): boolean {
  if (discovered.has(`${sx}:${sy}:${sz}`)) return true;
  if (SYSTEM_COORDS === null) return false; // home pocket: leave via RETURN
  return Math.hypot(sx - SYSTEM_COORDS[0], sy - SYSTEM_COORDS[1], sz - SYSTEM_COORDS[2]) <= JUMP_REACH + 1e-6;
}

// Installed ship modules: loaded with the profile, applied to live stats.
const upgrades = { boost: false, pulse: false, mining: false };

function applyUpgrades(): void {
  ship.boostMul = upgrades.boost ? 4.55 : 3.5;
  ship.pulseTop = (upgrades.pulse ? 42000 : 32000) * WORLD_SCALE;
  ship.pulseResponse = upgrades.pulse ? 1.2 : 0.8;
  miningRate = upgrades.mining ? 1 / 0.6 : 1;
}

// --- Server-authoritative inventory (phase 8): every local mutation is
// OPTIMISTIC and mirrored by an intent; the server's InvStateMsg acks are
// the truth the client converges to. ---
let invSeq = 0;
let invAck = 0;
// First server account echo of this connection — doLaunch holds the reveal on
// it (the echo is what dresses the avatar when the profile mirror is stale).
let invEchoSeen = false;
let firstInvEchoResolve: (() => void) | null = null;
function invIntent(m: {
  op: "move" | "use" | "equip" | "craft" | "spend" | "equipGear" | "unequip";
  a?: number;
  b?: number;
  eq?: "primary" | "secondary" | EquipSlot;
  recipe?: string;
  id?: string;
  n?: number;
}): void {
  net?.sendInvIntent({ ...m, seq: ++invSeq });
}

/** Take off the item worn in `slot`, returning it to the bag (soulbound — it has
 *  been worn). Refuses without mutating when there is no square for it. */
function unequipGear(slot: EquipSlot): void {
  const worn = rpg.inventory.equipped[slot];
  if (!worn) return;
  if (!unequipSlot(rpg.inventory, slot)) {
    hud.showToast("BAG FULL — NO ROOM TO STOW", 2200);
    return;
  }
  rpg.rev++;
  invIntent({ op: "unequip", eq: slot });
  invPanel.render();
  characterCreator.refreshStats();
  hud.showToast(`${ITEMS[worn].name.toUpperCase()} STOWED`, 1500);
}

/** Wear the gear/weapon at bag slot `i`. The stack LEAVES the bag — the square
 *  comes back, which is the whole point of the equip slots. */
function equipGear(i: number): void {
  const stack = rpg.inventory.slots[i];
  if (!stack) return;
  const def = ITEMS[stack.id];
  const slot = def ? equipSlotFor(def) : null;
  if (!slot) return;
  equipFromSlot(rpg.inventory, i);
  rpg.rev++;
  invIntent({ op: "equipGear", a: i });
  // Wearing a piece SHOWS it, unless the player has deliberately transmogged that
  // slot — an expressed preference always wins (the WoW rule).
  const shown = styleShowingEquipped(playerStyle);
  if (shown !== playerStyle) applyStyle(shown);
  invPanel.render();
  characterCreator.refreshStats();
  hud.showToast(`${def!.name.toUpperCase()} EQUIPPED`, 1500);
}

// The selector uses owned main-hand weapons; selection goes through the same
// inventory intent as the equipment screen so multiplayer ownership stays intact.
const weaponSelector=new WeaponSelector(id=>{
  if(mode!=='foot'||uiOverlayActive()||menu.visible||playerDyingT>0||homesteadBuild?.active||hsBeaconMode)return;
  if(rpg.equippedWeapon()?.id!==id){
    const slot=rpg.inventory.slots.findIndex(stack=>stack?.id===id);
    if(slot<0)return;
    actionSys.cancelPendingPunch();avatar.cancelAction();stowSwapTo=null;
    equipGear(slot);
  }
  rpg.armed=true;audio.sfx('ui.select');
});
function updateWeaponSelector():void {
  const visible=mode==='foot'&&launched&&!inVoid&&!uiOverlayActive()&&!menu.visible&&playerDyingT<=0&&!homesteadBuild?.active&&!hsBeaconMode;
  if(!visible){weaponSelector.element.hidden=true;return;}
  const ids=new Set<string>();const equipped=rpg.equippedWeapon();if(equipped)ids.add(equipped.id);
  for(const stack of rpg.inventory.slots){
    const def=stack&&ITEMS[stack.id];if(def?.weapon&&equipSlotFor(def)==='mainhand')ids.add(def.id);
  }
  const options:WeaponOption[]=[...ids].sort().map(id=>{
    const def=ITEMS[id],w=def.weapon!;
    return {id,label:def.name,detail:weaponSummary(w),kind:w.melee?'blade':w.hold?.stance==='underhand'?'minigun':'rifle'};
  });
  weaponSelector.update(options,equipped?.id??'',true);
}

// NPC conversations: greeting derived locally, relationship state from the
// server's npcRel echo (state + salt, never text).
let talkingNpcId: string | null = null;
const playerConversationLook = new ConversationLook();
let lastPortraitMs = 0; // live talking-head render throttle (~24fps while the dialog is open)
const dialogPanel = new DialogPanel({
  onTalk: (npcId) => net?.sendTalk(npcId),
  onHold: (npcId) => net?.sendTalk(npcId, "hold"),
  onShop: request => net?.sendNpcShop(request) ?? false,
  onQuestAction: (a) => {
    if (a.op === "deliver") questIntent({ op: "deliver", q: a.q, npc: a.npc, ob: a.ob });
    else questIntent({ op: "turnin", q: a.q, npc: a.npc });
  },
  onClose: () => {
    if (talkingNpcId) { net?.sendTalk(talkingNpcId, "end"); npcLife?.avatarOf(talkingNpcId)?.faceRig.stopSpeaking(); }
    talkingNpcId = null;
    avatar.setHeadLookTarget(null);
    npcLife?.setFocus(null, false, character.pos);
    pauseSuppressUntil = performance.now() + 900;
    if (mode === "fly" || mode === "foot" || mode === "vehicle") input.requestPointerLock();
  },
  // the typewriter IS the voice: each revealed character drives the persona's mouth
  onType: (c) => { if (talkingNpcId) npcLife?.avatarOf(talkingNpcId)?.faceRig.feedChar(c); },
  onTypeEnd: () => { if (talkingNpcId) npcLife?.avatarOf(talkingNpcId)?.faceRig.stopSpeaking(); },
});

// Combat-energy upgrades (C wheel → YOU → STATS). The server owns credits AND levels, so the
// page only asks; `onInvState` (the account echo) is what actually moves a level. NOTE: the
// ship-perk `upgrades` map below is a different thing entirely — these live on rpg.upgrades.
function buyUpgrade(track: UpgradeTrack): void {
  if (!net) {
    characterCreator.setError("UPGRADES NEED A CONNECTION");
    return;
  }
  net.sendUpgrade(track, ++invSeq);
}

/** STATS page for the YOU screen: the vitals we track today (integrity, energy,
 *  credits) + the four combat-energy upgrade tracks (same markup/classes as the
 *  old standalone upgrades panel, so its styles carry over). Re-rendered via
 *  characterCreator.refreshStats() on every server account echo. */
// NOTE (2026-08-10): equipStandard retired with standard issue — weapons are
// drops equipped from the bag (right-click → mainhand), like armor.

function youStatsHtml(): string {
  // A LOOTED weapon in the mainhand supersedes the standard-issue picks below
  // (equippedWeapon() resolves it first), so it shows as the active card.
  const lootedId = rpg.inventory.equipped.mainhand;
  const looted = lootedId ? ITEMS[lootedId] ?? null : null;
  const hp = Math.max(0, Math.round(rpg.health.hp));
  const hpMax = Math.max(1, Math.round(rpg.health.max));
  const en = Math.max(0, Math.round(character.energy));
  const enMax = Math.max(1, Math.round(character.energyMax));
  const bar = (kind: string, value: number, max: number) =>
    `<div class="yu-bar ${kind}"><i style="width:${Math.round((value / max) * 100)}%"></i></div>`;
  const tracks = UPGRADE_TRACKS.map((track) => {
    const def = UPGRADE_DEFS[track];
    const level = rpg.upgrades[track];
    const cost = nextUpgradeCost(rpg.upgrades, track);
    const pips = Array.from({ length: MAX_LEVEL }, (_, i) => `<i class="${i < level ? "on" : ""}"></i>`).join("");
    const effect = def.levels[Math.min(level, MAX_LEVEL - 1)];
    const button = cost === null
      ? `<button type="button" disabled>MAX</button>`
      : `<button type="button" data-track="${track}"${rpg.credits < cost ? " disabled" : ""}>${cost.toLocaleString()} CR</button>`;
    return `
      <article class="up-track">
        <div class="up-head"><strong>${def.name}</strong><span class="up-pips">${pips}</span></div>
        <p class="up-blurb">${def.blurb}</p>
        <div class="up-buy"><span class="up-effect">${cost === null ? "Fully upgraded" : `Next: ${effect}`}</span>${button}</div>
      </article>`;
  }).join("");
  return `
    <div class="yu-vitals">
      <div class="yu-stat">
        <div class="yu-row"><span class="cc-kicker">INTEGRITY</span><strong>${hp} / ${hpMax}</strong></div>
        ${bar("yu-hp", hp, hpMax)}
      </div>
      <div class="yu-stat">
        <div class="yu-row"><span class="cc-kicker">ENERGY</span><strong>${en} / ${enMax}</strong></div>
        ${bar("yu-energy", en, enMax)}
      </div>
      <div class="yu-stat yu-credits">
        <div class="yu-row"><span class="cc-kicker">CREDITS</span><strong>${rpg.credits.toLocaleString()}</strong></div>
      </div>
    </div>
    <fieldset><legend>ARMOR — THE WORN KIT</legend>
      <p class="up-note">Kit, not curve: armor shapes where you can go, never how hard you hit.</p>
      <div class="yu-vitals">
        <div class="yu-stat"><div class="yu-row"><span class="cc-kicker">SHIELD</span><strong>${armorNow.shieldMax > 0 ? `${armorNow.shieldMax} · ${armorNow.shieldRegen.toFixed(1)}/s` : "—"}</strong></div></div>
        <div class="yu-stat"><div class="yu-row"><span class="cc-kicker">SOAK</span><strong>${Math.round(armorNow.soak * 100)}%</strong></div></div>
        <div class="yu-stat"><div class="yu-row"><span class="cc-kicker">SEAL</span><strong>${armorNow.seal} / ${ARMOR_SEAL_MAX}${armorNow.hazardScale <= 0 ? " · SEALED" : ""}</strong></div></div>
        <div class="yu-stat"><div class="yu-row"><span class="cc-kicker">FLOW</span><strong>${armorNow.regenMul >= 1 ? "+" : ""}${Math.round((armorNow.regenMul - 1) * 100)}% regen</strong></div></div>
        <div class="yu-stat"><div class="yu-row"><span class="cc-kicker">MASS</span><strong>+${Math.round((armorNow.burnMul - 1) * 100)}% burn</strong></div></div>
      </div>
    </fieldset>
    <fieldset><legend>EQUIPMENT</legend>
      <p class="up-note">Weapons are drops — equip one from your pack (right-click). Z draws and stows it.</p>
      <div class="yu-equip">
        ${looted
          ? `<button type="button" data-track="unequip:mainhand" class="on" title="${looted.desc}"><b>${looted.name.toUpperCase()}</b><span>click to stow</span></button>`
          : `<button type="button" disabled><b>UNARMED</b><span>fists</span></button>`}
      </div>
    </fieldset>
    <fieldset><legend>ENERGY SYSTEMS</legend>
      <p class="up-note">One pool feeds every system. Spend on what you actually use.</p>
      <div class="up-list">${tracks}</div>
    </fieldset>`;
}

const invPanel = new InventoryPanel(rpg, {
  onChange: () => { }, // rev-driven save/render loops pick mutations up
  onIntent: (m) => invIntent(m),
  onClose: () => {
    if (mode === "fly" || mode === "foot" || mode === "vehicle") input.requestPointerLock();
  },
  onOutfit: () => openOutfit(),
  onGarage: () => openGarage(),
  onShipyard: () => openShipyard(),
  onEquip: (slot) => equipGear(slot),
  onUnequip: (slot) => unequipGear(slot),
  onInstall: (slot) => {
    const item = rpg.inventory.slots[slot];
    if (!item) return;
    const perk = ITEMS[item.id]?.perk;
    if (!perk) return;
    if (upgrades[perk]) {
      hud.showToast("ALREADY INSTALLED", 1800);
      return;
    }
    consumeAt(rpg.inventory, slot, 1);
    invIntent({ op: "spend", id: item.id, n: 1 });
    upgrades[perk] = true;
    applyUpgrades();
    rpg.rev++;
    invPanel.render();
    void saveUpgrades(upgrades);
    hud.showToast(`${ITEMS[item.id].name.toUpperCase()} INSTALLED`, 2400);
  },
});

// --- C3 Quests: an optimistic mirror of the server-authoritative quest log,
// plus the journal + board panels and the dialog DELIVER / TURN IN choices. ---
const quests = new QuestLog({
  toast: (line) => hud.showToast(line),
  systemDef: () => system.def,
});
let storyState = defaultStoryState();

/** Stamp a monotonic seq and dispatch a quest intent to the server. */
function questIntent(m: Omit<QuestIntentMsg, "t" | "seq">): void {
  quests.seq++;
  net?.sendQuestIntent({ ...m, seq: quests.seq });
}

const journalPanel = new JournalPanel(quests, {
  onIntent: (m) => questIntent(m),
  onClose: () => {
    pauseSuppressUntil = performance.now() + 900;
    if (mode === "fly" || mode === "foot" || mode === "vehicle") input.requestPointerLock();
  },
});

const questBoardPanel = new QuestBoardPanel(quests, {
  onIntent: (m) => questIntent(m),
  onBrowse: (townSeed) => questIntent({ op: "board", town: townSeed }),
  onClose: () => {
    pauseSuppressUntil = performance.now() + 900;
    if (mode === "fly" || mode === "foot" || mode === "vehicle") input.requestPointerLock();
  },
});

// Chat (flag-gated): focusing releases the pointer; closing re-locks with the
// same pause-bounce suppression every cursor panel uses.
/** The local player's own overhead chat bubble (remotes carry their own). */
const ownBubble = new OverheadBubble();
const chatPanel: ChatPanel | null = SOCIAL_ON
  ? new ChatPanel({
    onSend: (m) => net?.sendChat(m),
    self: () => activeCharacterRecord?.name ?? null,
    onFocusChange: (focused) => {
      if (focused) {
        if (document.pointerLockElement === canvas) document.exitPointerLock();
      } else {
        pauseSuppressUntil = performance.now() + 900;
        if ((mode === "fly" || mode === "foot" || mode === "vehicle") && launched) input.requestPointerLock();
      }
    },
  })
  : null;

// Friends (Phase 2, same flag as chat): the model adopts server pushes and
// toasts diffs; the panel (KeyO) renders from it. WHISPER hands off to chat.
const socialModel: SocialModel | null = SOCIAL_ON
  ? new SocialModel({ toast: (line) => hud.showToast(line, 2600) })
  : null;
// Parties (Phase 3): frames on the HUD, ops through the panel's PARTY tab.
const partyModel: PartyModel | null = SOCIAL_ON
  ? new PartyModel({ toast: (line) => hud.showToast(line, 2600) })
  : null;
const partyFrames = SOCIAL_ON ? new PartyFrames() : null;
// Guilds (Phase 4): roster + ranks live in the panel's GUILD tab; the visible
// half is the `[TAG] Name` badge over every member's head (net.ts bakes it).
const guildModel: GuildModel | null = SOCIAL_ON
  ? new GuildModel({ toast: (line) => hud.showToast(line, 3000) })
  : null;
const socialPanel: SocialPanel | null = SOCIAL_ON && socialModel
  ? new SocialPanel(socialModel, {
    onOp: (m) => net?.sendSocial(socialModel.op(m)),
    onWhisper: (name) => chatPanel?.prefillWhisper(name),
    ...(partyModel ? { party: partyModel } : {}),
    onPartyOp: (m) => { if (partyModel) net?.sendParty(partyModel.op(m)); },
    ...(guildModel ? { guild: guildModel } : {}),
    onGuildOp: (m) => { if (guildModel) net?.sendGuild(guildModel.op(m)); },
    /** Invite-nearby: whoever the room says is here right now. */
    nearby: () => Array.from(net?.remotes ?? [], ([id, r]) => ({ id, name: r.name })),
    here: () => system.def.id,
    onClose: () => {
      pauseSuppressUntil = performance.now() + 900;
      if ((mode === "fly" || mode === "foot" || mode === "vehicle") && launched) input.requestPointerLock();
    },
  })
  : null;

// --- Garage (v49): the claimed-chassis mirror + its panel. The model is a
// MIRROR of the server's collectionState; every selection round-trips. ---
const garage = new GarageModel({ toast: (line) => hud.showToast(line, 2400) });
const garagePanel = new GaragePanel(garage, {
  onActivate: (id) => {
    net?.sendCollection(garage.op({ op: "vehicle", id }));
    // Optimistic: the echo reconciles, and swapping a parked chassis right away
    // is the whole point of the button.
    garage.active = id;
    garage.rememberSelection(id);
    garage.rev++;
    garagePanel.render();
  },
  // Summoning also makes it the active pick — you clearly want this one now.
  onSummon: (id) => {
    if (garage.active !== id) {
      net?.sendCollection(garage.op({ op: "vehicle", id }));
      garage.active = id;
      garage.rememberSelection(id);
      garage.rev++;
    }
    summonVehicle(id);
  },
  onDismiss: () => dismissVehicle(),
  deployed: () => (vehicleSpawned ? groundVehicle.def.id : null),
  onClose: () => {
    pauseSuppressUntil = performance.now() + 900;
    if ((mode === "fly" || mode === "foot" || mode === "vehicle") && launched) input.requestPointerLock();
  },
});

/** DELIVER / TURN IN choices to surface on the persona a quest targets. */
function questDialogActions(npcId: string): DialogQuestAction[] {
  const out: DialogQuestAction[] = [];
  for (const inst of quests.act) {
    const def = quests.defOf(inst);
    if (!def) continue;
    if (quests.ready(inst)) {
      if (def.category === "campaign") {
        const giver = def.giver ?? quests.firstNpcTarget(inst);
        if (giver === npcId)
          out.push({ op: "turnin", q: inst.q, npc: npcId, label: `TURN IN — ${def.title.toUpperCase()}`, enabled: true });
      }
      continue;
    }
    const stage = def.stages[inst.st];
    if (!stage) continue;
    for (let oi = 0; oi < stage.objectives.length; oi++) {
      const obj = stage.objectives[oi];
      if (obj.kind !== "deliver" || (inst.prog[oi] ?? 0) >= obj.n) continue;
      if ((inst.tg[inst.st]?.[oi] ?? null) !== npcId) continue;
      const remaining = obj.n - (inst.prog[oi] ?? 0);
      const have = obj.item ? countItem(rpg.inventory, obj.item) : 0;
      const nm = obj.item ? ITEMS[obj.item]?.name ?? obj.item : "goods";
      out.push({
        op: "deliver",
        q: inst.q,
        npc: npcId,
        ob: oi,
        label: `DELIVER ${nm} ×${remaining}`,
        enabled: obj.item ? have >= remaining : true,
      });
    }
  }
  return out;
}

/** Re-surface the open persona's quest choices after a state change. */
function refreshDialogQuestActions(): void {
  const id = dialogPanel.currentNpcId();
  if (dialogPanel.visible && id) dialogPanel.setQuestActions(questDialogActions(id));
}

// Nearest quest-board kiosk to the player on foot (its SIM-space position,
// mirroring npcLife.poseWorld — tangent-plane offsets against the pad, then
// the planet's live pose). Only village+ settlements carry a board.
function nearestQuestBoard() {
  return settlementRenderer?.nearestBoard(character.pos, 8) ?? null;
}

// Headless-harness camera pin (skip footCam.update so a test can set any angle).
let devFreezeCam = false;
/** DEV: catalog weapon id forced into the player's hands, overriding the mode construct
 *  (window.__hl.devRifle). null = normal mode-driven loadout. */
let devHeldWeapon: string | null = null;
// Headless SCREENSHOT harness (R1 D5 / visual-capture.mjs): a world-space camera
// pin that overrides the chase/foot camera every frame so a capture script can
// frame an aerial (high, looking down) or a ground-level shot of a parked target
// WITHOUT moving the ship — residency is driven by the ship's position (focusPos),
// so the ship stays put over the settlement while the camera is repositioned
// freely. Eye + look are WORLD (system) space; the render loop rebases them by the
// live floating `origin` each frame. Null = normal cameras.
let devCamPin: {
  eye: THREE.Vector3; look: THREE.Vector3; up: THREE.Vector3;
  /** Planet-ANCHORED pin (W2d-3 harness fix): eye/look/up are recomputed each
   *  frame from the planet's LIVE pose using these planet-LOCAL coords — a
   *  world-space pin goes stale within a frame (the planet orbits on wall-clock
   *  time at ~hundreds of m/s, so a settle window turns the shot into a distant
   *  globe — the exact drift the R1 harness fought with re-holds). */
  anchor?: { pi: number; eyeL: THREE.Vector3; lookL: THREE.Vector3; upL: THREE.Vector3 };
} | null = null;
const _camPinTarget = new THREE.Vector3(); // scratch: render-space look-at for the pin
// Smoke tests and console debugging reach the live sim through this handle.
Object.assign(window, {
  __hl: {
    ship,
    character,
    weaponCycle,
    /** God perf overlay (engine/perf.ts) — e.g. __hl.perf.setGodAllowed(true) in a dev probe. */
    perf,
    /** LIVE painterly tuning — `__hl.painterly.paint = 0` for the honest A/B,
     *  `__hl.painterly.grain = 0`, etc. See fx/painterlyPost.ts. */
    painterly: PAINTERLY,
    /** Reversible art review; the production default is coastal. */
    visualStyle(name: keyof typeof VISUAL_STYLES) {
      const preset = VISUAL_STYLES[name];
      if (!preset) return;
      Object.assign(PAINTERLY, preset.post);
      Object.assign(PAINT_LIGHT, preset.light);
      Object.assign(ENVIRONMENT, preset.environment);
    },
    /** Hard bypass (no targets, no composite) — `__hl.painterlyPost.enabled = false`. */
    painterlyPost,
    /** Multi-slice scene draw (world/depthPartition.ts) — `.lastPasses` is the
     *  slice count actually rendered last frame. */
    depthPartition,
    /** LIVE painted-light tuning — `__hl.paintLight.strength = 0` restores three's
     *  own Lambert. See world/stylizedShading.ts. */
    paintLight: PAINT_LIGHT,
    /** LIVE wind tuning — `__hl.wind.gust = 0` for a steady breeze with no
     *  fronts, `.gain = 0` for still air. See world/flora/wind.ts. */
    wind: WIND_TUNING,
    windField: floraWind,
    /** LIVE grass tuning — `__hl.grass.nearFull` is the distance the per-blade
     *  density law starts thinning at. See world/flora/grassBlades.ts. */
    grass: GRASS_TUNING,
    /** DEV: the ESC-menu Detail distance slider's setter (terrain split radii
     *  AND flora fade radii, 0.6..2.5) — for perf probes sweeping draw distance
     *  without the menu. Not persisted. */
    devTerrainQuality: setTerrainQuality,
    /** DEV: flora fade scale ALONE (ground cover / rock / far-tree band
     *  radii), leaving terrain LOD as it is. */
    devFloraFade: setFloraFadeScale,
    /** LIVE daytime sky saturation — `__hl.sky.saturate = 1` is the raw colour. */
    sky: SKY,
    /** Far-field rain/snow curtain (fx/weatherVeil.ts) — `__hl.veil().group.visible = false`
     *  to take it out of a frame. */
    veil: () => weatherVeil,
    /** Near-field precipitation particles. */
    precipFx: () => precipFx,
    /** The sun's shadow settings, for dialling acne vs peter-panning live:
     *  `__hl.sunShadow().normalBias = 0.3`. three picks the change up next frame. */
    sunShadow: () => system.sunLight.shadow,
    /** The live wind uniforms, so a probe can mirror hlWindAt on the CPU. */
    windUniforms: WIND_UNIFORMS,
    footPlanet: () => footPlanet,
    mode: () => mode,
    system: () => system,
    net: () => net,
    /** Probe hook: connect networking without the auth/launch UI (headless smokes; the
     *  server still enforces its own auth policy — this only skips the CLIENT flow). */
    devStartNet: (name = "Probe") => { if (!net) startNet(name); },
    /** DEV/probe: reveal the sign-in fields, as if "press to start" was pressed. */
    devStartTitle: () => enterAuthView(),
    /** DEV/probe: show the load screen at a fixed progress to screenshot the unravel. */
    devShowLoader: (frac = 0.5, label = "MESHING TERRAIN") => { loadScreen.show(); loadScreen.setProgress(frac, label); },
    devHideLoader: () => loadScreen.hide(),
    autopilot: () => autopilot,
    /** DEV/probe: the live ground vehicle (pose, velocity, seat state) — e.g. to
     *  lift the chassis and check what a dismount from altitude does. */
    groundVehicle: () => groundVehicle,
    /** DEV/probe: board without the hold-F proximity ladder. */
    devBoardVehicle: (): boolean => { boardGroundVehicle(); return mode === "vehicle"; },
    /** DEV/probe: leave the seat, optionally after forcing a ground speed first —
     *  which is what selects the step-off or the bail, so a probe can exercise
     *  BOTH exits without having to drive. Reports the exit that was chosen. */
    devExitVehicle: (speed?: number) => {
      if (speed !== undefined) {
        vehicleSpawnForward.set(0, 0, -1).applyQuaternion(groundVehicle.quat);
        groundVehicle.vel.copy(vehicleSpawnForward).multiplyScalar(speed);
      }
      exitGroundVehicle();
      const t = vehicleMountTransition;
      return t ? { bail: Boolean(t.bail), duration: t.duration, side: t.side } : null;
    },
    /** DEV/probe: the live exit/entry transition, and the exit the speed would pick. */
    devVehicleTransition: () => (vehicleMountTransition
      ? {
        phase: vehicleMountTransition.phase,
        bail: Boolean(vehicleMountTransition.bail),
        elapsed: vehicleMountTransition.elapsed,
        duration: vehicleMountTransition.duration,
      }
      : null),
    devVehicleExitKind: () => (groundVehicleWouldBail() ? "bail" : "step-off"),
    /** DEV/probe: freeze an in-progress exit at clip time `t` (null resumes) so
     *  the COMPOSED pose — authored FK, ride-pose release, leg-over IK, socket
     *  contacts and root warp together — can be scrubbed the way Animation Lab
     *  scrubs the clip alone. The chassis holds still too, and the detach marker
     *  is suppressed, so the frame stays put. */
    devVehicleExitScrub: (t: number | null) => { devExitScrub = t; },
    /** DEV/probe: the resolved prologue sites for the active staged character
     *  (null outside the kickoff) — lets probes compare placements to truth. */
    devPrologueSites: () => {
      const record = activeCharacterRecord;
      const uid = record ? homeOwner(record.hometown.systemId) : null;
      return record && uid ? resolvePrologueSites(uid, record.hometown) : null;
    },
    /** DEV/probe: the ACTIVE character record (multi-character select). */
    devCharacter: () => activeCharacterRecord,
    /** DEV/probe: a fresh ID token, so a probe can re-read the authoritative
     *  character API instead of trusting the screen it just drove. */
    devIdToken: () => getIdToken(),
    /** DEV/probe: what each roster portrait body is actually made of. */
    devPortraits: () => characterPortraits.debug(),
    rpg,
    /** DEV/probe: open the dialog panel on an already-rendered npc view (bypasses the
     *  proximity interact ladder). Exercises the talking-face + live-portrait path. */
    devDialog: (npcId: string, name = "Probe Subject"): boolean => {
      if (!npcLife?.avatarOf(npcId)) return false;
      talkingNpcId = npcId;
      dialogPanel.open(
        {
          id: npcId, name, role: "wanderer", style: DEFAULT_STYLE, traits: ["curious", "patient"],
          u: 0, v: 0, roam: 0, home: -1, job: "none", jobData: [0, 0, 0, 0], source: "town",
        } as unknown as Parameters<typeof dialogPanel.open>[0],
        "PROBEVILLE", 0, null,
      );
      return true;
    },
    /** Tests hop to an empty system so their robots never haunt real rooms. */
    warpTo: (x: number, y: number, z: number) => switchSystem(`${x}:${y}:${z}`),
    /** DEV: enter an arbitrary home system CLIENT-SIDE (world gen only — the
     *  server room join still enforces owner-only; guests get rejected). */
    devHome: (uid: string) => switchSystem(homeSystemId(uid)),
    THREE,
    camera,
    deathPresentation,
    renderer,
    origin: () => origin,
    trails,
    hoverRing,
    resourceNodes: () => resourceNodes,
    wildlife: () => wildlife.debug(),
    /** Chat panel (hl-social kill-switch). Probes read .focused / .probeLines(). */
    chat: () => chatPanel,
    /** Friends model (same flag) — probes read .friends/.incoming/.rev. */
    social: () => socialModel,
    /** Social panel (KeyO) — probes drive .open()/.render() + its buttons. */
    socialPanel: () => socialPanel,
    /** Party model (same flag) — probes read .members/.invite/.rev. */
    party: () => partyModel,
    /** Guild model (same flag) — probes read .tag/.members/.invite/.rev. */
    guild: () => guildModel,
    /** The social marks the galactic chart draws (friends/party/rally). */
    chartMarks: () => chartPresence(),
    /** Over-head tags the local client currently renders, by pilot name. */
    tags: (): Record<string, string> => {
      const out: Record<string, string> = {};
      for (const r of net?.remotes.values() ?? []) out[r.name] = r.tag;
      return out;
    },
    /** Live overhead chat bubbles incl. the local player's (probes). */
    bubbles: (): { name: string; text: string }[] => [
      ...(net?.bubbles() ?? []),
      ...(ownBubble.text !== null && activeCharacterRecord
        ? [{ name: activeCharacterRecord.name, text: ownBubble.text }]
        : []),
    ],
    /**
     * CACHES (P6b) — the whole feature's probe seam in one object:
     *   `apertures`  how many sites are marked on the foot planet
     *   `nearest`    the aperture the F-prompt would offer (null = none in range)
     *   `instance`   the dressed run you are standing in (null = not in one)
     *   `prog`       the server's latest run progress
     */
    caches: () => ({
      system: SYSTEM_ID,
      inCache: inCacheInstance(),
      apertures: cacheApertures?.count ?? 0,
      aperturePlanet: cacheApertures?.planet ?? null,
      nearest: (() => {
        const local = footLocal(cacheProbeV);
        const n = local ? cacheApertures?.nearest(local) ?? null : null;
        // `local` rides along for probes (_farbeacon.mjs offsets from it) — plain array,
        // not the live Vector3, so page-context JSON serialization stays clean.
        return n ? { site: n.site.id, theme: n.theme, tier: n.tier, dist: n.dist, local: [n.local.x, n.local.y, n.local.z] } : null;
      })(),
      instance: liveCache?.describe() ?? null,
      prog: cacheProg,
      origin: cacheOrigin?.systemId ?? null,
      /** Our own PAD-LOCAL (x, z) — the frame the envelope rule is measured in.
       *  Probes watch this to see the server put a strayed player back. */
      pad: (() => {
        if (!liveCache) return null;
        const local = footLocal(cacheProbeV);
        return local ? liveCache.padLocalOf(local) : null;
      })(),
    }),
    /**
     * DEV/probe: query the planet's solid-geometry hooks at the CENTRE of every wall
     * box the live dressing built. `solid` should equal `tested` — anything less means
     * the boxes are drawn but not colliding, which is invisible until you walk at one.
     */
    devCacheSolid: (): { tested: number; solid: number } | null => {
      const planet = system.planets[0];
      const resolve = planet?.resolveHomesteadCollision ?? null;
      if (!liveCache || !resolve) return null;
      const out = { x: 0, y: 0, z: 0 };
      const v = new THREE.Vector3();
      let tested = 0, solid = 0;
      for (let i = 0; i < liveCache.dressing.walls.length; i++) {
        liveCache.wallProbe(i, v);
        tested++;
        if (resolve(v.x, v.y, v.z, 0.45, out)) solid++;
      }
      return { tested, solid };
    },
    /**
     * DEV/probe: does GUNFIRE stop at a cache wall?
     *
     * The sibling of `devCacheSolid`, for the other silent failure: until 2026-07-27
     * a cache's walls stopped the player but not their bolts, in either direction —
     * `fx/bolts.ts` swept `planet.settlementColliders`, which on a pad world is empty.
     * This drives the hook that fixed it, `planet.homesteadSegmentHit`, along the
     * SAME segment the bolt path builds: a 6 m shot straight through the middle of
     * every wall box, plus one control shot down the open middle of the entrance room
     * that must NOT be stopped.
     */
    devCacheBolt: (): { tested: number; stopped: number; control: boolean } | null => {
      const planet = system.planets[0];
      const hit = planet?.homesteadSegmentHit ?? null;
      if (!liveCache || !hit) return null;
      const v = new THREE.Vector3();
      let tested = 0, stopped = 0;
      for (let i = 0; i < liveCache.dressing.walls.length; i++) {
        const b = liveCache.dressing.walls[i];
        liveCache.wallProbe(i, v);
        // Across the box's THICKNESS: a shot along its length would run inside it.
        const nx = b.along === "x" ? 0 : 1, nz = b.along === "x" ? 1 : 0;
        const a = liveCache.localPointAt(b.cx - nx * 3, b.cz - nz * 3);
        const c = liveCache.localPointAt(b.cx + nx * 3, b.cz + nz * 3);
        const lift = (p: THREE.Vector3): THREE.Vector3 => p.setLength(v.length());
        lift(a); lift(c);
        tested++;
        if (hit(a.x, a.y, a.z, c.x, c.y, c.z, 0) >= 0) stopped++;
      }
      // Control: the entrance room's own centre is open floor by construction.
      const ent = liveCache.entranceRoom;
      const p0 = liveCache.localPointAt(ent.x - 1, ent.z);
      const p1 = liveCache.localPointAt(ent.x + 1, ent.z);
      const eye = liveCache.entranceLocal().length() + 1.2;
      p0.setLength(eye); p1.setLength(eye);
      return { tested, stopped, control: hit(p0.x, p0.y, p0.z, p1.x, p1.y, p1.z, 0) < 0 };
    },
    /**
     * DEV/probe: does gunfire stop at a CEILING? The third silent failure in the family
     * (`devCacheSolid` = feet, `devCacheBolt` = walls). A vertical shot from every room's
     * floor must die on the canonical lid in an enclosed theme and must NOT in `ruin`,
     * which is open sky by design.
     */
    devCacheCeiling: (): { theme: string; tested: number; stopped: number } | null => {
      const planet = system.planets[0];
      const hit = planet?.homesteadSegmentHit ?? null;
      if (!liveCache || !hit) return null;
      let tested = 0, stopped = 0;
      for (const r of liveCache.layout.rooms) {
        const a = liveCache.localPointAt(r.x, r.z);
        const up = a.clone().setLength(a.length() + 30);
        a.setLength(a.length() + 1.2);
        tested++;
        if (hit(a.x, a.y, a.z, up.x, up.y, up.z, 0) >= 0) stopped++;
      }
      return { theme: liveCache.info.theme, tested, stopped };
    },
    /**
     * DEV/probe: the ENVELOPE RULE, driven live. Teleports the player `over` metres
     * past the layout's half-extent — the escape a super-jump over a side wall
     * achieves — and returns their pad-local (x, z). The SERVER should relocate them
     * back to the entrance within a tick or two; poll `__hl.caches().pad` for it.
     */
    devCacheStray: (over = 40): [number, number] | null => {
      if (!liveCache) return null;
      const d = liveCache.layout.extent + over;
      const p = liveCache.localPointAt(d, 0);
      placeAtLocalAnchor(SYSTEM_ID, 0, [p.x, p.y, p.z], [0, 0, 1]);
      mode = "foot";
      return [d, 0];
    },
    /** DEV/probe: stand on the nearest aperture (planet-local teleport, then ground). */
    devGotoAperture: (): boolean => {
      const local = footLocal(cacheProbeV);
      const near = local ? cacheApertures?.nearest(local) ?? null : null;
      if (!near || !footPlanet) return false;
      const idx = system.planets.indexOf(footPlanet);
      if (idx < 0) return false;
      placeAtLocalAnchor(SYSTEM_ID, idx, [near.local.x, near.local.y, near.local.z], [0, 0, 1]);
      mode = "foot";
      return true;
    },
    /** Probe seam: stand at an arbitrary planet-local point on the CURRENT foot planet,
     *  facing `face` (planet-local). Used by _farbeacon.mjs to view an aperture from range. */
    devPlaceLocal: (p: [number, number, number], face: [number, number, number] = [0, 0, 1]): boolean => {
      if (!footPlanet) return false;
      const idx = system.planets.indexOf(footPlanet);
      if (idx < 0) return false;
      placeAtLocalAnchor(SYSTEM_ID, idx, p, face);
      mode = "foot";
      return true;
    },
    /** Homestead land claims (flag-gated). `count` lets probes confirm a beacon landed. */
    homesteads: () => homesteadMarkers,
    /** Placed-piece render layer (flag-gated); `count` = rendered pieces (probes). */
    homesteadPieces: () => homesteadPieces,
    /** Build-mode controller (flag-gated) — probes toggle .active + read .placement(). */
    homesteadBuild: () => homesteadBuild,
    /** DEV/probe: plant a claim beacon at the player's feet on the current foot planet. */
    devClaim: (tier = 0): boolean => plantBeaconHere(tier),
    /** Interiors P1: the live indoors signal (SettlementColliders.indoorsAt). */
    indoors: (): boolean => indoorsNow,
    /** DEV/probe (interiors P1): doors of every HOLLOW building in the player's
     *  current region, nearest first — planet-local outside point + inward
     *  facing, ready for devPlaceLocal. Derives from the region graph on
     *  demand (pure worldgen math; needs the region to be resident). */
    interiorDoors: (): { p: [number, number, number]; face: [number, number, number]; use: number; dist: number }[] => {
      const planet = footPlanet ?? system.nearestPlanet(character.pos)?.planet ?? null;
      const svc = planet?.regionService;
      if (!planet || !svc) return [];
      const local = character.pos.clone().sub(planet.pose.center).applyQuaternion(planet.pose.invQuat);
      const d = local.clone().normalize();
      const cell = dirToCell(planet.def.radius, LEVEL_REGION, d.x, d.y, d.z);
      const g = svc.region(cell);
      if (!g) return [];
      const st = buildStructures(g, planet.sampler, svc.index);
      const SIDE_DX = [0, 0, 1, -1], SIDE_DZ = [1, -1, 0, 0];
      const out: { p: [number, number, number]; face: [number, number, number]; use: number; dist: number }[] = [];
      const b = st.buildings;
      for (let i = 0; i < b.length / BUILDING_STRIDE; i++) {
        const o = i * BUILDING_STRIDE;
        if (!interiorEligible(b[o + 15], b[o + 17])) continue;
        const plan = buildingPlan({
          archetype: BUILDING_ARCHETYPES[Math.round(b[o + 13])] ?? "habitation",
          seed: b[o + 14] >>> 0, wM: b[o + 4], dM: b[o + 5], hM: b[o + 6],
          doorSide: planDoorSide(b[o + 3], b[o + 12]), hollow: true,
          floorRaiseM: b[o + 17],
        });
        if (plan.shell === null) continue;
        const c = new THREE.Vector3(b[o], b[o + 1], b[o + 2]);
        const up = c.clone().normalize();
        // Shared tangentFrame construction (structures.ts) in THREE form.
        const ref = Math.abs(up.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
        const t1 = new THREE.Vector3().crossVectors(ref, up).normalize();
        const t2 = new THREE.Vector3().crossVectors(up, t1);
        const yaw = b[o + 3];
        const W = t1.multiplyScalar(Math.cos(yaw)).addScaledVector(t2, Math.sin(yaw)).normalize();
        const D = new THREE.Vector3().crossVectors(W, up);
        const side = plan.door.side;
        const ox = plan.door.x + SIDE_DX[side] * 1.6;
        const oz = plan.door.z + SIDE_DZ[side] * 1.6;
        const p = c.clone().addScaledVector(W, ox).addScaledVector(D, oz).addScaledVector(up, 0.6);
        const inward = W.clone().multiplyScalar(-SIDE_DX[side]).addScaledVector(D, -SIDE_DZ[side]).normalize();
        out.push({
          p: [p.x, p.y, p.z], face: [inward.x, inward.y, inward.z],
          use: b[o + 15], dist: local.distanceTo(p),
        });
      }
      out.sort((a, q) => a.dist - q.dist);
      return out;
    },
    /** W2b settlement debug rig: the live renderer + a spiral-scan warp helper. */
    /** Where am I? Prints system galactic coords, planet, REGION CELL (face,x,y)
     *  and chart (u,v) — the exact identifiers a worldgen bug report needs. */
    whereAmI: (): string => {
      const pos = footPlanet !== null ? character.pos : ship.pos;
      const near = system.nearestPlanet(pos);
      if (!near) return "in deep space (no nearby planet)";
      const p = near.planet;
      const local = pos.clone().sub(p.pose.center).applyQuaternion(p.pose.invQuat);
      const d = local.clone().normalize();
      const cell = dirToCell(p.def.radius, LEVEL_REGION, d.x, d.y, d.z);
      const f = regionFrame(p.def.radius, cell);
      const u = p.def.radius * (f.t1[0] * d.x + f.t1[1] * d.y + f.t1[2] * d.z);
      const v = p.def.radius * (f.t2[0] * d.x + f.t2[1] * d.y + f.t2[2] * d.z);
      const s = `system ${system.def.id} · planet ${p.def.name || p.def.seed} (idx ${system.planets.indexOf(p)}) · cell (${cell.face},${cell.x},${cell.y}) · chart u=${u.toFixed(0)} v=${v.toFixed(0)} · alt ${(local.length() - p.surfaceRadiusWorld(local.clone().normalize().applyQuaternion(p.pose.quat))).toFixed(0)}m`;
      console.log("[whereAmI]", s);
      return s;
    },
    /** DEV: warp to the TRACKED quest's current objective — jumps star systems
     *  first when the quest lives elsewhere (camp:arrival = the home system).
     *  Analytic seed/canon resolution, so it works before anything has
     *  streamed in; parks the ship 80 m over the target in fly mode. Track a
     *  quest in the journal (J) first; fly/foot use (not docked/cutscene).
     *  Returns { system, jumped, x, y, z, via } or null (reason logged). */
    warpToQuest: () => warpToQuestTarget(),
    settlements: {
      renderer: () => settlementRenderer,
      /** Scan the WHOLE nearest planet (all 6 faces × cellsPerFace² region cells)
       *  nearest-first from the camera; warp to the first seed-bearing region whose
       *  highest tier is ≥ `minTier` (0 waystation … 5 metropolis; default 0 = any)
       *  and return its world coords (also logged with tier name, population, parcel
       *  count, and required-building satisfaction — the target cell is materialized,
       *  census cells are not). First checks the habitability gate: a non-terran/
       *  ocean planet is reported uninhabitable without scanning. e.g.
       *  `warpToNearest(2)` = nearest village-or-larger. ASYNC — Promise<coords|null>
       *  (chunked across frames); null on uninhabitable / no-such-tier planets. */
      warpToNearest: (minTier = 0) => warpToNearestSettlement(minTier),
      /** Exhaustive whole-planet survey of the nearest planet: logs every
       *  seed-bearing region cell (face/x/y, seed count, highest tier NAME,
       *  distance) then a per-tier totals line (waystation…metropolis counts from
       *  the registry deck) and returns the list. No per-cell materialization —
       *  tiers are read straight off the dealt seeds. Same habitability gate as
       *  warpToNearest. ASYNC — Promise<SeedCellHit[]>. */
      census: () => censusSettlements(),
      /** Probe grown-settlement building/wall collision at a PLANET-LOCAL metres
       *  point on the foot planet. Returns the applied push delta {x,y,z} or null when clear;
       *  smoke tests use it to confirm the solids the renderer draws actually block. */
      resolveCollision: (x: number, y: number, z: number, r = 0.45) => {
        const sc = footPlanet?.settlementColliders ?? null;
        if (!sc) return null;
        const o = { x: 0, y: 0, z: 0 };
        return sc.resolve(x, y, z, r, o) ? { x: o.x - x, y: o.y - y, z: o.z - z } : null;
      },
    },
    /**
     * R1 D5 SCREENSHOT HARNESS rig (scripts/visual-capture.mjs). Parks the ship over
     * a fixed cell (driving residency), pins the camera to an arbitrary world-space
     * eye/look for aerial vs ground shots WITHOUT moving the ship, and exposes a
     * readiness poll so the capture script settles on a fully-built target instead
     * of a fixed sleep. All test-only; no gameplay path touches these.
     */
    capture: {
      /** Park the ship over region cell (face,x,y) of the nearest planet at `up` m
       *  altitude. ASYNC (matches the settlements rig). Returns the world centre,
       *  outward radial, planet index, packed cell key, and planet radius, or null. */
      warpToCell: (face: number, x: number, y: number, up = 80, planetIdx = -1) => warpToCellCapture(face, x, y, up, planetIdx),
      /** RE-SNAP the parked ship + floating origin to a WORLD-space eye position (the
       *  cell centre + radial·park), zeroing velocity. Fly-mode gravity/carry slowly
       *  drifts the parked ship (and the origin) upward over a long settle window — for
       *  a fixed-surface-point camera pin that turns the ground shot into a distant
       *  globe. The harness calls this immediately BEFORE each pin so the shot fires
       *  from the intended altitude with the origin glued to the settlement. */
      hold: (pos: [number, number, number]) => {
        ship.pos.set(pos[0], pos[1], pos[2]);
        ship.prevPos.copy(ship.pos);
        ship.vel.set(0, 0, 0);
        ship.speed = 0;
        ship.throttle = 0;
        ship.snapVisual();
        origin.copy(ship.pos);
      },
      /** Pin the camera to a WORLD-space eye looking at a WORLD-space target (up
       *  defaults to +Y-ish; pass a radial for a stable ground horizon). Overrides
       *  the chase/foot camera every frame until unpin(). */
      pin: (eye: [number, number, number], look: [number, number, number], up?: [number, number, number]) => {
        devCamPin = {
          eye: new THREE.Vector3(eye[0], eye[1], eye[2]),
          look: new THREE.Vector3(look[0], look[1], look[2]),
          up: up ? new THREE.Vector3(up[0], up[1], up[2]).normalize() : new THREE.Vector3(0, 1, 0),
        };
      },
      /** Release the camera pin (chase/foot cameras resume). */
      unpin: () => { devCamPin = null; },
      /** LIVE planet-local surface anchor for a unit dir on planet `pi`: the
       *  planet-LOCAL surface point + surface radius (time-invariant — world
       *  coords are derived from the live pose by holdLocal/pinLocal). */
      liveCell: (pi: number, d: [number, number, number]): { local: [number, number, number]; surf: number } | null => {
        const p = system.planets[pi];
        if (!p) return null;
        const dir = new THREE.Vector3(d[0], d[1], d[2]).normalize();
        const surf = surfaceSupportRadius(p.sampler, dir.x, dir.y, dir.z);
        return { local: [dir.x * surf, dir.y * surf, dir.z * surf], surf };
      },
      /** Park the ship `up` m over the planet-LOCAL unit dir `d` of planet `pi`,
       *  derived from the LIVE pose — the drift-free hold (capture.hold pins a
       *  STALE world point the planet orbits away from). */
      holdLocal: (pi: number, d: [number, number, number], up = 80) => {
        const p = system.planets[pi];
        if (!p) return;
        const dir = new THREE.Vector3(d[0], d[1], d[2]).normalize();
        const surf = surfaceSupportRadius(p.sampler, dir.x, dir.y, dir.z);
        const world = dir.clone().multiplyScalar(surf + up).applyQuaternion(p.pose.quat).add(p.pose.center);
        ship.pos.copy(world);
        ship.prevPos.copy(ship.pos);
        ship.vel.set(0, 0, 0);
        ship.speed = 0;
        ship.throttle = 0;
        ship.snapVisual();
        origin.copy(ship.pos);
      },
      /** Planet-ANCHORED camera pin: eye/look/up given in planet-LOCAL coords of
       *  planet `pi`, recomputed from the live pose every frame — shots cannot
       *  drift while the planet orbits/spins under the camera. */
      pinLocal: (pi: number, eyeL: [number, number, number], lookL: [number, number, number], upL: [number, number, number]) => {
        devCamPin = {
          eye: new THREE.Vector3(),
          look: new THREE.Vector3(),
          up: new THREE.Vector3(0, 1, 0),
          anchor: {
            pi,
            eyeL: new THREE.Vector3(eyeL[0], eyeL[1], eyeL[2]),
            lookL: new THREE.Vector3(lookL[0], lookL[1], lookL[2]),
            upL: new THREE.Vector3(upL[0], upL[1], upL[2]).normalize(),
          },
        };
      },
      /** Readiness of a target cell: is its settlement group built (or legitimately
       *  empty), and how many restitch tasks remain queued across the renderer. The
       *  capture script polls this until built|empty AND restitchPending===0. */
      status: (pi: number, rk: number) => settlementRenderer?.captureStatus(pi, rk) ?? null,
      /** World-space surface point + outward radial at a planet-local unit dir on the
       *  nearest planet (for framing a ground shot at a road/parcel). */
      surfaceAt: (dx: number, dy: number, dz: number): { pos: [number, number, number]; radial: [number, number, number] } | null => {
        const near = system.nearestPlanet(footPlanet ? character.pos : ship.pos);
        if (!near) return null;
        const p = near.planet;
        const d = new THREE.Vector3(dx, dy, dz).normalize();
        const surf = surfaceSupportRadius(p.sampler, d.x, d.y, d.z);
        const local = d.clone().multiplyScalar(surf);
        const world = local.clone().applyQuaternion(p.pose.quat).add(p.pose.center);
        const radial = d.clone().applyQuaternion(p.pose.quat);
        return { pos: [world.x, world.y, world.z], radial: [radial.x, radial.y, radial.z] };
      },
      /**
       * World-space position + outward radial of a ≥3-way ROAD JUNCTION in region
       * cell (face,x,y) — the AMENDMENT-2 F4 target (a close ground shot AT a junction
       * framing the road surface / fillet fan). Materializes the same region graph the
       * renderer builds, counts node degree over the road edges, and returns the
       * highest-degree node (ties → nearest the cell centre, deterministic) converted
       * to world coords via the live planet pose. Returns null if the cell has no
       * junction (degree < 3 everywhere — e.g. a lone through-road). planetIdx targets
       * an exact planet (deterministic capture); < 0 uses the nearest planet. */
      junctionAt: (face: number, x: number, y: number, planetIdx = -1): { pos: [number, number, number]; radial: [number, number, number]; dir: [number, number, number]; degree: number } | null => {
        let planet: Planet | undefined;
        if (planetIdx >= 0) planet = system.planets[planetIdx];
        else planet = system.nearestPlanet(footPlanet ? character.pos : ship.pos)?.planet;
        if (!planet) return null;
        const cell: CellId = { face, x, y, level: LEVEL_REGION };
        const g = materializeRegion(planet.sampler, cell);
        if (g.edgeA.length === 0) return null;
        const deg = new Uint16Array(g.nodeU.length);
        for (let e = 0; e < g.edgeA.length; e++) { deg[g.edgeA[e]]++; deg[g.edgeB[e]]++; }
        // Node (u,v) are region-local metres from the cell CENTRE (frame origin), so
        // ties break toward (0,0) — the middle — for stable framing.
        const fr = g.frame;
        let best = -1, bestDeg = 2, bestD2 = Infinity;
        for (let n = 0; n < deg.length; n++) {
          if (deg[n] < 3) continue;
          const d2 = g.nodeU[n] ** 2 + g.nodeV[n] ** 2;
          if (deg[n] > bestDeg || (deg[n] === bestDeg && d2 < bestD2)) { best = n; bestDeg = deg[n]; bestD2 = d2; }
        }
        if (best < 0) return null;
        const dir: number[] = [0, 0, 0];
        localToDir(fr, g.nodeU[best], g.nodeV[best], dir);
        const d = new THREE.Vector3(dir[0], dir[1], dir[2]).normalize();
        const surf = surfaceSupportRadius(planet.sampler, d.x, d.y, d.z);
        const local = d.clone().multiplyScalar(surf);
        const world = local.clone().applyQuaternion(planet.pose.quat).add(planet.pose.center);
        const radial = d.clone().applyQuaternion(planet.pose.quat);
        return { pos: [world.x, world.y, world.z], radial: [radial.x, radial.y, radial.z], dir: [d.x, d.y, d.z], degree: bestDeg };
      },
    },
    npcLife: () => npcLife,
    /** Probe seam (parry/riposte/chain diagnostics — mirrors the lab's exposure). */
    actionSys: () => actionSys,
    /** Probe seam: the live soft lock (the lab exposes the same — lock-marker diagnostics).
     *  Lazy: `softLock` is constructed well below this handle (TDZ if captured eagerly). */
    softLock: () => softLock,
    parryWindow: () => ({ from: parryFrom, until: parryUntil, now: performance.now() }),
    nav: { canJumpTo, discovered: () => discovered, setNavTarget: (t: NavTarget) => setNavTarget(t), homeWorld: () => homeWorld },
    drones,
    /** DEV: flare a shield hex by hand — `__hl.shieldHex.hit(atRender, fromRender)`. */
    shieldHex,
    style: () => playerStyle,
    avatarGroup: () => avatar.group,
    avatarClips: () => avatar.locomotionKeys(),
    avatar,
    // Headless test driving: inject input + jump straight into foot mode.
    input,
    scene,
    footCam,
    ocean: () => oceanPatch,
    devFoot: () => devSpawnFoot(),
    devFly: () => devSpawnFly(),
    /** Inspect a generated waterfall from its downstream bank. */
    devWaterfall: (index = 0): string => {
      const pl = footPlanet;
      if (!pl) return "[devWaterfall] land or run __hl.devFoot() first";
      const falls = pl.sampler.rivers().falls;
      const fall = falls[Math.max(0, Math.min(falls.length - 1, Math.floor(index)))];
      if (!fall) return `[devWaterfall] no qualifying inland drops on ${pl.def.name}`;
      const up = new THREE.Vector3(fall.lip.x, fall.lip.y, fall.lip.z);
      const flow = new THREE.Vector3(fall.lip.fx, fall.lip.fy, fall.lip.fz);
      const side = new THREE.Vector3().crossVectors(flow, up).normalize();
      const dir = up.clone().addScaledVector(flow, (fall.runM + 12) / pl.def.radius)
        .addScaledVector(side, (fall.lip.half * 3) / pl.def.radius).normalize().applyQuaternion(pl.pose.quat);
      const pos = pl.pose.center.clone().addScaledVector(dir, pl.surfaceRadiusWorld(dir) + 1.3);
      character.restoreAt([pos.x, pos.y, pos.z], [0, 0, 0, 1], pl);
      const target = up.applyQuaternion(pl.pose.quat).multiplyScalar(pl.def.radius + fall.lip.ws * pl.sampler.amplitude).add(pl.pose.center);
      const heading = target.sub(pos);
      character.heading.copy(heading).addScaledVector(dir, -heading.dot(dir)).normalize();
      character.pitch = -0.15;
      const message = `[devWaterfall] ${pl.def.name} ${fall.id}: ${fall.dropM.toFixed(1)} m drop, ${fall.flightTime.toFixed(2)} s freefall`;
      hud.showToast(`WATERFALL — ${fall.dropM.toFixed(0)}M DROP`, 3600);
      return message;
    },
    /** DEV: teleport on foot to a river on the current foot planet — seated on
     *  the bank beside the water. `devRiver()` = the longest river at 70%
     *  downstream; `devRiver(3)` = river id 3; `devRiver(3, 0.2)` = near its
     *  source. No rivers on this planet → logs which planets in the system have
     *  them (rivers need a sea + riverDensity > 0). Get on foot first (devFoot). */
    devRiver: (riverIdx: number | null = null, along = 0.7): string => {
      const pl = footPlanet;
      if (!pl) return "[devRiver] not on foot — run __hl.devFoot() (or land) first";
      const g = pl.sampler.rivers();
      if (g.rivers.length === 0) {
        const others = system.planets
          .filter((q) => q !== pl && q.sampler.rivers().rivers.length > 0)
          .map((q) => `${q.def.name} (${q.sampler.rivers().rivers.length})`);
        const msg = `[devRiver] no rivers on ${pl.def.name}` +
          (others.length ? ` — river planets here: ${others.join(", ")}` : " — none in this system");
        console.log(msg);
        hud.showToast(`NO RIVERS ON ${pl.def.name.toUpperCase()}`, 3600);
        return msg;
      }
      let river = g.rivers[0];
      if (riverIdx !== null && g.rivers[riverIdx]) river = g.rivers[riverIdx];
      else for (const r of g.rivers) if (r.points.length > river.points.length) river = r;
      const k = Math.min(river.points.length - 1, Math.max(0, Math.round((river.points.length - 1) * along)));
      const pt = river.points[k];
      // Bank point: walk the cross-section outward and seat at the first spot
      // where the ground stands clear of the water surface — the true dry
      // line. A fixed offset can't work: lowland reaches carry wide damp
      // margins (waterline near the carve edge), gorge reaches are dry a few
      // metres out.
      const d = _drDir.set(pt.x, pt.y, pt.z);
      const side = _drSide.set(
        pt.fy * d.z - pt.fz * d.y,
        pt.fz * d.x - pt.fx * d.z,
        pt.fx * d.y - pt.fy * d.x,
      ).normalize();
      const wsM = pl.def.radius + pt.ws * pl.sampler.amplitude;
      let seatOff = pt.half * 3.2; // fallback: past the carve
      for (let off = pt.half * 1.15; off <= pt.half * 3.2; off += 4) {
        const l = 1 / Math.hypot(d.x + side.x * (off / pl.def.radius), d.y + side.y * (off / pl.def.radius), d.z + side.z * (off / pl.def.radius));
        const cx = (d.x + side.x * (off / pl.def.radius)) * l;
        const cy = (d.y + side.y * (off / pl.def.radius)) * l;
        const cz = (d.z + side.z * (off / pl.def.radius)) * l;
        if (pl.def.radius + pl.sampler.height(cx, cy, cz) > wsM + 0.6) { seatOff = off + 2; break; }
      }
      d.addScaledVector(side, seatOff / pl.def.radius).normalize();
      const worldDir = d.applyQuaternion(pl.pose.quat).normalize();
      const pos = pl.pose.center.clone().addScaledVector(worldDir, pl.surfaceRadiusWorld(worldDir) + 1.3);
      character.restoreAt([pos.x, pos.y, pos.z], [0, 0, 0, 1], pl);
      // Face the water: heading = toward the centreline, tangent to the surface
      // (the channel sits in a crevice now — from the rim you must LOOK at it).
      const toRiver = _drSide.multiplyScalar(-1).applyQuaternion(pl.pose.quat);
      toRiver.addScaledVector(worldDir, -toRiver.dot(worldDir)).normalize();
      character.heading.copy(toRiver); // camera + body ease onto it
      const info = `[devRiver] ${pl.def.name} river ${river.id}/${g.rivers.length - 1}, ` +
        `${(pt.s / 1000).toFixed(1)} km downstream (of ${(river.points[river.points.length - 1].s / 1000).toFixed(1)}), ` +
        `width ${(pt.half * 2).toFixed(0)} m, current ${pt.speed.toFixed(1)} m/s`;
      console.log(info);
      hud.showToast(`RIVER ${river.id} — ${(pt.half * 2).toFixed(0)}M WIDE`, 3600);
      return info;
    },
    devOutfit: () => openOutfit(),
    devCharacterEditor: () => openYou(),
    cutscenes: {
      open: () => openCutsceneEditor(),
      asset: () => cutsceneEditor?.currentAsset() ?? null,
    },
    /** DEV cloth test bed: a standalone drifter cape floating in the OVERWORLD, parented under
     *  the foot planet (rides the spin exactly like the terrain), anchor fixed (no player
     *  physics/animation), driven by the real ClothSim in the real co-rotating frame. Isolates
     *  the frame/spin/origin handling from the player character so cape jitter is diagnosable.
     *  Toggle: window.__hl.devCloak(). Optional `follow` keeps it in front of the camera. */
    devCloak: (follow = false) => toggleDevCloak(follow),
    devCloakAvatar: () => devCloak?.avatar ?? null,
    /** DEV: choose the cloth solver for the player. false = the shipped bone-chain cage
     *  (character/cloth.ts); true = PER-VERTEX PBD on the garment mesh (character/clothPbd.ts).
     *  Both ship — this A/Bs them live. Persisted, since the split happens when a garment is
     *  mounted and the mount happens on load. Also settable with ?pbdcloth=1. */
    /** DEV: collider source for the PBD path. true (default) = BVH over the real body/armour
     *  surfaces (three-mesh-bvh); false = the hand-placed capsule set. Persisted; ?pbdbvh=0. */
    devPbdBvh: (on = true) => {
      try { localStorage.setItem("hl-pbdbvh", on ? "1" : "0"); } catch { /* private mode */ }
      avatar.setPbdBvh(on);
      return on;
    },
    /** DEV: per-frame cost of the BVH collider pipeline (ms totals since last call). */
    pbdStats: () => avatar.pbdClothStats(),
    devPbdCloth: (on = true) => {
      try { localStorage.setItem("hl-pbdcloth", on ? "1" : "0"); } catch { /* private mode */ }
      avatar.setPbdCloth(on);
      return on;
    },
    pbdCloth: () => avatar.pbdClothEnabled,
    devInv: () => invPanel.open(),
    /** DEV: force the player's foot-grounding IK off (true = back on) to A/B the raw clip stance
     *  against the IK-solved one — e.g. window.__hl.devFootIK(false) at idle to check if the
     *  bow-legs come from the IK knee-pole solve or the idle clip itself. */
    devFootIK: (on = false) => { avatar.footIkEnabled = on; },
    /** DEV: hold a real catalog weapon instead of the mode construct (null = back to the mode).
     *  The player's loadout is still the three energy constructs, so this is how a carried gun gets
     *  into your hands at all: window.__hl.devRifle() then RMB to aim. */
    devRifle: (id: string | null = "rifle-mk1") => { devHeldWeapon = id; return devHeldWeapon; },
    devFreezeCam: (v: boolean) => { devFreezeCam = v; },
    devTimeWarp: (seconds: number) => { devTimeOffset = seconds; },
    /** DIAG: force the avatar body/clothing lighting floor. `devBodyFill(0)` kills the self-lit
     *  floor (isolates whether the "glowy at night" look is the floor vs the ambient response to
     *  the baked-bright vertex colours); `devBodyFill(1)` = full daylight floor; `devBodyFill()`
     *  (no arg / null) restores automatic day/night fading. */
    devBodyFill: (v: number | null = null) => { bodyFillOverride = v; },
    devFreezePoses: (t: number | null) => { devFrozenPoseTime = t; },
    devVolcanoTime: setVolcanicReviewTime,
    devWeatherTime: setWeatherReviewTime,
    worldTimeNow: () => simTime,
    weather: () => playerWeather,
    devWeather: (preset: WeatherPreset | null = null) => { WEATHER.preset = preset; return WEATHER; },
    atmosphere: WEATHER,
    precip: () => precipFx,
    /** DEV: bind the on-foot planet context to a body id (restoreAt moves the avatar but not this). */
    devFootPlanet: (id: string) => { footPlanet = system.planets.find((q) => q.def.id === id) ?? footPlanet; },
    /** DEBUG: weather at a planet-local unit dir on the foot planet, at the current worldTime. */
    weatherAt: (dx: number, dy: number, dz: number): WeatherColumn | null => {
      if (!footPlanet || !footPlanet.def.atmosphere) return null;
      const o: WeatherColumn = { cloud: 0, precip: 0, kind: 0, windx: 0, windy: 0, windz: 0, windSpeed: 0 };
      return sampleWeather(footPlanet.sampler, footPlanet.def, dx, dy, dz, worldTime(), o);
    },
    bolts,
    invIntent,
    station: () => station,
    worldTime,
    waypoint: {
      set: (id: string, name: string) => {
        waypoint = { id, name };
      },
      get: () => waypoint,
    },
    dock: { at: (i: number) => dockAt(i), undock: () => undock(), pad: () => dockedPad },
    // C3 quests: the live model, a raw intent sender, and a board force-open.
    quests: () => quests,
    questIntent: (m: Omit<QuestIntentMsg, "t" | "seq">) => questIntent(m),
    questBoardOpen: () => {
      const b = nearestQuestBoard();
      if (!b) return null;
      questBoardPanel.open(b.settlement, b.planet.def, b.roster, questEpoch(net?.serverNow() ?? Date.now() / 1000));
      return b.settlement.id;
    },
    /** PROBE: the TAB scan — is it on, and what is it pointing at right now (with
     *  each target's distance from the PLAYER, which is what the range cap gates on)?
     *  (The DOM markers fade in/out over rendered frames, which stall badly in
     *  headless; the model is the truth the fades are chasing.)
     *  `elev` is each anchor's elevation in DEGREES from the camera's local
     *  horizontal. Place tags should match `horizon`, the geometric tangent
     *  elevation (negative at camera height). `clamp` reports the state the
     *  surface projection gates on (mode + footPlanet). */
    scan: () => {
      const from = mode === "vehicle" ? groundVehicle.pos : footPlanet ? character.pos : ship.pos;
      const camW = new THREE.Vector3().copy(camera.position).add(origin);
      const upPlanet = footPlanet ?? system.nearestPlanet(camW)?.planet ?? null;
      const renderCenter = upPlanet ? upPlanet.group.position.clone().add(origin) : null;
      const up = renderCenter ? camW.clone().sub(renderCenter).normalize() : null;
      const eyeR = renderCenter ? camW.distanceTo(renderCenter) : 0;
      const surfaceR = upPlanet && renderCenter ? markerHorizonRadius(upPlanet, renderCenter, camW) : 0;
      const horizon = eyeR > 0
        ? Number((-Math.acos(Math.max(0, Math.min(1, surfaceR / eyeR))) * 180 / Math.PI).toFixed(2))
        : null;
      const v = new THREE.Vector3();
      return {
        on: bodyMarkersOn,
        mode,
        horizon,
        clamp: { foot: footPlanet ? (footPlanet.def.name || footPlanet.def.seed) : null, active: mode === "foot" || mode === "vehicle" },
        // The key carries the settlement ID: settlement NAMES repeat across cells
        // (a "Reunion" on one world is not the "Reunion" on the next), so anything
        // comparing scan output against another list has to compare identities.
        targets: buildBodyTargets().map((t) => ({
          name: t.name,
          key: t.key ?? t.name,
          // trueDist first: on-surface tags carry a horizon-clamped ANCHOR in
          // pos, so measuring the anchor would misreport the subject's range.
          d: Math.round(t.trueDist ?? t.pos.distanceTo(from)),
          elev: up ? Number((Math.asin(Math.max(-1, Math.min(1, v.copy(t.pos).sub(camW).normalize().dot(up)))) * 180 / Math.PI).toFixed(2)) : null,
        })),
      };
    },
    /** PROBE: what the PlaceIndex itself holds near the player, BEFORE the scan's
     *  hush/occlusion/count filters — separates "the index missed it" from "a filter
     *  dropped it" when a town that should be tagged is not. */
    placesNear: () => {
      const onSurface = (mode === "foot" || mode === "vehicle") && footPlanet;
      const planet = onSurface ? footPlanet : system.nearestPlanet(ship.pos)?.planet ?? null;
      if (!planet) return [];
      const from = mode === "vehicle" ? groundVehicle.pos : onSurface ? character.pos : ship.pos;
      const range = mode === "vehicle" ? PLACE_RANGE_VEHICLE : onSurface ? PLACE_RANGE_FOOT : PLACE_RANGE_FLY;
      const v = new THREE.Vector3();
      return places.near(planet, from, range, []).map((p) => ({
        id: p.id,
        name: p.name,
        d: Math.round(from.distanceTo(places.world(planet, p, v))),
      }));
    },
    /** PROBE: every dealt settlement seed on a planet, planet-local — the candidate
     *  sites the scan measures range against (ghosts included; the scan drops those). */
    placeSeeds: (id: string): [number, number, number][] => {
      const planet = system.planets.find((q) => q.def.id === id);
      return planet ? places.seeds(planet).map((s) => [s.local.x, s.local.y, s.local.z]) : [];
    },
    /** PROBE: nearest board kiosk to the player — its town seed + SIM-space pos. */
    nearestBoard: () => {
      const b = nearestQuestBoard();
      return b
        ? { seed: b.settlement.id, name: b.settlement.name, planetId: b.planet.def.id, pos: [b.pos.x, b.pos.y, b.pos.z] as [number, number, number] }
        : null;
    },
    /** PROBE: analytic SIM-space position of a W4 resident by id at tSec. */
    npcPoseWorld: (npcId: string, tSec?: number) => {
      const t = typeof tSec === "number" ? tSec : Date.now() / 1000;
      return npcLife?.poseWorldById(npcId, t) ?? null;
    },
  },
});

/** Swap the live ship for one built from `layout`; returns the new group. */
function applyShipLayout(layout: ShipLayout): THREE.Group {
  currentLayout = layout;
  // A rebuilt group is visible by default — carry the old state over, or this
  // silently re-shows the hull a shipless character just had hidden.
  const wasVisible = built.group.visible;
  scene.remove(built.group);
  disposeShip(built);
  built = buildShipFromLayout(layout, 0.58);
  built.group.visible = wasVisible;
  built.group.position.copy(ship.pos).sub(origin);
  built.group.quaternion.copy(ship.quat);
  scene.add(built.group);
  trails.setTips(built.thrusterTips);
  return built.group;
}

const origin = new THREE.Vector3();
const _audioPt = new THREE.Vector3(); // scratch: array hit-points → Vector3 for positional SFX
const _boltDir = new THREE.Vector3(); // scratch: bolt-contact travel dir → victim flinch
// Reused per-frame lists of OTHER entities that emit positional sound (so a ship/
// player flying away attenuates + pans). Rebuilt each frame from net.remotes.
const _audioShips: { id: number; pos: THREE.Vector3; speed: number }[] = [];
const _audioJetters: { id: number; pos: THREE.Vector3; jet: string; power?:number }[] = [];
const _musicLocal = new THREE.Vector3();
let musicPlace: PlaceContext | undefined;
let musicPlaceQueryAt = 0;
// Audio: one listener on the shared camera; emitters are placed at worldPos−origin
// (the floating origin cancels against the camera). Context starts suspended until
// the launch gesture unlocks it.
audio.init(camera, origin);

const footstepProbe = new FootstepSurfaceProbe();
const footstepPoint = new THREE.Vector3(), footstepUp = new THREE.Vector3(), footstepDelta = new THREE.Vector3();
function playerFootstepSurface(side: 0 | 1): FootstepSurface {
  if (!footPlanet) return 'stone';
  // Probe in the same interpolated render frame as the visible floor. Project
  // the contacting ankle onto the controller's floor plane so an animated
  // raised foot cannot accidentally query a tabletop or miss the floor.
  const foot = avatar.group.getObjectByName(side === 0 ? 'foot_l' : 'foot_r');
  avatar.group.updateWorldMatrix(true, false);
  footstepUp.set(0, 1, 0).transformDirection(avatar.group.matrixWorld);
  if (foot) { foot.updateWorldMatrix(true, false); foot.getWorldPosition(footstepPoint); }
  else avatar.group.getWorldPosition(footstepPoint);
  avatar.group.getWorldPosition(footstepDelta);
  footstepDelta.sub(footstepPoint);
  footstepPoint.addScaledVector(footstepUp, footstepDelta.dot(footstepUp));
  return footstepProbe.sampleAt(footPlanet, footstepPoint);
}

// Foot IK ground sampler: lets the avatar query the planet surface (point + normal) beneath any
// render-space position so it can plant + tilt each foot to the terrain. Reads the LIVE footPlanet +
// origin each call (both change on warp / every frame). No-op (false) when not on a planet.
{
  const _gpDir = new THREE.Vector3();
  const _gpNrm: number[] = [0, 0, 0];
  const _gpInv = new THREE.Quaternion();
  const _gpCliffPoint = [0, 0, 0], _gpCliffHit = cliffHit();
  avatar.groundProbe = (worldPos, outPoint, outNormal) => {
    if (!footPlanet) return false;
    // Match the NPC probe: avatar, visible terrain, point and normal all use
    // the planet's interpolated RENDER transform. Mixing pose (latest sim tick)
    // with the interpolated avatar made planted player feet sawtooth at rest.
    _gpDir.copy(worldPos).sub(footPlanet.group.position)
      .applyQuaternion(_gpInv.copy(footPlanet.group.quaternion).invert());
    const r = _gpDir.length();
    if (r < 1e-3) return false;
    _gpDir.multiplyScalar(1 / r);
    let groundR = surfaceSupportRadius(footPlanet.sampler, _gpDir.x, _gpDir.y, _gpDir.z);
    _gpCliffPoint[0] = _gpDir.x * (r + .3); _gpCliffPoint[1] = _gpDir.y * (r + .3); _gpCliffPoint[2] = _gpDir.z * (r + .3);
    const ledge = footPlanet.terrainSolids?.supportBelow(_gpCliffPoint, 2, _gpCliffHit);
    const onLedge = ledge && Math.hypot(...ledge.point) > groundR;
    if (onLedge) groundR = Math.hypot(...ledge.point);
    outPoint.copy(_gpDir).multiplyScalar(groundR)
      .applyQuaternion(footPlanet.group.quaternion).add(footPlanet.group.position);
    if (onLedge) { _gpNrm[0] = ledge.normal[0]; _gpNrm[1] = ledge.normal[1]; _gpNrm[2] = ledge.normal[2]; }
    else surfaceSupportNormal(footPlanet.sampler, _gpDir.x, _gpDir.y, _gpDir.z, _gpNrm, 2.0);
    outNormal.set(_gpNrm[0], _gpNrm[1], _gpNrm[2])
      .applyQuaternion(footPlanet.group.quaternion).normalize();
    return true;
  };
}

// System-space re-ground for humanoid enemies (DroneSwarm.driveHumanoid): plant
// their feet on THIS client's terrain. Drones stream in WORLD space, but a foot
// character on the spinning planet floats/sinks by the terrain-height diff at
// the pose/clock offset — re-grounding to the local surface re-plants them.
const _grDir = new THREE.Vector3();
const _grCliffContacts = new TerrainContacts();
function groundForDrones(sysPos: THREE.Vector3, outPoint: THREE.Vector3, outUp: THREE.Vector3): boolean {
  if (!footPlanet) return false;
  _grDir.copy(sysPos).sub(footPlanet.pose.center);
  const r = _grDir.length();
  if (r < 1e-3) return false;
  outUp.copy(_grDir).divideScalar(r); // radial up
  let groundR = footPlanet.surfaceRadiusWorld(outUp);
  const ledge = _grCliffContacts.support(footPlanet, sysPos, .1, Math.max(2, r - groundR));
  if (ledge !== null && ledge > groundR) groundR = ledge;
  outPoint.copy(footPlanet.pose.center).addScaledVector(outUp, groundR);
  return true;
}

// Spawn high above the first planet's day side, facing it. Jittered per
// session so simultaneous pilots don't stack into apparent "clones".
function placeShipAtSpawn(): void {
  const p0 = system.planets[0];
  const c0 = p0.pose.center;
  const toStar = c0.clone().multiplyScalar(-1).normalize();
  const jitter = new THREE.Vector3(
    0.25 + (Math.random() - 0.5) * 0.3,
    0.35 + (Math.random() - 0.5) * 0.3,
    0.2 + (Math.random() - 0.5) * 0.3,
  );
  const spawnDir = toStar.addScaledVector(jitter, 1).normalize();
  ship.pos.copy(c0).addScaledVector(spawnDir, p0.def.radius * (3.4 + Math.random() * 0.4));
  resolveArrivalClearance(ship.pos);
  const look = new THREE.Matrix4().lookAt(ship.pos, c0, new THREE.Vector3(0, 1, 0));
  ship.quat.setFromRotationMatrix(look);
  ship.vel.set(0, 0, 0);
  ship.speed = 0;
  // Arrive parked, simply FACING the planet — accelerating is the player's call.
  ship.throttle = 0;
  ship.snapVisual(); // teleport: don't interpolate across it
  origin.copy(ship.pos);
}
placeShipAtSpawn();
resetIntroBackground();

// --- Networking ---
let net: NetClient | null = null;
let netStatus: { s: "connecting" | "online" | "offline"; players: number } = {
  s: "offline",
  players: 0,
};

function startNet(name: string): void {
  net = new NetClient(
    scene,
    name,
    SYSTEM_ID,
    () => {
      // Void editors (locker/hangar): the live sim pose is frozen in world
      // coords while the planet turns — streaming it made onlookers watch
      // this player drift off the ground. Stream the VOID ANCHOR instead:
      // the exact local spot they left (and will return to), re-derived
      // from the live pose, zero velocity.
      if (inVoid && voidAnchor) {
        const pose = voidAnchor.planet.pose;
        const pl = system.planets.indexOf(voidAnchor.planet);
        netLocV.copy(voidAnchor.shipLocal).applyQuaternion(pose.quat).add(pose.center);
        const shipW: [number, number, number] = [netLocV.x, netLocV.y, netLocV.z];
        const sl = voidAnchor.shipLocal;
        if (footPlanet) {
          netSLocV.copy(voidAnchor.charLocal).applyQuaternion(pose.quat).add(pose.center);
          const cl = voidAnchor.charLocal;
          return {
            p: [netSLocV.x, netSLocV.y, netSLocV.z],
            q: [character.quat.x, character.quat.y, character.quat.z, character.quat.w],
            v: [0, 0, 0],
            k: "foot" as const,
            sp: shipW,
            sq: [ship.quat.x, ship.quat.y, ship.quat.z, ship.quat.w],
            ...(pl >= 0
              ? {
                pl,
                lp: [cl.x, cl.y, cl.z] as [number, number, number],
                lr: [
                  voidAnchor.charLocalQ.x,
                  voidAnchor.charLocalQ.y,
                  voidAnchor.charLocalQ.z,
                  voidAnchor.charLocalQ.w,
                ] as [number, number, number, number],
                slp: [sl.x, sl.y, sl.z] as [number, number, number],
                slr: [
                  voidAnchor.shipLocalQ.x,
                  voidAnchor.shipLocalQ.y,
                  voidAnchor.shipLocalQ.z,
                  voidAnchor.shipLocalQ.w,
                ] as [number, number, number, number],
              }
              : {}),
          };
        }
        return {
          p: shipW,
          q: [ship.quat.x, ship.quat.y, ship.quat.z, ship.quat.w],
          v: [0, 0, 0] as [number, number, number],
          ...(pl >= 0
            ? {
              pl,
              lp: [sl.x, sl.y, sl.z] as [number, number, number],
              lr: [
                voidAnchor.shipLocalQ.x,
                voidAnchor.shipLocalQ.y,
                voidAnchor.shipLocalQ.z,
                voidAnchor.shipLocalQ.w,
              ] as [number, number, number, number],
            }
            : {}),
        };
      }
      if (groundVehicle.occupied && footPlanet) {
        const pl = system.planets.indexOf(footPlanet);
        const pose = footPlanet.pose;
        netLocV.copy(groundVehicle.pos).sub(pose.center).applyQuaternion(pose.invQuat);
        netLocQ.copy(groundVehicle.quat).premultiply(pose.invQuat);
        return {
          p: [groundVehicle.pos.x, groundVehicle.pos.y, groundVehicle.pos.z],
          q: [groundVehicle.quat.x, groundVehicle.quat.y, groundVehicle.quat.z, groundVehicle.quat.w],
          v: [groundVehicle.vel.x, groundVehicle.vel.y, groundVehicle.vel.z],
          k: "vehicle" as const,
          veh: groundVehicle.def.id,
          vb: groundVehicle.def.boost ? groundVehicle.boostEnergy / groundVehicle.def.boost.capacity : 0,
          ...(pl >= 0 ? {
            pl,
            lp: [netLocV.x, netLocV.y, netLocV.z] as [number, number, number],
            lr: [netLocQ.x, netLocQ.y, netLocQ.z, netLocQ.w] as [number, number, number, number],
          } : {}),
        };
      }
      return footPlanet
        ? {
          p: [character.pos.x, character.pos.y, character.pos.z],
          q: [character.quat.x, character.quat.y, character.quat.z, character.quat.w],
          v: [character.vel.x, character.vel.y, character.vel.z],
          k: "foot" as const,
          // The parked ship's pose — omitted entirely when no ship is owned,
          // or remotes would render a ghost hull nobody has.
          ...(shipOwned ? {
            sp: [ship.pos.x, ship.pos.y, ship.pos.z] as [number, number, number],
            sq: [ship.quat.x, ship.quat.y, ship.quat.z, ship.quat.w] as [number, number, number, number],
          } : {}),
          // v9: planet-LOCAL anchoring, computed in OUR OWN frame — our
          // ground contact is exact here; receivers attach it to the
          // ground they render. No clock anywhere in the loop.
          ...((): Partial<PlayerState> => {
            const pl = system.planets.indexOf(footPlanet!);
            if (pl < 0) return {};
            const pose = footPlanet!.pose;
            netLocV.copy(character.pos).sub(pose.center).applyQuaternion(pose.invQuat);
            netLocQ.copy(character.quat).premultiply(pose.invQuat);
            const a: Partial<PlayerState> = {
              pl,
              lp: [netLocV.x, netLocV.y, netLocV.z],
              lr: [netLocQ.x, netLocQ.y, netLocQ.z, netLocQ.w],
            };
            if (shipOwned) {
              netSLocV.copy(ship.pos).sub(pose.center).applyQuaternion(pose.invQuat);
              netSLocQ.copy(ship.quat).premultiply(pose.invQuat);
              a.slp = [netSLocV.x, netSLocV.y, netSLocV.z];
              a.slr = [netSLocQ.x, netSLocQ.y, netSLocQ.z, netSLocQ.w];
            }
            return a;
          })(),
          ...(rpg.primaryWeapon() ? { w: rpg.primaryWeapon()!.id } : {}),
          // Holstered-but-equipped GUN: remotes render it on the back (constructs stow to nothing).
          ...((): Partial<PlayerState> => {
            const eq = rpg.equippedWeapon();
            return !rpg.heldWeapon() && eq?.weapon && !eq.weapon.construct && eq.weapon.class === "main"
              ? { sw: eq.id } : {};
          })(),
          // v15 animation state so remotes render flight/aim, not just ground locomotion.
          ...((): Partial<PlayerState> => {
            const md = character.moveDirLocal();
            const a: Partial<PlayerState> = {};
            if (character.jet === "hover") a.j = 1;
            else if (character.jet === "glide") a.j = 2;
            else if (character.jet === "dive") a.j = 3;
            if(character.flightPower>.01)a.fp=+character.flightPower.toFixed(2);
            if(character.flightLaunchRemaining>0)a.fl=+character.flightLaunchRemaining.toFixed(3);
            if(character.divePreparation>0)a.dp=+character.divePreparation.toFixed(3);
            if(character.flightApproach>.01)a.fa=+character.flightApproach.toFixed(2);
            if(character.wallRunState){a.wr=character.wallRunState;a.wt=+character.wallRunTime.toFixed(2);}
            if(character.ledgeClimbState){a.lc=character.ledgeClimbState;a.lt=+character.ledgeClimbTime.toFixed(3);a.le=character.ledgeAnchor.toArray().map(v=>+v.toFixed(3)) as [number,number,number];}
            if (footAimAllowed()) { a.am = true; a.pt = +character.pitch.toFixed(2); }
            if (!character.onGround) a.air = true;
            if (character.crouching) a.cr = true;
            // (hard-land stagger flag retired — remotes derive the landing from their own air→ground
            //  edge + streamed climb (a.cl); the avatar picks phase/tier locally)
            if (combatBlocking) a.bl = true; // raised shield guard — the server soaks 75%
            if (shieldAbsorbing) a.sh = true; // shield mode with energy — the server soaks 50%
            // Invulnerable window (dodge i-frame / parry) — the server reads this to negate an
            // incoming hit it can't time itself (SERVER_AUTHORITATIVE_HP mitigation).
            if (character.iframeT > 0 || parryActive()) a.iv = true;
            // The PARRY window specifically (rides beside iv): the server STAGGERS a melee
            // attacker landing in it and DEFLECTS a bolt — consequences, not just negation.
            if (parryActive()) a.py = true;
            if (character.submergeK > 0.05) a.sub = +character.submergeK.toFixed(2); // wade/swim → remote pose
            if (Math.abs(md.fwd) > 0.01) a.mf = +md.fwd.toFixed(2);
            if (Math.abs(md.right) > 0.01) a.mr = +md.right.toFixed(2);
            if (Math.abs(md.climb) > 0.05) a.cl = +md.climb.toFixed(2);
            return a;
          })(),
        }
        : {
          p: [ship.pos.x, ship.pos.y, ship.pos.z],
          q: [ship.quat.x, ship.quat.y, ship.quat.z, ship.quat.w],
          v: [ship.vel.x + shipCarryVel.x, ship.vel.y + shipCarryVel.y, ship.vel.z + shipCarryVel.z],
          ...(dockedPad !== null ? { dock: dockedPad } : {}),
          // Low flight anchors like a walker: a ship inside the carry
          // band is ground-relative to everyone watching from the
          // surface, and the receive-side blend fades it out by 2.6R.
          ...((): Partial<PlayerState> => {
            if (dockedPad !== null) return {};
            const near = system.nearestPlanet(ship.pos);
            if (!near || near.surfaceDist > near.planet.def.radius * 1.6) return {};
            const pl = system.planets.indexOf(near.planet);
            if (pl < 0) return {};
            const pose = near.planet.pose;
            netLocV.copy(ship.pos).sub(pose.center).applyQuaternion(pose.invQuat);
            netLocQ.copy(ship.quat).premultiply(pose.invQuat);
            return {
              pl,
              lp: [netLocV.x, netLocV.y, netLocV.z],
              lr: [netLocQ.x, netLocQ.y, netLocQ.z, netLocQ.w],
            };
          })(),
        };
    },
    () => currentLayout,
    () => playerStyle,
    (s, players) => {
      netStatus = { s, players };
    },
    (remote, g, d, hardpointId) => {
      const muzzle = new THREE.Vector3();
      const dir = new THREE.Vector3();
      if (remote.kind === "foot") {
        // On foot: one bolt from the held weapon along the streamed aim.
        const def = remote.weaponId ? ITEMS[remote.weaponId] : undefined;
        if (!d || !def?.weapon) return;
        if (!(remote.avatar && remote.avatar.muzzleWorld(muzzle))) muzzle.copy(remote.sysPos);
        else muzzle.add(origin); // render -> system space
        dir.set(...d).normalize();
        bolts.spawn(muzzle, dir, droneVelZero, g, {
          speed: def.weapon.projectileSpeed,
          damage: def.weapon.damage,
          wp: def.id, range: def.weapon.range, predicted: SERVER_AUTHORITATIVE_BOLTS,
          ignore: { kind: "char", id: remote.id },
        });
        remote.avatar?.shoot();
        audio.sfx("weapon.remote.fire", { at: muzzle });
        return;
      }
      if (remote.kind === "vehicle") {
        const vehicle = vehicleDef(remote.vehicleId ?? "");
        const hardpoint = vehicle && hardpointId
          ? vehicle.hardpoints.find((candidate) => candidate.id === hardpointId)
          : null;
        const weapon = vehicle && hardpoint?.weapon ? vehicleWeapon(vehicle, hardpoint.weapon) : null;
        if (!hardpoint || hardpoint.kind !== "weapon" || hardpoint.group !== g || !weapon) return;
        muzzle.set(...hardpoint.position).applyQuaternion(remote.quat).add(remote.sysPos);
        if (d) dir.set(...d).normalize();
        else dir.set(...hardpoint.direction).applyQuaternion(remote.quat).normalize();
        bolts.spawn(muzzle, dir, droneVelZero, g, {
          speed: weapon.projectileSpeed,
          damage: weapon.damage,
          ignore: { kind: "ship", id: remote.id },
        });
        audio.sfx("weapon.ship.fire", { at: muzzle });
        return;
      }
      // In flight: bolts from every mount in the group, along the
      // shooter's converged aim when streamed (mount axes otherwise) —
      // same exact-line model as local fire, speed-boosted by closing rate.
      for (const mount of remote.mounts) {
        if (mount.group !== g) continue;
        muzzle.copy(mount.offset).applyQuaternion(remote.quat).add(remote.sysPos);
        if (d) dir.set(...d).normalize();
        else dir.copy(mount.dir).applyQuaternion(remote.quat);
        bolts.spawn(muzzle, dir, droneVelZero, g, {
          speed: 700 + Math.max(0, remote.vel.dot(dir)),
          ignore: { kind: "ship", id: remote.id },
        });
      }
      audio.sfx("weapon.ship.fire", { at: remote.sysPos });
    },
    (to, p) => {
      wormholes.spawn(p, to);
      audio.sfx("ship.warp", { at: _audioPt.set(p[0], p[1], p[2]) }); // remote jump → positional
    },
    {
      snapshot: (list, timeMs) => drones.applySnapshot(list, timeMs),
      fire: (msg) => {
        // All clients render the drone's bolt; only the target's collider
        // takes damage when it connects (drained below). Spawn from the NPC
        // AS DRAWN (humanoid muzzle / rendered body), not msg.p — the server's
        // sim pose is interp-buffer + planet-carry metres from the visible
        // enemy, which read as "bolts from nearby the NPC" (user 2026-08-17).
        // Direction stays the server's (its aim is the truth; a small parallel
        // origin shift is the price of the bolt leaving the visible gun).
        if (!drones.fireOriginSys(msg.id, origin, fireMuzzle)) fireMuzzle.set(...msg.p);
        fireDir.set(...msg.d);
        bolts.spawn(fireMuzzle, fireDir, droneVelZero, 2, {
          speed: msg.bs ?? 130,
          damage: msg.dmg ?? 8,
          ignore: { kind: "drone", id: msg.id },
        });
        bolts.muzzleFlash(fireMuzzle); // same origin anchor the player's shots get
        audio.sfx("weapon.drone.fire", { at: fireMuzzle });
      },
      melee: (msg) => { drones.melee(msg); },
      act: (msg) => { drones.act(msg); },
      death: (msg) => {
        // Mesh kinds return their position → boom. Humanoids return null: they play the
        // knockdown reel and fade instead (drones.death) — no explosion on a body.
        const at = drones.death(msg.id);
        if (at) {
          bolts.flash(at, 3);
          audio.sfx("explosion.drone", { at });
        }
      },
      loot: (items) => {
        audio.sfx("loot");
        const names: string[] = [];
        for (const it of items) {
          addItem(rpg.inventory, it.id, it.n);
          names.push(`${ITEMS[it.id]?.name ?? it.id} ×${it.n}`);
        }
        rpg.rev++;
        hud.showToast(`SALVAGE — ${names.join(", ").toUpperCase()}`, 3200);
      },
      nodes: (list) => {
        for (const { id, left } of list) latestNodeState.set(id, left);
        resourceNodes?.setLeft(list);
      },
      mined: (msg) => {
        // The grant lands via the server's InvStateMsg push — only the
        // toast is client-side here.
        const name = ITEMS[msg.item]?.name ?? msg.item;
        hud.showToast(
          msg.left > 0 ? `+${msg.n} ${name.toUpperCase()} (${msg.left} LEFT)` : `+${msg.n} ${name.toUpperCase()} — VEIN DRY`,
          1600,
        );
      },
    },
    () => getIdToken(),
    () => system,
    (id) => accessTicketFor(id),
    () => activeCharacterRecord?.id ?? null,
  );
  net.onSystemReveal = async (reveal) => {
    await acceptSystemReveal(reveal);
    if (reveal.systemId === SYSTEM_ID) rebuildRevealedResources();
  };
  net.onWildlife = msg => wildlife.snapshot(msg);
  net.onTradeState = (st) => {
    if (st.phase === "invite") {
      // Inviter sees a toast; the target gets the accept prompt.
      if (stationPanel.visible) stationPanel.showInvite(st.peer?.name ?? "PILOT");
      hud.showToast(`TRADE INVITE — ${(st.peer?.name ?? "pilot").toUpperCase()}`, 3000);
      return;
    }
    if (st.phase === "open") {
      if (!tradeWindow.visible) tradeWindow.open(st.peer?.name ?? "PILOT");
      tradeWindow.setState(
        st.you ?? { items: [], credits: 0 },
        st.them ?? { items: [], credits: 0 },
        st.youConfirmed ?? false,
        st.themConfirmed ?? false,
        st.err,
      );
      return;
    }
    if (st.phase === "done") {
      tradeWindow.close();
      hud.showToast("TRADE COMPLETE", 2600);
      return;
    }
    tradeWindow.close();
    stationPanel.hideInvite();
    if (st.err) hud.showToast(st.err, 2600);
  };
  net.onBoardState = (listings, _seq, err) => {
    stationPanel.setBoard(listings, err);
    if (err) hud.showToast(err, 2400);
  };
  net.onMarketState = (prices, _seq, err) => {
    stationPanel.setPrices(prices, err);
    if (err) hud.showToast(err, 2400);
  };
  net.onInvState = (rawInv, credits, fuelKit, seq, rawUpgrades, err) => {
    // The reveal gate resolves on the FIRST echo of the connection — even a
    // guest-seat echo (below) must release it, or a broken session waits out
    // the full timeout on every login.
    invEchoSeen = true;
    firstInvEchoResolve?.();
    firstInvEchoResolve = null;
    // GUEST-SEAT GUARD: signed in locally, but the server didn't verify the
    // login — its "account" is a blank guest inventory. Adopting it here wiped
    // the restored bag (worn armor included), and the periodic cloud save then
    // persisted the wipe. Keep the locally-restored truth instead.
    if (authUser && net && !net.authed) return;
    if (seq > 0) invAck = seq;
    // Upgrade levels are pure server truth (no optimistic local edit to protect), so they
    // converge on EVERY echo — including a refused purchase, which arrives with `err`.
    const levels = validateUpgrades(rawUpgrades);
    if (levels) {
      rpg.applyUpgrades(levels);
      character.applyCombatStats(rpg.stats);
    }
    if (err) {
      if (characterCreator.active) characterCreator.setError(err);
      hud.showToast(err, 2400);
    }
    // Converge only when nothing is in flight — an ack for an older intent
    // (or a grant racing a local edit) must not roll back optimistic state;
    // the final ack carries the cumulative truth anyway.
    if (invAck !== invSeq) return;
    const inv = validateInventory(rawInv);
    if (!inv) return;
    rpg.inventory = inv;
    rpg.credits = credits;
    rpg.fuelKit = fuelKit;
    rpg.rev++;
    // The account echo is where the AUTHORITATIVE equipped map lands on connect
    // — re-derive the worn look from it (login restored from the local profile
    // mirror, which can be stale or absent on a fresh device).
    const shown = styleShowingEquipped(playerStyle);
    if (shown !== playerStyle) applyStyle(shown);
    stationPanel.refresh();
    characterCreator.refreshStats(); // the YOU screen's stats page, when open
  };
  // Authoritative HP (SERVER_AUTHORITATIVE_HP): the server owns damage/regen/respawn. The
  // streamed value reconciles against still-pending local PREDICTIONS (applyServerHealth keeps
  // them subtracted for ~RTT so the bar doesn't pop back up). Death is stream-driven only.
  net.onHealth = (hp, max, dead, sh, shMax) => {
    // Flare the hex shell when the SHIELD eats a hit (the authoritative pool
    // dropped) — the flare is the only thing that says "absorbed, not hurt".
    // NOT after a shield→energy draw: the stream dips then too (plus the
    // reconcile bounce), and a conversion the player asked for must not read
    // as an impact.
    if (sh !== undefined && sh < rpg.shield - 0.5 && !dead && footPlanet
      && performance.now() - lastShieldDrawAt > 1200
      && performance.now() - lastDirectionalShieldFlareAt > 600) {
      _hexFrom.copy(character.pos).addScaledVector(character.up, 1.0);
      _hexAt.copy(_hexFrom).addScaledVector(camera.getWorldDirection(_hexDir), 4);
      shieldHex.setColor(playerStyle.auraColor ?? 0xffffff); // the shield IS the ki
      shieldHex.hit(_hexFrom.sub(origin), _hexAt.sub(origin));
      if (sh <= 0 && rpg.shield > 0) audio.sfx("combat.block"); // the break
    }
    rpg.applyServerHealth(hp, max, dead, sh, shMax);
    if (dead && !serverDead) { serverDead = true; doDeath(); }
    else if (!dead) serverDead = false;
  };
  // NPC relationship echo → the open dialog (state + salt, text derived).
  net.onNpcRel = (m) => dialogPanel.applyRel(m);
  net.onNpcShop = (m) => dialogPanel.applyShop(m);
  // Owner-only quest state → the log mirror (which diffs for toasts); refresh
  // any open journal / board / dialog choices off the new state.
  net.onQuestState = (m) => {
    quests.apply(m);
    if (journalPanel.visible) journalPanel.render();
    if (questBoardPanel.visible) questBoardPanel.refresh();
    refreshDialogQuestActions();
    void syncPrologueBeats(); // SOUL kickoff: quest progress drives the stage machine
  };
  net.onStoryState = (m) => {
    storyState = m.state;
    dialogPanel.applyStory(m);
  };
  net.onQuestBoard = (m) => questBoardPanel.onBoard(m);
  // Chat relay → the log (server-sanitized; the panel renders textContent-only)
  // + a WoW-style overhead bubble for LOCAL lines. The sender's own bubble
  // rides the server ECHO (delivery receipt) — never the optimistic send —
  // attached to whatever currently renders the player (avatar on foot, hull
  // otherwise; `built` is the live group even across refits).
  // Room scrollback on join/warp — fills the log so it isn't empty at login.
  // History only ever touches the panel: no bubbles for lines already said.
  net.onChatHist = (m) => chatPanel?.history(m.lines);
  // Friends: adopt every push (login seed / op acks / presence), re-render
  // the panel when it's up (the journal's rev-driven idiom).
  // Garage/wardrobe (v49): adopt the push. It does NOT touch the parked chassis —
  // what is deployed is whatever you summoned, not whatever is marked active.
  net.onCollection = (m) => {
    garage.apply(m);
    if (garagePanel.visible) garagePanel.render();
    characterCreator.refreshWardrobe();
  };
  net.onSocialState = (m) => {
    socialModel?.apply(m);
    if (socialPanel?.visible) socialPanel.render();
  };
  // Party: same adopt-and-rerender loop; the frames pick it up on the next
  // frame (they compare `rev`), and `jump` arms the follow-jump prompt.
  net.onPartyState = (m) => {
    partyModel?.apply(m, performance.now());
    if (socialPanel?.visible) socialPanel.render();
  };
  // Guild: same adopt-and-rerender loop (the login push may carry a reconcile
  // notice — "you were removed while you were away" — which the model toasts).
  net.onGuildState = (m) => {
    guildModel?.apply(m, performance.now());
    if (socialPanel?.visible) socialPanel.render();
  };
  // CACHES: ONE handler for both jobs the frame does, because the server sends the
  // same frame for both — the instance you may now enter, and the live progress of
  // the one you are in. Which it is comes from comparing the id, never from a flag.
  net.onCacheState = (m) => {
    const info = cacheInfoOf(m);
    if (!info) return; // malformed frame: never guess a dungeon
    cacheProg = m.prog;
    if (info.instance !== SYSTEM_ID) {
      enterCacheInstance(info);
      return;
    }
    cacheInfo = info;
    liveCache?.applyProg(m.prog);
  };
  // CONTAINMENT (the envelope rule). The server owns the bound; this is purely
  // "you are near it" / "you have been put back". Edge-triggered, so the toast fires
  // once per crossing rather than every tick.
  net.onCacheEnv = (m) => {
    if (!liveCache) return; // not in a dressed instance: nothing to warn about or move
    if (m.to && Array.isArray(m.to) && m.to.length === 2 && Number.isFinite(m.to[0]) && Number.isFinite(m.to[1])) {
      // Seat on OUR OWN ground at the server's pad-local point: the server sends x/z
      // and deliberately not a height, because the dressing's floor is a client fact.
      // placeAtLocalAnchor is the same seam entry, the homecoming and the prologue
      // spawn through — grounding and the carry frame are not reinvented here.
      const back = liveCache.localPointAt(m.to[0], m.to[1], new THREE.Vector3());
      placeAtLocalAnchor(SYSTEM_ID, 0, [back.x, back.y, back.z], liveCache.entranceFacing());
      mode = "foot";
      hud.showToast("CONTAINMENT FIELD — RETURNED TO ENTRANCE", 2800);
      return;
    }
    if (m.warn) hud.showToast("CONTAINMENT FIELD — TURN BACK", 2200);
  };
  net.onChatMsg = (m) => {
    chatPanel?.append(m);
    if (m.ch !== "local" || m.err || m.from === "") return;
    if (m.from === activeCharacterRecord?.name) {
      if (mode === "foot") ownBubble.show(m.text, avatar.group, 6, 3.0);
      else ownBubble.show(m.text, built.group, 14, 5.1);
    } else {
      net?.showBubbleFor(m.from, m.text);
    }
  };
  // Homesteads: authoritative land-claim snapshot for the current system (owner-only push).
  // A refused intent (over budget / caps / too far) rides `err` → toast. Beacons are
  // validated on the wire (like invState.inventory) before they become markers.
  net.onHsState = (m) => {
    if (m.err) hud.showToast(m.err, 2400);
    if (!homesteadMarkers) return;
    homesteadMarkers.setSelf(currentUid());
    const beacons: Beacon[] = [];
    for (const raw of m.beacons ?? []) {
      const b = validateBeacon(raw);
      if (b) beacons.push(b);
    }
    homesteadMarkers.setBeacons(beacons);
    const priorRooms = new Map<string, ReturnType<HomesteadPieces["roomsOf"]>>();
    if (hsSnapshotReady && homesteadPieces) {
      for (const beacon of beacons) priorRooms.set(beacon.id, homesteadPieces.roomsOf(beacon.id));
    }
    // Placed pieces ride the same authoritative push, validated per entry.
    const pieces: Record<string, PlacedPiece[]> = {};
    for (const [beaconId, raws] of Object.entries(m.pieces ?? {})) {
      const list: PlacedPiece[] = [];
      for (const raw of raws) {
        const p = validatePlacedPiece(raw);
        if (p) list.push(p);
      }
      if (list.length > 0) pieces[beaconId] = list;
    }
    homesteadPieces?.setAll(beacons, pieces);
    if (hsSnapshotReady && homesteadPieces) {
      for (const beacon of beacons) announceHomesteadRoomChange(
        priorRooms.get(beacon.id) ?? [], homesteadPieces.roomsOf(beacon.id));
    }
    hsSnapshotReady = true;
    // The owner's cross-system roster (panel data).
    hsMine = [];
    for (const raw of m.mine ?? []) {
      const b = validateBeacon(raw);
      if (b) hsMine.push(b);
    }
    const founding = foundingBeaconHereOrCurrent();
    if (founding && hsTrackedId === null) hsTrackedId = founding.id;
    announceFoundingStep();
    if (claimPanel?.visible) {
      const fresh = claimPanelBeaconId ? beacons.find((b) => b.id === claimPanelBeaconId) ?? null : null;
      openClaimPanel(fresh); // re-render with the authoritative state
    }
  };
  net.onSettlementState = (m) => {
    settlementPending.delete(m.beaconId);
    if (m.err) hud.showToast(m.err, 2400);
    const state = validateSettlementStateWire(m.state, m.beaconId);
    if (state) {
      settlementStates.set(m.beaconId, state);
      homesteadResidentLife?.applyOwner(settlementPublicState(state));
      announceFoundingStep();
      const freshEvents = state.events.filter((event) => !settlementSeenEvents.has(event.id));
      const completedGoods = new Map<string, number>();
      for (const event of freshEvents) {
        settlementSeenEvents.add(event.id);
        for (const stack of event.outputs) completedGoods.set(stack.id, (completedGoods.get(stack.id) ?? 0) + stack.n);
      }
      const progression = newSettlementProgressionNotices(m.beaconId, state);
      if (freshEvents.length > 0) {
        const goods = [...completedGoods].map(([id, n]) => `${n} ${ITEMS[id]?.name ?? id}`).join(" + ");
        const orders = freshEvents.length === 1 ? "WORK ORDER COMPLETE" : `${freshEvents.length} WORK ORDERS COMPLETE`;
        const ready = progression.length > 0 ? ` · ${progression.join(" · ")}` : "";
        hud.showToast(`${orders} — ${goods.toUpperCase()}${ready}`, 5200);
      } else if (progression.length > 0) {
        hud.showToast(progression.join(" · "), 3800);
      }
      if (state.events.length > 0) {
        net?.sendSettlement({
          seq: ++settlementSeq,
          beaconId: m.beaconId,
          op: "ack",
          eventIds: state.events.map((event) => event.id),
        });
      }
    }
    if (claimPanel?.visible && claimPanelBeaconId === m.beaconId) {
      const fresh = hsMine.find((beacon) => beacon.id === m.beaconId) ?? null;
      openClaimPanel(fresh);
    }
  };
  net.onSettlementPublic = (m) => {
    const records: SettlementPublicV1[] = [];
    for (const raw of m.settlements ?? []) {
      const record = validateSettlementPublicWire(raw);
      if (record) records.push(record);
    }
    homesteadResidentLife?.apply(records, m.full === true);
  };
  net.onHsDelta = (m) => {
    if (!homesteadMarkers) return;
    if (m.op === "piece-add" || m.op === "piece-remove") {
      if (homesteadPieces && m.beaconId) {
        const beforeRooms = homesteadPieces.roomsOf(m.beaconId);
        if (m.op === "piece-remove") {
          // A cascaded demolish removes the whole `pids` set (target + floaters).
          const pids = m.pids ?? (typeof m.pid === "number" ? [m.pid] : []);
          for (const pid of pids) homesteadPieces.applyPieceDelta("piece-remove", m.beaconId, null, pid);
        } else {
          homesteadPieces.applyPieceDelta(m.op, m.beaconId, m.piece ? validatePlacedPiece(m.piece) : null, m.pid);
        }
        const afterRooms = homesteadPieces.roomsOf(m.beaconId);
        announceHomesteadRoomChange(beforeRooms, afterRooms);
        announceFoundingStep();
        if (claimPanel?.visible && claimPanelBeaconId === m.beaconId) {
          const current = hsMine.find((beacon) => beacon.id === m.beaconId) ?? null;
          openClaimPanel(current);
          requestSettlementState(m.beaconId);
        }
      }
      return;
    }
    const beacon = m.beacon ? validateBeacon(m.beacon) : null;
    homesteadMarkers.applyDelta(m.op, beacon, m.beaconId);
    if (homesteadPieces) {
      if (m.op === "remove" && m.beaconId) homesteadPieces.removeBeacon(m.beaconId);
      else if (beacon) homesteadPieces.ensureBeacon(beacon); // container for future piece deltas
    }
  };
  // Server-verified god privilege → reveal the GOD MODE button in the ESC menu.
  // On a reconnect that drops the privilege, also close an open spawner.
  net.onGod = (on) => {
    menu.setGodMode(on);
    perf.setGodAllowed(on);
    perf.setScene(scene); // footer skin/bone census (walked only at 4 Hz paints)
    if (!on) godPanel.close();
    // GUEST-SEAT WARNING: we are signed in but the SERVER could not verify the
    // login (bad/missing server auth config, stale token). Say so loudly — this
    // used to be silent, and the blank guest echo it produces was adopted and
    // then SAVED over the real cloud inventory (armor vanished on next login).
    if (authUser && net && !net.authed) {
      hud.showToast("SERVER COULD NOT VERIFY YOUR LOGIN — PLAYING AS GUEST, PROGRESS WILL NOT SAVE", 6000);
      console.warn("[net] signed in locally but the server seated this session as a GUEST — check the server's FIREBASE_PROJECT_ID / emulator env");
    }
  };
  net.onKicked = () => {
    const el = document.createElement("div");
    el.id = "session-lockout";
    el.innerHTML = `
      <div class="sl-card">
        <h2>SIGNED IN ELSEWHERE</h2>
        <p>This account just connected from another screen, so this session
        has been disconnected. Your progress is saved.</p>
        <button id="sl-reload">TAKE OVER ON THIS SCREEN</button>
      </div>`;
    document.body.appendChild(el);
    document.getElementById("sl-reload")!.addEventListener("click", () => window.location.reload());
    document.exitPointerLock?.();
  };
  net.connect();
}

// --- Hyperlight travel: nav targets, charge, jump cinematic, wormholes ---
const CHARGE_TIME = 0.9; // seconds of held Space (star streaks ramp up)
/** When (into the ride) the destination loads in, hidden behind the tunnel. */
const ARRIVE_T = 3.5;
/** Total ride: ARRIVE_T plus the tunnel-dissolve reveal of the new system. */
/** Arrival reveal: the planet warp-zooms IN first, then the tunnel
 *  dissolves around it — the walls must outlive the zoom, or the player
 *  watches the destination pop in through thin air. */
const REVEAL_T = 2.2;
/** Fraction of the reveal by which the planet zoom is DONE. */
const REVEAL_ZOOM_K = 0.7;
/** Tunnel walls hold full opacity until here, then dissolve. */
const REVEAL_HOLD_K = 0.55;
/** Minimum post-arrival ride before the reveal may start (s) — even an
 *  instant mesh shouldn't snap the tunnel away. */
const REVEAL_MIN_HOLD = 2.0;
/** Charge only accrues while facing within ~20° of the heading. */
const ALIGN_CHARGE = 0.94;
/** Steering more than ~57° off the heading aborts the jump entirely. */
const ALIGN_ABORT = 0.55;
const tunnel = new WarpTunnel(scene);
const wormholes = new WormholeField(scene);
/** Lateral stick room inside the tunnel (tunnel radius is 150). */
const TUNNEL_BOUND = 6; // a lean within the fixed frame, nothing more
let navTarget: NavTarget | null = null;
let chargeT = 0;
let headingAligned = false;
let jump: {
  t: number;
  target: NavTarget;
  dir: THREE.Vector3;
  speed: number;
  /** Ride orientation; the tunnel aligns to it and the ship eases onto it. */
  quat: THREE.Quaternion;
  /** Tunnel centerline position (system space); the ship drifts around it. */
  path: THREE.Vector3;
  /** Lateral drift: raw mouse target, eased actual (smooth frame-to-frame). */
  latTX: number;
  latTY: number;
  latX: number;
  latY: number;
  /** True once the departure system's bodies have warped out of view. */
  departed: boolean;
  /** True once the destination system has loaded in behind the tunnel. */
  arrived: boolean;
  /** Reveal progress — only advances once the destination's terrain is
   *  meshed, so the tunnel doubles as a real loading screen. */
  revealT: number;
  /** Destination bodies revealed (walls started dissolving). */
  shown: boolean;
} | null = null;
let pilotName = "Pilot";
let shipStretch = 1;
let starStreakAmt = 0;
// Star-Fox tilt inside the warp tube (render-frame scratch).
const jumpTiltE = new THREE.Euler();
const jumpTiltQ = new THREE.Quaternion();
const jumpTargetQ = new THREE.Quaternion();
const navDirV = new THREE.Vector3();
const fwdV = new THREE.Vector3();
const jumpCamV = new THREE.Vector3();
/** Render pose: sim states interpolated by the accumulator fraction. */
const visPos = new THREE.Vector3();
const visQuat = new THREE.Quaternion();
/** Cosmetic bank angle (rad) the hull leans into mouse turns. */
let shipBank = 0;
const bankQ = new THREE.Quaternion();
const BANK_AXIS = new THREE.Vector3(0, 0, 1);

function navDirOf(target: NavTarget): THREE.Vector3 {
  // C4: no lattice geometry on either end of a home jump — the fold opens
  // along the ship's current nose (matches beginJump's ride-out behavior).
  const explicit = target.id !== undefined ? parseSystemRef(target.id) : null;
  if (explicit?.kind === "home" || SYSTEM_COORDS === null) {
    return navDirV.set(0, 0, -1).applyQuaternion(ship.quat).normalize();
  }
  return navDirV
    .set(target.sx - SYSTEM_COORDS[0], target.sy - SYSTEM_COORDS[1], target.sz - SYSTEM_COORDS[2])
    .normalize();
}

/** Cleared-of-planets heading actually flown (lattice headings are fiction
 *  anyway, so we deflect them to keep every planet off the warp axis). */
let navHeading: THREE.Vector3 | null = null;

/**
 * Deflect a heading so the ray ahead clears every planet by a wide margin —
 * the warp corridor never aims AT a planet; nearby ones zip past beside you.
 */
function clearHeading(dir: THREE.Vector3): THREE.Vector3 {
  const d = dir.clone();
  const c = new THREE.Vector3();
  const offAxis = new THREE.Vector3();
  for (let pass = 0; pass < 2; pass++) {
    for (const p of system.planets) {
      c.copy(p.pose.center).sub(ship.pos);
      const proj = c.dot(d);
      if (proj <= 0) continue; // behind us
      const clearR = p.def.radius * 1.45;
      offAxis.copy(d).multiplyScalar(proj).sub(c); // planet center -> ray
      const dist = offAxis.length();
      if (dist >= clearR) continue;
      if (dist > 1e-3) offAxis.divideScalar(dist);
      else offAxis.set(0, 1, 0).applyQuaternion(ship.quat); // dead-center: deflect "up"
      // Re-aim at the clearance point beside the planet.
      d.copy(c.addScaledVector(offAxis, clearR)).normalize();
    }
  }
  return d;
}

function setNavTarget(target: NavTarget): void {
  if (!discovered.has(navTargetId(target)) && !canJumpTo(target.sx, target.sy, target.sz)) {
    hud.showToast("UNCHARTED SPACE — ROUTE THROUGH ADJACENT SYSTEMS", 3200);
    return;
  }
  forceNavTarget(target);
}

/** Set the destination WITHOUT the reach/charted guard — for targets that are
 *  jumpable by affordance (your own home pocket; your own homestead systems,
 *  which you necessarily visited). The jump itself is unchanged. */
function forceNavTarget(target: NavTarget): void {
  navTarget = target;
  headingAligned = false;
  ship.suppressPulse = true;
  navHeading = clearHeading(navDirOf(target));
  ship.headingDir = navHeading.clone();
  hud.showToast(`HEADING: ${target.name.toUpperCase()} — COMING ABOUT`, 4000);
}

/**
 * Jump to another galaxy: rebuild the world and rejoin the right room.
 *
 * With `arrivalQuat` (hyperlight arrivals), the ship's orientation is
 * SACRED: instead of rotating the ship toward a canned spawn pose (which
 * made the whole view swing as the tunnel dissolved), the arrival position
 * is solved from the constraint "a planet sits directly on the nose".
 */
function switchSystem(
  id: string,
  arrivalQuat?: THREE.Quaternion,
): void {
  resetPlayerDeath();
  vehicleSpawned = false;
  vehicleGround = null;
  summonPending = null; // a call-in does not survive leaving the system
  summonT = 0;
  groundVehicle.vacateSeat();
  avatar.setMounted(false);
  groundVehicleView.group.visible = false;
  wormholes.clear();
  switchFromV.copy(ship.pos);
  resourceNodes?.dispose();
  latestNodeState.clear();
  homesteadMarkers?.dispose();
  homesteadPieces?.dispose();
  homesteadResidentLife?.dispose();
  hsSnapshotReady = false;
  homesteadBuild?.dispose(); // drops the ghost parented to the old system's planet
  settlementRenderer?.dispose();
  npcLife?.dispose();
  wildlife.clear();
  system.dispose();
  SYSTEM_ID = id;
  system = new StarSystem(scene, id);
  SYSTEM_COORDS = system.coords;
  if (SYSTEM_COORDS !== null) lastLattice = id;
  resourceNodes = new ResourceNodes(system);
  if (HOMESTEADS_ON) {
    homesteadMarkers = new HomesteadMarkers(system, currentUid());
    homesteadPieces = new HomesteadPieces(system);
    homesteadResidentLife = new HomesteadResidentLife(scene);
    wireHomesteadColliders(); // fresh Planet objects need the hooks re-assigned
  }
  settlementRenderer = new SettlementRenderer(system);
  places.clear(); // the new system's planets are different worlds entirely
  npcLife = new RegionNpcLife(scene);
  npcLife.setRenderer(settlementRenderer);
  cacheApertures?.dispose();
  cacheApertures = new CacheApertures();
  // CACHES: a `cache:` id means the planet we just built IS a dungeon pad — dress it.
  // MUST run after wireHomesteadColliders(): both want the planet's solid-geometry
  // hooks, and in an instance the cache owns them outright (see liveCache.ts).
  syncLiveCache();
  station.dispose();
  station = new StationVis(scene, system.def);
  if (arrivalQuat) placeShipAtArrival(arrivalQuat);
  else placeShipAtSpawn();
  resetIntroBackground();
  // Carry the afterburner ribbons across the teleport: translating the
  // history keeps the exhaust continuous through an arrival (a reset
  // blinked it); orientation is preserved on jumps, so translation is exact.
  switchFromV.copy(ship.pos).sub(switchFromV);
  trails.carryFrame(ZERO_VEL, IDENTITY_Q, switchFromV, 1);
  if (net) {
    net.dispose();
    net = null;
  }
  if (launched) {
    startNet(pilotName);
    void persistPosition().catch(() => { });
    hud.showSystemBanner(system.def.name);
    recordDiscovery(SYSTEM_ID, true); // no-ops for home ids
  }
}

// ── CACHES (P6b): apertures, entry, the live instance, exit ───────────────────
//
// The flow, end to end:
//   1. `cacheApertures` marks every site on the foot planet (pure f(planet seed)).
//   2. Standing at one, the F-ladder offers a HOLD (never a tap — F is also talk,
//      board and quest-board, and a tap there is somebody else's verb).
//   3. The hold sends `cacheEnter` with the SITE only. The server decides the
//      instance: party key, epoch, theme and tier all come back in `cacheState`.
//   4. `cacheState` naming a system we are not in ⇒ `switchSystem(instance)`. That
//      builds a real Planet over the pad SystemDef, and `syncLiveCache()` dresses it.
//   5. Standing at the entrance again, a second HOLD leaves: `cacheLeave`, back to
//      the origin system, restored to the pose we left from.
//
// Nothing here decides progress. Gates open because the SERVER said so, in the
// `prog` of a later `cacheState` — the client never opens its own gate (P5's rule).

/** How close to an aperture the ENTER prompt appears, metres (lateral). The server
 *  re-checks with a much looser 60 m leash; this is the "you are standing here" range. */
const CACHE_ENTER_RANGE_M = 6;
/** How close to the entrance room's centre the EXIT prompt appears, metres. */
const CACHE_EXIT_RANGE_M = 6;

/** Aperture markers for the planet the player is on (null before first foot planet). */
let cacheApertures: CacheApertures | null = null;
/** The dressed instance, when SYSTEM_ID is a `cache:` id. */
let liveCache: LiveCache | null = null;
/** Regeneration inputs from the most recent `cacheState`. */
let cacheInfo: CacheInstanceInfo | null = null;
/** The server's latest run progress for {@link cacheInfo}'s instance. */
let cacheProg: { gates: number[]; keys: number[]; bossAlive: boolean } | null = null;
/**
 * Where to put the player back when they leave — captured at the moment of entry,
 * BODY-LOCAL (planet id + local pose), never absolute. Bodies orbit and spin while a
 * run is going on, and a 15-minute tier-1 expedition is long enough for an absolute
 * world position to be kilometres of vacuum by the time it is restored. This is the
 * spawn/resume law the logout path already follows.
 */
let cacheOrigin: {
  systemId: string;
  planetIndex: number;
  local: [number, number, number];
  facing: [number, number, number];
} | null = null;
const cacheLocalV = new THREE.Vector3();
const cacheFacingV = new THREE.Vector3();

/** True while the player is inside a cache instance. */
function inCacheInstance(): boolean {
  return parseSystemRef(SYSTEM_ID)?.kind === "cache";
}

/** The player's PLANET-LOCAL position on the foot planet (the frame every cache
 *  proximity test is measured in), or null when not on foot. */
function footLocal(out: THREE.Vector3): THREE.Vector3 | null {
  if (!footPlanet) return null;
  return out.copy(character.pos).sub(footPlanet.pose.center).applyQuaternion(footPlanet.pose.invQuat);
}

/**
 * Build (or drop) the live instance for the CURRENT system. Called at the end of every
 * `switchSystem`, so the dressing's lifetime is exactly the pad planet's lifetime.
 */
function syncLiveCache(): void {
  liveCache?.dispose();
  liveCache = null;
  if (!inCacheInstance()) return;
  // A pad planet with no matching cacheState is a cache we cannot regenerate — the
  // C4 fail-loud rule: draw nothing rather than an arbitrary dungeon.
  if (!cacheInfo || cacheInfo.instance !== SYSTEM_ID) {
    console.error(`[cache] in ${SYSTEM_ID} with no matching cacheState — undressed`);
    return;
  }
  const planet = system.planets[0];
  if (!planet) return;
  liveCache = new LiveCache(planet, cacheInfo);
  if (cacheProg) liveCache.applyProg(cacheProg);
}

/** Remember where we are standing, body-local, so leaving can put us back. */
function captureCacheOrigin(): void {
  if (!footPlanet) return;
  const idx = system.planets.indexOf(footPlanet);
  if (idx < 0) return;
  const local = footLocal(cacheLocalV);
  if (!local) return;
  cacheFacingV.set(0, 0, -1).applyQuaternion(character.quat).applyQuaternion(footPlanet.pose.invQuat);
  cacheOrigin = {
    systemId: SYSTEM_ID,
    planetIndex: idx,
    local: [local.x, local.y, local.z],
    facing: [cacheFacingV.x, cacheFacingV.y, cacheFacingV.z],
  };
}

/** Enter (or re-enter) the instance the server just described. */
function enterCacheInstance(info: CacheInstanceInfo): void {
  cacheInfo = info;
  // placeAtLocalAnchor switches the system for us (and runs syncLiveCache on the way),
  // then seats + grounds the character — the same seam the homecoming and the prologue
  // spawn through, so grounding, the ship park and the carry frame are not reinvented.
  // Two steps, in this order: the SWITCH builds the pad planet and (via
  // syncLiveCache) its dressing, so the entrance room's ground exists before anyone
  // asks where it is; the PLACEMENT then seats and grounds the player on it.
  // placeAtLocalAnchor's own `switchSystem` call is a no-op by then (same id).
  switchSystem(info.instance);
  if (!liveCache) {
    hud.showToast("CACHE UNAVAILABLE", 2400);
    return;
  }
  const spawn = liveCache.entranceLocal(new THREE.Vector3());
  placeAtLocalAnchor(
    info.instance, 0,
    [spawn.x, spawn.y, spawn.z],
    liveCache.entranceFacing(),
  );
  mode = "foot";
  // TODO(P8): the starter hull is parked beside the player by placeAtLocalAnchor and
  // by switchSystem's own spawn — flyable, inside a dungeon. Harmless (the pad is a
  // barren sphere with nothing on it) but it wants suppressing with the entry polish.
  hud.showSystemBanner(`${info.theme.toUpperCase()} CACHE — TIER ${info.tier}`);
}

/** Leave the instance and go back to the aperture we came in through. */
function leaveCacheInstance(): void {
  const origin = cacheOrigin;
  net?.sendCacheLeave();
  cacheInfo = null;
  cacheProg = null;
  if (!origin) {
    // Nothing to return to (a reload inside an instance). Fall back to the same
    // RETURN target the home pocket uses rather than stranding the player.
    switchSystem(lastLattice ?? BASE_SYSTEM);
    mode = "foot";
    return;
  }
  placeAtLocalAnchor(origin.systemId, origin.planetIndex, origin.local, origin.facing);
  mode = "foot";
  cacheOrigin = null;
  hud.showToast("LEFT THE CACHE", 2000);
}

/** The HUD tracker line while inside a run: keys, gates, boss. Null outside one. */
function cacheHudLine(): string | null {
  if (!liveCache || !cacheProg) return null;
  const keys = cacheProg.keys?.length ?? 0;
  const gates = liveCache.dressing.gates.length;
  const open = cacheProg.gates?.length ?? 0;
  const boss = cacheProg.bossAlive ? "BOSS ALIVE" : "BOSS DOWN";
  const locks = gates > 0 ? ` · GATES ${open}/${gates}` : "";
  return `${liveCache.info.theme.toUpperCase()} CACHE T${liveCache.layout.tier} — KEYS ${keys}${locks} · ${boss}`;
}

const cacheProbeV = new THREE.Vector3();
/**
 * F must come UP between two cache holds.
 *
 * ⚠ THE BUG THIS EXISTS FOR, because it is not obvious and it bit immediately: a
 * hold that CHANGES THE WORLD leaves the key still down in the new one. `holdAct`
 * latches per action key, and entering swaps the key from "cache-enter" to
 * "cache-exit" — which resets the latch, so half a second later the still-held F
 * walked the player straight back out of the dungeon they had just entered (and,
 * when the exit rung was not up yet, into the starter hull parked beside them
 * instead). Measured in the live probe on the first run. The generic latch cannot
 * fix this: it is doing exactly what it is documented to do.
 */
let cacheFGuard = false;

/**
 * The cache rung of the on-foot F-ladder: enter at an aperture, leave at the entrance.
 *
 * HOLD, not tap. F is already talk, quest-board and interact, and every one of those
 * is a TAP — a tap that also warps you into a dungeon is the kind of thing you do once
 * by accident and never forgive. The board/disembark rungs set that precedent.
 */
function cacheInteract(): { key: string; text: string; run: () => void } | null {
  const local = footLocal(cacheProbeV);
  if (!local) return null;
  if (liveCache) {
    if (liveCache.distanceToEntrance(local) > CACHE_EXIT_RANGE_M) return null;
    return { key: "cache-exit", text: "F (HOLD) — LEAVE CACHE", run: leaveCacheInstance };
  }
  if (inCacheInstance()) return null; // in an undressed instance: no aperture to offer
  const near = cacheApertures?.nearest(local) ?? null;
  if (!near || near.dist > CACHE_ENTER_RANGE_M) return null;
  return {
    key: "cache-enter",
    text: `F (HOLD) — ENTER CACHE (${near.theme.toUpperCase()} · TIER ${near.tier})`,
    run: () => {
      captureCacheOrigin();
      // The SITE is the whole claim. Key, epoch, theme and tier are the server's.
      net?.sendCacheEnter(near.site.id, near.tier);
      hud.showToast("OPENING THE CACHE…", 1800);
    },
  };
}

/** Arrival pose for hyperlight: keep the ride orientation, put a planet
 *  dead ahead. Prefers the planet whose day side faces the approach. */
function placeShipAtArrival(quat: THREE.Quaternion): void {
  const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(quat);
  let best = system.planets[0];
  let bestDot = -2;
  const v = new THREE.Vector3();
  for (const p of system.planets) {
    if (p.def.parentId) continue; // arrive at planets; moons are scenery here
    // Heading aligned with the planet's outward (from-star) direction means
    // we fly in from the star side — the lit hemisphere greets us.
    const d = fwd.dot(v.copy(p.pose.center).normalize());
    if (d > bestDot) {
      bestDot = d;
      best = p;
    }
  }
  ship.quat.copy(quat);
  ship.pos.copy(best.pose.center).addScaledVector(fwd, -best.def.radius * 3.6);
  resolveArrivalClearance(ship.pos);
  // Exit the wormhole MOVING — cruising toward the planet, not parked.
  ship.vel.copy(fwd).multiplyScalar(350);
  ship.speed = 350;
  ship.throttle = 0.45;
  ship.snapVisual();
  origin.copy(ship.pos);
}

// --- The ground MOVES: anyone on or near a body rides its spin and orbit.
// Applied analytically per fixed tick (rotation about the live axis +
// translation with the orbiting center), fading out between 2R and 2.6R so
// there is no velocity cliff at the boundary of a body's influence.
const carryQ = new THREE.Quaternion();
const carryAxis = new THREE.Vector3();
const carryDelta = new THREE.Vector3();
/** Frame velocity the carry applied this tick (m/s): bolts and the
 *  streamed net state add it so projectiles and remote extrapolation ride
 *  the rotating frame instead of missing sideways (Coriolis). */
const shipCarryVel = new THREE.Vector3();
const charCarryVel = new THREE.Vector3();
const carryFrom = new THREE.Vector3();
const ZERO_VEL = new THREE.Vector3();
const IDENTITY_Q = new THREE.Quaternion();
const switchFromV = new THREE.Vector3();
const jumpTrailV = new THREE.Vector3();
const dockLook = new THREE.Matrix4();
const dockNose = new THREE.Vector3();
// v9 sender-side local-anchor scratch.
const netLocV = new THREE.Vector3();
const netLocQ = new THREE.Quaternion();
const netSLocV = new THREE.Vector3();
const netSLocQ = new THREE.Quaternion();
const vehicleCarryQ = new THREE.Quaternion();
const vehicleCarryAxis = new THREE.Vector3();

/** Last applied ship-carry transform (trails ride the same frame). */
const lastCarry = {
  q: new THREE.Quaternion(),
  prevCenter: new THREE.Vector3(),
  delta: new THREE.Vector3(),
  k: 0,
};

function carryWithBody(
  dt: number,
  pos: THREE.Vector3,
  vel: THREE.Vector3 | null,
  quat: THREE.Quaternion | null,
  carryVelOut?: THREE.Vector3,
): void {
  carryVelOut?.set(0, 0, 0);
  if (carryVelOut === shipCarryVel) lastCarry.k = 0;
  carryFrom.copy(pos);
  // Eligibility is judged against the PREVIOUS pose — where the body WAS
  // when this entity last rode it. After a long gap (hidden tab → clock
  // snap), the planet has orbited kilometers in ONE tick: measured against
  // the CURRENT center the player no longer qualified for the carry and
  // was stranded in vacuum while their planet sailed on.
  let nearP: import("./world/planet").Planet | null = null;
  let nearD = Infinity;
  for (const p of system.planets) {
    const d = pos.distanceTo(p.posePrev.center);
    if (d < nearD) {
      nearD = d;
      nearP = p;
    }
  }
  if (!nearP) return;
  const def = nearP.def;
  const R = def.radius;
  const k = Math.max(0, Math.min(1, (2.6 * R - nearD) / (0.6 * R)));
  if (k <= 0) return;
  // EXACT pose delta from the lockstep tick — never re-derived from a
  // clock, so the ground and its passengers move as one rigid frame.
  const dAngle = (nearP.pose.spinAngle - nearP.posePrev.spinAngle) * k;
  carryAxis.set(def.spin.axis[0], def.spin.axis[1], def.spin.axis[2]);
  carryQ.setFromAxisAngle(carryAxis, dAngle);
  pos.sub(nearP.posePrev.center).applyQuaternion(carryQ).add(nearP.posePrev.center);
  vel?.applyQuaternion(carryQ);
  quat?.premultiply(carryQ);
  carryDelta.copy(nearP.pose.center).sub(nearP.posePrev.center);
  pos.addScaledVector(carryDelta, k);
  if (carryVelOut && dt > 0) {
    carryVelOut.copy(pos).sub(carryFrom).divideScalar(dt);
  }
  if (carryVelOut === shipCarryVel) {
    lastCarry.q.copy(carryQ);
    lastCarry.prevCenter.copy(nearP.posePrev.center);
    lastCarry.delta.copy(carryDelta);
    lastCarry.k = k;
  }
}

function carryGroundVehicle(): void {
  if (!vehicleSpawned || !vehicleGround) return;
  const p = vehicleGround.planet;
  vehicleCarryAxis.set(p.def.spin.axis[0], p.def.spin.axis[1], p.def.spin.axis[2]);
  vehicleCarryQ.setFromAxisAngle(vehicleCarryAxis, p.pose.spinAngle - p.posePrev.spinAngle);
  groundVehicle.pos.sub(p.posePrev.center).applyQuaternion(vehicleCarryQ).add(p.pose.center);
  groundVehicle.vel.applyQuaternion(vehicleCarryQ);
  groundVehicle.angularVel.applyQuaternion(vehicleCarryQ);
  groundVehicle.quat.premultiply(vehicleCarryQ).normalize();
  groundVehicle.viewHeading.applyQuaternion(vehicleCarryQ).normalize();
}

// --- Hold-to-act: timed key holds (board, disembark) fill the shared HUD
// bar. Latched on completion — the key must come UP before the next hold
// starts, so disembarking with F still held never instantly re-boards.
const HOLD_ACT_S = 0.5;
const holdAct = {
  key: null as string | null,
  t: 0,
  latched: false,
  touched: false,
  /** Accumulate while `down`; true exactly once when the hold completes. */
  tick(key: string, down: boolean, dt: number): boolean {
    this.touched = true;
    if (this.key !== key) {
      this.key = key;
      this.t = 0;
      this.latched = false;
    }
    if (!down) {
      this.t = 0;
      this.latched = false;
      return false;
    }
    if (this.latched) return false;
    this.t += dt;
    if (this.t >= HOLD_ACT_S) {
      this.latched = true;
      this.t = 0;
      return true;
    }
    return false;
  },
  /** 0..1 fill while a hold is running, else null. */
  frac(): number | null {
    return this.key !== null && this.t > 0 ? Math.min(1, this.t / HOLD_ACT_S) : null;
  },
  reset(): void {
    this.key = null;
    this.t = 0;
    this.latched = false;
  },
};

// --- Mining: hold F on a live vein. On foot you work the rock up close;
// from the ship a beam reaches out. Cycles complete locally and CLAIM from
// the server, which owns depletion — the grant arrives as a "mined" msg.
let mineNodeId: number | null = null;
let mineT = 0;
const beamFromV = new THREE.Vector3();
const beamToV = new THREE.Vector3();
/** Mining-cycle speed multiplier (Resonance Amp installs raise it). */
let miningRate = 1;

/**
 * Runs the G-hold extraction loop and returns the mining line for the
 * interact prompt (null = no vein in range). Mining lives on its OWN key
 * so it composes with F-interacts (disembark next to a vein, etc.).
 */
function tickMining(dt: number, onFoot: boolean): string | null {
  const focus = onFoot ? character.pos : ship.pos;
  const range = onFoot ? MINE_RANGE_FOOT : MINE_RANGE_SHIP;
  const found = resourceNodes?.nearest(focus, range) ?? null;
  if (!found) {
    mineNodeId = null;
    mineT = 0;
    return null;
  }
  const kind = NODE_KINDS[found.node.kind];
  const left = resourceNodes!.leftOf(found.node.id);
  if (input.isDown("KeyG")) {
    if (mineNodeId !== found.node.id) {
      mineNodeId = found.node.id;
      mineT = 0;
    }
    mineT += dt * miningRate;
    if (mineT >= MINE_CYCLE_S) {
      mineT -= MINE_CYCLE_S;
      net?.sendMine(found.node.id);
    }
    return `MINING ${kind.name.toUpperCase()} — ${Math.round((mineT / MINE_CYCLE_S) * 100)}%`;
  }
  mineNodeId = null;
  mineT = 0;
  return `G (HOLD) — MINE ${kind.name.toUpperCase()} (${left} LEFT)`;
}

/** Environmental drain (hp/s) for a body's archetype — on foot, unshielded. */
const hazardColumn=createSurfaceColumn(),hazardLocal=new THREE.Vector3();
const ventExposure=createVolcanicExposure();
function hazardOf(planet:Planet): { rate: number; label: string } | null {
  // ⚠ A CACHE PAD IS NOT WEATHER. `cachePadSystem` builds its planet as `barren`
  // because that is the cheapest featureless archetype to render — but barren
  // means VACUUM here, 2.2 hp/s, so every dungeon in the game quietly killed the
  // player while they explored it. The archetype is a look; the hazard is a place.
  if (inCacheInstance()) return null;
  const arch=planet.def.archetype;
  if(arch==='volcanic'||seaSurface(planet.def)==='lava') {
    hazardLocal.copy(character.pos).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat);
    volcanicExposure(planet.sampler,hazardLocal.x,hazardLocal.y,hazardLocal.z,volcanicTime(worldTime()),ventExposure);
    const radius=hazardLocal.length();hazardLocal.normalize();
    surfaceColumn(planet.sampler,planet.def,hazardLocal.x,hazardLocal.y,hazardLocal.z,hazardColumn);
    const contact=lavaContactRate(hazardColumn,radius);
    if(contact>0)return{rate:contact,label:'MOLTEN LAVA'};
    if(ventExposure.rate>3)return{rate:ventExposure.rate,label:'VENT HEAT'};
    if(ventExposure.buildup>.12)return{rate:3,label:'VENT BUILDUP'};
  }
  switch (arch) {
    case "volcanic":
      return { rate: 3, label: "EXTREME HEAT" };
    case "barren":
      return { rate: 2.2, label: "VACUUM" };
    case "ice":
      return { rate: 1.6, label: "DEEP COLD" };
    case "desert":
      return { rate: 1, label: "HEAT" };
    default:
      return null;
  }
}

/**
 * HUD biome readout: classify the ground under a WORLD position from the same
 * climate fields the scatter system plants by, so the label always matches
 * what's growing around the player. Null when the planet has no sampler yet.
 */
const biomeTs = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
const biomeDir = new THREE.Vector3();
/** What the BIOME row reads inside a cache instance — the theme, not the pad's rock. */
const CACHE_BIOME_LABEL: Record<CacheTheme, string> = {
  ruin: "RUIN CACHE",
  vault: "VAULT CACHE",
  hollow: "HOLLOW CACHE",
};
function biomeLabel(planet: Planet, worldPos: THREE.Vector3): string | null {
  if (!planet.sampler || !planet.pose.invQuat) return null;
  biomeDir.copy(worldPos).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat).normalize();
  planet.sampler.sample(biomeDir.x, biomeDir.y, biomeDir.z, biomeTs, true);
  const ts = biomeTs;
  const r = planet.def.radius + ts.h * planet.sampler.amplitude;
  const arch = planet.def.archetype;
  if (arch === "desert") return ts.mountain > 0.55 ? "ROCKY WASTE" : "DUNE SEA";
  if (arch === "ice") return ts.mountain > 0.55 ? "GLACIER PEAKS" : "ICE SHEET";
  if (arch === "volcanic") return "ASHLANDS";
  if (arch !== "terran" && arch !== "ocean") return "BARRENS";
  const sea = planet.def.seaRadius;
  if (sea !== null && r < sea - 6) return "OCEAN";
  if (sea !== null && r < sea + 1.5) return "SHORELINE";
  if (ts.town > 0.02) return "SETTLEMENT";
  if (ts.river > 0.25) return "RIVERLANDS";
  if (ts.temp < -0.55) return "SNOWFIELD";
  if (ts.mountain > 0.6) return "MOUNTAINS";
  const sm = (x: number): number => Math.min(1, Math.max(0, x));
  const forestK = sm((ts.moist + 0.1) / 0.5) * sm((ts.temp + 0.5) / 0.35) * sm((0.8 - ts.temp) / 0.35);
  if (forestK > 0.45) {
    // Mirror the seasonAt bands: green forest by default, gold on the turn.
    if (ts.temp < -0.25) return "WINTER FOREST";
    if (ts.temp < -0.12) return "AUTUMN FOREST";
    return "FOREST";
  }
  if (ts.moist < -0.35) return "BADLANDS";
  if (ts.moist < 0.02) return "STEPPE";
  if (ts.moist > 0.5) return "WETLANDS";
  return "GRASSLAND";
}

/** Push a spawn point clear of EVERY body — you never warp into a moon. */
function resolveArrivalClearance(pos: THREE.Vector3): void {
  const off = new THREE.Vector3();
  for (let pass = 0; pass < 3; pass++) {
    for (const p of system.planets) {
      off.copy(pos).sub(p.pose.center);
      const d = off.length();
      const min = p.def.radius * 2.4;
      if (d < min) {
        if (d < 1e-3) off.set(0, 1, 0);
        else off.divideScalar(d);
        pos.copy(p.pose.center).addScaledVector(off, min);
      }
    }
  }
}

/**
 * Enter the warp tunnel: announce the wormhole to the room, then ride
 * inside the tunnel for ARRIVE_T + REVEAL_T (pure effect — the destination loads
 * instantly) with the normal chase camera following the whole way.
 */
let lastFuelWarn = 0;
let jumpPreparing = false;
const revealBypass = new Set<string>();

/**
 * Follow-jump: chase a party member who just warped out. The party only
 * supplies the destination — every other rule (fuel for uncharted space, the
 * reach test, sealed reveals) still applies, so this is exactly the jump the
 * pilot could have charted themselves.
 */
function followPartyJump(to: string): void {
  const ref = parseSystemRef(to);
  if (!ref) return;
  // A cache id is a non-lattice instance id (no coords to jump toward) and the
  // client has no way in yet — the cache renderer lands in Phase 4.
  if (ref.kind === "cache") return;
  const name = systemDefFor(to)?.name ?? "PARTY";
  if (ref.kind === "home") beginJump({ sx: 0, sy: 0, sz: 0, name, id: to });
  else beginJump({ sx: ref.c[0], sy: ref.c[1], sz: ref.c[2], name, ...(ref.kind === "sealed" ? { id: to } : {}) });
}

function beginJump(target: NavTarget, towardPos?: THREE.Vector3): void {
  const targetId = navTargetId(target);
  const targetRef = parseSystemRef(targetId);
  // The key release completes before fuel is spent or the tunnel starts. A
  // legacy public system can still load if only its additive secret overlay
  // is unavailable; a sealed v2 system has no SystemDef without the reveal.
  if (!hasSystemReveal(targetId) && !revealBypass.has(targetId)) {
    if (jumpPreparing) return;
    if (!net) {
      if (targetRef?.kind === "sealed") hud.showToast("SYSTEM KEY UNAVAILABLE — CONNECT TO DISCOVER", 3000);
      return;
    }
    jumpPreparing = true;
    chargeT = 0;
    hud.showToast("DECRYPTING DISCOVERY SIGNAL…", 1800);
    const toward = towardPos?.clone();
    void net.requestSystemReveal(targetId).then(() => {
      jumpPreparing = false;
      beginJump(target, toward);
    }).catch((error) => {
      jumpPreparing = false;
      if (targetRef?.kind === "sealed") {
        console.warn("[discover] sealed reveal refused", error);
        hud.showToast("SYSTEM REMAINS SEALED", 2800);
      } else {
        revealBypass.add(targetId);
        beginJump(target, toward);
      }
    });
    return;
  }
  // C4: home jumps are a private fold — no lattice reach test, no chart, no
  // fuel (your own doorstep is always reachable; RETURN likewise).
  const homeBound = targetRef?.kind === "home";
  if (!homeBound && !towardPos && !discovered.has(targetId) && !canJumpTo(target.sx, target.sy, target.sz)) {
    hud.showToast("UNCHARTED SPACE — ROUTE THROUGH ADJACENT SYSTEMS", 3200);
    return;
  }
  // A cell burns only when the destination is UNCHARTED (wormhole rides
  // included — someone else's tunnel still strains your drive).
  if (!homeBound && !discovered.has(targetId)) {
    if (!consumeItem(rpg.inventory, "fuel-cell", 1)) {
      hud.showToast("NO HYPERLIGHT CELL — UNCHARTED JUMPS NEED FUEL", 2600);
      return;
    }
    invIntent({ op: "spend", id: "fuel-cell", n: 1 });
    rpg.rev++;
  }
  // Announce first so the old room sees the wormhole at our departure point.
  audio.sfx("ship.warp");
  net?.sendWarp(targetId, [ship.pos.x, ship.pos.y, ship.pos.z]);
  // No orientation snap: a charged jump rides out along the CURRENT nose;
  // joining a wormhole eases onto its bearing (using the ship's own up, so
  // there's no roll jolt either).
  let dir: THREE.Vector3;
  let rideQuat: THREE.Quaternion;
  if (towardPos) {
    dir = towardPos.clone().sub(ship.pos).normalize();
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(ship.quat);
    const look = new THREE.Matrix4().lookAt(new THREE.Vector3(), dir, up);
    rideQuat = new THREE.Quaternion().setFromRotationMatrix(look);
  } else {
    dir = new THREE.Vector3(0, 0, -1).applyQuaternion(ship.quat);
    rideQuat = ship.quat.clone();
  }
  jump = {
    t: 0,
    target,
    dir,
    speed: Math.max(Math.abs(ship.speed), 300),
    quat: rideQuat,
    path: ship.pos.clone(),
    latTX: 0,
    latTY: 0,
    latX: 0,
    latY: 0,
    departed: false,
    arrived: false,
    revealT: 0,
    shown: false,
  };
  mode = "jump";
  navTarget = null;
  navHeading = null;
  chargeT = 0;
  ship.suppressPulse = false;
  ship.headingDir = null;
  ship.pulse = false;
  hud.setInteract(null);
}


// --- Weapons ---
const lastFire: Record<1 | 2, number> = { 1: 0, 2: 0 };

const lastVehicleFire = new Map<string, number>();
const vehicleFireTarget = new THREE.Vector3();
const vehicleFireCameraOrigin = new THREE.Vector3();
const vehicleFireMuzzle = new THREE.Vector3();
const vehicleFireDir = new THREE.Vector3();

/** Fire each weapon socket controlled by the occupied seat. The server
 * independently resolves the same vehicle/socket/weapon chain and enforces
 * per-mount RPM; the local bolts are immediate predicted visuals. */
function tryVehicleFire(g: 1 | 2): void {
  if (footPlanet?.terrainSolidsReady === false) return;
  const seat = groundVehicle.activeSeat;
  if (!seat) return;
  const now = performance.now();
  camDirV.set(0, 0, -1).applyQuaternion(camera.quaternion).normalize();
  // The reticle is the camera's centre ray. Starting this same direction at
  // the lower chassis produces a parallel ray that intersects ground early;
  // find the camera ray's real world-space contact, then converge each muzzle.
  vehicleFireCameraOrigin.copy(camera.position).add(origin);
  const targetDistance = aimDistance(vehicleFireCameraOrigin, camDirV);
  const pl = footPlanet ? system.planets.indexOf(footPlanet) : -1;
  for (const id of seat.hardpoints ?? []) {
    const hardpoint = groundVehicle.def.hardpoints.find((candidate) => candidate.id === id);
    if (!hardpoint?.weapon || hardpoint.kind !== "weapon" || hardpoint.group !== g) continue;
    const weapon = vehicleWeapon(groundVehicle.def, hardpoint.weapon);
    if (!weapon) continue;
    const last = lastVehicleFire.get(hardpoint.id) ?? 0;
    if (now - last < 60_000 / weapon.rpm) continue;
    if (!groundVehicle.hardpointPose(hardpoint.id, vehicleFireMuzzle, vehicleFireDir)) continue;
    convergeVehicleWeapon(
      vehicleFireMuzzle,
      vehicleFireCameraOrigin,
      camDirV,
      targetDistance,
      vehicleFireTarget,
      vehicleFireDir,
    );
    lastVehicleFire.set(hardpoint.id, now);
    bolts.spawn(vehicleFireMuzzle, vehicleFireDir, droneVelZero, g, {
      speed: weapon.projectileSpeed,
      damage: weapon.damage,
      mine: true,
      predicted: SERVER_AUTHORITATIVE_BOLTS,
      ignore: { kind: "ship", id: LOCAL_SHIP },
    });
    net?.sendFire(g, [vehicleFireDir.x, vehicleFireDir.y, vehicleFireDir.z], {
      o: [vehicleFireMuzzle.x, vehicleFireMuzzle.y, vehicleFireMuzzle.z],
      pl: pl >= 0 ? pl : undefined,
      ct: net?.aimTimeMs() ?? undefined,
      vh: hardpoint.id,
      vw: weapon.id,
    });
    audio.sfx("weapon.ship.fire", { at: vehicleFireMuzzle });
    audio.notifyCombat();
  }
}

/**
 * Live spread: base widens with bloom (sustained fire), movement, and
 * being airborne — the reticle ring renders exactly this value.
 */
function currentSpread(): number {
  const def = rpg.primaryWeapon();
  if (!def?.weapon) return 0.01;
  const moveK = Math.min(1, character.groundSpeed / 6);
  const airK = character.onGround ? 0 : 1;
  return weaponSpread(weaponStats(def.weapon),weaponCycle.bloom,moveK,!!airK,character.aiming?1:0);
}


/** Fire the held gun from the muzzle toward the reticle's convergence point (TPS crosshair).
 *  The ARM CANNON (construct) shoots on the cannon upgrade track's numbers; REAL carried guns
 *  (rifles) shoot on their OWN WeaponDef stats — damage/rpm/energy/range/recoil/bloom all come
 *  from the catalog row, which is what makes two rifles feel different. Every accepted local shot spends
 *  energy only after the readiness and cooldown gates pass. */
let lastReadyInput = "";
function footAimAllowed(): boolean {
  return character.aiming && !(mode !== "foot" || uiOverlayActive() || playerDyingT > 0
    || stowSwapTo !== null || homesteadBuild?.active === true || hsBeaconMode
    || character.jet === "glide" || character.jet === "dive");
}
function updateFootReadiness() {
  const itemId = rpg.heldWeapon()?.id ?? null;
  const aimHeld = footAimAllowed();
  const blocked = !aimHeld;
  const edge = `${mode}:${itemId}:${aimHeld}:${blocked}`;
  const pose = resolveCharacterPose({
    itemId, aimHeld, blocked, speed: character.groundSpeed,
    crouching: character.crouching, grounded: character.onGround,
  });
  if (edge !== lastReadyInput) { lastReadyInput = edge; net?.sendState(); }
  return pose;
}

function tryFootFire(): void {
  if (footPlanet?.terrainSolidsReady === false) return;
  if (!updateFootReadiness().canFire) return;
  // The crystal lance assembles over ~0.3 s after RMB: no energy or cooldown is
  // spent until the bore is seated, so a held trigger fires the moment it is.
  if (avatar.crystalWeaponEnabled && !avatar.crystalWeaponReady) return;
  const def = rpg.primaryWeapon();
  if (!def?.weapon) return;
  const stats = rpg.stats;
  const wpn = def.weapon;
  const isConstruct = wpn.construct !== undefined;
  const rpm = isConstruct ? stats.cannonRpm : wpn.rpm;
  const cost = isConstruct ? stats.cannonCost : wpn.energyCost ?? 3;
  const damage = isConstruct ? stats.cannonDamage : wpn.damage;
  const tune=weaponStats({...wpn,rpm});
  if (!weaponCycle.ready(tune)) return;
  if (!character.spendEnergy(cost)) {
    audio.sfx("ui.deny");
    return;
  }
  // Sample the spread BEFORE this shot's own bloom kick: the ring drawn at the
  // trigger pull is the promise, and bloom is the price of the NEXT shot. (The
  // old order made even the first shot pay its own bloom — a longshot opener
  // flew 3.1× wider than the tight ring on screen.)
  const spread = currentSpread();
  weaponCycle.commit(tune);
  // Fire-time camera kick REMOVED (2026-08-13, user call: "weird camera shake
  // when firing") — recoil feedback lives in the reticle bloom + audio; the
  // hit-taken kick in takeHitReaction stays.
  //
  // ⚠⚠ NO AIM MATH HERE. The whole solve — reticle ray, convergence, muzzle,
  // direction, spread, and the server fire intent — lives in flushFootBolt(),
  // which runs after THIS frame's camera is finalized. Solving here (sim loop)
  // used a camera and muzzle from a different frame than the spawn, which
  // parallel-shifted the bolt line by up to a tick of carry and put shots
  // visibly OUTSIDE the reticle (user report 2026-08-17). This function only
  // pays the costs and queues the shot; feel (anim + audio) stays instant.
  pendingBolt = {
    speed: wpn.projectileSpeed,
    // Upgraded damage applies to what the CLIENT resolves (drones). PvP bolts are
    // simulated by the server, which reads the def's base damage from `wp` below.
    damage,
    range: wpn.range,
    wp: def.id, // rides into the PvE hit claim at contact
    spread,
    reach: wpn.range ?? 220,
  };
  avatar.shoot();
  audio.sfx("weapon.player.fire", { at: character.pos });
  audio.notifyCombat();
}

/** Disc-spread scratch (perpendicular basis around the aim direction). */
const _sprU = new THREE.Vector3();
const _sprV = new THREE.Vector3();

/**
 * Resolve + fire the queued shot — called AFTER the frame's camera is FINALIZED
 * (post footCam.update + camera.updateMatrixWorld), so EVERY input to the solve
 * — the reticle ray, the drawn muzzle, the colliders — comes from THIS frame.
 *
 * ⚠⚠ THE SOLVE MUST NEVER SPLIT ACROSS FRAMES. It used to: tryFootFire (sim
 * loop) computed the direction from a sim-anchored muzzle and last frame's
 * camera, then the spawn used the RENDERED muzzle — a parallel shift of up to a
 * tick of planet carry (~1.3–2.7 m). At a 20–50 m convergence that is 1.5–7°
 * off the reticle center (user report 2026-08-17: "we can shoot outside the
 * reticle"). One frame, one solve, one truth — and the SAME muzzle + direction
 * go to the server (sendFire lives here too), so the predicted visual and the
 * authoritative bolt fly the same line.
 *
 * SPREAD is sampled on a DISC perpendicular to the aim (radius ≤ the spread
 * half-angle), so the deviation can never exceed the reticle ring's edge — the
 * old per-axis cube sampling reached √2× the ring on diagonal rolls. The ring
 * CONTAINS the shot, by construction.
 */
function flushFootBolt(alpha: number): void {
  const shot = pendingBolt;
  if (!shot) return;
  pendingBolt = null;
  // Reticle ray from THIS frame's finalized camera.
  camera.getWorldDirection(fireDir);
  camera.getWorldPosition(fireAimFrom).add(origin); // → system space
  {
    // Converge where the reticle actually RESTS, out to the WEAPON'S range:
    // terrain first (the aimDistance march the vehicle guns already use — a
    // hillside at 300 m is a real rest point), then any collider that cuts the
    // ray closer. (The old flat 150 m cap made the longshot climb above the
    // reticle past 150 m — 2026-08-13, probe client/test/_longshotaim.mjs.)
    let bestT = aimDistance(fireAimFrom, fireDir, shot.reach, 384, 1.5);
    for (const c of bolts.colliders) {
      if (c.kind === "char" && c.id === LOCAL_CHAR) continue;
      _rcTo.copy(c.pos).sub(fireAimFrom);
      const t = _rcTo.dot(fireDir);
      if (t < 1.2 || t > bestT) continue; // behind / beyond the current best
      const r = c.radius + 0.25;
      if (_rcTo.lengthSq() - t * t <= r * r) bestT = t;
    }
    fireAimAt.copy(fireAimFrom).addScaledVector(fireDir, bestT);
  }
  // The muzzle as DRAWN this frame (avatar posed above; render → system space).
  const renderedMuzzle = avatar.muzzleWorld(fireMuzzle);
  if (renderedMuzzle) fireMuzzle.add(origin);
  else fireMuzzle.copy(character.pos).addScaledVector(character.up, 1.28).addScaledVector(fireDir, 0.55);
  // Fire from the muzzle THROUGH the reticle's converged point.
  fireDir.copy(fireAimAt).sub(fireMuzzle).normalize();
  // Disc spread: uniform over a disc of angular radius `spread`, perpendicular
  // to the aim — max deviation == the ring edge, never past it.
  _sprU.crossVectors(fireDir, character.up);
  if (_sprU.lengthSq() < 1e-6) _sprU.set(1, 0, 0).cross(fireDir);
  _sprU.normalize();
  _sprV.crossVectors(fireDir, _sprU); // unit by construction
  const sprAng = Math.random() * Math.PI * 2;
  const sprRad = shot.spread * Math.sqrt(Math.random());
  fireDir
    .addScaledVector(_sprU, Math.cos(sprAng) * sprRad)
    .addScaledVector(_sprV, Math.sin(sprAng) * sprRad)
    .normalize();
  // No shooter-velocity inheritance: bolts are CARRIED by the body frame every tick, and the
  // SERVER's authoritative bolt is pure dir × speed — inheriting here bent the predicted visual
  // off both the converged aim line and the shot the server actually simulates.
  bolts.spawn(fireMuzzle, fireDir, ZERO_VEL, 1, {
    speed: shot.speed,
    damage: shot.damage,
    range: shot.range,
    wp: shot.wp,
    mine: true,
    predicted: SERVER_AUTHORITATIVE_BOLTS,
    renderFrame: renderedMuzzle ? { planets: system.planets, alpha } : undefined,
    ignore: { kind: "char", id: LOCAL_CHAR },
  });
  // The tracer's spawn stub already grows directly out of the muzzle. Do not
  // also activate a world-space billboard here: even with identical system
  // coordinates, its centre and the streak's centre project as two separate
  // bright origins (especially while the planet/camera frame is moving), which
  // reads as a second parallel bolt above the real one. One shot, one visual.
  if (SERVER_AUTHORITATIVE_BOLTS) {
    // Fire INTENT: muzzle origin, the planet frame it resolves in, the view-time we aimed
    // at (for lag-comp), and the weapon id (the server reads speed/damage from the def).
    const pl = footPlanet ? system.planets.indexOf(footPlanet) : -1;
    net?.sendFire(1, [fireDir.x, fireDir.y, fireDir.z], {
      o: [fireMuzzle.x, fireMuzzle.y, fireMuzzle.z],
      pl: pl >= 0 ? pl : undefined,
      ct: net?.aimTimeMs() ?? undefined,
      wp: shot.wp,
    });
  } else {
    net?.sendFire(1, [fireDir.x, fireDir.y, fireDir.z]);
  }
}

/** Local combat state this tick (set in the foot loop). `combatBlocking` = a raised shield
 *  guard (RMB in shield mode); `shieldAbsorbing` = shield mode with energy left, which soaks
 *  passively. Both go out on the wire — the SERVER mitigates damage from them (rooms.damagePlayer)
 *  and the incoming-damage path bills the absorbed portion to energy. */
let combatBlocking = false;
let shieldAbsorbing = false;
/** PARRY window: an MMB rising edge opens a brief window (performance.now() timestamp). A hit landing
 *  while now < parryUntil parries — no damage + knockback — instead of a (low-damage) block. */
let parryUntil = 0;
/** The window's OPENING edge (the sword parry has an 80 ms startup where a hit is EATEN —
 *  that raise is the skill; the shield parry opens at the press, parryFrom = now). */
let parryFrom = 0;
/** The parry window is LIVE right now (both defensive ladders + the streamed flags read this). */
const parryActive = (): boolean => {
  const now = performance.now();
  return now >= parryFrom && now < parryUntil;
};
// (Sword tuning + buffer/stinger/follow/punch state → combat/actionSystem.ts, 2026-07-21.)
/** SOULS-STYLE SOFT LOCK (combat/softLock.ts): RMB focus acquires, F cycles, facing away
 *  (sustained) / release / death drops it. The orbit magnetism + the stinger + every other
 *  melee decision live in combat/actionSystem.ts now; the lock marker render is below. */
const softLock = new SoftLock();
const _lockUpN = new THREE.Vector3();
let lockMarker: THREE.Sprite | null = null;
/** The swing streak (lazy — built on the first render tick with the fx). */
let bladeTrail: BladeTrail | null = null;
/** Impact sparks — the "it LANDED" burst (lazy, same pattern). */
let hitSparks: HitSparks | null = null;
const _hsPos = new THREE.Vector3();
const _btBase = new THREE.Vector3();
const _btTip = new THREE.Vector3();
/** SWEPT blade contact scratch (fixed tick) — see client/src/combat/meleeContact.ts for the frame the
 *  contact resolves in and why. `_meleeFrame` is the PLANET the attacker stands on, posed on THIS TICK
 *  (live refs into footPlanet.pose, no copies); `_meleeTargets`/`_meleeHits` are reused buffers so the
 *  per-tick, per-target loop allocates nothing. */
const _meleeFrame: BodyFrame = {
  index: -1,
  center: new THREE.Vector3(),
  quat: new THREE.Quaternion(),
  invQuat: new THREE.Quaternion(),
};
const _meleeTargets: MeleeTarget[] = [];
/** The body frame the swept melee resolves in: the planet under the attacker's feet, at this tick's
 *  pose. Null in space / off-body → the sweep falls back to system space (the identity case). */
function meleeBodyFrame(): BodyFrame | null {
  if (!footPlanet) return null;
  const idx = system.planets.indexOf(footPlanet);
  if (idx < 0) return null;
  _meleeFrame.index = idx;
  _meleeFrame.center = footPlanet.pose.center;
  _meleeFrame.quat = footPlanet.pose.quat;
  _meleeFrame.invQuat = footPlanet.pose.invQuat;
  return _meleeFrame;
}
// ── THE ACTION SYSTEM (combat/actionSystem.ts) — the whole melee decision layer, shared
// verbatim with the combat lab. This host is main's world adapter: drone targets in,
// net claims + fx out, the PvP punch discipline on the wire. ──
const actionSys = new ActionSystem({
  character, avatar, input, softLock,
  meleeActive: () => rpg.heldMelee() !== null && homesteadBuild?.active !== true && !hsBeaconMode,
  cameraForward: (out) => camera.getWorldDirection(out),
  targets: () => drones.list(),
  meleeTargets: () => drones.collectMeleeTargets(_meleeTargets),
  bodyFrame: () => meleeBodyFrame(),
  targetRadius: DRONE_RADIUS,
  bladeDamage: () => (rpg.heldMelee() ? rpg.stats.bladeDamage : 0),
  onHit: (id, pos, dmg, launch, heavy, actionId) => {
    // `launch` rides the claim (the pop / the juggle re-lift / the slam — server-capped);
    // `actionId` names the action so the server caps damage per-action (the riposte's 3.5×).
    if (!drones.usesServerHits(id)) net?.sendDroneHit(id, dmg, launch, actionId);
    if (launch !== undefined) drones.juggleHit(id); // airborne victims snap a LiftAir hit
    if (!drones.usesServerHits(id)) dmgNums.spawn([pos.x, pos.y, pos.z], dmg);
    audio.sfx("melee.hit.blade", { at: pos });
    audio.notifyCombat("hit");
    feedback.meleeHit(heavy ? "heavy" : "light");
    input.rumble(heavy ? 0.5 : 0.3, 90);
    // The LANDED cue (user call 2026-07-23): an impact spark at the contact point + the
    // victim flashes white — no ambiguity about whether the swing connected.
    hitSparks?.spawn(_hsPos.copy(pos), heavy);
    drones.hitFlash(id);
  },
  onSwing: () => {
    audio.sfx("melee.swing.blade");
    audio.notifyCombat();
  },
  // THE PARRY fired (RMB tap): open the window from the action's table timing — the same
  // parryFrom/Until the shield parry writes, so all three defensive ladders + the streamed
  // `iv`/`py` flags serve both modes.
  onParry: (def) => {
    parryFrom = performance.now() + def.startupMs;
    parryUntil = parryFrom + def.activeMs;
  },
  // The completed swing on the wire (PvP) — sent by the system at the ACTIVE CLOSE or on
  // chain, never for a cancelled swing (THE CANCEL INVARIANT; see combatActions.ts).
  sendPunch: (id) => {
    camera.getWorldDirection(fireDir);
    net?.sendPunch([fireDir.x, fireDir.y, fireDir.z], rpg.stats.bladeDamage, id);
  },
});

/** VICTIM-side scratch for a RELAYED swing: the ATTACKER's inverse body rotation, and us inside it. Same
 *  trick as above, from the other end of the sword — the arc is baked in the attacker's character-local
 *  frame, so we bring OUR body into THEIR frame rather than transforming the arc. */
const _atkInvQ = new THREE.Quaternion();
const _selfLocal = new THREE.Vector3();
// (PARRY_KNOCK_FORCE retired 2026-07-22: the server now STAGGERS a parried melee attacker
//  via the streamed `py` window — the shove fought the stagger's ground seat.)
/**
 * Route incoming damage through the SHIELD and return what gets through to hp.
 *
 * Shield mode soaks half; a raised guard (RMB) soaks three quarters. The soaked portion is
 * paid for out of ENERGY at the shield track's rate, so absorbing is a real cost and an empty
 * pool means you eat the hit in full. These are the same two constants the SERVER applies from
 * the streamed `sh`/`bl` flags (rooms.damagePlayer) — the client is billing itself for the
 * absorption the server already granted, not deciding it.
 */
function shieldSoak(dmg: number, from?: readonly [number, number, number]): number {
  // HALO-SHIELD FLARE AT THE REAL CONTACT (2026-08-13, user report): predicted
  // hits carry the bolt/melee origin through here, but only the old combat-
  // STANCE shield used it — the Halo shield's flare fell through to the
  // onHealth stream fallback, whose direction is faked from the CAMERA, so
  // every flare appeared behind the player. Flare HERE with the true origin
  // and let the stream fallback stand down (lastDirectionalShieldFlareAt).
  if (dmg > 0 && rpg.shield > 0.5 && footPlanet) {
    lastDirectionalShieldFlareAt = performance.now();
    _hexFrom.copy(character.pos).addScaledVector(character.up, 1.0);
    if (from) _hexAt.set(from[0], from[1], from[2]);
    else _hexAt.copy(_hexFrom).addScaledVector(camera.getWorldDirection(_hexDir), 4);
    shieldHex.setColor(playerStyle.auraColor ?? 0xffffff);
    shieldHex.hit(_hexFrom.clone().sub(origin), _hexAt.clone().sub(origin));
  }
  if (!shieldAbsorbing || dmg <= 0) return dmg;
  const through = Math.max(1, Math.round(dmg * (combatBlocking ? GUARD_SOAK : SHIELD_SOAK)));
  character.drainEnergy((dmg - through) * rpg.stats.absorbCost);
  // The flare is the shield: it is the only thing on screen that says the hit was absorbed.
  // Render space (world − origin), which is where the avatar is drawn.
  _hexFrom.copy(character.pos).addScaledVector(character.up, 1.0);
  // No known origin (a stray bolt, a hazard) still deserves a flare — put it in front of you.
  if (from) _hexAt.set(from[0], from[1], from[2]);
  else _hexAt.copy(_hexFrom).addScaledVector(camera.getWorldDirection(_hexDir), 4);
  shieldHex.setColor(playerStyle.auraColor ?? 0xffffff); // the shield IS the ki
  shieldHex.hit(_hexFrom.sub(origin), _hexAt.sub(origin));
  audio.sfx("combat.block");
  // A hit that lands on a RAISED guard also drives the body back — the kickbox cover flinch, picked
  // by the side it came FROM. Passive absorption (shield mode, guard down) only flares the hex: there
  // is no guard to drive back. A parry beats both and is played by the parry path instead.
  if (combatBlocking && performance.now() >= parryUntil) avatar.blockHit(hitSide(from));
  return through;
}
/** Which side an incoming hit came FROM, in the player's own frame — the cover flinch to play.
 *  Unknown origins read as frontal: the guard faces where you are looking. */
function hitSide(from?: readonly [number, number, number]): "f" | "b" | "l" | "r" {
  if (!from) return "f";
  _sideD.set(from[0], from[1], from[2]).sub(character.pos);
  _sideFwd.set(0, 0, -1).applyQuaternion(character.quat);
  _sideD.addScaledVector(character.up, -_sideD.dot(character.up)); // flatten onto the tangent plane
  if (_sideD.lengthSq() < 1e-6) return "f";
  _sideD.normalize();
  _sideRight.copy(_sideFwd).cross(character.up);
  const f = _sideD.dot(_sideFwd);
  const r = _sideD.dot(_sideRight);
  if (Math.abs(f) >= Math.abs(r)) return f >= 0 ? "f" : "b";
  return r >= 0 ? "r" : "l";
}
const _sideD = new THREE.Vector3();
const _sideFwd = new THREE.Vector3();
const _sideRight = new THREE.Vector3();
const _hexPlayer = new THREE.Vector3();
const _hexFrom = new THREE.Vector3();
const _hexAt = new THREE.Vector3();
const _hexDir = new THREE.Vector3();
/** Incoming weapon damage to the LOCAL player. Under server HP it's a PREDICTION (instant bar
 *  drop + feel; the authoritative `hp` stream confirms ~RTT later, and only the server kills);
 *  legacy mode applies it for real. Mitigation happens BEFORE this, in shieldSoak — which mirrors
 *  what the server decides from the streamed iv/bl/sh flags. */
function applyIncoming(dmg: number): void {
  if (SERVER_AUTHORITATIVE_HP) rpg.predictDamage(dmg);
  else rpg.damage(dmg, "weapon");
}
/** THREAT RING pool (2026-08-17, user ask — CoD/Halo style): each entry is a
 *  system-space direction player→attacker, aged out over THREAT_LIFE. Bearings
 *  are re-derived against the live camera every frame (the arcs rotate as you
 *  turn); repeat fire from ~the same bearing refreshes its arc instead of
 *  stacking new ones. */
const threatArcs: { dir: THREE.Vector3; t: number }[] = [];
const THREAT_LIFE = 1.7;
const THREAT_FADE = 0.6;
const _thD = new THREE.Vector3();
const _thF = new THREE.Vector3();
const _thR = new THREE.Vector3();
let threatShown = false;
function noteThreat(from?: readonly [number, number, number]): void {
  if (!from) return;
  _thD.set(from[0], from[1], from[2]).sub(character.pos);
  _thD.addScaledVector(character.up, -_thD.dot(character.up)); // bearing lives in the tangent plane
  if (_thD.lengthSq() < 1e-4) return;
  _thD.normalize();
  for (const a of threatArcs) {
    if (a.dir.dot(_thD) > 0.87) { a.dir.copy(_thD); a.t = THREAT_LIFE; return; } // ≈ same shooter
  }
  if (threatArcs.length >= 5) threatArcs.shift(); // pool cap — drop the oldest
  threatArcs.push({ dir: _thD.clone(), t: THREAT_LIFE });
}
/** An UN-NEGATED hit landed on us (post shield-soak): the hurt cue + the directional FLINCH —
 *  the victim half of the feedback stack (UAL hit_* clips). Shield absorption keeps the hex
 *  flare as its only reaction, and an in-flight ATTACK suppresses the flinch (a stagger that
 *  eats your own swing is agency theft — DMC lights don't knock you out of a string). */
function takeHitReaction(from?: readonly [number, number, number], dmg = 12): void {
  if (playerDyingT > 0) return;
  if (from) { lastHitFromV.set(from[0], from[1], from[2]); lastHitValid = true; }
  noteThreat(from); // the threat ring shows the bearing even when the shield soaks the hit
  if (shieldAbsorbing) return;
  audio.sfx("player.hurt");
  // VISIBLE hurt (2026-08-02, user: "damage to the player is hard to see"): red vignette
  // pulse scaled by the hit + a doubled camera kick, on top of the flinch/rumble.
  hud.hurtPulse(Math.min(1, dmg / 30));
  feedback.kick(0.06);
  input.rumble(0.45, 120);
  if (!avatar.activeAction()) avatar.flinch(hitSide(from));
}
/** Enemy melee predictions DEFERRED to the strike's impact time (DroneMeleeMsg.wu): the server
 *  schedules its damage at emit+wu with an impact-time reach re-check, so predicting at event
 *  RECEIPT (= windup start) would bill hits the windup lets you escape — HP rubber-banding. */
const pendingEnemyMelee: { m: { id: number; origin: [number, number, number]; dir: [number, number, number]; dmg: number; reach: number }; at: number }[] = [];
/** Crosshair convergence range for ship guns (m). */
const CONVERGENCE = 1400;
/** Bolt muzzle speed for ship guns — bolts.ts DEFAULT_SPEED. */
const SHIP_BOLT_SPEED = 700;
const fireTargetV = new THREE.Vector3();
const shipFireCameraOrigin = new THREE.Vector3();
const camDirV = new THREE.Vector3();
const leadL = new THREE.Vector3();

const noseDirV = new THREE.Vector3();

/**
 * THE reticle direction — there is only one. At speed it's the velocity
 * vector (in space that IS the nose; in atmosphere it carries the aero
 * sink, so the reticle rides the true flight path near the ground); when
 * slow/hovering it eases back to the nose. Guns fire at this, the pip
 * marks this, star headings read this.
 */
const reticleUpV = new THREE.Vector3();

function reticleDir(out: THREE.Vector3): THREE.Vector3 {
  noseDirV.set(0, 0, -1).applyQuaternion(visQuat);
  const sp = ship.vel.length();
  const k = Math.min(1, Math.max(0, (sp - 12) / 30));
  if (k <= 0) out.copy(noseDirV);
  else {
    out.copy(ship.vel).divideScalar(sp);
    // The velocity vector can only be "the reticle" while it's sane —
    // pulse-drive speeds and reverse thrust fall back to the nose.
    if (out.dot(noseDirV) < 0.2) out.copy(noseDirV);
    else out.copy(noseDirV).lerp(out, k).normalize();
  }
  // Near the ground the heading sits a touch LOWER: ground flying reads
  // level when the reticle hugs the terrain ahead, not the sky.
  if (ship.airDensity > 0.05) {
    const nearK = Math.max(0, Math.min(1, 1 - ship.surfaceDist / 500)) * ship.airDensity;
    if (nearK > 0) {
      const np = system.nearestPlanet(ship.pos);
      if (np) {
        reticleUpV.copy(ship.pos).sub(np.planet.pose.center).normalize();
        out.addScaledVector(reticleUpV, -0.055 * nearK).normalize();
      }
    }
  }
  return out;
}

/**
 * Distance along the aim ray to the first terrain/sea contact, capped at
 * CONVERGENCE. The pip marks the gun CONVERGENCE point — but wing muzzles
 * sit below the centerline, so against CLOSE terrain the bolts were still
 * at muzzle height and struck below the reticle. Converging guns AND pip
 * on the true impact point makes the reticle exact at every range.
 */
const aimRayV = new THREE.Vector3();
const aimCliffEnd = new THREE.Vector3();
const aimTerrainContacts = new TerrainContacts();
function aimDistance(
  from: THREE.Vector3,
  dir: THREE.Vector3,
  cap = CONVERGENCE,
  steps = 48,
  // Minimum march step (m). Ships keep the coarse 4 m; the FOOT solve passes a
  // finer floor — an eye-height ray grazing rolling ground skims its bumps at
  // ~2 m altitude, and 4 m samples step clean over the contact (verified by
  // _longshotaim.mjs: missed contacts fell back to max-range convergence and
  // landed grazing shots metres long).
  stepFloor = 4,
): number {
  const near = system.nearestPlanet(from);
  if (!near || near.surfaceDist > cap) return cap;
  const planet = near.planet;
  aimCliffEnd.copy(from).addScaledVector(dir, cap);
  const cliffT = aimTerrainContacts.sweep(planet, from, aimCliffEnd);
  if (cliffT !== null) cap *= cliffT;
  let d = 0;
  let prev = 0;
  for (let i = 0; i < steps && d < cap; i++) {
    aimRayV.copy(from).addScaledVector(dir, d).sub(planet.pose.center);
    const r = aimRayV.length();
    aimRayV.divideScalar(r || 1);
    const alt =
      r - Math.max(planet.surfaceRadiusWorld(aimRayV), planet.def.seaRadius ?? -Infinity);
    if (alt <= 0) {
      // One bisection step between the last clear sample and this one.
      return Math.min(cap, Math.max(8, (prev + d) / 2));
    }
    prev = d;
    d += Math.max(stepFloor, alt * 0.8); // adaptive march: big steps high up
  }
  return cap;
}

/**
 * The ship weapon reticle is a WORLD-space ray from the camera, matching the
 * ground-vehicle contract. Its direction still follows the ship's flight-path
 * marker, but its elevated origin prevents low wing muzzles from converging on
 * terrain beneath the point the player can actually see.
 */
function shipWeaponAimRay(outOrigin: THREE.Vector3, outDirection: THREE.Vector3): number {
  outOrigin.copy(camera.position).add(origin);
  reticleDir(outDirection);
  return aimDistance(outOrigin, outDirection);
}

function tryFire(g: 1 | 2): void {
  const pendingTerrain = system.nearestPlanet(ship.pos);
  if (pendingTerrain && pendingTerrain.surfaceDist <= 9000 && pendingTerrain.planet.terrainSolidsReady === false) return;
  // Guns lock under hyperlight drives: a ship still accelerating on pulse
  // OVERTAKES its own bolts (speed is snapshotted at fire) — physically
  // silly and reads as bolts sinking below the hull.
  if (ship.pulse || chargeT > 0) return;
  const now = performance.now();
  if (now - lastFire[g] < FIRE_COOLDOWN_MS) return;
  // Bolts fire from the ship the player SEES (interpolated render pose, not
  // the sim pose a tick ahead of it) and travel EXACTLY along the line from
  // each muzzle to the RETICLE's convergence point — the reticle is truth.
  // The ship's closing speed folds into the bolt's scalar speed (so you
  // never outrun your own fire), instead of bending the trajectory.
  const targetDistance = shipWeaponAimRay(shipFireCameraOrigin, camDirV);
  const muzzle = new THREE.Vector3();
  const avgDir = new THREE.Vector3();
  let fired = false;
  for (const mount of built.weaponMounts) {
    if (mount.group !== g) continue;
    muzzle.copy(mount.offset).applyQuaternion(visQuat).add(visPos);
    // Use the exact same camera-reticle convergence solve as ground vehicles:
    // every differently placed mount meets at one visible world-space point.
    convergeVehicleWeapon(
      muzzle,
      shipFireCameraOrigin,
      camDirV,
      targetDistance,
      fireTargetV,
      leadL,
    );
    bolts.spawn(muzzle, leadL, ZERO_VEL, g, {
      speed: SHIP_BOLT_SPEED + Math.max(0, ship.vel.dot(leadL)),
      mine: true,
      ignore: { kind: "ship", id: LOCAL_SHIP },
    });
    avgDir.add(leadL);
    fired = true;
  }
  if (fired) {
    lastFire[g] = now;
    avgDir.normalize();
    audio.sfx("weapon.ship.fire", { at: muzzle });
    audio.notifyCombat();
    net?.sendFire(g, [avgDir.x, avgDir.y, avgDir.z]);
  }
}

// --- Modes: intro → fly ⇄ menu ⇄ shipyard; jump = warp cinematic;
// foot = disembarked on a planet surface ---
type Mode = "intro" | "creator" | "cutscene" | "cutscene-editor" | "fly" | "menu" | "builder" | "jump" | "foot" | "vehicle" | "outfit" | "docked";
let mode: Mode = "intro";
/** Where ESC-menu/builder resume lands. */
let returnMode: "fly" | "foot" | "vehicle" = "fly";

// Terrain detail distance: persisted locally, applied before first meshes.
const savedQuality = parseFloat(localStorage.getItem("hl-quality") ?? "1") || 1;
setTerrainQuality(savedQuality);

const travelLog = new TravelLog({
  onClose: () => menu.show(), // back to the ESC menu it came from
});
const controlsPanel = new ControlsPanel({
  onClose: () => menu.show(),
});
// --- Station (phase 8): the system's orbital trade hub ---
let station = new StationVis(scene, system.def);
let dockedPad: number | null = null;
/** Render-clock time the station math runs on (simTime + frame fraction). */
function worldTime(): number {
  return simTime + accumulator;
}

function dockAt(pad: number): void {
  dockedPad = pad;
  mode = "docked";
  ship.vel.set(0, 0, 0);
  ship.speed = 0;
  ship.throttle = 0;
  stationPanel.open(station.def?.name ?? "STATION", pad, rpg.credits);
  audio.sfx("ui.dock");
  releaseCursor();
  hud.setInteract(null);
  hud.showToast(`DOCKED — ${(station.def?.name ?? "STATION").toUpperCase()}`, 2600);
}

function undock(): void {
  if (mode !== "docked") return;
  if (tradeWindow.visible) net?.sendTrade({ op: "cancel" });
  tradeWindow.close();
  const w = dockedPad !== null ? station.pad(dockedPad, worldTime()) : null;
  dockedPad = null;
  stationPanel.close();
  mode = "fly";
  audio.sfx("ship.undock");
  if (w) {
    ship.pos.copy(w.pos).addScaledVector(w.up, 14);
    ship.vel.copy(w.up).multiplyScalar(10);
    ship.speed = 10;
    ship.throttle = 0; // drift clear — throttle is the pilot's choice

    ship.snapVisual();
  }
  input.requestPointerLock();
}

let marketSeq = 0;
const stationPanel = new StationPanel({
  onUndock: () => undock(),
  onQuote: () => net?.sendMarket({ seq: ++marketSeq, op: "quote" }),
  onTrade: (op, id, n) => net?.sendMarket({ seq: ++marketSeq, op, id, n }),
  stockOf: (id) => countUnbound(rpg.inventory, id), // tradeable = unbound only (v49)
  credits: () => rpg.credits,
  dockedPilots: () =>
    net
      ? [...net.remotes.values()]
        .filter((r) => r.dockPad !== null)
        .map((r) => ({ id: r.id, name: r.name }))
      : [],
  onInvite: (id) => net?.sendTrade({ op: "invite", target: id }),
  onAccept: () => net?.sendTrade({ op: "accept" }),
  onDecline: () => net?.sendTrade({ op: "decline" }),
  onBoard: (m) => net?.sendBoard({ ...m, seq: ++marketSeq }),
  held: () => {
    const ids = new Set<string>();
    for (const st of rpg.inventory.slots) if (st) ids.add(st.id);
    return [...ids];
  },
});

const tradeWindow = new TradeWindow({
  onOffer: (offer) => net?.sendTrade({ op: "offer", offer }),
  onConfirm: () => net?.sendTrade({ op: "confirm" }),
  onCancel: () => net?.sendTrade({ op: "cancel" }),
  stockOf: (id) => countUnbound(rpg.inventory, id), // tradeable = unbound only (v49)
  credits: () => rpg.credits,
  held: () => {
    const ids = new Set<string>();
    for (const s of rpg.inventory.slots) if (s) ids.add(s.id);
    return [...ids];
  },
});

// --- System map (minimap + chart SYSTEM tab) ---
const MAP_ARCH_COLORS: Record<string, string> = {
  terran: "#5ec46a",
  ocean: "#3f8fd6",
  desert: "#d6a35f",
  ice: "#9fd8ff",
  volcanic: "#e0633a",
  barren: "#8a8f98",
};

/** The system as sensor nodes — shared by the minimap and the chart tab. */
function buildSystemNodes(): SensorNode[] {
  const nodes: SensorNode[] = [];
  nodes.push({ x: 0, y: 0, z: 0, color: `#${system.def.star.color.toString(16).padStart(6, "0")}`, size: 5, shape: "ringdot", label: undefined });
  system.planets.forEach((p, i) => {
    const moon = !!p.def.parentId;
    nodes.push({
      x: p.pose.center.x,
      y: p.pose.center.y,
      z: p.pose.center.z,
      color: MAP_ARCH_COLORS[p.def.archetype] ?? "#9cb8d4",
      size: moon ? 1.6 : p.def.radius > 4200 * WORLD_SCALE ? 4.4 : p.def.radius > 2200 * WORLD_SCALE ? 3.4 : 2.6,
      label: moon ? undefined : p.def.name,
      ring: !moon,
      id: `p:${i}|${p.def.name}`,
      selected: waypoint?.id === `p:${i}`,
    });
  });
  if (station.def) {
    const sp = stationPose(station.def, worldTime());
    nodes.push({
      x: sp.center[0],
      y: sp.center[1],
      z: sp.center[2],
      color: "#ffd27f",
      size: 4.5,
      shape: "diamond",
      label: station.def.name,
      ring: true,
      ringDash: true,
      id: `station|${station.def.name}`,
      selected: waypoint?.id === "station",
    });
  }
  if (net) {
    for (const r of net.remotes.values()) {
      nodes.push({ x: r.sysPos.x, y: r.sysPos.y, z: r.sysPos.z, color: "#8fe0c8", size: 2.2 });
    }
  }
  const focus = footPlanet ? character.pos : ship.pos;
  mapFwd.set(0, 0, -1).applyQuaternion(footPlanet ? character.quat : ship.quat);
  nodes.push({
    x: focus.x,
    y: focus.y,
    z: focus.z,
    color: "#7fd8ff",
    size: 4,
    shape: "arrow",
    heading: Math.atan2(mapFwd.x, -mapFwd.z),
  });
  return nodes;
}
const mapFwd = new THREE.Vector3();
const minimapView = new SensorView();
minimapView.yaw = 0;
minimapView.tilt = 1.02;

// Tab brings the companion into the player's palm. Place scanning is an explicit
// companion action now; tracked waypoints continue to work independently.
const bodyMarkers = new BodyMarkers();
let bodyMarkersOn = false;
const companion = new Companion(scene);
let companionWarmSystem: typeof system | null = null;
let companionWarmPlanet: Planet | null = null;
let companionWarmAbort: AbortController | null = null;
const companionMotion = matchMedia("(prefers-reduced-motion: reduce)");
companion.reducedMotion = companionMotion.matches;
companionMotion.addEventListener("change", e => { companion.reducedMotion = e.matches; });
let companionNext: CompanionAction | null = null;
const companionMenu = new CompanionMenu({
  onReact: () => companion.react(),
  onPointer: (x, y) => companion.pointer.set(x, y),
  onAction: action => {
    companionNext = action;
    companion.transition.open = false;
    companionMenu.hide();
    input.reset();
    pauseSuppressUntil = performance.now() + 1400;
    if (action === "resume" || action === "signals") input.requestPointerLock();
  },
});
window.addEventListener("keydown", e => {
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  if (companionMenu.visible) { companionMenu.key(e); return; }
  if (companion.transition.active && e.code !== "Tab") {
    e.preventDefault(); e.stopImmediatePropagation(); return;
  }
  if (e.code !== "Tab" || input.typing || e.defaultPrevented) return;
  if (!launched || (mode !== "fly" && mode !== "foot" && mode !== "vehicle")) return;
  if (menu.visible || playerDyingT > 0 || vehicleMountTransition) return;
  if (uiOverlayActive() && !companion.transition.active) return;
  e.preventDefault(); e.stopImmediatePropagation();
  if (e.repeat) return;
  companionNext = null;
  companion.transition.open = true;
  avatar.cancelAction(); actionSys.releaseAttackMode();
  avatar.setWeapon(null); avatar.setAim(false, 0); avatar.setSwordFocus(false); avatar.setWalkMode(false);
  input.reset();
  companionMenu.show(bodyMarkersOn);
  pauseSuppressUntil = performance.now() + 1400;
  if (document.pointerLockElement === canvas) document.exitPointerLock();
}, true);

function updateCompanion(dt: number): void {
  // A double press can open and close before the first render tick. The UI still
  // needs its close/handoff even though the pose never left zero.
  const wasActive = companion.transition.active || !companionMenu.el.hidden;
  if (wasActive && (playerDyingT > 0 || !["fly", "foot", "vehicle"].includes(mode))) {
    companion.transition.open = false;
    companionMenu.hide();
    companionNext = null;
  }
  companion.update(dt);
  if (!wasActive || companion.transition.active) return;
  companionMenu.finishClose();
  avatar.setCompanionPose(0, 0);
  input.reset();
  const action = companionNext;
  companionNext = null;
  if (action === "navigate") openNavigation();
  else if (action === "inventory") invPanel.open();
  else if (action === "journal") journalPanel.toggle();
  else if (action === "character") openYou();
  else if (action === "signals") bodyMarkersOn = !bodyMarkersOn;
}

/** True when another body's sphere eclipses the segment camera→target. */
const occFromV = new THREE.Vector3();
const occToV = new THREE.Vector3();
const occCV = new THREE.Vector3();
const markerGroundCenterV = new THREE.Vector3();
const markerLocalV = new THREE.Vector3();
const markerInvQ = new THREE.Quaternion();

/** Interpolated planet centre in the system/world coordinate frame consumed by
 * DOM markers. Terrain renders at `planet.group`, not at the latest sim tick. */
function markerPlanetCenter(planet: Planet, out: THREE.Vector3): THREE.Vector3 {
  return out.copy(planet.group.position).add(origin);
}

/** Planet-local point → interpolated render-world point. */
function markerLocalWorld(planet: Planet, local: THREE.Vector3, out: THREE.Vector3): THREE.Vector3 {
  return out.copy(local).applyQuaternion(planet.group.quaternion)
    .add(planet.group.position).add(origin);
}

/** Latest-sim world point → interpolated render-world point. */
function markerSimWorld(planet: Planet, simWorld: THREE.Vector3, out: THREE.Vector3): THREE.Vector3 {
  markerLocalV.copy(simWorld).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat);
  return markerLocalWorld(planet, markerLocalV, out);
}

/** Terrain/sea radius beneath the render camera, evaluated in the same
 * interpolated planet frame as the visible ground. */
function markerHorizonRadius(planet: Planet, center: THREE.Vector3, eye = occFromV): number {
  markerInvQ.copy(planet.group.quaternion).invert();
  markerLocalV.copy(eye).sub(center).applyQuaternion(markerInvQ).normalize();
  return Math.max(
    surfaceSupportRadius(planet.sampler, markerLocalV.x, markerLocalV.y, markerLocalV.z),
    planet.def.seaRadius ?? -Infinity,
  );
}

function tagOccluded(target: THREE.Vector3, skip: Planet | null): boolean {
  occFromV.copy(camera.position).add(origin);
  occToV.copy(target).sub(occFromV);
  const len2 = occToV.lengthSq();
  if (len2 < 1) return false;
  for (const p of system.planets) {
    if (p === skip) continue;
    occCV.copy(p.pose.center).sub(occFromV);
    const t = Math.max(0.001, Math.min(0.999, occCV.dot(occToV) / len2));
    const r = p.def.radius * 0.98;
    occCV.addScaledVector(occToV, -t).negate();
    if (occCV.lengthSq() < r * r) return true;
  }
  return false;
}

/** Tag visibility ranges (m). */
const TAG_RANGE_PLANET = 250_000;
const TAG_RANGE_MOON = 250_000;
const TAG_RANGE_NODE = 1500;
const TAG_RANGE_DRONE = 1000;

// --- Flight waypoint: set from the system map, cleared on arrival ---
let waypoint: { id: string; name: string } | null = null;
const waypointV = new THREE.Vector3();
/** Resolve the waypoint's LIVE position; false if it no longer exists. */
function waypointPos(out: THREE.Vector3): boolean {
  if (!waypoint) return false;
  if (waypoint.id === "station") {
    if (!station.def) return false;
    const sp = stationPose(station.def, worldTime());
    out.set(sp.center[0], sp.center[1], sp.center[2]);
    return true;
  }
  const idx = Number(waypoint.id.split(":")[1]);
  const p = system.planets[idx];
  if (!p) return false;
  out.copy(p.pose.center);
  return true;
}
/** Arrival radius: inside it the waypoint clears (once). */
function waypointArriveR(): number {
  if (!waypoint) return 0;
  if (waypoint.id === "station") return 2500;
  const p = system.planets[Number(waypoint.id.split(":")[1])];
  return p ? p.def.radius * 2.6 : 2500;
}

function buildBodyTargets(): import("./ui/bodyMarkers").BodyTarget[] {
  const out: import("./ui/bodyMarkers").BodyTarget[] = [];
  occFromV.copy(camera.position).add(origin);
  // Surface state decided ONCE up front: the quest marker and the settlement
  // tags below both horizon-clamp against it. On a spherical planet a far
  // target's TRUE position sits below the local horizon, so its projected tag
  // landed under the player. Display anchors use the planet's INTERPOLATED
  // render pose and the exact tangent-horizon elevation; trueDist remains in
  // the simulation frame so labels still report gameplay distance.
  // "On the ground" is the MODE, not footPlanet: dev warps null footPlanet
  // while the player keeps riding (the speeder still works), and the clamp,
  // the self-occlusion skip, and player-relative distances must all survive
  // that — hence the nearest-planet fallback.
  const onGround = mode === "foot" || mode === "vehicle";
  const surfaceFrom = mode === "vehicle" ? groundVehicle.pos : character.pos;
  const groundPlanet = onGround ? footPlanet ?? system.nearestPlanet(surfaceFrom)?.planet ?? null : null;
  const groundHorizonR = groundPlanet
    ? markerHorizonRadius(groundPlanet, markerPlanetCenter(groundPlanet, markerGroundCenterV))
    : 0;
  // The waypoint outranks the nearby-signals toggle: visible until reached.
  if (waypoint && waypointPos(waypointV)) {
    out.push({
      key: "waypoint",
      name: `◈ ${waypoint.name}`,
      pos: waypointV.clone(),
      color: "#ff9df5",
    });
  }
  // Tracked homestead (panel TRACK): a cyan ⌂ at the beacon's live position,
  // horizon-clamped like the quest marker; auto-clears on arrival.
  if (hsTrackedId) {
    const tb = hsMine.find((b) => b.id === hsTrackedId) ?? null;
    const tp = tb ? system.planets.find((pl) => pl.def.id === tb.planetId) ?? null : null;
    if (tb && tp) {
      markerLocalV.set(tb.anchor[0], tb.anchor[1], tb.anchor[2]);
      hsTrackV.copy(markerLocalV).applyQuaternion(tp.pose.quat).add(tp.pose.center);
      const onFootPlanet = groundPlanet !== null && tp === groundPlanet;
      const trueDist = onFootPlanet ? hsTrackV.distanceTo(surfaceFrom) : undefined;
      if (onFootPlanet && trueDist !== undefined && trueDist < 25) {
        hsTrackedId = null; // arrived
        hud.showToast("HOMESTEAD REACHED ⌂", 1800);
      } else {
        if (onFootPlanet) {
          markerLocalWorld(tp, markerLocalV, hsTrackV);
          horizonAnchor(occFromV, markerGroundCenterV, hsTrackV, groundHorizonR, false, hsTrackV);
        }
        out.push({
          key: "homestead",
          name: "⌂ HOMESTEAD",
          pos: hsTrackV.clone(),
          color: "#2fd8ff",
          trueDist,
        });
      }
    }
  }
  // Tracked quest: prefer the current persona's live pose (ambient or canon),
  // then fall back to the loaded settlement centre.
  const tqi = quests.trackedInst();
  const tqSeed = tqi ? quests.questTownSeed(tqi) : null;
  if (tqi) {
    const def = quests.defOf(tqi);
    const cur = quests.currentObjective(tqi);
    const npcTarget = quests.ready(tqi)
      ? quests.firstNpcTarget(tqi)
      : cur ? (tqi.tg[tqi.st]?.[cur.oi] ?? null) : null;
    const npcPose = npcTarget && (npcTarget.includes(":npc:") || npcTarget.includes(":canon:"))
      ? npcLife?.poseWorldById(npcTarget, worldTime()) ?? null
      : null;
    // One push for all three resolution branches: the ◆ marker horizon-clamps
    // exactly like the settlement tags when its subject is on the planet under
    // the player's feet (an objective town past the horizon otherwise dropped
    // the marker under the crosshair).
    const pushQuest = (onFootPlanet: boolean): void => {
      const trueDist = onFootPlanet ? questPosV.distanceTo(surfaceFrom) : undefined;
      // Convert the sim pose to the interpolated terrain pose before using the
      // CAMERA as the projection eye. Mixing those frames made the anchor step
      // at 60 Hz while the camera moved smoothly.
      if (onFootPlanet) {
        markerSimWorld(groundPlanet!, questPosV, questPosV);
        horizonAnchor(occFromV, markerGroundCenterV, questPosV, groundHorizonR, false, questPosV);
      }
      out.push({
        key: "quest",
        name: `◆ ${(def?.title ?? "QUEST").slice(0, 22)}`,
        pos: questPosV,
        color: "#ffcf5a",
        sub: quests.ready(tqi) ? "TURN IN" : cur?.obj.text,
        trueDist,
      });
    };
    if (npcPose) {
      questPosV.set(npcPose.pos[0], npcPose.pos[1], npcPose.pos[2]);
      pushQuest(groundPlanet !== null && npcPose.planetId === groundPlanet.def.id);
    } else {
      const target = tqSeed ? settlementRenderer?.byId(tqSeed) ?? null : null;
      if (target) {
        questPosV.copy(target.pos);
        pushQuest(groundPlanet !== null && target.planet === groundPlanet);
      } else if (activeCharacterRecord
        && tqi.q === HOME_ARRIVAL_QUEST_ID
        && activeCharacterRecord.hometown.capitalLocalPosition
        && SYSTEM_ID === activeCharacterRecord.hometown.systemId) {
        const planet = system.planets[activeCharacterRecord.hometown.planetIndex];
        questPosV.set(
          activeCharacterRecord.hometown.capitalLocalPosition[0],
          activeCharacterRecord.hometown.capitalLocalPosition[1],
          activeCharacterRecord.hometown.capitalLocalPosition[2],
        ).applyQuaternion(planet.pose.quat).add(planet.pose.center);
        pushQuest(groundPlanet !== null && planet === groundPlanet);
      }
    }
  }
  if (!bodyMarkersOn) return out;
  if (mode === "fly") {
    for (const p of system.planets) {
      const moon = !!p.def.parentId;
      const d = occFromV.distanceTo(p.pose.center);
      if (d > (moon ? TAG_RANGE_MOON : TAG_RANGE_PLANET)) continue;
      if (d < p.def.radius * 2.2) continue; // you're THERE — no tag noise
      if (tagOccluded(p.pose.center, p)) continue;
      out.push({
        name: p.def.name,
        pos: p.pose.center,
        color: MAP_ARCH_COLORS[p.def.archetype] ?? "#9cb8d4",
      });
    }
    if (station.def) {
      const sp = stationPose(station.def, worldTime());
      bodyStationV.set(sp.center[0], sp.center[1], sp.center[2]);
      const sd = occFromV.distanceTo(bodyStationV);
      if (sd <= TAG_RANGE_PLANET && sd > 2000 && !tagOccluded(bodyStationV, null)) {
        out.push({ name: station.def.name, pos: bodyStationV, color: "#ffd27f" });
      }
    }
  } else if ((mode === "foot" || mode === "vehicle") && footPlanet) {
    // Ground scan: mineable veins and hostiles (players already have
    // their own markers). Both stay at TRUE positions — they're close-range
    // (≤1.5 km) and effectively line-of-sight; only far settlements clamp.
    if (resourceNodes) {
      for (const n of resourceNodes.scan(surfaceFrom, TAG_RANGE_NODE)) {
        if (n.pos.distanceTo(surfaceFrom) < 18) continue; // mining prompt range
        if (tagOccluded(n.pos, footPlanet)) continue;
        const kind = NODE_KINDS[n.kind];
        out.push({
          key: `node:${n.id}`,
          name: kind?.name ?? n.kind,
          pos: n.pos,
          color: `#${(kind?.color ?? 0xffffff).toString(16).padStart(6, "0")}`,
          sub: `${n.left} LEFT`,
        });
      }
    }
    for (const d of drones.list()) {
      const dd = d.pos.distanceTo(surfaceFrom);
      if (dd > TAG_RANGE_DRONE || dd < 50) continue; // close = you SEE it
      if (tagOccluded(d.pos, footPlanet)) continue;
      out.push({
        key: `drone:${d.id}`,
        name: `${d.kind} drone`,
        pos: d.pos,
        color: "#ff5566",
      });
    }
  }
  // PLACES — the settlements NEAR YOU, from the world (PlaceIndex) rather than
  // from whatever the renderer has drawn. Range is measured from the PLAYER, and
  // it is a hard cap: the old scan tagged every settlement the streamer still had
  // in memory, so a long flight left towns half a world away pinned to the edge of
  // the screen. The place you are standing in (< 350 m) stays quiet — you're there.
  // ON FOOT the planet under your feet must NOT occlude them: camera and town both
  // sit on the sphere, so the chord between them dips inside it and every tag would
  // read as "behind the planet" (the veins/hostiles scans skip it for the same
  // reason). From the cockpit the sphere IS the horizon — keep it.
  const scanPlanet = groundPlanet ?? system.nearestPlanet(ship.pos)?.planet ?? null;
  if (scanPlanet) {
    const skipPlanet = groundPlanet;
    const range = mode === "vehicle" ? PLACE_RANGE_VEHICLE : onGround ? PLACE_RANGE_FOOT : PLACE_RANGE_FLY;
    const from = onGround ? surfaceFrom : ship.pos;
    placeHits.length = 0;
    for (const place of places.near(scanPlanet, from, range, placeBuf)) {
      places.world(scanPlanet, place, placeV);
      if (occFromV.distanceTo(placeV) < PLACE_HUSH) continue;
      if (tagOccluded(placeV, skipPlanet)) continue;
      placeHits.push({ place, pos: placeV.clone(), d: from.distanceTo(placeV) });
    }
    // NEAREST-FIRST, capped: range alone does not bound the CLUTTER. A settled
    // stretch of coast can hold twenty towns inside four kilometres, and twenty
    // edge arrows collapse into an unreadable smear of overlapping labels. Show
    // the closest handful — the ones you could actually walk to next.
    placeHits.sort((a, b) => a.d - b.d);
    // Keep the previous roster while each place remains in range. Async region
    // summaries otherwise insert a new town every frame and repack the entire
    // horizon. "Eight useful nearby places" is perceptually better than an
    // exact nearest-eight list that continuously changes identity.
    if (placeTagPlanetId !== scanPlanet.def.id) {
      placeTagPlanetId = scanPlanet.def.id;
      placeTagKeys.length = 0;
    }
    placeSelectedHits.length = 0;
    for (const key of placeTagKeys) {
      const rank = placeHits.findIndex((h) => h.place.id === key);
      if (rank >= 0 && placeSelectedHits.length < MAX_PLACE_TAGS) {
        placeSelectedHits.push(placeHits[rank]);
      }
    }
    for (const hit of placeHits) {
      if (placeSelectedHits.length >= MAX_PLACE_TAGS) break;
      if (!placeSelectedHits.some((h) => h.place.id === hit.place.id)) placeSelectedHits.push(hit);
    }
    placeTagKeys.splice(0, placeTagKeys.length, ...placeSelectedHits.map((h) => h.place.id));

    for (const hit of placeSelectedHits) {
      // Place labels always sit on the tangent horizon. Convert their static
      // planet-local point through the interpolated render pose first; the true
      // range rides along separately and remains a gameplay/sim measurement.
      markerLocalWorld(scanPlanet, hit.place.local, hit.pos);
      if (groundPlanet) {
        horizonAnchor(occFromV, markerGroundCenterV, hit.pos, groundHorizonR, true, hit.pos);
      }
      out.push({
        key: `settlement:${hit.place.id}`,
        name: hit.place.name,
        pos: hit.pos,
        color: TIER_TAG_COLORS[hit.place.tier] ?? "#ffd9a0",
        sub: settlementTierName(hit.place.tier),
        trueDist: hit.d,
        placement: groundPlanet ? "horizon" : "world",
      });
    }
  }
  return out;
}
/** Scratch for the tracked-quest ◆ marker's anchor (one per frame, consumed
 *  by bodyMarkers.update the same frame it's built). */
const questPosV = new THREE.Vector3();
/** Scan-tag hue per settlement tier (order = TIER_NAMES) — categorical,
 *  cool→warm→regal so tiers read apart at a glance; the sub line still prints
 *  the tier NAME, so hue is reinforcement, not the only channel. */
const TIER_TAG_COLORS = [
  "#b3bec6", // 0 waystation — steel gray: infrastructure, not civilization
  "#a4d98a", // 1 hamlet     — moss green
  "#6fd8cf", // 2 village    — teal
  "#ffd9a0", // 3 town       — the legacy settlement peach (continuity anchor)
  "#ffa257", // 4 city       — amber-orange
  "#c9a2ff", // 5 metropolis — violet: unmistakably the apex
] as const;
/** How far from the PLAYER a settlement still earns a scan marker (m). */
const PLACE_RANGE_FOOT = 4000;
/** Speeders cover ground quickly, so their city scan reaches farther ahead. */
const PLACE_RANGE_VEHICLE = 10000;
const PLACE_RANGE_FLY = 10000;
/** The place you're standing in doesn't need an arrow pointing at it (m). */
const PLACE_HUSH = 350;
/** Most place tags on screen at once, nearest first. */
const MAX_PLACE_TAGS = 8;
const places = new PlaceIndex();
const placeBuf: Place[] = [];
const placeHits: { place: Place; pos: THREE.Vector3; d: number }[] = [];
const placeSelectedHits: { place: Place; pos: THREE.Vector3; d: number }[] = [];
const placeTagKeys: string[] = [];
let placeTagPlanetId: string | null = null;
const placeV = new THREE.Vector3();
const bodyStationV = new THREE.Vector3();

const minimapCanvas = document.createElement("canvas");
minimapCanvas.id = "minimap";
minimapCanvas.width = 158;
minimapCanvas.height = 158;
document.body.appendChild(minimapCanvas);
const minimapCtx = minimapCanvas.getContext("2d");
let minimapTimer = 0;
const groundMinimap = new GroundMinimap();
const groundMapPlaces: Place[] = [];
const groundMapMarkers: GroundMapMarker[] = [];
const groundMapForward = new THREE.Vector3();
const groundMapLocal = new THREE.Vector3();

const playerMarkers = new PlayerMarkers();
const markerUp = new THREE.Vector3(0, 1, 0);
const shipMarker = new ShipMarker();

const menu = new PauseMenu({
  onResume: () => resumeFlight(),
  onLog: () => {
    menu.hide();
    travelLog.open(discovered, BASE_SYSTEM, SYSTEM_ID);
  },
  onControls: () => {
    menu.hide();
    controlsPanel.open();
  },
  onGod: () => {
    menu.hide();
    godPanel.open();
  },
  onLogout: () => {
    // Keep auth alive until the final position checkpoint has been accepted.
    // Durable Firestore caching still protects the write if the network is slow.
    void checkpointAndLogout();
  },
  quality: savedQuality,
  onQuality: (q) => {
    setTerrainQuality(q);
    localStorage.setItem("hl-quality", String(q));
  },
  resScale,
  onResScale: (s) => {
    resScale = s;
    applyRenderScale();
    // painterlyPost rebuilds its targets when it sees the ratio move; the ocean
    // reflection target is sized from the drawing buffer and must be told.
    painterlyPost.setSize(window.innerWidth, window.innerHeight);
    syncReflectionSize();
    localStorage.setItem("hl-res-scale", String(s));
  },
  invertY: localStorage.getItem("hl-invert-y") === "1",
  onInvertY: (on) => {
    input.invertY = on;
    localStorage.setItem("hl-invert-y", on ? "1" : "0");
  },
  netStats: () =>
    `NETWORK ${netStatus.s.toUpperCase()} · PILOTS ${netStatus.s === "online" ? netStatus.players : "—"}`,
});
input.invertY = localStorage.getItem("hl-invert-y") === "1";

// God-mode item spawner (only revealed when the SERVER flags this player as god,
// via net.onGod below). Spawns route through the server's authoritative grant.
/** God-mode: plop a join-ready prefab ahead of the player on their own claim.
 *  Grants the exact build cost, then places pieces staggered past the server's
 *  250ms homestead guard — the building assembles piece by piece. */
function plopGodPrefab(prefabId: string): void {
  // Restore gameplay FIRST: the god panel opens from the ESC menu, which parks
  // `mode` at "menu" — guarding before resuming refused the plop even when the
  // player was standing on their claim. resumeFlight() puts returnMode back.
  resumeFlight();
  const prefab = homesteadPrefabById(prefabId);
  const planet = footPlanet;
  if (!prefab || !net) return;
  if (mode !== "foot" || !planet) {
    hud.showToast("PLOP PREFAB — STAND ON YOUR CLAIM ON FOOT", 2400);
    return;
  }
  const g = planet.group;
  const inv = g.quaternion.clone().invert();
  const playerLocal = new THREE.Vector3().copy(character.pos).sub(origin).sub(g.position).applyQuaternion(inv);
  const beacon = homesteadMarkers?.beaconAt(planet.def.id, [playerLocal.x, playerLocal.y, playerLocal.z]) ?? null;
  const uid = currentUid();
  if (!beacon || uid === null || (beacon.ownerUid !== uid && !beacon.allowlist.includes(uid))) {
    hud.showToast("PLOP PREFAB — STAND INSIDE YOUR OWN CLAIM", 2600);
    return;
  }
  const up = playerLocal.clone().normalize();
  const fwd = new THREE.Vector3();
  camera.getWorldDirection(fwd);
  fwd.applyQuaternion(inv).addScaledVector(up, -fwd.dot(up));
  if (fwd.lengthSq() < 1e-4) {
    fwd.crossVectors(Math.abs(up.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0), up);
  }
  fwd.normalize();
  const aim = playerLocal.clone().addScaledVector(fwd, 5);
  const dir = aim.clone().normalize();
  const anchor = dir.clone().multiplyScalar(surfaceSupportRadius(planet.sampler, dir.x, dir.y, dir.z));
  // Tangent frame AT the anchor so the floor sits flat there; the prefab's
  // door side (-z) faces back toward the player.
  const upA = anchor.clone().normalize();
  const fwdA = fwd.clone().addScaledVector(upA, -fwd.dot(upA)).normalize();
  const rightA = new THREE.Vector3().crossVectors(upA, fwdA).normalize();
  const base = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(rightA, upA, fwdA));
  for (const stack of homesteadPrefabCost(prefab)) net.sendGodSpawn(stack.id, stack.n);
  const axisY = new THREE.Vector3(0, 1, 0);
  prefab.pieces.forEach((piece, index) => {
    const t = PIECE_CATALOG.findIndex((entry) => entry.id === piece.id);
    if (t < 0) return;
    window.setTimeout(() => {
      if (!net) return;
      const p = new THREE.Vector3(piece.p[0], piece.p[1], piece.p[2]).applyQuaternion(base).add(anchor);
      const q = new THREE.Quaternion().setFromAxisAngle(axisY, piece.yaw ?? 0).premultiply(base);
      net.sendPlace({ seq: ++hsSeq, beaconId: beacon.id, piece: {
        t, p: [p.x, p.y, p.z], q: [q.x, q.y, q.z, q.w], mat: 0,
      } });
    }, 200 + index * 320);
  });
  hud.showToast(`PLOPPING ${prefab.name.toUpperCase()} — ${prefab.functions.join(" + ").toUpperCase()}`, 2800);
}

const godPanel = new GodPanel({
  onTogglePerf: () => perf.toggle(),
  onPlopPrefab: (prefabId) => plopGodPrefab(prefabId),
  perfOn: () => perf.isOn,
  onSpawn: (id, n) => net?.sendGodSpawn(id, n),
  onAddCredits: (amount) => net?.sendGodCredits(amount),
  onSpawnEnemy: (kind, count, calm) => {
    // No reticle: drop the group on the ground in FRONT of the player (~50 m,
    // well within 100 m). The server re-seats every drone onto the terrain, so
    // only the lateral aim point matters. Planetside only (needs a surface).
    if (!footPlanet) {
      hud.showToast("ENEMY SPAWN — ON FOOT ON A PLANET", 2400);
      resumeFlight();
      return;
    }
    const up = character.pos.clone().sub(footPlanet.pose.center).normalize(); // radial "up"
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
    fwd.addScaledVector(up, -fwd.dot(up)); // flatten onto the ground tangent plane
    if (fwd.lengthSq() < 1e-4) {
      // Looking straight up/down — pick any horizontal heading off the radial.
      const seed = Math.abs(up.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
      fwd.crossVectors(seed, up);
    }
    fwd.normalize();
    const at = character.pos.clone().addScaledVector(fwd, 50);
    const spread = Math.min(40, 8 + count * 2);
    net?.sendDroneSpawn(kind, count, [at.x, at.y, at.z], spread, calm);
    resumeFlight();
    hud.showToast(`SPAWNED ${count}× ${kind.toUpperCase()} AHEAD${calm ? " — CALM (approach to provoke)" : ""}`, 2400);
  },
  onSpawnVehicle: (def) => spawnGodVehicle(def),
  listAssetVehicles: async () =>
    (await listAssetDocs()).filter((doc) => doc.kind === "vehicle").map((doc) => ({ id: doc.id, name: doc.name })),
  onSpawnAssetVehicle: async (docId) => {
    const doc = (await listAssetDocs()).find((entry) => entry.id === docId);
    if (!doc) return;
    const def = compileVehicleDef(doc.asset);
    const issues = vehicleDefinitionIssues(def);
    if (issues.length) { console.warn("[assets] vehicle def issues:", issues); return; }
    spawnGodVehicle(def, () => new ProceduralAssetVehicleView(doc.asset));
  },
  onClose: () => menu.show(),
  // Cutscene Lab now lives in the God Mode → HARNESSES section (dev-only in-world workbench).
  ...(import.meta.env.DEV ? { onCutscene: () => void openCutsceneEditor() } : {}),
});

// --- Ship hangar (cloud inventory) ---
let activeShipId: string | null = null;

// NOTE (2026-08-10): persistShip (save/save-as-new) retired with the builder —
// selection persists through the board handler below (localStorage + profile
// mirror + refit broadcast + activeShipId pointer).

/**
 * NO DEFAULT SHIP (vehicle-tiers, user call 2026-08-10): a new character owns
 * no ship at all — they walk until they earn a registration and board it in
 * the shipyard. Ownership = a ship has been boarded at least once (the
 * profile's ship mirror / hangar pointer), derived at enterCharacter.
 *
 * Guests keep a ship: they have no ledger to earn one through, and a shipless
 * dev session would be useless.
 *
 * ⚠ The default only stands for GUESTS (doLaunch pins it). Every authed entry
 * point must set it before any spawn decision reads it: enterCharacter derives
 * it from the profile, and the creation runners (runCharacterIntro /
 * runPrologueIntro) zero it — a just-created character has no profile to
 * derive from, and the leaked default was exactly why a ship flew in for
 * brand-new players.
 */
let shipOwned = true;

// The shipyard is a SELECTION screen (2026-08-10): the builder is retired, so
// the handler set shrinks to list / board / remove. The card list leads with
// the virtual entries — the stock frame everyone owns, then every EARNED hull —
// followed by ships saved in the builder era, which stay boardable.
const shipyard = new Shipyard(camera, canvas, {
  onDone: () => resumeFlight(),
  hangar: {
    list: async () => {
      const docs = authUser ? await listShipDocs() : [];
      // Earned hulls (v49): the entitlement lives on the collection ledger, not
      // in Firestore ships, so they appear as cards without a saved doc.
      for (const variant of PART_VARIANTS.hull) {
        if (!garage.gear.includes(shipUnlockToken(variant))) continue;
        if (docs.some((d) => layoutHull(d.layout) === variant)) continue;
        docs.unshift({
          id: `hull:${variant}`,
          name: variant.toUpperCase(),
          layout: hullLayout(variant),
          updatedAt: 0,
        });
      }
      // NO stock card (user call 2026-08-10): new players have no ship at all —
      // an empty hangar is the honest state until a registration is earned.
      return docs;
    },
    board: (doc: ShipDoc) => {
      const group = applyShipLayout(doc.layout);
      const virtual = doc.id.startsWith("hull:") || doc.id.startsWith("stock:");
      activeShipId = virtual ? null : doc.id;
      currentLayoutTime = Date.now();
      saveLocalLayout(doc.layout, currentLayoutTime, currentUid() ?? "guest");
      net?.sendRefit(doc.layout);
      if (authUser) {
        void saveShip(doc.layout, currentLayoutTime).catch(() => { });
        void setActiveShipId(virtual ? null : doc.id, doc.name).catch(() => { });
      }
      // FIRST ship ever: it becomes real here. It flies in and lands beside
      // the player once they leave the shipyard (the autopilot callback).
      if (!shipOwned) {
        shipOwned = true;
        built.group.visible = true;
        if (footPlanet) {
          ship.pos.copy(character.pos).addScaledVector(character.up, 260);
          ship.quat.copy(character.quat);
          ship.vel.set(0, 0, 0);
          ship.speed = 0;
          ship.throttle = 0;
          ship.snapVisual();
          autopilot = new Autopilot(ship, footPlanet);
          hud.showToast(`${doc.name} INBOUND — IT WILL SET DOWN NEARBY`, 3200);
        }
      }
      return group;
    },
    remove: async (id) => {
      await deleteShipDoc(id);
      if (id === activeShipId) {
        activeShipId = null;
        void setActiveShipId(null).catch(() => { });
      }
      return listShipDocs();
    },
    current: () => currentLayout,
  },
});

function resumeFlight(): void {
  if (characterCreator.active && characterCreator.saving) return;
  menu.hide();
  if (shipyard.active) shipyard.close();
  if (mode === "outfit" && characterCreator.active) characterCreator.close();
  hud.setPanelsVisible(true); // restore the HUD the outfitter hid
  exitVoid();
  mode = returnMode;
  input.requestPointerLock();
}

function openMenu(): void {
  if (shipyard.active) shipyard.close();
  // mode "menu" has NO stepAction call on the fixed tick, so the combat-action clock would FREEZE
  // here mid-swing (while strikeT/comboWindowT keep draining on render dt). Close the menu and the
  // stale action resumes and sweeps its remaining arc — against drones that moved while the menu was
  // up. Abandon the swing instead: a swing you interrupted with the menu is a swing you gave up.
  avatar.cancelAction();
  actionSys.releaseAttackMode();
  menu.show();
  mode = "menu";
}

// --- The void: editors strip the world away so the player is NOWHERE ---
// while they tinker; exitVoid restores play exactly where it left off.
let inVoid = false;

/** Body-frame anchor while a void editor is open: the planet keeps
 *  rotating and orbiting — re-derive world positions on exit so nobody
 *  resumes inside a hillside that moved. */
let voidAnchor: {
  planet: import("./world/planet").Planet;
  shipLocal: THREE.Vector3;
  charLocal: THREE.Vector3;
  shipLocalQ: THREE.Quaternion;
  charLocalQ: THREE.Quaternion;
} | null = null;

function enterVoid(showShip: boolean, pureBlack = false): void {
  const nearV = system.nearestPlanet(footPlanet ? character.pos : ship.pos);
  if (nearV && nearV.surfaceDist < nearV.planet.def.radius * 1.6) {
    const inv = nearV.planet.pose.invQuat;
    const c = nearV.planet.pose.center;
    voidAnchor = {
      planet: nearV.planet,
      shipLocal: ship.pos.clone().sub(c).applyQuaternion(inv),
      charLocal: character.pos.clone().sub(c).applyQuaternion(inv),
      shipLocalQ: ship.quat.clone().premultiply(inv),
      charLocalQ: character.quat.clone().premultiply(inv),
    };
  } else {
    voidAnchor = null;
  }
  inVoid = true;
  system.setBodiesVisible(false);
  // The outfitter wants a featureless black screen — kill the starfield too
  // (setBodiesVisible leaves the stars up for the shipyard's starry void).
  system.setStarfieldVisible(!pureBlack);
  station.setVisible(false);
  drones.setVisible(false);
  net?.setWorldVisible(false);
  trails.setVisible(false);
  wormholes.clear();
  avatar.group.visible = false;
  built.group.visible = showShip;
}

function exitVoid(): void {
  if (!inVoid) return;
  inVoid = false;
  system.setBodiesVisible(true);
  system.setStarfieldVisible(true);
  station.setVisible(true);
  drones.setVisible(true);
  net?.setWorldVisible(true);
  trails.setVisible(true);
  // Not unconditional: the creation runners exit the void with NO ship owned,
  // and re-showing the hull here was one of the brand-new-player ship leaks.
  built.group.visible = shipOwned;
  avatar.group.visible = footPlanet !== null;
  if (voidAnchor) {
    // Ride the frame we left from: same spot on the (moved) ground.
    const q = voidAnchor.planet.pose.quat;
    const c = voidAnchor.planet.pose.center;
    ship.pos.copy(voidAnchor.shipLocal).applyQuaternion(q).add(c);
    ship.snapVisual();
    if (footPlanet) {
      character.pos.copy(voidAnchor.charLocal).applyQuaternion(q).add(c);
      character.vel.set(0, 0, 0);
      character.snapVisual();
    }
    voidAnchor = null;
  }
}

/**
 * Exit pointer lock NOW and re-exit over the next frames: lock GRANTS are
 * async, and one requested moments earlier (closing a panel, the wheel's
 * keyup) could land after a UI's exit call — cursor gone until a click.
 */
function releaseCursor(): void {
  // Kill racing re-lock REQUESTS at the source (panel onClose handlers,
  // the wheel's keyup) — they fire in arbitrary order around the open.
  input.suppressLock(600);
  const exit = () => {
    if (document.pointerLockElement) document.exitPointerLock();
  };
  exit();
  requestAnimationFrame(() => {
    exit();
    requestAnimationFrame(exit);
  });
}

function openShipyard(): void {
  menu.hide();
  mode = "builder";
  enterVoid(true); // the ship floats alone in the void
  // Shipless: no preview hull until a card is boarded — showing the default
  // frame would display a ship the player does not own.
  built.group.visible = shipOwned;
  shipyard.open(built.group, built.group.position.clone());
  releaseCursor();
}

// --- Outfit editor: locker + mannequin, same void treatment ---
let activeOutfitId: string | null = null;

const characterCreator = new CharacterCreator(camera, canvas, scene);
const cutscenePlayer = new CutscenePlayer(camera);
let cutsceneEditor: CutsceneEditor | null = null;
const cutsceneEditorReady: Promise<CutsceneEditor | null> = import.meta.env.DEV
  ? import("./cutscene/editor").then(({ CutsceneEditor: Editor }) => {
    cutsceneEditor = new Editor(camera, canvas);
    return cutsceneEditor;
  })
  : Promise.resolve(null);

/** GARAGE chip → the chassis picker. A cursor panel over the world (no void
 *  treatment): you are choosing from a list, not posing a mannequin. */
function openGarage(): void {
  if (mode !== "fly" && mode !== "foot" && mode !== "vehicle") return;
  if (invPanel.visible) invPanel.close();
  garagePanel.open();
  // Ask for a fresh read: another session may have claimed something.
  net?.sendCollection(garage.op({ op: "refresh" }));
  releaseCursor();
}

/** The inventory's outfit shortcut opens the same editor on its Outfit page. */
function openOutfit(): void { openYou("outfit"); }

/** Character (left on C): a draft preview; Save commits, Cancel/Escape discards. */
function openYou(initialMode: "appearance" | "outfit" = "appearance"): void {
  if (mode !== "fly" && mode !== "foot") return;
  if (invPanel.visible) invPanel.close();
  returnMode = mode;
  enterVoid(false, true);
  hud.setPanelsVisible(false);
  mode = "outfit";
  characterCreator.open({
    mode: "edit", name: pilotName, style: { ...playerStyle }, initialMode,
    ownedTokens: () => [...garage.gear, ...equippedStyleTokens(rpg.inventory.equipped)],
    wardrobe: authUser ? {
      list: listOutfitDocs,
      async save(id, name, style) {
        if (id) await updateOutfitDoc(id, { name, style }, Date.now());
        else id = await createOutfitDoc(name, style, Date.now());
        return { id, name, style };
      },
      async remove(id) {
        await deleteOutfitDoc(id);
        if (id === activeOutfitId) {
          activeOutfitId = null;
          await setActiveOutfitId(null);
        }
      },
    } : undefined,
    stats: {
      render: youStatsHtml,
      onAction: (track) => track.startsWith("unequip:")
        ? unequipGear(track.slice(8) as EquipSlot)
        : buyUpgrade(track as UpgradeTrack),
    },
    async onSubmit(draft, outfit) {
      // Keep the draft open on a persistence error so retrying loses no work.
      if (authUser?.emailVerified) {
        await saveStyle(draft.style, outfit);
      }
      if (outfit !== undefined) {
        activeOutfitId = outfit?.id ?? null;
      }
      if (styleTimer !== null) { clearTimeout(styleTimer); styleTimer = null; }
      applyStyle(draft.style);
      publishStyleAfterCooldown(false);
      characterCreator.close();
      resumeFlight();
    },
    onCancel: () => resumeFlight(),
  });
  net?.sendCollection(garage.op({ op: "refresh" }));
  releaseCursor();
}

// --- Disembark / board: low and slow in the atmosphere, step out anywhere.
// The ship follows you down and LANDS nearby (vehicle-tiers V1) — the landing
// spot is the grounding point you return to (hold F to board), persisted
// across sessions. Hold V summons it to a new spot, on a 5-minute cooldown. ---

/**
 * Disembarkable: anywhere in (or just above) the atmosphere band, at real
 * flight speeds — the drop + jetpack handle the rest. Pulse is still out.
 */
function canDisembark(): boolean {
  const near = system.nearestPlanet(ship.pos);
  if (!near) return false;
  const h = near.planet.def.atmosphere?.height;
  const band = h ? h * 4.5 : near.planet.def.radius * 0.22;
  return ship.surfaceDist < band && Math.abs(ship.speed) < 150;
}

/** DEV/test only: jump straight into foot mode over the first planet, bypassing
 *  the menu/auth/flight so a headless harness can drive + observe the avatar. */
function devSpawnFoot(): void {
  devSpawnFly();
  disembark();
}

/** DEV/test only: launch straight into FLY mode over the first planet. */
function devSpawnFly(): void {
  const p0 = system.planets[0];
  if (!p0) return;
  const dir = new THREE.Vector3(0.3, 0.6, 0.2).normalize();
  const surf = p0.surfaceRadiusWorld(dir);
  ship.pos.copy(p0.pose.center).addScaledVector(dir, surf + 20);
  ship.vel.set(0, 0, 0);
  ship.speed = 0;
  ship.throttle = 0;
  ship.snapVisual();
  origin.copy(ship.pos);
  if (!launched) {
    launched = true;
    intro.classList.add("hidden");
  }
  mode = "fly";
  returnMode = "fly";
}

/** A seed-bearing cell found by a whole-planet scan: its region cell, the seed
 *  count, and the highest tier present (0 waystation … 5 metropolis — see
 *  TIER_NAMES). Census reports the tier NAME; warp gates on tier ≥ a min. */
interface SeedCellHit {
  cell: CellId;
  seeds: number;
  maxTier: number;
  /** Surface arc distance (m) from the scan's reference direction to this cell. */
  distM: number;
}

/** Highest tier (0..5) among a cell's seeds (0 if empty — callers gate on the
 *  seed count, not this). W2c-1 census v2 reports tiers by NAME (TIER_NAMES),
 *  read straight off the registry-dealt seed — no materialization needed. */
function maxTierOf(seeds: SettlementSeed[]): number {
  let m = 0;
  for (const s of seeds) if (s.tier > m) m = s.tier;
  return m;
}

/** Cheap required-building summary for a materialized region: how many of the
 *  primary tier's TIER_REQUIREMENTS USE_* codes are present (at ≥ their spec
 *  multiplicity) among the built parcels, plus the codes still missing. Pure over
 *  the graph's parcel uses — no buildStructures/carve pass (warp-only, one cell). */
function requiredSummary(g: CityGraph): { satisfied: number; total: number; missing: number[] } {
  const tier = g.tier < 0 ? 0 : g.tier;
  const have = new Map<number, number>();
  for (const p of g.parcels) if (p.poly.length >= 8) have.set(p.use, (have.get(p.use) ?? 0) + 1);
  const want = new Map<number, number>();
  for (const u of TIER_REQUIREMENTS[tier]) want.set(u, (want.get(u) ?? 0) + 1);
  let satisfied = 0;
  const missing: number[] = [];
  for (const [u, wc] of want) {
    if ((have.get(u) ?? 0) >= wc) satisfied++;
    else missing.push(u);
  }
  return { satisfied, total: want.size, missing };
}

/** Surface arc distance (m) between two unit dirs on a sphere of radius R. */
function arcDistM(R: number, ax: number, ay: number, az: number, bx: number, by: number, bz: number): number {
  const dot = Math.min(1, Math.max(-1, ax * bx + ay * by + az * bz));
  return Math.acos(dot) * R;
}

// Cells sampled per frame-yield during a whole-planet scan. This is a debug
// tool — seconds are acceptable — but chunking keeps the tab responsive and lets
// progress logs land; the scan yields to requestAnimationFrame between chunks.
const SCAN_CHUNK = 256;

/**
 * Walk an ordered region-cell list, sampling settlementSeeds per cell and
 * invoking `onHit` for every seed-bearing cell (returning true from `onHit`
 * stops the scan early — the warp uses this to stop at the nearest hit; the
 * census never stops). Progress is logged every ~500 cells so large planets
 * don't look hung, and the loop yields to the frame loop every SCAN_CHUNK cells.
 */
async function scanPlanetCells(
  planet: Planet,
  refx: number, refy: number, refz: number,
  order: CellId[],
  tag: string,
  onHit: (hit: SeedCellHit) => boolean,
): Promise<void> {
  const R = planet.def.radius;
  const total = order.length;
  const c: number[] = [0, 0, 0];
  for (let i = 0; i < total; i++) {
    const cell = order[i];
    const seeds = settlementSeeds(planet.sampler, cell);
    if (seeds.length > 0) {
      cellCenterDir(R, cell, c);
      const hit: SeedCellHit = {
        cell,
        seeds: seeds.length,
        maxTier: maxTierOf(seeds),
        distM: arcDistM(R, refx, refy, refz, c[0], c[1], c[2]),
      };
      if (onHit(hit)) return;
    }
    if ((i + 1) % 500 === 0) console.log(`[settlements] ${tag} scanned ${i + 1}/${total}…`);
    if ((i + 1) % SCAN_CHUNK === 0) await new Promise<void>((res) => setTimeout(res, 0));
  }
}

/**
 * DEV settlement debug rig (window.__hl.settlements.warpToNearest). Finds the
 * nearest planet to the player and either explains why it has no settlements or
 * warps to its nearest one. The scan ALWAYS proceeds — the W2c-1 habitability
 * carve-out means the TierRegistry still deals WAYSTATIONS on hostile
 * (non-terran/ocean) worlds (only tier ≥ hamlet is gated out there), so those
 * planets are not empty. Two outcomes are distinguished by the minTier floor:
 *
 *   (1) HOSTILE WORLD + minTier ≥ 1 — hostile planets host waystations ONLY, so a
 *       request for tier ≥ hamlet cannot be satisfied. We report this and return
 *       null WITHOUT wasting a scan (nothing at that floor can exist).
 *   (2) OTHERWISE — we scan the WHOLE planet (all 6 faces × cellsPerFace² region
 *       cells), nearest-first by chord distance of each cell centre from the
 *       camera direction, stopping at the first seed-bearing cell at or above the
 *       tier floor. If none is found the planet is empty-at-that-floor (sparse
 *       seeds), reported as such.
 *
 * The scan is chunked across frames (setTimeout(0) every SCAN_CHUNK cells), so
 * the API is ASYNC: it returns Promise<coords|null> (was a sync coords|null).
 *
 * WARP-HELPER FINDING (brief): the existing `__hl.warpTo` is `switchSystem(x,y,z)`
 * — a GALACTIC star-system jump keyed on universe coordinates, NOT a surface
 * teleport, so calling it with a settlement's world position would fling the
 * player to an unrelated star system. There is no surface-teleport helper on
 * __hl, so this reuses the `devSpawnFly` idiom directly (position the ship over
 * the target, rebase the floating origin, enter fly mode) — a genuine in-system
 * warp to the coordinates it just logged.
 */
async function warpToNearestSettlement(minTier = 0): Promise<{ x: number; y: number; z: number } | null> {
  const ref = footPlanet ? character.pos : ship.pos; // player's system-space position
  const near = system.nearestPlanet(ref);
  if (!near) { console.warn("[settlements] warpToNearest: no planet nearby"); return null; }
  const planet = near.planet;
  const R = planet.def.radius;
  const label = planet.def.name || planet.def.seed;
  const minName = TIER_NAMES[Math.max(0, Math.min(5, minTier))];
  // (1) Habitability carve-out — hostile (non-terran/ocean) worlds host WAYSTATIONS
  // ONLY (the TierRegistry deals them; tier ≥ hamlet is gated out there). A request
  // for a settlement of tier ≥ 1 therefore can never be satisfied, so we short-circuit.
  const arch = planet.def.archetype;
  const hostile = arch !== "terran" && arch !== "ocean";
  if (hostile && minTier >= 1) {
    console.log(`[settlements] ${label} is ${arch} — no settlements of tier ≥ ${minTier} (${minName}) — hostile worlds host waystations only.`);
    return null;
  }
  // (2) Whole-planet scan, nearest-first from the camera direction. minTier gates
  // the target: skip cells whose highest tier is below the requested floor
  // (warpToNearest(TIER_VILLAGE) = nearest village-or-larger; default = any).
  const localDir = new THREE.Vector3().copy(ref).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat).normalize();
  const order = planScan(R, localDir.x, localDir.y, localDir.z);
  console.log(`[settlements] scanning ${label} (${arch}) — ${order.length} region cells, nearest-first, tier ≥ ${minTier} (${minName})…`);
  let found: SeedCellHit | null = null;
  await scanPlanetCells(planet, localDir.x, localDir.y, localDir.z, order, "warpToNearest", (hit) => {
    if (hit.maxTier < minTier) return false; // below the tier floor — keep scanning
    found = hit;
    return true; // stop at the first (nearest) qualifying seed-bearing cell
  });
  if (found === null) {
    console.log(`[settlements] no settlement of tier ≥ ${minTier} (${minName}) on this planet (${order.length} cells scanned) — try a lower minTier or another terran planet`);
    return null;
  }
  const hit: SeedCellHit = found;
  const f = regionFrame(R, hit.cell);
  const surf = surfaceSupportRadius(planet.sampler, f.cx, f.cy, f.cz);
  const localPt = new THREE.Vector3(f.cx * surf, f.cy * surf, f.cz * surf);
  const world = localPt.clone().applyQuaternion(planet.pose.quat).add(planet.pose.center);
  const tierName = TIER_NAMES[hit.maxTier];
  // Materialize ONLY the target cell (census stays materialization-free) to report
  // the built size + required-building satisfaction of the primary settlement.
  const g = materializeRegion(planet.sampler, hit.cell);
  const req = requiredSummary(g);
  const missNames = req.missing.length ? ` missing[${req.missing.join(",")}]` : "";
  console.log(
    `[settlements] nearest settlement face=${hit.cell.face} x=${hit.cell.x} y=${hit.cell.y} ` +
    `seeds=${hit.seeds} tier=${hit.maxTier} (${tierName}) pop=${g.population} parcels=${g.parcels.length} ` +
    `required ${req.satisfied}/${req.total} satisfied${missNames} dist=${hit.distM.toFixed(0)} m ` +
    `→ world (${world.x.toFixed(1)}, ${world.y.toFixed(1)}, ${world.z.toFixed(1)})`,
  );
  // Warp the ship to hover above the settlement (devSpawnFly idiom; see the
  // WARP-HELPER FINDING above on why __hl.warpTo is unsuitable here).
  const outDir = localPt.clone().normalize().applyQuaternion(planet.pose.quat);
  ship.pos.copy(world).addScaledVector(outDir, 80);
  ship.vel.set(0, 0, 0);
  ship.speed = 0;
  ship.throttle = 0;
  ship.snapVisual();
  origin.copy(ship.pos);
  if (!launched) { launched = true; intro.classList.add("hidden"); }
  footPlanet = null;
  mode = "fly";
  returnMode = "fly";
  return { x: world.x, y: world.y, z: world.z };
}

/**
 * DEV quest warp (window.__hl.warpToQuest): park the ship 80 m over the TRACKED
 * quest's current objective, jumping star systems first when the quest lives
 * elsewhere (camp:arrival = the player's home system). Resolution prefers live
 * state (NPC pose, loaded renderer) and falls back to the ANALYTIC seed
 * position — pure worldgen math that needs no residency, which is what makes
 * the cross-system jump work before anything has streamed in; the settlement
 * then streams in around the parked ship (residency follows focusPos).
 */
function warpToQuestTarget(): { system: string; jumped: boolean; x: number; y: number; z: number; via: string } | null {
  const tqi = quests.trackedInst();
  if (!tqi) { console.warn("[warpToQuest] no tracked quest — track one in the journal (J)"); return null; }
  const def = quests.defOf(tqi);
  const cur = quests.currentObjective(tqi);
  const npcTarget = quests.ready(tqi)
    ? quests.firstNpcTarget(tqi)
    : cur ? (tqi.tg[tqi.st]?.[cur.oi] ?? null) : null;
  const anchor = quests.questAnchor(tqi);
  // camp:arrival tracked before its canon targets resolved still knows its
  // home from the character record.
  const targetSystem = anchor?.systemId
    ?? (tqi.q === HOME_ARRIVAL_QUEST_ID ? activeCharacterRecord?.hometown.systemId ?? null : null);
  if (!targetSystem) {
    console.warn(`[warpToQuest] "${def?.title ?? tqi.q}" anchors to nothing physical (no town/npc target)`);
    return null;
  }
  const jumped = targetSystem !== SYSTEM_ID;
  if (jumped) switchSystem(targetSystem);

  // Resolve a surface world position, first hit wins. The live paths are
  // same-system only — a jump just rebuilt npcLife/settlementRenderer empty.
  const worldV = new THREE.Vector3();
  let planet: Planet | null = null;
  let via = "";
  const npcPose = !jumped && npcTarget ? npcLife?.poseWorldById(npcTarget, worldTime()) ?? null : null;
  if (npcPose) {
    planet = system.planets.find((p) => p.def.id === npcPose.planetId) ?? null;
    if (planet) { worldV.set(npcPose.pos[0], npcPose.pos[1], npcPose.pos[2]); via = "npc-pose"; }
  }
  if (via === "" && !jumped && anchor?.settlementId) {
    const hit = settlementRenderer?.byId(anchor.settlementId) ?? null;
    if (hit) { planet = hit.planet; worldV.copy(hit.pos); via = "renderer"; }
  }
  if (via === "" && anchor?.settlementId) {
    // Analytic settlement id → position: the id names its planet, region cell
    // and seed slot; the tier deal reproduces the seed with zero residency.
    const parsed = parseRegionSettlementId(anchor.settlementId);
    planet = parsed ? system.planets.find((p) => p.def.seed === parsed.planetSeed) ?? null : null;
    if (parsed && planet) {
      const frame = regionFrame(planet.def.radius, parsed.regionId);
      const dealt = settlementSeeds(planet.sampler, parsed.regionId);
      const d: [number, number, number] = [frame.cx, frame.cy, frame.cz];
      const seed = dealt.find((s) => s.k === parsed.settlementK);
      if (seed) { d[0] = seed.dx; d[1] = seed.dy; d[2] = seed.dz; via = "seed"; }
      else {
        // Chronicle ids can name SPRAWL pseudo-seeds (k ≥ 1000) that the base
        // deal doesn't hold; failing both, the cell centre is close enough for
        // a dev warp (~a couple of km worst case) and says so in the log.
        const dir = [0, 0, 0];
        const sp = sprawlSeeds(planet.sampler, parsed.regionId, frame, dealt).find((s) => s.k === parsed.settlementK);
        if (sp) { localToDir(frame, sp.u, sp.v, dir); d[0] = dir[0]; d[1] = dir[1]; d[2] = dir[2]; via = "sprawl-seed"; }
        else via = "cell-centre (approx — settlementK not in the deal)";
      }
      const surf = surfaceSupportRadius(planet.sampler, d[0], d[1], d[2]);
      worldV.set(d[0] * surf, d[1] * surf, d[2] * surf).applyQuaternion(planet.pose.quat).add(planet.pose.center);
    }
  }
  if (via === "" && npcTarget) {
    // Canon target (home:<uid>[.<cid>]:canon:<slug>): no settlement in the id —
    // the shared resolver re-derives the capital placement from the HOME id.
    const canon = parseCanonicalNpcId(npcTarget);
    const placement = canon ? resolveHomeCanonicalNpc(canon.home, canon.slug) : null;
    planet = placement ? system.planets[placement.planetIndex] ?? null : null;
    if (placement && planet) {
      const frame = regionFrame(planet.def.radius, placement.regionId);
      const dir = [0, 0, 0];
      const arrival=placement.settlement.arrival??placement.settlement;
      localToDir(frame, arrival.u, arrival.v, dir);
      const surf = surfaceSupportRadius(planet.sampler, dir[0], dir[1], dir[2]);
      worldV.set(dir[0] * surf, dir[1] * surf, dir[2] * surf).applyQuaternion(planet.pose.quat).add(planet.pose.center);
      via = "canon-capital";
    }
  }
  if (via === "" && activeCharacterRecord
    && activeCharacterRecord.hometown.systemId === targetSystem
    && activeCharacterRecord.hometown.capitalLocalPosition) {
    planet = system.planets[activeCharacterRecord.hometown.planetIndex] ?? null;
    if (planet) {
      const lp = activeCharacterRecord.hometown.capitalLocalPosition;
      worldV.set(lp[0], lp[1], lp[2]).applyQuaternion(planet.pose.quat).add(planet.pose.center);
      via = "hometown-record";
    }
  }
  if (via === "" || planet === null) {
    console.warn(`[warpToQuest] "${def?.title ?? tqi.q}" — could not resolve a surface position (anchor=${JSON.stringify(anchor)}, npcTarget=${npcTarget})`);
    return null;
  }

  // Park the ship above the target (devSpawnFly idiom, as warpToNearest).
  const outDir = worldV.clone().sub(planet.pose.center).normalize();
  ship.pos.copy(worldV).addScaledVector(outDir, 80);
  ship.vel.set(0, 0, 0);
  ship.speed = 0;
  ship.throttle = 0;
  ship.snapVisual();
  origin.copy(ship.pos);
  if (!launched) { launched = true; intro.classList.add("hidden"); }
  footPlanet = null;
  mode = "fly";
  returnMode = "fly";
  console.log(
    `[warpToQuest] "${def?.title ?? tqi.q}" stage ${tqi.st} — ${quests.ready(tqi) ? "TURN IN" : cur?.obj.text ?? "?"} ` +
    `· target=${via} · ${jumped ? `JUMPED → ${targetSystem}` : "in-system"} ` +
    `→ world (${worldV.x.toFixed(1)}, ${worldV.y.toFixed(1)}, ${worldV.z.toFixed(1)})`,
  );
  return { system: targetSystem, jumped, x: worldV.x, y: worldV.y, z: worldV.z, via };
}

/**
 * R1 D5 screenshot-harness warp: park the ship hovering `up` metres above the
 * surface centre of a SPECIFIC region cell (face,x,y) on the nearest planet —
 * unlike warpToNearest, the cell is given, not searched, so the capture script
 * targets the exact deterministically-chosen cells the harness picked. Residency
 * (terrain + settlements) is driven by the ship's position (focusPos), so parking
 * the ship here materializes the settlement + its resident 3×3 ring while the
 * camera is repositioned freely. Returns the cell's world-space surface centre +
 * packed cell key + planet index (the camera pin + readiness poll key off these).
 */
async function warpToCellCapture(
  face: number, x: number, y: number, up = 80, planetIdx = -1,
): Promise<{ center: [number, number, number]; radial: [number, number, number]; parkPos: [number, number, number]; localDir: [number, number, number]; parkUp: number; pi: number; rk: number; R: number } | null> {
  // planetIdx ≥ 0 targets an EXACT planet (deterministic capture — the terran
  // subject is planets[1] in the chosen system, not the nearest at spawn); < 0
  // falls back to the nearest planet (console use).
  let planet: Planet;
  if (planetIdx >= 0) {
    if (planetIdx >= system.planets.length) { console.warn(`[capture] warpToCell: no planet at index ${planetIdx}`); return null; }
    planet = system.planets[planetIdx];
  } else {
    const near = system.nearestPlanet(footPlanet ? character.pos : ship.pos);
    if (!near) { console.warn("[capture] warpToCell: no planet nearby"); return null; }
    planet = near.planet;
  }
  const R = planet.def.radius;
  const cell: CellId = { face, x, y, level: LEVEL_REGION };
  const f = regionFrame(R, cell);
  // Frame the PRIMARY SETTLEMENT SEED, not the cell centre (W2d-3 harness fix):
  // seeds are dealt anywhere in the ~2 km cell, so a cell-centre frame at a
  // 190-340 m aerial can miss the settlement entirely (an all-ocean shot).
  let aim: [number, number, number] = [f.cx, f.cy, f.cz];
  const dealt = settlementSeeds(planet.sampler, cell);
  if (dealt.length > 0) {
    let prim = dealt[0];
    for (const s of dealt) if (s.tier > prim.tier || (s.tier === prim.tier && s.k < prim.k)) prim = s;
    aim = [prim.dx, prim.dy, prim.dz];
  }
  const surf = surfaceSupportRadius(planet.sampler, aim[0], aim[1], aim[2]);
  const localPt = new THREE.Vector3(aim[0] * surf, aim[1] * surf, aim[2] * surf);
  const world = localPt.clone().applyQuaternion(planet.pose.quat).add(planet.pose.center);
  const outDir = localPt.clone().normalize().applyQuaternion(planet.pose.quat);
  const pi = system.planets.indexOf(planet);
  // Park the ship above the cell (devSpawnFly idiom) — this drives residency.
  ship.pos.copy(world).addScaledVector(outDir, up);
  ship.vel.set(0, 0, 0);
  ship.speed = 0;
  ship.throttle = 0;
  ship.snapVisual();
  origin.copy(ship.pos);
  if (!launched) { launched = true; intro.classList.add("hidden"); }
  footPlanet = null;
  mode = "fly";
  returnMode = "fly";
  console.log(`[capture] parked over face=${face} x=${x} y=${y} on ${planet.def.name || planet.def.seed} (pi=${pi}) → world (${world.x.toFixed(1)}, ${world.y.toFixed(1)}, ${world.z.toFixed(1)})`);
  return {
    center: [world.x, world.y, world.z],
    radial: [outDir.x, outDir.y, outDir.z],
    parkPos: [ship.pos.x, ship.pos.y, ship.pos.z],
    // Time-invariant anchor (W2d-3 harness fix): the FRAMED point's planet-local
    // unit dir (primary seed, or cell centre when seedless) + park altitude —
    // holdLocal/pinLocal re-derive live world coords from it.
    localDir: aim,
    parkUp: up,
    pi, rk: packCell(cell), R,
  };
}

/**
 * DEV settlement survey (window.__hl.settlements.census). Same nearest-planet
 * selection as warpToNearest, but EXHAUSTIVE: it scans every region cell and logs
 * each seed-bearing one (face/x/y, seed count, max tier, distance), returning the
 * full list. Hostile (non-terran/ocean) worlds are NOT skipped — they host
 * waystations (the habitability carve-out), so census reports them too; the
 * message just flags the world as waystations-only. The fly-through's planet
 * survey tool — resolves the "is this planet empty or just sparse?" question
 * directly. Async (chunked scan): returns Promise<SeedCellHit[]>.
 */
async function censusSettlements(): Promise<SeedCellHit[]> {
  const ref = footPlanet ? character.pos : ship.pos;
  const near = system.nearestPlanet(ref);
  if (!near) { console.warn("[settlements] census: no planet nearby"); return []; }
  const planet = near.planet;
  const R = planet.def.radius;
  const label = planet.def.name || planet.def.seed;
  const arch = planet.def.archetype;
  // Habitability carve-out: hostile (non-terran/ocean) worlds are NOT denied — the
  // TierRegistry deals WAYSTATIONS there (tier ≥ hamlet is gated out), so the scan
  // proceeds and reports them; the message just flags the world as waystations-only.
  if (arch !== "terran" && arch !== "ocean") {
    console.log(`[settlements] ${label} is ${arch} — hostile world: waystations only (no tier≥hamlet settlements).`);
  }
  const localDir = new THREE.Vector3().copy(ref).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat).normalize();
  const order = planScan(R, localDir.x, localDir.y, localDir.z);
  console.log(`[settlements] census of ${label} (${arch}) — scanning ${order.length} region cells…`);
  const list: SeedCellHit[] = [];
  await scanPlanetCells(planet, localDir.x, localDir.y, localDir.z, order, "census", (hit) => {
    console.log(
      `[settlements] census face=${hit.cell.face} x=${hit.cell.x} y=${hit.cell.y} ` +
      `seeds=${hit.seeds} maxTier=${hit.maxTier} (${TIER_NAMES[hit.maxTier]}) dist=${hit.distM.toFixed(0)} m`,
    );
    list.push(hit);
    return false; // exhaustive — never stop
  });
  // Per-tier totals: the whole-planet settlement count by tier, straight off the
  // registry deck (cheap — no per-cell materialization; census reports tiers only).
  const counts = tierRegistryFor(planet.sampler).counts;
  const total = counts.reduce((a, b) => a + b, 0);
  const byTier = TIER_NAMES.map((name, t) => `${name}=${counts[t]}`).join(" ");
  console.log(`[settlements] census tier totals: ${byTier}  (total=${total} settlements)`);
  console.log(`[settlements] census complete: ${list.length} seed-bearing cell(s) of ${order.length} scanned on ${label}`);
  return list;
}

function disembark(): void {
  const near = system.nearestPlanet(ship.pos);
  if (!near) return;
  resetPlayerDeath();
  footPlanet = near.planet;
  // No teleport: drop out of the hull right here and fly down.
  character.spawnAirborne(ship.pos, ship.quat, ship.vel, footPlanet);
  autopilot = new Autopilot(ship, footPlanet);
  footCam.zoom = 1;
  avatar.group.visible = true;
  avatar.resetCloth(); // fresh settled drape at the spawn pose (no stale-state cape explosion)
  built.group.visible = true; // the ship shows even from a cockpit view
  // Rebase the floating origin at the boots: walking-scale rendering should
  // never sit kilometers of float32 away from the origin.
  origin.copy(character.pos);
  returnMode = "foot";
  mode = "foot";
  hud.showToast("DROPPING — HOLD SPACE TO HOVER · TAP AGAIN TO GLIDE", 4200);
}

// NOTE (vehicle-tiers V0): there is deliberately no applyActiveVehicle() any more.
// ACTIVE (what V summons) and DEPLOYED (what is parked in the world) are now separate:
// summoning a Banshee and then setting the scout active must not make the Banshee
// vanish. The garage push updates the model; only a summon touches the world.

/**
 * Why you can't summon right now, or null if you can (v49 / vehicle-tiers V0).
 *
 * A refusal REASON rather than a boolean, because every one of these is a thing
 * the player is about to be told — silence here reads as a broken key.
 */
function summonRefusal(id: string): string | null {
  if (!footPlanet) return "SUMMON — LAND ON A PLANET FIRST";
  if (mode === "vehicle") return "ALREADY DRIVING";
  if (!character.onGround) return "SUMMON — FEET ON THE GROUND FIRST";
  // NOT INSIDE A CACHE. The instance is a room you walk; the garage stays outside
  // with the hull. (Previously this gated the auto-deploy, which otherwise dropped
  // a speeder in the middle of a dungeon with a BOARD prompt fighting the exit hold.)
  if (inCacheInstance()) return "NO ROOM IN HERE";
  if (!VEHICLES[id]) return "NO SUCH CHASSIS";
  // Before the collection push lands the model is empty; refuse rather than
  // guess. Nothing is owned implicitly any more (2026-08-10) — the old starter
  // allowance here is why SCOUT SPEEDER INBOUND fired for brand-new players.
  if (!garage.seeded) return "READING YOUR GARAGE…";
  if (!garage.owns(id)) return "NOT IN YOUR GARAGE";
  return null;
}

/** Seconds left on the call-in, or 0. The delay is what makes summoning read as an
 *  act you took rather than auto-deploy with extra steps. */
let summonT = 0;
let summonPending: string | null = null;
const SUMMON_CALL_IN_S = 1.2;

// The drop point is FROZEN at summon time, in the planet's spinning local frame:
// the marker below shows it, the chassis lands exactly on it, and walking away
// mid-call-in doesn't drag the landing along with you.
const summonAnchorL = new THREE.Vector3();
const summonNormalL = new THREE.Vector3();
const summonForwardL = new THREE.Vector3();

/**
 * The "your ride lands HERE" marker: a subtle ground circle that FILLS as the
 * call-in completes — a thin outline ring plus an inner disc that grows from
 * the centre to the rim.
 *
 * ⚠ It is a CHILD of the planet's render group with a transform set ONCE at
 * summon time, exactly like homestead pieces and settlement lights. The first
 * version recomputed world position per frame from `pose` — the SIM transform,
 * stepped at the fixed tick — while everything around it rides the planet's
 * render-interpolated transform, so the marker jittered against the ground
 * (the onboarding-cutscene lesson: ride planet.group, never the sim pose).
 */
const summonMarker = (() => {
  const g = new THREE.Group();
  const outlineMat = new THREE.MeshBasicMaterial({
    color: 0x56d9ff, transparent: true, opacity: 0.28, side: THREE.DoubleSide, depthWrite: false,
  });
  const outline = new THREE.Mesh(new THREE.RingGeometry(1.62, 1.78, 48), outlineMat);
  outline.rotation.x = -Math.PI / 2; // flat on the ground; the group's +Y = surface normal
  const fillMat = new THREE.MeshBasicMaterial({
    color: 0x56d9ff, transparent: true, opacity: 0.14, side: THREE.DoubleSide, depthWrite: false,
  });
  const fill = new THREE.Mesh(new THREE.CircleGeometry(1.62, 48), fillMat);
  fill.rotation.x = -Math.PI / 2;
  g.add(outline, fill);
  g.visible = false;
  return { g, fill };
})();
const _smUp = new THREE.Vector3(0, 1, 0);

/** Anchor the marker at the frozen drop point, in the planet's LOCAL frame.
 *  Called once per summon; after this the render transform carries it. */
function placeSummonMarker(planet: Planet): void {
  if (summonMarker.g.parent !== planet.group) planet.group.add(summonMarker.g);
  summonMarker.g.position.copy(summonAnchorL).addScaledVector(summonNormalL, 0.08);
  summonMarker.g.quaternion.setFromUnitVectors(_smUp, summonNormalL);
  summonMarker.g.visible = true;
}

/** Render-loop: drive the fill with call-in progress; hide when nothing is inbound. */
function updateSummonMarker(): void {
  if (summonPending === null || !footPlanet) {
    summonMarker.g.visible = false;
    return;
  }
  const progress = 1 - Math.max(0, Math.min(1, summonT / SUMMON_CALL_IN_S));
  // scale(0) makes a degenerate matrix; floor just above zero.
  summonMarker.fill.scale.setScalar(Math.max(0.02, progress));
  summonMarker.g.visible = true;
}

/**
 * Call a chassis down beside the player. ONE OUT AT A TIME: this despawns whatever is
 * currently parked, WoW-mount style — a field of abandoned speeders is not a feature.
 *
 * Nothing deploys on its own any more. Auto-deploy meant every player rode the 46 m/s
 * starter scout forever and never saw the rest of the garage.
 */
function summonVehicle(id: string): void {
  const refusal = summonRefusal(id);
  if (refusal) {
    hud.showToast(refusal, 2200);
    return;
  }
  if (summonPending === id) return; // already inbound
  // Pick the drop point NOW and freeze it in the planet's spinning frame — the
  // marker shows this exact spot and the chassis lands on it, whether or not
  // the player wanders during the call-in.
  if (!footPlanet) return;
  const ground = vehicleGround ?? new PlanetVehicleGround(footPlanet);
  vehicleSpawnForward.copy(character.heading).addScaledVector(character.up, -character.heading.dot(character.up)).normalize();
  vehicleSpawnRight.crossVectors(vehicleSpawnForward, character.up).normalize();
  vehicleSpawnSample.point.copy(character.pos).addScaledVector(vehicleSpawnRight, 7);
  if (!ground.sample(vehicleSpawnSample.point, vehicleSpawnSample)) {
    hud.showToast("NO CLEAR GROUND BESIDE YOU", 2200);
    return;
  }
  const pose = footPlanet.pose;
  summonAnchorL.copy(vehicleSpawnSample.point).sub(pose.center).applyQuaternion(pose.invQuat);
  summonNormalL.copy(vehicleSpawnSample.normal).applyQuaternion(pose.invQuat);
  summonForwardL.copy(vehicleSpawnForward).applyQuaternion(pose.invQuat);
  summonPending = id;
  summonT = SUMMON_CALL_IN_S;
  placeSummonMarker(footPlanet);
  hud.showToast(`${VEHICLES[id].name} INBOUND`, 1800);
}

/** Land the pending summon at the FROZEN drop point. Returns false if the spot
 *  no longer samples (shouldn't happen — it sampled at summon time), in which
 *  case the call stays pending and retries next tick. */
function completeSummon(id: string): boolean {
  if (!footPlanet) return false;
  const def = VEHICLES[id];
  if (!def) return false;
  if (groundVehicle.def.id !== def.id) {
    groundVehicleView.dispose();
    groundVehicle = new GroundVehicle(def);
    groundVehicleView = createVehicleView(def);
    scene.add(groundVehicleView.group);
  }
  vehicleGround = new PlanetVehicleGround(footPlanet);
  const pose = footPlanet.pose;
  vehicleSpawnSample.point.copy(summonAnchorL).applyQuaternion(pose.quat).add(pose.center);
  if (!vehicleGround.sample(vehicleSpawnSample.point, vehicleSpawnSample)) return false;
  // Forward re-orthogonalized against the freshly-sampled normal.
  vehicleSpawnForward.copy(summonForwardL).applyQuaternion(pose.quat);
  vehicleSpawnForward
    .addScaledVector(vehicleSpawnSample.normal, -vehicleSpawnForward.dot(vehicleSpawnSample.normal))
    .normalize();
  groundVehicle.spawn(
    vehicleSpawnSample.point.addScaledVector(vehicleSpawnSample.normal, vehicleSpawnClearance(groundVehicle.def)),
    vehicleSpawnForward,
    vehicleSpawnSample.normal,
  );
  vehicleSpawned = true;
  groundVehicleView.group.visible = true;
  hud.showToast(`${def.name} — HOLD F TO BOARD`, 3200);
  return true;
}

/** Send the parked chassis away. Refuses while it is being ridden. */
function dismissVehicle(): void {
  if (!vehicleSpawned) return;
  if (groundVehicle.occupied || mode === "vehicle") {
    hud.showToast("STEP OFF FIRST", 1800);
    return;
  }
  vehicleSpawned = false;
  groundVehicleView.group.visible = false;
  hud.showToast(`${groundVehicle.def.name} STOWED`, 1600);
}

/** V tap: summon the active chassis, or stow the one already parked. No implicit
 *  starter fallback — an empty garage refuses with the reason, it never guesses. */
function toggleSummonActive(): void {
  const id = garage.active ?? garage.vehicles[0] ?? null;
  if (id !== null && vehicleSpawned && groundVehicle.def.id === id && !groundVehicle.occupied) {
    dismissVehicle();
    return;
  }
  if (id === null) {
    hud.showToast(garage.seeded ? "NO VEHICLE REGISTERED — CLAIM A KEYCARD" : "READING YOUR GARAGE…", 2600);
    return;
  }
  summonVehicle(id);
}

/**
 * Teach the summon once per session, the first time the player stands on ground with a
 * garage to summon from.
 *
 * Removing auto-deploy takes away something players used to get for free, and an absent
 * vehicle with no explanation reads as a bug rather than a change.
 */
let summonTaught = false;
function maybePromptSummon(): void {
  if (summonTaught || vehicleSpawned || summonPending !== null) return;
  if (!footPlanet || !character.onGround || inCacheInstance()) return;
  if (!garage.seeded || garage.vehicles.length === 0) return;
  summonTaught = true;
  hud.showToast("V — SUMMON YOUR VEHICLE", 4200);
}

// --- The ship summon (vehicle-tiers V1): hold V, 5-minute cooldown -----------
/** Seconds V must be held before the SHIP summon fires (a tap is the vehicle). */
const SHIP_SUMMON_HOLD_S = 0.6;
const SHIP_SUMMON_COOLDOWN_MS = 5 * 60 * 1000;
let keyVHeld = 0;
let keyVShipFired = false;
/** performance.now() after which the next ship summon is allowed. Starts ready:
 *  the cooldown prices USE, not login. Session-local for now, per the spec. */
let shipSummonReadyAt = 0;

/**
 * Call the ship over: the callback sweep it already flies, terminating in a
 * LANDING beside the player rather than a hover. The cooldown is the whole
 * economy here — with a free recall the grounding point means nothing.
 */
function summonShip(): void {
  if (!shipOwned) {
    hud.showToast("NO SHIP REGISTERED — EARN ONE AND BOARD IT IN THE SHIPYARD", 2800);
    return;
  }
  if (!footPlanet || !autopilot) return; // not on foot = you're already in it
  if (autopilot.phase === "landed" && character.pos.distanceTo(ship.pos) < 60) {
    hud.showToast("YOUR SHIP IS RIGHT THERE", 1800);
    return;
  }
  if (autopilot.phase === "callback") {
    hud.showToast("SHIP ALREADY INBOUND", 1800);
    return;
  }
  const now = performance.now();
  if (now < shipSummonReadyAt) {
    const left = Math.ceil((shipSummonReadyAt - now) / 1000);
    hud.showToast(`SHIP SUMMON READY IN ${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`, 2200);
    return;
  }
  shipSummonReadyAt = now + SHIP_SUMMON_COOLDOWN_MS;
  autopilot.call();
  audio.sfx("ui.select");
  hud.showToast("SHIP INBOUND — IT WILL SET DOWN NEARBY", 2600);
}

// --- Face-to-face player interaction (v49) -----------------------------------
const _lookDir = new THREE.Vector3();
const _lookTo = new THREE.Vector3();
/** Scratch for the ladder's per-tick looked-at result. */
let lookPilot: { id: number; name: string } | null = null;

/**
 * The pilot the camera is looking at, within `maxDist` metres — the aim-cone
 * shape every look-to-interact affordance here uses. Remote positions are the
 * newest streamed state (system-space, like character.pos).
 */
function lookedAtPlayer(maxDist = 30): { id: number; name: string } | null {
  if (!net) return null;
  let best: { id: number; name: string } | null = null;
  let bestDot = 0.986; // ~9.6° half-angle
  _lookDir.set(0, 0, -1).applyQuaternion(camera.quaternion);
  for (const [id, r] of net.remotes) {
    _lookTo.set(r.netPos[0], r.netPos[1], r.netPos[2]).sub(character.pos);
    const d = _lookTo.length();
    if (d < 1.2 || d > maxDist) continue;
    const a = _lookTo.divideScalar(d).dot(_lookDir);
    if (a > bestDot) {
      bestDot = a;
      best = { id, name: r.name };
    }
  }
  return best;
}

const playerPanel = new PlayerPanel({
  onTrade: (t) => {
    // The venue rule is the server's (in person OR both docked) — just ask.
    net?.sendTrade({ op: "invite", target: t.id });
    hud.showToast(`TRADE INVITE — ${t.name.toUpperCase()}`, 2000);
  },
  onWhisper: (t) => chatPanel?.prefillWhisper(t.name),
  onPartyInvite: (t) => {
    if (partyModel) net?.sendParty(partyModel.op({ op: "invite", player: t.id }));
    else hud.showToast("PARTIES ARE OFFLINE", 1800);
  },
  onClose: () => {
    pauseSuppressUntil = performance.now() + 900;
    if ((mode === "fly" || mode === "foot" || mode === "vehicle") && launched) input.requestPointerLock();
  },
});

/** Advance a pending call-in. Called from the on-foot tick. */
function updateSummon(dt: number): void {
  if (summonPending === null) return;
  summonT -= dt;
  if (summonT > 0) return;
  const id = summonPending;
  // A bad drop point (mid-air over a cliff edge, unsampled ground) keeps the call
  // pending and retries rather than silently eating the summon.
  if (completeSummon(id)) summonPending = null;
  else summonT = 0.25;
}

function replaceGroundVehicle(
  def: import("@hyperlight/shared").VehicleDef,
  position: THREE.Vector3,
  forward: THREE.Vector3,
  up: THREE.Vector3,
  occupied: boolean,
  makeView?: () => VehicleView,
): void {
  vehicleMountTransition = null;
  vehicleMountPlayback = null;
  groundVehicleView.dispose();
  groundVehicle = new GroundVehicle(def);
  groundVehicleView = makeView ? makeView() : createVehicleView(def);
  scene.add(groundVehicleView.group);
  groundVehicle.spawn(position, forward, up);
  if (occupied) groundVehicle.occupySeat();
  groundVehicleView.group.visible = true;
  vehicleSpawned = true;
}

function spawnGodVehicle(
  def: import("@hyperlight/shared").VehicleDef,
  makeView?: () => VehicleView,
): void {
  if (!footPlanet) {
    hud.showToast("VEHICLE SPAWN — LAND ON A PLANET FIRST", 2600);
    resumeFlight();
    return;
  }
  vehicleGround = new PlanetVehicleGround(footPlanet);
  const wasDriving = groundVehicle.occupied;
  if (wasDriving) {
    vehicleSpawnForward.set(0, 0, -1).applyQuaternion(groundVehicle.quat);
    replaceGroundVehicle(def, groundVehicle.pos.clone(), vehicleSpawnForward, groundVehicle.up, true, makeView);
    returnMode = "vehicle";
  } else {
    vehicleSpawnForward.copy(character.heading).addScaledVector(character.up, -character.heading.dot(character.up)).normalize();
    vehicleSpawnSample.point.copy(character.pos).addScaledVector(vehicleSpawnForward, 8);
    if (!vehicleGround.sample(vehicleSpawnSample.point, vehicleSpawnSample)) {
      hud.showToast("NO VALID VEHICLE GROUND", 2200);
      resumeFlight();
      return;
    }
    replaceGroundVehicle(
      def,
      vehicleSpawnSample.point.addScaledVector(vehicleSpawnSample.normal, vehicleSpawnClearance(def)),
      vehicleSpawnForward,
      vehicleSpawnSample.normal,
      false,
      makeView,
    );
  }
  resumeFlight();
  hud.showToast(`${def.name} SPAWNED`, 2200);
}

function boardGroundVehicle(): void {
  if (!vehicleSpawned || !footPlanet || character.pos.distanceTo(groundVehicle.pos) > vehicleBoardRange()) return;
  vehicleGround ??= new PlanetVehicleGround(footPlanet);
  const candidate = groundVehicle.def.seats.find((seat) => seat.role === "driver");
  if (!candidate) return;
  const seat = groundVehicle.reserveSeat(candidate.id);
  if (!seat) {
    hud.showToast("NO DRIVER SEAT AVAILABLE", 2200);
    return;
  }
  // Enter the chase camera on the BIKE'S centreline regardless of approach
  // direction; inheriting the character heading can start the boom sideways.
  groundVehicle.alignViewToChassis();
  groundVehicle.snapVisual();
  // Derive left/right only for the leg-kick variant. Entry itself is valid from
  // every direction and begins exactly at the visible character root.
  vehicleMountEntry.copy(character.pos).sub(groundVehicle.pos)
    .applyQuaternion(vehicleMountRootQuat.copy(groundVehicle.quat).invert());
  const mountSide: -1 | 1 = vehicleMountEntry.x > 0 ? 1 : -1;
  const transition = seat.transition;
  vehicleMountPlayback = loadedVehicleMountAnimations.get(groundVehicle.def.id) ?? null;
  vehicleMountTransition = {
    phase: "mounting",
    side: mountSide,
    elapsed: 0,
    duration: vehicleMountPlayback?.asset.duration ?? transition?.mountDuration ?? 0.82,
    approachFraction: transition?.approachFraction ?? 0.35,
    arcHeight: transition?.arcHeight ?? 0.35,
    // Capture the pose the player actually SAW last render, not the fixed-step
    // character pose fractionally ahead of it; otherwise frame one can pop.
    startPosition: avatar.group.position.clone().add(origin),
    startRotation: avatar.group.quaternion.clone(),
  };
  captureCharacterMountAnchor();
  avatar.group.visible = true;
  avatar.setMountedBlend(0, seat.riderPose);
  mode = "vehicle";
  returnMode = "vehicle";
  // Camera ownership changes on the first animation frame, independently of
  // seat occupancy/control ownership. Never let the foot rig chase a body that
  // is now moving in vehicle-local space (it can solve below terrain).
  cameraHandoff = null;
  groundVehicleCamera.snap();
  hud.setInteract(null);
}

/** The seat the F key acts on, whether or not occupancy has been handed over. */
function groundVehicleExitSeat(): VehicleSeatDef {
  return groundVehicle.activeSeat
    ?? groundVehicle.def.seats.find((seat) => seat.role === "driver")
    ?? groundVehicle.def.seats[0];
}

/** Ground-relative chassis speed — what decides step-off versus bail. Uses the
 * SURFACE-TANGENT speed rather than `forwardSpeed`, because a sideways drift or
 * a reverse run is exactly as committed as a forward one. */
function groundVehicleExitSpeed(): number {
  vehicleMountUp.copy(groundVehicle.vel)
    .addScaledVector(groundVehicle.up, -groundVehicle.vel.dot(groundVehicle.up));
  return vehicleMountUp.length();
}

/** Is the chassis moving fast enough that stepping off would read as a warp? */
function groundVehicleWouldBail(): boolean {
  const seat = groundVehicleExitSeat();
  return groundVehicleExitSpeed() >= vehicleBailSpeed(groundVehicle.def, seat);
}

function exitGroundVehicle(): void {
  if (mode !== "vehicle" || !footPlanet || vehicleMountTransition) return;
  const seat = groundVehicleExitSeat();
  const transition = seat.transition;
  // TWO exits, chosen by how fast the chassis is actually travelling. A step-off
  // at 20 m/s reads as the rider being warped to the ground beside a moving
  // vehicle — at speed the only honest exit is to throw yourself clear.
  const bailing = groundVehicleWouldBail();
  // An authored exit if the chassis has one, otherwise the procedural arc. Its
  // duration wins, exactly as the mount's does, so the clip is never cut short.
  vehicleMountPlayback = (bailing ? loadedVehicleBailAnimations : loadedVehicleDismountAnimations)
    .get(groundVehicle.def.id)
    // A chassis with no authored bail still bails — on its step-off clip, which
    // looks wrong but keeps the airborne handoff (and the physics) correct.
    ?? loadedVehicleDismountAnimations.get(groundVehicle.def.id)
    ?? null;
  // Body and camera share chassis-forward through the entire dismount. Without
  // this, camera-directed steering can leave the chase rig orbiting sideways
  // while the animated body remains vehicle-forward.
  groundVehicle.alignViewToChassis();
  groundVehicleCamera.snap();
  const impulse = vehicleBailImpulse(seat);
  vehicleMountTransition = {
    phase: "dismounting",
    side: Math.sign((seat.exit[0])) >= 0 ? 1 : -1,
    elapsed: 0,
    duration: bailing
      ? vehicleMountPlayback?.asset.duration ?? transition?.bailDuration ?? 0.44
      : vehicleMountPlayback?.asset.duration ?? transition?.dismountDuration ?? 0.42,
    approachFraction: transition?.approachFraction ?? 0.35,
    arcHeight: transition?.arcHeight ?? 0.35,
    startPosition: groundVehicle.pos.clone(),
    startRotation: groundVehicle.quat.clone(),
    ...(bailing
      ? { bail: { lift: impulse.lift, push: impulse.push, gravity: vehicleExitGravity() } }
      : {}),
  };
  if (bailing) {
    audio.sfx("jump", { at: groundVehicle.pos });
    hud.showToast("BAILING OUT", 1200);
  }
  hud.setInteract(null);
}

/** Local-up gravity for the bail's separation arc. The chassis' own authored
 * figure, not the character controller's: the arc is measured RELATIVE to the
 * chassis, and reusing its number keeps rider and vehicle in one physics. */
function vehicleExitGravity(): number {
  return groundVehicle.def.handling.gravity;
}

/** Complete the gameplay handoff at the exit animation's detach marker. */
function completeGroundVehicleExit(): void {
  if (!footPlanet) return;
  const transition = vehicleMountTransition;
  const bail = transition?.bail ?? null;
  const seat = groundVehicle.activeSeat
    ?? groundVehicle.def.seats.find((candidate) => candidate.role === "driver")
    ?? groundVehicle.def.seats[0];
  if (bail && transition) {
    // Hand over the EXACT point and velocity the animated body is flying on, so
    // the character controller continues one trajectory instead of starting a
    // new one. Both are chassis-relative for the same reason the arc is (see
    // mountTransition): a world-space capture drifts with the planet's carry.
    vehicleSeatPose(seat, groundVehicle.pos, groundVehicle.quat, vehicleMountEntry, vehicleMountRootQuat);
    vehicleBailPose(
      transition, bail, vehicleMountEntry, groundVehicle.quat,
      vehicleMountRoot, vehicleMountRootQuat, transition.elapsed,
    );
    vehicleMountEntry.copy(vehicleMountRoot);
  } else {
    // Resolve the same live chassis-relative socket used by the animation. This
    // keeps the detach point moving with a coasting vehicle until the last frame.
    vehicleSeatExitPosition(seat, groundVehicle.pos, groundVehicle.quat, vehicleMountEntry);
  }
  character.restoreAt(
    [vehicleMountEntry.x, vehicleMountEntry.y, vehicleMountEntry.z],
    [groundVehicle.quat.x, groundVehicle.quat.y, groundVehicle.quat.z, groundVehicle.quat.w],
    footPlanet,
    // The socket IS where the rider already is — never re-drop it to the surface,
    // or the handoff undoes the animation and warps them to the ground.
    { keepAltitude: true, preserveEnergy: true },
  );
  if (bail && transition) {
    // A bail keeps the full chassis momentum INCLUDING the vertical push-off —
    // the whole point is to be thrown clear and land on your own.
    vehicleBailVelocity(transition, bail, transition.elapsed, vehicleMountRoot);
    character.vel.copy(vehicleMountRoot).applyQuaternion(groundVehicle.quat).add(groundVehicle.vel);
  } else {
    // Carry ground-relative chassis momentum across detach, but discard spring
    // velocity along the surface normal so a suspension bounce cannot launch or
    // bury the newly grounded character.
    character.vel.copy(groundVehicle.vel)
      .addScaledVector(character.up, -groundVehicle.vel.dot(character.up));
  }
  // restoreAt derives facing + heading together from the chassis quaternion.
  // Do not replace that body-aligned heading with a camera-directed view.
  groundVehicle.vacateSeat();
  vehicleMountTransition = null;
  vehicleMountPlayback = null;
  // Carry the source camera with the newly detached rider while blending to
  // the foot rig. A frozen source makes a moving player appear to swing away
  // before the camera catches up.
  beginCameraHandoff(origin, character.pos, bail ? 0.42 : 0.28);
  avatar.setMounted(false);
  avatar.group.visible = true;
  avatar.resetCloth();
  mode = "foot";
  returnMode = "foot";
  footCam.zoom = 1;
  input.requestPointerLock();
  hud.showToast(bail ? "CLEAR — BRACE FOR THE LANDING" : "DISEMBARKED", 1600);
}

/** Capture the actual visible character root in chassis space. The anchor then
 * travels with a vehicle that begins moving before the rider reaches the seat. */
function captureCharacterMountAnchor(): void {
  vehicleMountEntry.copy(avatar.group.position).add(origin);
  vehicleMountStartLocal.copy(vehicleMountEntry).sub(vehicleVisPos)
    .applyQuaternion(vehicleMountRootQuat.copy(vehicleVisQuat).invert());
}

/** Resolve the captured mount root against the chassis' interpolated pose. */
function vehicleSeatEntryPosition(out: THREE.Vector3): THREE.Vector3 {
  return out.copy(vehicleMountStartLocal).applyQuaternion(vehicleVisQuat).add(vehicleVisPos);
}

/** How far the dismount endpoint may be settled DOWN onto the terrain — a step
 *  off a chassis sitting at its ride height, no more.
 *
 *  DERIVED, not a constant. A flat 1.2 m was under the scout's own hover height
 *  (0.75 m probe clearance + 0.35 m probe inset = 1.10 m at the socket) as soon
 *  as the ground fell away even slightly across the 2.2 m exit offset — so the
 *  settle was skipped on ordinary sloping terrain and a deliberate step-off
 *  detached in mid-air and finished as a short fall, replacing the authored plant
 *  with the generic landing. The allowance is the chassis' powered ride height
 *  plus one real step, which still refuses an actual altitude drop (a roof-parked
 *  or airborne chassis) and lets the ordinary fall own that instead. */
function vehicleExitStepDown(def: import("@hyperlight/shared").VehicleDef): number {
  let ride = 0;
  for (const probe of def.probes) ride = Math.max(ride, probe.rideHeight - probe.position[1]);
  return ride + 0.6;
}

/** Resolve a dismount socket against a live chassis pose and settle its endpoint
 * onto the current terrain — but only within one step.
 *
 * This runs the dismount ANIMATION's endpoint as well as the gameplay handoff,
 * so an unconditional settle warped the rider (visually and for real) from a
 * hovering, airborne or roof-parked chassis straight down to the terrain
 * radius. Below-ground sockets still get pushed out; anything higher keeps the
 * chassis' own altitude and lets the ordinary fall/landing take it from there
 * (Character.clampToGround, which unlike the terrain-only vehicle sampler knows
 * about roofs, homestead floors and boulders). */
function vehicleSeatExitPosition(
  seat: VehicleSeatDef,
  position: THREE.Vector3,
  rotation: THREE.Quaternion,
  out: THREE.Vector3,
): THREE.Vector3 {
  out.set(...seat.exit).applyQuaternion(rotation).add(position);
  if (vehicleGround?.sample(out, vehicleSpawnSample)) {
    const centre = vehicleGround.center;
    const drop = out.distanceTo(centre) - vehicleSpawnSample.point.distanceTo(centre);
    if (drop <= vehicleExitStepDown(groundVehicle.def)) {
      out.copy(vehicleSpawnSample.point).addScaledVector(vehicleSpawnSample.normal, 0.025);
    }
  }
  return out;
}

/** Fire the authored markers a forward-moving playhead just crossed.
 *
 * Both directions are dispatched. The exit used to complete on DURATION alone,
 * which made everything an exit clip authored after its detach marker dead
 * animation — and reaching the marker is the point: it is where the body is
 * standing (or clear of the chassis) and the player should have control back.
 * Returns true when the marker ENDED the transition, so the caller stops
 * touching state that no longer exists. */
function dispatchVehicleMountEvents(previousTime: number, currentTime: number): boolean {
  const playback = vehicleMountPlayback;
  const phase = vehicleMountTransition?.phase;
  if (!playback || !phase) return false;
  for (const event of humanoidAnimationEventsBetween(playback.asset, previousTime, currentTime)) {
    // Control and camera ownership intentionally begin on interaction press.
    // Occupancy alone waits for the authored visual contact marker.
    if (phase === "mounting" && event.type === "attach-seat" && !groundVehicle.occupied) {
      groundVehicle.occupyReservedSeat();
    }
    if (phase === "dismounting" && event.type === "detach-seat") {
      completeGroundVehicleExit();
      return true;
    }
  }
  return false;
}

function syncAvatarVehicleMountClip(playback: LoadedHumanoidAnimation | null): void {
  if (installedVehicleMountClip === playback) return;
  avatar.setAuthoringClip(playback?.clip ?? null, false);
  installedVehicleMountClip = playback;
}

/** DEV: toggle the floating-cape cloth test bed. Parents a cape-only Avatar under the foot
 *  planet's group so it rides the spin like the terrain, then anchors it a few metres in front
 *  of the camera (once, or every frame if `follow`). The real ClothSim runs it in the real
 *  co-rotating frame — so if THIS cape jitters, the jitter is in the frame/spin handling, not
 *  the player's animation/physics. See the per-frame updateDevCloak() in the render loop. */
function toggleDevCloak(follow: boolean): void {
  if (devCloak) {
    footPlanet?.group.remove(devCloak.avatar.group);
    devCloak.avatar.dispose(scene);
    devCloak = null;
    return;
  }
  if (!footPlanet) return;
  const a = new Avatar(scene);
  a.setStyle({ ...playerStyle, head: "none",  arms: "none",  torso: "drifter", legs: "none",  feet: "none" });
  scene.remove(a.group); // reparent under the planet so it rides the spin like the terrain
  footPlanet.group.add(a.group);
  devCloak = { avatar: a, follow };
  placeDevCloak();
}

const _dcFwd = new THREE.Vector3();
const _dcUp = new THREE.Vector3();
const _dcTarget = new THREE.Vector3();
/** Anchor the dev cape a few metres in front of the camera, converted to planet-local (it is a
 *  child of footPlanet.group) so it sits fixed on the spinning surface. */
function placeDevCloak(): void {
  if (!devCloak || !footPlanet) return;
  _dcFwd.set(0, 0, -1).applyQuaternion(camera.quaternion);
  _dcUp.copy(camera.position).add(origin).sub(footPlanet.pose.center).normalize();
  // world target: in front of the camera, a touch up so it floats clear of the ground
  _dcTarget.copy(camera.position).add(origin).addScaledVector(_dcFwd, 4).addScaledVector(_dcUp, -0.4);
  // → planet-local (undo center + spin), then orient +Y radially out
  _dcTarget.sub(footPlanet.pose.center).applyQuaternion(footPlanet.pose.invQuat);
  const g = devCloak.avatar.group;
  g.position.copy(_dcTarget);
  g.quaternion.setFromUnitVectors(_UP_Y, _dcUp.copy(_dcTarget).normalize());
}
const _UP_Y = new THREE.Vector3(0, 1, 0);

function boardShip(): void {
  if (!shipOwned) return; // no hull exists to board (and no prompt offered one)
  resetPlayerDeath();
  groundVehicle.vacateSeat();
  avatar.setMounted(false);
  footPlanet = null;
  autopilot = null;
  avatar.group.visible = false;
  ship.vel.set(0, 0, 0);
  ship.speed = 0;
  ship.throttle = 0;
  ship.pulse = false;
  ship.snapVisual();
  returnMode = "fly";
  mode = "fly";
  // SNAP the chase camera's smoothing state to the ship's pose. It still
  // holds whatever it had at disembark; easing from there made the camera
  // whip and flip through the boarding cut.
  visPos.copy(ship.pos);
  visQuat.copy(ship.quat);
  smoothPos.copy(ship.pos);
  smoothQuat.copy(ship.quat);
  upQuat.copy(ship.quat);
  hud.showToast("ABOARD — THROTTLE UP WHEN READY", 2600);
}

// --- Action menu (hold C) + galactic chart ---
const navChart = new NavChart({
  onSelect: (target) => {
    setNavTarget(target);
    input.requestPointerLock();
  },
  onClose: () => input.requestPointerLock(),
});

function openNavigation(): void {
    const near = system.nearestPlanet(ship.pos);
    const nearOk = near && near.surfaceDist < near.planet.def.radius * 3;
    // C4 travel affordance: JUMP HOME from lattice space (authed pilots), or
    // RETURN to the last lattice system from inside the home pocket.
    const uid = currentUid();
    const atHome = homeOwner(SYSTEM_ID) !== null;
    let homeJump: { label: string; target: NavTarget } | null = null;
    if (atHome) {
      const backId = lastLattice ?? BASE_SYSTEM;
      const backRef = parseSystemRef(backId);
      // "Somewhere on the lattice": home pockets and cache instances (non-
      // lattice instance ids — renderer lands in Phase 4) have no coords to
      // return to, so neither is offered as the way back.
      if (backRef !== null && backRef.kind !== "home" && backRef.kind !== "cache") {
        const backDef = systemDefFor(backId) ?? sealedSystemStub(...backRef.c);
        homeJump = {
          label: `⮐ RETURN — ${backDef.name.toUpperCase()}`,
          target: {
            sx: backRef.c[0], sy: backRef.c[1], sz: backRef.c[2], name: backDef.name,
            ...(backRef.kind === "sealed" ? { id: backId } : {}),
          },
        };
      }
    } else if (uid !== null && activeCharacterRecord) {
      homeJump = {
        label: "⌂ JUMP HOME",
        // The ACTIVE character's own home pocket (`home:<uid>.<cid>`).
        target: { sx: 0, sy: 0, sz: 0, name: "HOME", id: activeCharacterRecord.hometown.systemId },
      };
    }
    navChart.open(
      SYSTEM_COORDS,
      {
        known: new Set(discovered.keys()),
        base: BASE_SYSTEM,
        homeWorldName: homeWorldName(),
        nearestWorld: nearOk ? near.planet.def.name : null,
        onSetHome: () => {
          if (!near) return;
          const idx = system.planets.indexOf(near.planet);
          homeWorld = { system: SYSTEM_ID, planet: idx };
          void saveHomeWorld(homeWorld);
          hud.showToast(`HOME WORLD SET — ${near.planet.def.name.toUpperCase()}`, 3000);
        },
      },
      {
        systemName: system.def.name,
        systemNodes: () => buildSystemNodes(),
        neighbors: system.neighbors,
        onWaypoint: (id, name) => {
          waypoint = waypoint?.id === id ? null : { id, name };
          hud.showToast(waypoint ? `WAYPOINT SET — ${name.toUpperCase()}` : "WAYPOINT CLEARED", 2200);
        },
      },
      // The grounding point (vehicle-tiers V1): the ship stays where it landed.
      // There is deliberately NO free recall from the chart any more — the
      // cooldown-gated summon (hold V) is the only way to move it to you.
      undefined,
      homeJump,
      chartPresence, // where your friends and party are + the rally flag
    );
    if (document.pointerLockElement === canvas) document.exitPointerLock();
}

const actionMenu = new ActionMenu({
  onNavigate: openNavigation,
  // Right quadrant: open the pack + fabricator. The wheel holds pointer lock,
  // so release it for the cursor (keyup won't re-lock while invPanel is up).
  onInventory: () => {
    invPanel.open();
    if (document.pointerLockElement === canvas) document.exitPointerLock();
  },
  // Left quadrant: CHARACTER — appearance, outfits and stats (openYou releases
  // the cursor itself via releaseCursor).
  onYou: () => openYou(),
  onVehicle: (category) => {
    if (category === "space") {
      if (mode !== "foot" || !footPlanet || !character.onGround || inCacheInstance()) {
        hud.showToast("SUMMON — STAND OUTSIDE ON PLANET GROUND FIRST", 2600);
        return;
      }
      summonShip();
      return;
    }
    const id = garage.selectedFor(category);
    if (!id) {
      hud.showToast(garage.seeded ? `NO ${category.toUpperCase()} VEHICLE IN YOUR GARAGE` : "READING YOUR GARAGE…", 2600);
      return;
    }
    summonVehicle(id);
  },
  // Wheel-open mouse deltas must not slingshot the camera on resume.
  onClosed: () => input.consumeMouse(),
});

/**
 * Social marks for the galactic chart: online friends who share their system,
 * party members, and the party's rally flag. The chart folds each one into the
 * star it belongs to (see navChart.ChartPresence).
 */
function chartPresence(): ChartPresence[] {
  const out: ChartPresence[] = [];
  for (const f of socialModel?.friends ?? []) {
    if (f.online && f.system) out.push({ system: f.system, label: f.name, kind: "friend" });
  }
  for (const m of partyModel?.members ?? []) {
    if (!m.me && m.online && m.system) out.push({ system: m.system, label: m.name, kind: "party" });
  }
  const rally = partyModel?.rally;
  if (rally) out.push({ system: rally.system, label: `RALLY · ${rally.by}`, kind: "rally" });
  return out;
}

/** True while a deliberate (non-pause) overlay owns the cursor. */
function uiOverlayActive(): boolean {
  return companion.transition.active || actionMenu.visible || navChart.visible || invPanel.visible || dialogPanel.visible || journalPanel.visible || questBoardPanel.visible || (buildPalette?.visible ?? false) || (claimPanel?.visible ?? false) || (chatPanel?.focused ?? false) || (socialPanel?.visible ?? false);
}

// CURSOR-UI LOCK GUARD: pointer-lock grants are async, and a request
// issued just before a cursor UI opened (panel onClose, the wheel keyup)
// can land AFTER every exit call — no timing window catches it reliably.
// Event-driven instead: any grant that arrives while a cursor UI is up is
// exited on the spot. (The action WHEEL is the one overlay that wants the
// lock held — it runs a virtual cursor.)
document.addEventListener("pointerlockchange", () => {
  if (!document.pointerLockElement) return;
  const cursorUI =
    companionMenu.visible ||
    mode === "builder" ||
    mode === "outfit" ||
    mode === "docked" ||
    mode === "menu" ||
    navChart.visible ||
    invPanel.visible ||
    dialogPanel.visible ||
    journalPanel.visible ||
    questBoardPanel.visible ||
    travelLog.visible ||
    controlsPanel.visible ||
    (chatPanel?.focused ?? false) ||
    (socialPanel?.visible ?? false);
  if (cursorUI) document.exitPointerLock();
});

// Journal (KeyJ): the quest log. Same gate the C-wheel uses (in-game, not
// while the nav chart owns the screen). Opening drops pointer lock; closing
// re-grabs it via the panel's onClose.
window.addEventListener("keydown", (e) => {
  if (e.ctrlKey || e.metaKey) return; // native browser/editor shortcuts
  if (e.code !== "KeyJ" || e.repeat) return;
  if ((mode !== "fly" && mode !== "foot" && mode !== "vehicle") || !launched || navChart.visible) return;
  if (questBoardPanel.visible) return; // the board owns F/J-space while open
  journalPanel.toggle();
  if (journalPanel.visible && document.pointerLockElement === canvas) document.exitPointerLock();
});

// Social panel (KeyO): friends/requests/blocks. Mirrors the journal's gate.
window.addEventListener("keydown", (e) => {
  if (e.ctrlKey || e.metaKey) return; // native browser/editor shortcuts
  if (!socialPanel || e.code !== "KeyO" || e.repeat) return;
  if ((mode !== "fly" && mode !== "foot" && mode !== "vehicle") || !launched || navChart.visible) return;
  if (journalPanel.visible || questBoardPanel.visible || (chatPanel?.focused ?? false)) return;
  socialPanel.toggle();
  if (socialPanel.visible && document.pointerLockElement === canvas) document.exitPointerLock();
});

// Chat (Enter) — on by default (hl-social=0 kills). Opens + focuses the input
// row; from there the field owns the keyboard (Input typing gate + the panel's
// stopPropagation) until Enter sends or Esc closes.
window.addEventListener("keydown", (e) => {
  if (e.ctrlKey || e.metaKey) return; // native browser/editor shortcuts
  if (!chatPanel || (e.code !== "Enter" && e.code !== "NumpadEnter") || e.repeat) return;
  if (chatPanel.focused) return; // the field's own handler owns Enter now
  if ((mode !== "fly" && mode !== "foot" && mode !== "vehicle") || !launched || navChart.visible) return;
  if (uiOverlayActive() || menu.visible) return;
  e.preventDefault();
  chatPanel.openInput();
});

// Land-claim key (KeyB) — homesteads on by default (opt out hl-homesteads=0). Foot only,
// standing on a planet. TAP outside any claim → plant; TAP inside your own claim
// → hint (and never double-plants a stacked beacon); HOLD ~1.5s inside your own
// claim → abandon it (the server refuses while pieces remain).
window.addEventListener("keydown", (e) => {
  if (e.ctrlKey || e.metaKey) return; // native browser/editor shortcuts
  if (!HOMESTEADS_ON || e.code !== "KeyB" || e.repeat) return;
  if (mode !== "foot" || !launched || navChart.visible) return;
  if (journalPanel.visible || questBoardPanel.visible) return;
  if (!footPlanet) { hud.showToast("STAND ON A PLANET TO CLAIM", 1800); return; }
  if (hsBeaconMode) { exitBeaconMode(); hud.showToast("BEACON PLACEMENT CANCELLED", 1200); return; }
  hsBArmed = true; // TAP opens the homestead panel on keyup; HOLD (inside your claim) abandons
});

// B keyup: a TAP inside your own claim (no abandon fired) opens the claim panel.
let hsBArmed = false;
window.addEventListener("keyup", (e) => {
  if (e.code !== "KeyB" || !hsBArmed) return;
  hsBArmed = false;
  if (abandonSentFor !== null) return; // the hold consumed this press
  if (mode !== "foot" || !launched || claimPanel?.visible || hsBeaconMode) return;
  if (activeCharacterRecord?.stage === "prologue:founding") {
    const founding = foundingBeaconHereOrCurrent();
    if (!founding) { enterBeaconMode(); return; }
    const own = ownBeaconHere();
    if (own?.id !== founding.id) {
      hsTrackedId = founding.id;
      hud.showToast("FOLLOW THE ⌂ HOMESTEAD MARKER", 2200);
      return;
    }
  }
  openClaimPanel(ownBeaconHere(), true); // panel opens ANYWHERE on foot; claim section when inside
  if (document.pointerLockElement === canvas) document.exitPointerLock();
});

// Build mode (KeyH, the hammer) — toggle inside an own/permitted claim. While
// active: [ ] cycle piece, R rotate, LMB (not aiming) place, Delete demolish aimed.
window.addEventListener("keydown", (e) => {
  if (e.ctrlKey || e.metaKey) return; // native browser/editor shortcuts
  if (!HOMESTEADS_ON || !homesteadBuild || e.code !== "KeyH" || e.repeat) return;
  if (mode !== "foot" || !launched || navChart.visible) return;
  if (journalPanel.visible || questBoardPanel.visible) return;
  if (homesteadBuild.active) {
    homesteadBuild.active = false;
    homesteadBuild.putDown();
    homesteadPieces?.setHighlight(null, null);
    audio.sfx("ui.select");
    hud.showToast("BUILD MODE OFF", 1200);
    return;
  }
  if (!footPlanet || !homesteadMarkers) return;
  const pose = footPlanet.pose;
  _hsLocal.copy(character.pos).sub(pose.center).applyQuaternion(pose.invQuat);
  const beacon = homesteadMarkers.beaconAt(footPlanet.def.id, [_hsLocal.x, _hsLocal.y, _hsLocal.z]);
  const uid = currentUid();
  const permitted = beacon !== null && uid !== null
    && (beacon.ownerUid === uid || beacon.allowlist.includes(uid));
  if (!permitted) { hud.showToast("STAND INSIDE YOUR CLAIM TO BUILD", 1800); return; }
  homesteadBuild.active = true;
  homesteadBuild.putDown(); // start EMPTY-HANDED — pick a piece via E
  audio.sfx("ui.select");
  hud.showToast("BUILD MODE — E PIECES · SCROLL ROTATE · R AXIS · T SNAP POINT · V FREE PLACE · LMB PLACE · Q EMPTY HANDS · X DEMOLISH · H EXIT", 4600);
});

// Build-mode SCROLL = rotate the ghost about the current axis (R cycles the
// axis). ONE wheel notch = EXACTLY one rotation step: a discrete click
// (deltaMode 1/2, or a pixel delta big enough to be a notch) steps once with
// NO remainder carry — carrying leftovers made alternate notches double-step.
// Trackpad micro-deltas still accumulate to a step's worth, reset on fire.
let hsWheelAcc = 0;
window.addEventListener("wheel", (e) => {
  if (!homesteadBuild?.active || mode !== "foot") { hsWheelAcc = 0; return; }
  e.preventDefault();
  const dy = e.deltaY !== 0 ? e.deltaY : e.deltaX;
  if (dy === 0) return;
  const discrete = e.deltaMode !== 0 || Math.abs(dy) >= 50;
  if (discrete) {
    homesteadBuild.rotate(dy > 0 ? 1 : -1);
    hsWheelAcc = 0;
  } else {
    hsWheelAcc += dy;
    if (Math.abs(hsWheelAcc) >= 50) {
      homesteadBuild.rotate(hsWheelAcc > 0 ? 1 : -1);
      hsWheelAcc = 0;
    }
  }
}, { passive: false });

// Driving SCROLL = dolly the chase camera, all the way in to the same
// over-the-shoulder frame right-click gives on foot. Pointer lock is the "am I
// actually driving" test — it is released whenever a panel or the C wheel owns
// the cursor, and scrolling those must not move the camera.
window.addEventListener("wheel", (e) => {
  if (mode !== "vehicle" || !launched || !document.pointerLockElement) return;
  const dy = e.deltaY !== 0 ? e.deltaY : e.deltaX;
  if (dy === 0) return;
  e.preventDefault();
  groundVehicleCamera.zoomBy(dy);
}, { passive: false });

// Fullscreen Esc ownership (YouTube-playable style): with the Keyboard Lock
// API engaged, fullscreen Chrome delivers Escape to the page as an ordinary
// key — pause menu, panel closes — instead of instantly dropping fullscreen.
// The browser's own press-AND-HOLD Esc gesture becomes the exit, complete
// with its native "press and hold ESC" toast. One
// eager call is the documented pattern: the lock lies dormant until the page
// is fullscreen and re-engages on every entry. Outside Chromium
// `navigator.keyboard` is undefined and Esc keeps its native behavior.
// While engaged it also captures the POINTER-lock escape latch, so a tapped
// Esc mid-gameplay reaches the keydown handler below with the pointer still
// locked — that path releases the cursor itself.
void (navigator as unknown as { keyboard?: { lock?: (keys?: string[]) => Promise<void> } })
  .keyboard?.lock?.(["Escape"])?.catch(() => {});

window.addEventListener("keydown", (e) => {
  if (e.ctrlKey || e.metaKey) return; // native browser/editor shortcuts
  if (e.code !== "KeyC" || e.repeat) return;
  if (actionMenu.visible) return; // keep the vehicle submenu through the next C press
  if ((mode !== "fly" && mode !== "foot") || !launched || navChart.visible) return;
  // Pointer lock stays HELD: the wheel runs a virtual cursor from center.
  actionMenu.show(mode === "foot" ? "foot" : "fly");
});
window.addEventListener("keyup", (e) => {
  if (e.code !== "KeyC" || !actionMenu.visible) return;
  // Any lock churn right after the wheel must not read as "open the pause
  // menu" (grant/deny bounces — same latch family as the Escape case).
  pauseSuppressUntil = performance.now() + 900;
  actionMenu.release(); // flick-select: fires the hovered quadrant (if any)
  // Don't re-grab the cursor if the flick opened a cursor UI (chart/inventory).
  if (!navChart.visible && !invPanel.visible && (mode === "fly" || mode === "foot" || mode === "vehicle")) {
    input.requestPointerLock();
  }
});

// ESC exits pointer lock natively; treat any unlock during flight as "menu"
// unless one of our own overlays deliberately released the cursor.
/**
 * Escape and pointer lock are natural enemies: closing the C wheel with
 * Escape re-requests lock, but the browser's escape latch may bounce it,
 * and the resulting unlock event would read as "player wants the pause
 * menu". Unlock events inside this window are ignored.
 */
let pauseSuppressUntil = 0;
/** A single Escape press fires BOTH the keydown handler AND a pointerlockchange (the native pointer
 *  unlock). Without a guard they race and the pause menu double-engages — opens twice, or opens then
 *  the same press's other event closes it. This timestamps the last Escape-driven menu open/close so
 *  the second event of the same press is ignored. */
let menuActionAt = 0;
const ESC_DEBOUNCE = 250;

document.addEventListener("pointerlockchange", () => {
  const now = performance.now();
  if (
    (mode === "fly" || mode === "foot" || mode === "vehicle") &&
    launched &&
    !uiOverlayActive() &&
    document.pointerLockElement !== canvas &&
    now > pauseSuppressUntil &&
    now - menuActionAt > ESC_DEBOUNCE
  ) {
    menuActionAt = now;
    openMenu();
  }
});
// ESC while not pointer-locked (menu open, or lock unavailable).
window.addEventListener("keydown", (e) => {
  if (e.ctrlKey || e.metaKey) return; // native browser/editor shortcuts
  if (e.code !== "Escape" || e.defaultPrevented || e.repeat) return;
  if (travelLog.visible) {
    travelLog.close(); // its onClose re-opens the ESC menu
    return;
  }
  if (controlsPanel.visible) {
    controlsPanel.close();
    return;
  }
  if (godPanel.visible) {
    godPanel.close(); // its onClose re-opens the ESC menu
    return;
  }
  if (actionMenu.visible) {
    // Escape inside the wheel = just close the wheel, never the pause menu.
    actionMenu.hide();
    pauseSuppressUntil = performance.now() + 900; // see pointerlockchange
    if (mode === "fly" || mode === "foot" || mode === "vehicle") input.requestPointerLock();
    return;
  }
  if (invPanel.visible) {
    invPanel.close(); // onClose re-locks the pointer
    // The re-lock can bounce off the browser's escape latch and fire an unlock event —
    // suppress it so closing the inventory with Esc exits the inventory ONLY, never the
    // pause menu (same guard the C-wheel uses).
    pauseSuppressUntil = performance.now() + 900;
    return;
  }
  if (dialogPanel.visible) {
    dialogPanel.close(); // onClose re-locks + suppresses the pause bounce
    return;
  }
  if (questBoardPanel.visible) {
    questBoardPanel.close(); // onClose re-locks + suppresses the pause bounce
    return;
  }
  if (journalPanel.visible) {
    journalPanel.close(); // onClose re-locks + suppresses the pause bounce
    return;
  }
  if (socialPanel?.visible) {
    socialPanel.close(); // onClose re-locks + suppresses the pause bounce
    return;
  }
  if (garagePanel.visible) {
    garagePanel.close(); // onClose re-locks + suppresses the pause bounce
    return;
  }
  if (playerPanel.visible) {
    playerPanel.close(); // onClose re-locks + suppresses the pause bounce
    return;
  }
  if (navChart.visible) {
    navChart.close();
    if (mode === "fly") input.requestPointerLock();
    return;
  }
  if (mode === "outfit") {
    resumeFlight(); // DONE — back to where the player was
    return;
  }
  if (mode === "cutscene-editor") {
    cutsceneEditor?.close();
    return;
  }
  if (mode === "docked") {
    undock();
    return;
  }
  // Debounce vs the pointerlockchange that the SAME Escape press also fires (see menuActionAt): if
  // that already opened/closed the menu a moment ago, ignore this keydown so it doesn't double-fire.
  const now = performance.now();
  if (now - menuActionAt < ESC_DEBOUNCE) return;
  // In the pause menu, ESC first steps back out of a sub-view (Audio/Video → main list).
  if (mode === "menu" && menu.backOut()) {
    menuActionAt = now;
    return;
  }
  if (mode === "menu") {
    menuActionAt = now;
    resumeFlight();
  } else if (
    (mode === "fly" || mode === "foot" || mode === "vehicle") &&
    launched &&
    !uiOverlayActive()
  ) {
    // Reaching here still pointer-LOCKED means the fullscreen keyboard lock
    // swallowed the native Esc unlock — release the cursor ourselves. (Without
    // the keyboard lock this branch races the pointerlockchange path for the
    // same press; menuActionAt + ESC_DEBOUNCE keep it single-fire either way.)
    menuActionAt = now;
    if (input.pointerLocked) document.exitPointerLock();
    openMenu();
  }
  // builder handles its own Escape (save & exit to flight)
});

// --- Intro: accounts are mandatory (verified email -> pilot name -> fly) ---
const intro = document.getElementById("intro")!;
const nameInput = document.getElementById("pilot-name") as HTMLInputElement;

// --- Title screen visuals: faint starfield + the SOUL MERIDIAN line motif ---
const introStars = new IntroStars();
const introLine = new MeridianLine({ splitFrac: 0.58 });
intro.insertBefore(introLine.el, intro.firstChild);
intro.insertBefore(introStars.el, intro.firstChild);
introStars.start();
introLine.start();
// Loading reuses this same line (see LoadScreen): logging in doesn't re-render it.
loadScreen.attach(intro, introLine, document.getElementById("load-status")!);

/** Retrigger the line's left-to-right flare-in wipe. */
function flareLine(): void {
  introLine.el.classList.remove("flare");
  void introLine.el.offsetWidth; // reflow so the CSS animation restarts
  introLine.el.classList.add("flare");
}

/** The title's one-time entrance (first page load only): the line flares in
 *  left-to-right, then the wordmark + buttons fade in, then the stars fade in
 *  behind them. Returning via the back button does NOT replay this — the line
 *  just stirs and the copy cross-fades back in (see exitAuthView). */
const introEnterTimers: number[] = [];
function playBrandEntrance(): void {
  introEnterTimers.forEach(clearTimeout);
  intro.classList.remove("brand-in"); // reset so the fade replays
  flareLine();
  introEnterTimers.push(
    window.setTimeout(() => intro.classList.add("brand-in"), 320), // names + buttons
    window.setTimeout(() => intro.classList.add("stars-in"), 640), // then the stars
  );
}
playBrandEntrance(); // flare synchronously at mount so there's no un-clipped flash

// Pause the title's animation loops whenever the overlay is hidden (launch),
// and resume them if it ever comes back (a failed homecoming re-shows it).
new MutationObserver(() => {
  if (intro.classList.contains("hidden")) { introStars.stop(); introLine.stop(); }
  else { introStars.start(); introLine.start(); }
}).observe(intro, { attributes: true, attributeFilter: ["class"] });

// "Press to start" reveals the sign-in fields; the line stirs on the switch.
const pressStart = document.getElementById("press-start")!;
let startPressed = false;
function enterAuthView(): void {
  if (startPressed) return;
  startPressed = true;
  void audio.unlock(); // this gesture satisfies the autoplay policy
  intro.dataset.view = "auth";
  introLine.stir(1.1);
  requestAnimationFrame(() => document.getElementById("auth-email")?.focus());
}
// "back" (top-left) returns to the wordmark and replays its entrance in reverse.
const introBack = document.getElementById("intro-back")!;
function exitAuthView(): void {
  if (!startPressed) return;
  startPressed = false;
  intro.dataset.view = "brand"; // cross-fades the auth copy out, the wordmark back in
  introLine.stir(1); // just move the fanout around — no left-to-right flare on the way back
}
introBack.addEventListener("click", (e) => { e.stopPropagation(); exitAuthView(); });

const startGateOpen = (): boolean =>
  intro.dataset.view === "brand" && !intro.classList.contains("hidden");
pressStart.addEventListener("click", enterAuthView);
intro.addEventListener("pointerdown", () => { if (startGateOpen()) enterAuthView(); });
window.addEventListener("keydown", (e) => {
  if (e.ctrlKey || e.metaKey) return; // native browser/editor shortcuts
  if (startGateOpen()) { enterAuthView(); return; }
  if (e.key === "Escape" && intro.dataset.view === "auth" && !intro.classList.contains("hidden")) exitAuthView();
});

let launched = false;
let authUser: AuthUser | null = null;
let activeCharacterRecord: CharacterRecordV1 | null = null;
let needsHometownSpawn = false;
/** Saved state to resume at, loaded from the profile. */
let resume: {
  pos: [number, number, number];
  quat: [number, number, number, number];
  posBody: string | null;
  foot: {
    body: string | null;
    p: [number, number, number];
    q: [number, number, number, number];
  } | null;
  /** Landed-ship anchor (V1 grounding point), spin-locked local frame. */
  shipLand: { body: string; p: [number, number, number] } | null;
} | null = null;

const ui = {
  status: document.getElementById("auth-status")!,
  statusText: document.getElementById("auth-status-text")!,
  retry: document.getElementById("auth-retry") as HTMLButtonElement,
  forms: document.getElementById("auth-forms")!,
  actions: document.getElementById("auth-actions")!,
  verify: document.getElementById("auth-verify")!,
  verifyEmail: document.getElementById("verify-email")!,
  signedIn: document.getElementById("auth-signed-in")!,
  who: document.getElementById("auth-who")!,
  error: document.getElementById("auth-error")!,
  launchRow: document.getElementById("launch-row")!,
  chars: document.getElementById("char-select")!,
  configMissing: document.getElementById("config-missing")!,
};

type AuthScreenState = "checking" | "forms" | "verify" | "restoring" | "chars" | "ready" | "error" | "none";

let lastAuthState: AuthScreenState | null = null;
function showAuthState(state: AuthScreenState, message?: string): void {
  const statusVisible = state === "checking" || state === "restoring" || state === "error";
  ui.status.style.display = statusVisible ? "flex" : "none";
  ui.statusText.textContent = message ?? (state === "restoring" ? "loading pilot…" : "restoring session…");
  ui.retry.style.display = state === "error" ? "inline" : "none";
  ui.forms.style.display = state === "forms" ? "block" : "none";
  ui.actions.style.display = state === "forms" ? "flex" : "none";
  ui.verify.style.display = state === "verify" ? "block" : "none";
  ui.signedIn.style.display = state === "ready" || state === "chars"
    || (state === "error" && authUser !== null) ? "flex" : "none";
  // flex, not block: the roster's column layout + gap come from the stylesheet,
  // and an inline `block` would silently override them.
  ui.chars.style.display = state === "chars" ? "flex" : "none";
  ui.launchRow.style.display = state === "ready" ? "block" : "none";
  // The unravelled strands react whenever the screen's state changes.
  if (state !== lastAuthState) {
    lastAuthState = state;
    if (startPressed) introLine.stir(0.7);
  }
}

/** Resolve once clips are loaded AND the spawn-area terrain is hole-free (8 s
 *  terrain cap). Drives the load-screen bar. The frame loop is already meshing
 *  terrain and the avatar already started clip loading, so this only observes. */
function assetsReady(): Promise<void> {
  const startT = performance.now();
  void getClipsAndReports(); // ensure loading has begun (idempotent; cached)
  // Restart the terrain sustained-idle window from HERE: idleFrames otherwise
  // carries the title-screen orbit's long-settled count, so the very first poll
  // could read "ready" before the spawn's meshing wave starts (trees pop in).
  system.resetTerrainIdle();
  return new Promise<void>((resolve) => {
    function poll(): void {
      const { loaded, total } = getClipProgress();
      const focus = footPlanet ? character.pos : ship.pos;
      // Surface spawns also wait for the exact settlement-carve neighbourhood
      // used by collision. General mesh idleness alone can occur while its
      // IndexedDB/worker region batch is still in flight.
      const spawnSurfaceOk = !footPlanet || footPlanet.spawnSurfaceReady(focus);
      const terrainOk = spawnSurfaceOk && system.terrainSettled(focus);
      const st = computeLoaderState({
        animLoaded: loaded,
        animTotal: total,
        terrainOk,
        elapsedMs: performance.now() - startT,
      });
      loadScreen.setProgress(st.frac, st.label);
      // The generic terrain mesh has an eight-second best-effort cap, but a
      // surface spawn's collision/carve data is correctness-critical: never
      // reveal an on-foot player against a provisional surface.
      if (st.done && spawnSurfaceOk) resolve();
      else requestAnimationFrame(poll);
    }
    requestAnimationFrame(poll);
  });
}

let onboardingStarting = false;
const onboardingLocal = new THREE.Vector3();
const onboardingWorld = new THREE.Vector3();
const onboardingUpL = new THREE.Vector3();
const onboardingForwardL = new THREE.Vector3();
const onboardingRightL = new THREE.Vector3();
const onboardingUpW = new THREE.Vector3();
const onboardingForwardW = new THREE.Vector3();
const onboardingRightW = new THREE.Vector3();
const onboardingTarget = new THREE.Vector3();
const onboardingQ = new THREE.Quaternion();

/** Core on-foot placement at a planet-local anchor. The starter hull parks
 *  beside the player unless `shipLocal` names a planet-local park instead
 *  (the prologue leaves it waiting at the capital gates — the walk is real). */
function placeAtLocalAnchor(
  systemId: string,
  planetIndex: number,
  localPosition: readonly [number, number, number],
  localFacing: readonly [number, number, number],
  shipLocal: readonly [number, number, number] | null = null,
): Planet {
  if (SYSTEM_ID !== systemId) switchSystem(systemId);
  const planet = system.planets[planetIndex];
  onboardingLocal.set(...localPosition);
  onboardingUpL.copy(onboardingLocal).normalize();
  onboardingForwardL.set(...localFacing)
    .addScaledVector(onboardingUpL, -onboardingForwardL.dot(onboardingUpL))
    .normalize();
  onboardingRightL.crossVectors(onboardingForwardL, onboardingUpL).normalize();
  onboardingWorld.copy(onboardingLocal).applyQuaternion(planet.pose.quat).add(planet.pose.center);
  onboardingUpW.copy(onboardingUpL).applyQuaternion(planet.pose.quat).normalize();
  onboardingForwardW.copy(onboardingForwardL).applyQuaternion(planet.pose.quat).normalize();
  onboardingTarget.copy(onboardingWorld).add(onboardingForwardW);
  onboardingQ.setFromRotationMatrix(new THREE.Matrix4().lookAt(
    onboardingWorld, onboardingTarget, onboardingUpW,
  ));

  footPlanet = planet;
  character.restoreAt(
    [onboardingWorld.x, onboardingWorld.y, onboardingWorld.z],
    [onboardingQ.x, onboardingQ.y, onboardingQ.z, onboardingQ.w],
    planet,
  );
  // NO DEFAULT SHIP: a shipless character gets no parked hull and no autopilot
  // — the ship materializes the first time one is boarded in the shipyard.
  if (!shipOwned) {
    autopilot = null;
    built.group.visible = false;
  } else {
    if (shipLocal) {
      // Distant park: seat above the given planet-local anchor.
      ship.pos.set(...shipLocal).applyQuaternion(planet.pose.quat).add(planet.pose.center)
        .addScaledVector(
          new THREE.Vector3(...shipLocal).normalize().applyQuaternion(planet.pose.quat), 18);
    } else {
      // Park the hull just outside the square, high enough that its autopilot
      // can take over without clipping a generated roof.
      ship.pos.copy(character.pos)
        .addScaledVector(onboardingRightW.copy(onboardingRightL).applyQuaternion(planet.pose.quat), 34)
        .addScaledVector(onboardingUpW, 18)
        .addScaledVector(onboardingForwardW, -16);
    }
    ship.quat.copy(onboardingQ);
    ship.vel.set(0, 0, 0);
    ship.speed = 0;
    ship.throttle = 0;
    ship.snapVisual();
    autopilot = new Autopilot(ship, planet);
  }
  avatar.group.visible = true;
  avatar.resetCloth();
  origin.copy(character.pos);
  returnMode = "foot";
  return planet;
}

/** Place a fresh pilot on foot at the server-selected planet-local hometown anchor. */
function placeAtHometown(record: CharacterRecordV1): Planet {
  return placeAtLocalAnchor(
    record.hometown.systemId,
    record.hometown.planetIndex,
    record.hometown.localPosition,
    record.hometown.localFacing,
  );
}

/** Place a staged pilot at the current beat's site. New characters begin at
 *  a deterministic clear founding site; old mid-crawl saves still resolve their
 *  cache approach so the compatibility path remains resumable. */
function placeAtPrologue(record: CharacterRecordV1): Planet | null {
  const uid = homeOwner(record.hometown.systemId);
  if (!uid) return null;
  const sites = resolvePrologueSites(uid, record.hometown);
  if (!sites) return null;
  if (record.stage === "prologue:founding") {
    // The old fixed gate could fall inside modern, larger town footprints.
    // Face outward from the capital so the first aimed beacon stays in the
    // shared resolver's guaranteed-clear founding margin.
    const founding = new THREE.Vector3(...sites.foundingLocalPosition);
    const up = founding.clone().normalize();
    const away = founding.clone().sub(new THREE.Vector3(...sites.capitalLocalPosition));
    away.addScaledVector(up, -away.dot(up)).normalize();
    return placeAtLocalAnchor(
      sites.systemId, sites.planetIndex, sites.foundingLocalPosition, [away.x, away.y, away.z]);
  }
  // THE APPROACH (user call 2026-07-23 — "we shouldn't just start fighting"): touch
  // down ~90 m SHORT of the cache along the facing. The garrison seeds AT the cache,
  // and spawning inside it opened the game mid-fight; now the arrival cutscene frames
  // the walk-in and the fight starts when the PLAYER closes.
  const a = new THREE.Vector3(...sites.cacheLocalPosition);
  const up = a.clone().normalize();
  const back = new THREE.Vector3(...sites.cacheLocalFacing);
  back.addScaledVector(up, -back.dot(up)).normalize();
  const r = a.length();
  a.addScaledVector(back, -PROLOGUE_APPROACH_M).normalize().multiplyScalar(r);
  return placeAtLocalAnchor(
    sites.systemId,
    sites.planetIndex,
    [a.x, a.y, a.z],
    sites.cacheLocalFacing,
    sites.gateLocalPosition, // "the bikes are gone" — your ride waits at the gates
  );
}

/** Landfall touches down this far short of the cache (m) — the walk-in beat. */
const PROLOGUE_APPROACH_M = 90;

/** SOUL kickoff — the ARRIVAL (beat 1, user call 2026-07-23): a skippable descent over
 *  the weaver cache before control is handed over. Cache-anchored rig, forward = the
 *  approach line back toward the landfall spawn, so the last frame settles behind the
 *  arriving pilot. Landfall-stage only — salvage/homeward relogs skip the replay. */
async function playLandfallArrival(record: CharacterRecordV1): Promise<void> {
  if (mode !== "foot") return;
  const uid = homeOwner(record.hometown.systemId);
  const sites = uid ? resolvePrologueSites(uid, record.hometown) : null;
  if (!sites || SYSTEM_ID !== record.hometown.systemId) return;
  const planet = system.planets[sites.planetIndex];
  const cacheL = new THREE.Vector3(...sites.cacheLocalPosition);
  const upL = cacheL.clone().normalize();
  const approach = new THREE.Vector3(...sites.cacheLocalFacing);
  approach.addScaledVector(upL, -approach.dot(upL)).normalize().negate(); // cache → spawn
  const frame = anchorCutsceneFrame(
    sites.cacheLocalPosition,
    [approach.x, approach.y, approach.z],
    planet,
  );
  hud.setPanelsVisible(false);
  mode = "cutscene";
  await cutscenePlayer.play(LANDFALL_CUTSCENE, () => frame, {
    capital: record.hometown.capitalName.toUpperCase(),
  });
  mode = "foot";
  hud.setPanelsVisible(true);
}

function hometownCutsceneFrame(record: CharacterRecordV1, planet: Planet): CutsceneFrame {
  return anchorCutsceneFrame(record.hometown.localPosition, record.hometown.localFacing, planet);
}

/** Anchor-relative cutscene rig at any planet-local point (shared by the
 *  homecoming and the SOUL reveal — same render-pose caveats). */
function anchorCutsceneFrame(
  localPosition: readonly [number, number, number],
  localFacing: readonly [number, number, number],
  planet: Planet,
): CutsceneFrame {
  const anchorL = new THREE.Vector3(...localPosition);
  const upL = anchorL.clone().normalize();
  const forwardL = new THREE.Vector3(...localFacing)
    .addScaledVector(upL, -new THREE.Vector3(...localFacing).dot(upL))
    .normalize();
  const rightL = new THREE.Vector3().crossVectors(forwardL, upL).normalize();
  const anchorW = new THREE.Vector3();
  const rightW = new THREE.Vector3();
  const upW = new THREE.Vector3();
  const forwardW = new THREE.Vector3();
  // planet.group IS the render pose: interpolated between the last two sim ticks
  // (same alpha as the avatar) and already rebased by the floating origin. The sim
  // pose (planet.pose) steps once per tick, so a camera keyed off it jitters against
  // the ground it is framing — every frame, at the planet's surface speed.
  return {
    point(local, out) {
      anchorW.copy(anchorL).applyQuaternion(planet.group.quaternion).add(planet.group.position);
      rightW.copy(rightL).applyQuaternion(planet.group.quaternion);
      upW.copy(upL).applyQuaternion(planet.group.quaternion);
      forwardW.copy(forwardL).applyQuaternion(planet.group.quaternion);
      return out.copy(anchorW)
        .addScaledVector(rightW, local[0])
        .addScaledVector(upW, local[1])
        .addScaledVector(forwardW, local[2]);
    },
    up(out) { return out.copy(upL).applyQuaternion(planet.group.quaternion); },
  };
}

/** Open the developer workbench against the real procedural hometown scene. */
async function openCutsceneEditor(): Promise<string> {
  const editor = await cutsceneEditorReady;
  if (!editor) return "Cutscene Lab is available only in development builds.";
  if (!launched || !activeCharacterRecord) return "Launch an active character first.";
  const record = activeCharacterRecord;
  if (SYSTEM_ID !== record.hometown.systemId) return "Travel home before opening Cutscene Lab.";
  const planet = placeAtHometown(record);
  const frame = hometownCutsceneFrame(record, planet);
  menu.hide();
  releaseCursor();
  hud.setPanelsVisible(false);
  mode = "cutscene-editor";
  editor.open({
    asset: HOMECOMING_CUTSCENE,
    frame: () => frame,
    tokens: {
      world: planet.def.name.toUpperCase(),
      hometown: record.hometown.settlementName.toUpperCase(),
      capital: record.hometown.capitalName.toUpperCase(),
      distance: Math.max(1, Math.round(record.hometown.distanceToCapitalM / 1000)).toString(),
    },
    onClose: () => {
      mode = "foot";
      returnMode = "foot";
      hud.setPanelsVisible(true);
      input.requestPointerLock();
    },
  });
  return "Cutscene Lab opened.";
}

/**
 * Enter the loading phase of a creation handoff, with the body DARK.
 *
 * The creator hands over with the SHARED camera still framing its mannequin at
 * the void's origin (characterCreator.open: eye 0,1.2,3.5 looking at 0,0,0, up
 * +Y), and nothing re-poses it until the cutscene/foot rig runs after the load.
 * Placement rebases the floating origin onto the body, so the real avatar lands
 * dead centre of that lens — but wearing its destination's surface orientation,
 * whose up is an arbitrary direction against the creator's +Y. `#intro` fades
 * back in over 0.8 s (style.css), which is far more than long enough to watch
 * the body lie sideways. Stay hidden until the loader owns the screen.
 */
function beginIntroLoad(): void {
  avatar.group.visible = false;
  loadScreen.show();
}

/** Close the loading phase: the loader still covers the screen for its 700 ms
 *  outro, so the reveal lands behind it. Mirrors exitVoid's visibility rule. */
function endIntroLoad(): void {
  avatar.group.visible = footPlanet !== null;
  loadScreen.hide();
}

/** Creation → loaded home world → intro timeline → active character → first quest join. */
async function runCharacterIntro(record: CharacterRecordV1): Promise<void> {
  if (onboardingStarting || launched) return;
  onboardingStarting = true;
  activeCharacterRecord = record;
  try {
    characterCreator.close();
    document.body.classList.remove("intro-active");
    document.body.classList.add("intro-background-ready");
    hud.setPanelsVisible(false);
    mode = "cutscene";
    pilotName = record.name;
    applyStyle(record.style);
    // NO DEFAULT SHIP: a just-created character owns nothing — enterCharacter
    // never ran (there is no profile yet), so the module default / a previous
    // session's ownership would otherwise leak through and fly a hull in.
    shipOwned = false;
    built.group.visible = false;
    autopilot = null;
    const planet = placeAtHometown(record);
    exitVoid();
    beginIntroLoad();
    await assetsReady();
    await avatar.styleSettled(); // dressed before the homecoming close-up
    endIntroLoad();
    const frame = hometownCutsceneFrame(record, planet);
    await cutscenePlayer.play(HOMECOMING_CUTSCENE, () => frame, {
      world: planet.def.name.toUpperCase(),
      hometown: record.hometown.settlementName.toUpperCase(),
      capital: record.hometown.capitalName.toUpperCase(),
      distance: Math.max(1, Math.round(record.hometown.distanceToCapitalM / 1000)).toString(),
    });
    const active = await activateCharacter(record.id);
    if (!active) throw new Error("character activation failed");
    activeCharacterRecord = active;
    launched = true;
    launching = false;
    mode = "foot";
    returnMode = "foot";
    hud.setPanelsVisible(true);
    startNet(active.name);
    hud.showSystemBanner(active.hometown.settlementName);
    input.requestPointerLock();
    void persistPosition().catch(() => { });
  } catch (error) {
    // Creation is already safely persisted. Return to the authenticated title
    // state so RETRY reloads that intro-gated record and resumes the homecoming.
    loadScreen.cancel(); // abort the loading phase; restore the title line
    mode = "intro";
    intro.classList.remove("hidden");
    intro.dataset.view = "auth";
    startPressed = true;
    document.body.classList.add("intro-active");
    hud.setPanelsVisible(false);
    showAuthState("error", "COULDN’T BEGIN YOUR HOMECOMING");
    throw error;
  } finally {
    onboardingStarting = false;
  }
}

/** SOUL kickoff: creation → landfall at the cache ruin → LIVE play. There is
 *  no opening cinematic — the crawl itself is the intro; the homecoming
 *  cutscene waits at the capital (docs/design/soul-kickoff.md). Graduation
 *  happens server-side when the founding resident is established, never via
 *  activateCharacter. */
async function runPrologueIntro(record: CharacterRecordV1, resumeStyle?: CharacterStyle): Promise<void> {
  if (onboardingStarting || launched) return;
  onboardingStarting = true;
  activeCharacterRecord = record;
  try {
    characterCreator.close();
    document.body.classList.remove("intro-active");
    document.body.classList.add("intro-background-ready");
    hud.setPanelsVisible(false);
    pilotName = record.name;
    // A RELOG resume passes the profile-derived look (worn armor included) —
    // stomping it with the bare creation style was why a mid-prologue
    // character's kit streamed in AFTER the reveal (user-reported).
    applyStyle(resumeStyle ?? record.style);
    // NO DEFAULT SHIP (same as runCharacterIntro): zero the ownership BEFORE
    // placeAtPrologue, whose gate-park branch would otherwise seat a hull at
    // the cache gates for a character that owns nothing.
    shipOwned = false;
    built.group.visible = false;
    autopilot = null;
    const planet = placeAtPrologue(record);
    if (!planet) throw new Error("prologue sites unavailable");
    exitVoid();
    beginIntroLoad();
    await assetsReady();
    // RE-SEAT after the load (the house teleport rule): the first placement converted the
    // local anchor to world at T0, and the planet rotated through the whole asset wait —
    // the probe measured ~500 m of drift, which put the "approach" spawn nowhere near the
    // cache. Local anchors are spin-invariant; re-placing now is exact.
    placeAtPrologue(record);
    // Connect BEFORE the reveal — this path used to startNet after endIntroLoad,
    // so the join burst (armor echo, stats, quests, god) landed on a revealed
    // world and visibly popped in. Same gate as doLaunch.
    invEchoSeen = false;
    startNet(record.name);
    await awaitJoinBurst();
    await avatar.styleSettled(); // dressed before the arrival reveal
    if (jump) {
      // FINISH THE ARRIVAL WARP DETERMINISTICALLY (probe find 2026-07-23): the title
      // launch leaves the wormhole reveal in flight, and its completion stomps
      // mode="fly" WHENEVER it lands — it wedged the arrival cutscene mid-play, and
      // before that it only lost to the foot assignment by racing it. The kickoff
      // arrives ON FOOT: close the jump and show the system now.
      jump = null;
      system.setBodiesVisible(true);
      station.setVisible(true);
      system.setArrivalZoom(1);
    }
    endIntroLoad();
    launched = true;
    launching = false;
    mode = "foot";
    returnMode = "foot";
    hud.setPanelsVisible(true);
    if (record.stage === "prologue:founding") {
      // New start or relog enters the resumable geometry-derived founding
      // prompt directly; no narrative crawl is required.
      prologueRevealPlayed = true;
      void playSoulReveal(false);
    } else {
      // THE ARRIVAL (user call 2026-07-23 — creation must not drop straight into the
      // game): landfall opens on the skippable descent over the cache, then control.
      if (record.stage === "prologue:landfall") await playLandfallArrival(record);
      hud.showSystemBanner("THE CACHE APPROACH");
    }
    input.requestPointerLock();
  } catch (error) {
    // Creation is already safely persisted. Return to the authenticated title
    // state so RETRY reloads the staged record and resumes the crawl.
    // Networking may already be up (pre-reveal connect) — tear it down, or the
    // title screen keeps a live session the retry then double-joins.
    net?.dispose();
    net = null;
    loadScreen.cancel();
    mode = "intro";
    intro.classList.remove("hidden");
    intro.dataset.view = "auth";
    startPressed = true;
    document.body.classList.add("intro-active");
    hud.setPanelsVisible(false);
    showAuthState("error", "COULDN’T BEGIN HOMESTEAD FOUNDING");
    throw error;
  } finally {
    onboardingStarting = false;
  }
}

/** Founding prompt at the capital. The cutscene branch remains for legacy
 *  characters finishing the retired walk-home stage; direct founding starts
 *  and relogs show the prompt without replaying it. */
let prologueRevealPlayed = false;
async function playSoulReveal(withCutscene: boolean): Promise<void> {
  const record = activeCharacterRecord;
  if (!record || SYSTEM_ID !== record.hometown.systemId) return;
  const uid = homeOwner(record.hometown.systemId);
  const sites = uid ? resolvePrologueSites(uid, record.hometown) : null;
  if (!sites) return;
  if (withCutscene && mode === "foot" && footPlanet) {
    // Pilot-relative rig (the capital is ALIVE — a town-centre anchor would
    // drag the camera through its massing): anchor at the arriving player's
    // feet, forward toward home, camera sweeping down the walk line behind.
    const inv = footPlanet.pose.quat.clone().invert();
    const meL = character.pos.clone().sub(footPlanet.pose.center).applyQuaternion(inv);
    const upL = meL.clone().normalize();
    const towardHome = new THREE.Vector3(...sites.capitalLocalPosition).sub(meL);
    towardHome.addScaledVector(upL, -towardHome.dot(upL)).normalize();
    const frame = anchorCutsceneFrame(
      [meL.x, meL.y, meL.z],
      [towardHome.x, towardHome.y, towardHome.z],
      footPlanet,
    );
    hud.setPanelsVisible(false);
    mode = "cutscene";
    await cutscenePlayer.play(SOUL_REVEAL_CUTSCENE, () => frame, {
      capital: record.hometown.capitalName.toUpperCase(),
    });
    mode = "foot";
    hud.setPanelsVisible(true);
    input.requestPointerLock();
  }
  hud.showSystemBanner("FOUND A HOMESTEAD — PRESS B TO PLACE YOUR BEACON");
}

/** SOUL kickoff — beat driver. The crawl quest carries progress; this maps it
 *  onto the server stage machine (strictly ordered, server-validated) and
 *  fires the finale. Rides every owner quest-state push — including the
 *  server-side completion at graduation, which is how the client learns the
 *  successful resident bootstrap settled the character. */
const PROLOGUE_LADDER = ["prologue:landfall", "prologue:salvage", "prologue:homeward", "prologue:founding"] as const;
let prologueSyncBusy = false;
async function syncPrologueBeats(): Promise<void> {
  const record = activeCharacterRecord;
  if (!record || !isPrologueStage(record.stage) || prologueSyncBusy) return;
  prologueSyncBusy = true;
  try {
    // Graduation: the founding resident bootstrap completed server-side.
    if (quests.isDone(PROLOGUE_QUEST_ID)) {
      const settled = await loadCharacter(record.id);
      if (settled) activeCharacterRecord = settled;
      hud.showSystemBanner("THE SOUL SETTLES — THIS GROUND IS YOURS");
      return;
    }
    const inst = quests.act.find((i) => i.q === PROLOGUE_QUEST_ID);
    if (!inst) return;
    // Quest stage → character stage (the crawl quest has TWO stages: the
    // fight+salvage at the cache, then the walk; the strict server ladder
    // still steps through `salvage` — the while-loop advances one at a time).
    const want = (["prologue:landfall", "prologue:homeward", "prologue:founding"] as const)[Math.min(inst.st, 2)];
    let cur = activeCharacterRecord?.stage;
    while (cur && cur !== want && isPrologueStage(cur)) {
      const next = PROLOGUE_LADDER[PROLOGUE_LADDER.indexOf(cur as (typeof PROLOGUE_LADDER)[number]) + 1];
      if (!next) break;
      const advanced = await advanceCharacterStage(record.id, next);
      if (!advanced) break;
      activeCharacterRecord = advanced;
      cur = advanced.stage;
    }
    if (cur === "prologue:founding" && !prologueRevealPlayed) {
      prologueRevealPlayed = true;
      void playSoulReveal(true);
    }
  } finally {
    prologueSyncBusy = false;
  }
}

// The walk-home beat completes by REACHING the capital: report arrival
// while close; the server re-validates against the town apron. Cheap 2 s poll,
// self-silencing outside the homeward beat.
setInterval(() => {
  const record = activeCharacterRecord;
  if (!launched || !record || record.stage !== "prologue:homeward" || !footPlanet) return;
  if (SYSTEM_ID !== record.hometown.systemId) return;
  const inst = quests.act.find((i) => i.q === PROLOGUE_QUEST_ID);
  if (!inst || inst.st !== 1) return; // stage 1 = the walk home (goto)
  const cap = record.hometown.capitalLocalPosition;
  if (!cap) return;
  const capW = new THREE.Vector3(...cap)
    .applyQuaternion(footPlanet.pose.quat)
    .add(footPlanet.pose.center);
  if (capW.distanceTo(character.pos) < 800) {
    net?.sendQuestIntent({ op: "reach", q: PROLOGUE_QUEST_ID, town: record.hometown.capitalSettlementId, seq: 0 });
  }
}, 2000);

function openCharacterCreator(prefillName = "", prefillStyle: CharacterStyle = DEFAULT_STYLE): void {
  if (characterCreator.active || onboardingStarting) return;
  intro.classList.add("hidden");
  document.body.classList.remove("intro-active");
  document.body.classList.add("intro-background-ready");
  hud.setPanelsVisible(false);
  mode = "creator";
  enterVoid(false, true);
  releaseCursor();
  characterCreator.open({
    mode: "create",
    name: prefillName,
    style: prefillStyle,
    async onSubmit(draft) {
      try {
        const record = await createCharacter(draft.name, draft.style, KICKOFF_ON);
        if (!record) throw new Error("invalid character response");
        // The fresh slot is the active character from here on — every
        // checkpoint (pose, style, pointers) files under it.
        noteCharacter(record.id);
        if (isPrologueStage(record.stage)) await runPrologueIntro(record);
        else await runCharacterIntro(record);
      } catch (error) {
        if (error instanceof CharacterApiError && error.code === "name-taken") {
          characterCreator.setError("That name is already in use.");
        } else {
          console.error("[creation]", error);
          characterCreator.setError("Couldn’t create this character. Try again.");
        }
      }
    },
  });
}

/** Hold the loader for the JOIN BURST (first account echo): the echo is what
 *  dresses the avatar and seeds stats/quests/god when the profile mirror is
 *  stale. Burst-aware, not a stopwatch — the local handshake (token verify +
 *  Firestore reads) legitimately takes seconds; give up only when the socket
 *  dies or at a generous cap. No-op for guests and already-arrived echoes. */
async function awaitJoinBurst(): Promise<void> {
  if (!authUser || invEchoSeen) return;
  loadScreen.setProgress(1, "SYNCING PILOT RECORD…");
  await new Promise<void>((res) => {
    const done = (): void => {
      clearTimeout(cap);
      clearInterval(watch);
      firstInvEchoResolve = null;
      res();
    };
    const cap = window.setTimeout(done, 20000);
    const watch = window.setInterval(() => {
      if (net === null || net.status === "offline") done();
    }, 400);
    firstInvEchoResolve = done;
  });
}

async function doLaunch(name: string): Promise<void> {
  if (launched || launching) return;
  launching = true;
  pilotName = name;
  // Guests always fly (no ledger to earn through); a logout from a shipless
  // character must not strand the next guest session on foot.
  if (!authUser) {
    shipOwned = true;
    built.group.visible = true;
  }
  document.body.classList.remove("intro-active");
  document.body.classList.add("intro-background-ready");
  camera.filmOffset = 0;
  camera.updateProjectionMatrix();
  if (needsHometownSpawn && activeCharacterRecord) {
    placeAtHometown(activeCharacterRecord);
    needsHometownSpawn = false;
  } else if (resume) {
    // Ship pos is either absolute (deep space / legacy) or an offset from a body's
    // orbital centre — rebuild it against that body's CURRENT centre so the ship
    // returns to the planet it was parked at, wherever the planet has orbited to.
    const shipBody = resume.posBody ? system.planetById(resume.posBody) : null;
    if (shipBody) ship.pos.copy(shipBody.pose.center).add(new THREE.Vector3(...resume.pos));
    else ship.pos.set(...resume.pos);
    ship.quat.set(...resume.quat).normalize();
    ship.vel.set(0, 0, 0);
    ship.speed = 0;
    ship.throttle = 0.15;
    ship.snapVisual();
    origin.copy(ship.pos);
    // Logged out on foot: resume standing there, ship parked where it was.
    if (resume.foot) {
      const foot = resume.foot;
      // New saves store the body id + a LOCAL pose: re-anchor to that exact body
      // and convert back to world at its CURRENT (orbited + spun) position, so we
      // land on the same spot no matter how long the player was away. Legacy saves
      // carry no body id and absolute coords — restore those against the nearest
      // body as before (self-heals to the new format on next logout).
      let planet: Planet | null = null;
      let worldP = foot.p;
      let worldQ = foot.q;
      if (foot.body) {
        planet = system.planetById(foot.body);
        if (planet) {
          const wp = new THREE.Vector3(...foot.p)
            .applyQuaternion(planet.pose.quat)
            .add(planet.pose.center);
          const wq = new THREE.Quaternion()
            .copy(planet.pose.quat)
            .multiply(new THREE.Quaternion(...foot.q));
          worldP = [wp.x, wp.y, wp.z];
          worldQ = [wq.x, wq.y, wq.z, wq.w];
        }
      } else {
        planet = system.nearestPlanet(new THREE.Vector3(...foot.p))?.planet ?? null;
      }
      if (planet) {
        ship.throttle = 0;
        footPlanet = planet;
        character.restoreAt(worldP, worldQ, footPlanet);
        // The grounding point (V1): a ship that was PARKED resumes parked, on
        // the same patch of dirt — the anchor is in the planet's spinning local
        // frame and landedAt re-seats it on current terrain. A ship that was in
        // transit at logout flies to the player and lands (the callback).
        // No ship owned → no hull, no autopilot (walking is the whole state).
        if (!shipOwned) {
          autopilot = null;
          built.group.visible = false;
        } else if (resume.shipLand && resume.shipLand.body === planet.def.id) {
          autopilot = Autopilot.landedAt(ship, footPlanet, new THREE.Vector3(...resume.shipLand.p));
        } else {
          autopilot = new Autopilot(ship, footPlanet);
        }
        avatar.group.visible = true;
        avatar.resetCloth(); // login/restore: settled cape at the restore pose, not a stale one
        origin.copy(character.pos);
        returnMode = "foot";
      }
    }
  }
  // Turn the title into the loading screen (same line, now converging), and wait
  // for assets before gameplay.
  loadScreen.show();
  await assetsReady();
  // FULL CHARACTER BEFORE REVEAL (2026-08-11, user call): connect FIRST and
  // hold the loader for the server's account echo — the cloud profile mirror
  // can be stale (or wiped), and the echo is what actually dresses the avatar
  // (styleShowingEquipped in onInvState). Bounded: an unreachable server
  // releases after 4 s rather than hanging login.
  invEchoSeen = false;
  startNet(name);
  await awaitJoinBurst();
  // …then the worn kit's GLB pieces: setStyle streams them async — hold the
  // extra beat so the player never sees a bare body dressing itself. Never
  // hangs: a missing piece resolves null and settles.
  await avatar.styleSettled();
  // The planet kept orbiting/spinning and its settlement surface could have
  // changed while the loader was open. Make the last pre-reveal operation an
  // authoritative seating against the now-resident analytic/collider surface.
  if (returnMode === "foot" && footPlanet) {
    character.seatOnGround(footPlanet);
    origin.copy(character.pos);
  }
  loadScreen.hide();
  launched = true;
  launching = false;
  mode = returnMode;
  input.requestPointerLock();
  hud.showSystemBanner(system.def.name);
  if (resume) hud.showToast("WELCOME BACK, PILOT", 2600);
}

let authRevision = 0;

/** Auth can change while Firestore/HTTP is in flight. Never apply one
 *  account's data to the next account occupying this tab. */
function authStale(revision: number, loadingUid: string): boolean {
  return revision !== authRevision || authUser?.uid !== loadingUid || launched;
}

function applyAuthState(user: AuthUser | null): void {
  const revision = ++authRevision;
  authUser = user;
  if (launched) return;
  // Any auth transition invalidates the roster on screen — drop its bodies
  // rather than leave five avatars posing for a screen nobody is looking at.
  closeCharacterSelect();
  if (!user) {
    showAuthState("forms");
    return;
  }
  if (!user.emailVerified) {
    ui.verifyEmail.textContent = user.email ?? "";
    showAuthState("verify");
    return;
  }
  ui.who.textContent = user.email ?? "signed in";
  ui.error.textContent = "";
  showAuthState("restoring");
  const loadingUid = user.uid;
  noteUid(loadingUid); // the roster validator pins hometown ids to this uid
  // Multi-character accounts: the server-authoritative roster decides what the
  // title shows — the creator for a fresh account, else the character select.
  void listCharacters()
    .then((characters) => {
      if (authStale(revision, loadingUid)) return;
      if (characters.length === 0) {
        showAuthState("restoring", "OPENING CHARACTER CREATOR…");
        queueMicrotask(() => openCharacterCreator());
        return;
      }
      showCharacterSelect(characters);
    })
    .catch((err) => {
      if (authStale(revision, loadingUid)) return;
      console.error("[auth] character roster failed", err);
      showAuthState("error", "COULDN’T LOAD YOUR CHARACTERS");
    });
}

/** One line of state under the name on a roster card. */
function characterSubtitle(record: CharacterRecordV1): string {
  const hometown = record.hometown.settlementName.toUpperCase();
  if (isPrologueStage(record.stage)) return `MID-CRAWL — ${hometown}`;
  if (record.status !== "active" || !record.intro.completed) return `AWAITING HOMECOMING — ${hometown}`;
  // Placeholder until the profile prefetch resolves the ACTUAL last location —
  // the roster never claims a place it hasn't verified.
  return "LOCATING…";
}

/**
 * The hometown rendered in the location format — the truthful place for a
 * character who has never checkpointed a logout anywhere else (they are still
 * standing where they were made).
 */
function hometownLocationLine(record: CharacterRecordV1): string {
  const settlement = record.hometown.settlementName.toUpperCase();
  const sys = systemDefFor(record.hometown.systemId);
  const planet = sys?.planets[record.hometown.planetIndex];
  return sys && planet
    ? `${settlement} · ${planet.name.toUpperCase()} · ${sys.name.toUpperCase()}`
    : settlement;
}

/**
 * Where the character last logged out — system, world, and (when the pilot
 * stood inside one) settlement — resolved from the profile's resume
 * checkpoint. Null = nothing checkpointed yet, or a state whose status line
 * (mid-crawl / awaiting homecoming) says more than a place would; callers fall
 * back to the hometown rendered as a location.
 */
function lastSeenSubtitle(profile: Profile, record: CharacterRecordV1): string | null {
  if (isPrologueStage(record.stage) || record.status !== "active" || !record.intro.completed) return null;
  if (!profile.system) return null;
  const sys = systemDefFor(profile.system);
  if (!sys) {
    // A sealed frontier system whose seed this client hasn't been handed —
    // the place is real, but this screen may not name it.
    return parseSystemRef(profile.system)?.kind === "sealed" ? "THE SEALED FRONTIER" : null;
  }
  const sysName = sys.name.toUpperCase();
  const bodyId = profile.foot?.body ?? profile.shipLand?.body ?? profile.posBody;
  const planet = bodyId !== null && bodyId !== undefined ? sys.planets.find((p) => p.id === bodyId) : undefined;
  if (!planet) return `ADRIFT — ${sysName}`; // logged out in open space
  const planetName = planet.name.toUpperCase();
  // A settlement is only meaningful for an ON-FOOT logout on that body. The
  // analytic whole-planet footprint query works straight off the def — no
  // region residency, no city graph. Slack matches the walk-in banner's feel.
  const foot = profile.foot;
  if (foot && foot.body === planet.id) {
    try {
      const hit = settlementInfluenceAt(new PlanetSampler(planet), foot.p[0], foot.p[1], foot.p[2], 60);
      if (hit) {
        const id = `${planet.seed}:stl:${hit.regionId.face}:${hit.regionId.x}:${hit.regionId.y}:${hit.seedK}`;
        return `${settlementName(id).toUpperCase()} · ${planetName} · ${sysName}`;
      }
    } catch { /* settlement naming is garnish — the planet line stands */ }
  }
  return `${planetName} · ${sysName}`;
}

/** The login roster: one card per character plus a new-character slot. */
/** Live full-length portraits for the roster carousel (built per render). */
const characterPortraits = new CharacterPortraits();

/**
 * Profile prefetch, keyed by character id — kicked off the moment the roster
 * shows, for two reasons: the card portraits can upgrade to the character's
 * ACTUAL look (worn armor + transmog, not the creation style), and picking a
 * character reuses the already-inflight fetch instead of starting one, so
 * entering the world doesn't pay the Firestore round-trip again. Single-use:
 * enterCharacter consumes its entry (a retry refetches fresh).
 */
const profilePrefetch = new Map<string, Promise<Profile>>();
/** Serializes the per-card last-seen lookups: resolving a settlement builds a
 *  cold PlanetSampler (~250 ms), and eight of those landing together would
 *  visibly hitch the title. One at a time, with a breath between. */
let rosterLocationQueue: Promise<void> = Promise.resolve();
/** True while the select screen owns the title — gates the portrait pump. */
let rosterOpen = false;
/** Carousel teardown (key/resize listeners) installed by showCharacterSelect. */
let rosterTeardown: (() => void) | null = null;

function closeCharacterSelect(): void {
  rosterOpen = false;
  rosterTeardown?.();
  rosterTeardown = null;
  characterPortraits.clear();
}

function showCharacterSelect(characters: CharacterRecordV1[]): void {
  rosterTeardown?.();
  rosterTeardown = null;
  ui.chars.replaceChildren();
  const title = document.createElement("div");
  title.className = "char-select-title";
  title.textContent = "CHOOSE YOUR CHARACTER";
  ui.chars.appendChild(title);

  // Full-length pilots in a scroll-snapped carousel. The CENTRED pilot is the
  // active one: clicking them enters the world, clicking a neighbour only
  // brings them to centre — no accidental login from a half-visible card.
  const carousel = document.createElement("div");
  carousel.className = "char-carousel";
  const viewport = document.createElement("div");
  viewport.className = "char-carousel-viewport";
  const track = document.createElement("div");
  track.className = "char-carousel-track";
  viewport.appendChild(track);

  const cards: HTMLElement[] = [];
  const centerOf = (card: HTMLElement) =>
    card.offsetLeft + card.offsetWidth / 2 - viewport.clientWidth / 2;
  const isCentered = (card: HTMLElement) =>
    Math.abs(centerOf(card) - viewport.scrollLeft) < card.offsetWidth * 0.35;
  const centerCard = (card: HTMLElement, smooth = true) =>
    viewport.scrollTo({ left: centerOf(card), behavior: smooth ? "smooth" : "auto" });

  // The cover-flow read: each card scales and dims by its distance from the
  // viewport centre. Dimming is BRIGHTNESS, never opacity — a translucent body
  // lets the animated title world bleed through and reads as the background
  // drawing over the character. Passive scroll → one rAF, so the effect never
  // jams scrolling itself.
  let carouselRaf = 0;
  const updateCarousel = () => {
    carouselRaf = 0;
    const mid = viewport.scrollLeft + viewport.clientWidth / 2;
    for (const card of cards) {
      const d = Math.min(1, Math.abs(card.offsetLeft + card.offsetWidth / 2 - mid) / (card.offsetWidth * 1.6));
      card.style.transform = `scale(${(1 - 0.16 * d).toFixed(4)})`;
      card.style.filter = `brightness(${(1 - 0.45 * d).toFixed(3)})`;
    }
  };
  const scheduleCarousel = () => {
    if (!carouselRaf) carouselRaf = requestAnimationFrame(updateCarousel);
  };
  viewport.addEventListener("scroll", scheduleCarousel, { passive: true });

  // End padding lets the first/last card reach the centre line; it depends on
  // the viewport's LIVE width, so it recomputes on resize.
  const layout = () => {
    const pad = Math.max(0, viewport.clientWidth / 2 - (cards[0]?.offsetWidth ?? 0) / 2);
    track.style.paddingLeft = track.style.paddingRight = `${pad}px`;
    scheduleCarousel();
  };

  const portraits: { style: CharacterStyle; canvas: HTMLCanvasElement }[] = [];
  for (const record of characters) {
    // The card is a DIV, not a button: it holds the pick target AND the delete
    // control, and a button inside a button is invalid markup that browsers
    // silently un-nest (which drops the delete click entirely).
    const card = document.createElement("div");
    card.className = "char-card";

    const pick = document.createElement("button");
    pick.type = "button";
    pick.className = "char-pick";
    const canvas = document.createElement("canvas");
    canvas.className = "char-portrait";
    // Hidden until the character's REAL look (profile prefetch below) is
    // applied and its pieces are mounted — no bare-creation-style flash.
    canvas.style.visibility = "hidden";
    const text = document.createElement("span");
    text.className = "char-card-text";
    const name = document.createElement("span");
    name.className = "char-card-name";
    name.textContent = record.name;
    const sub = document.createElement("span");
    sub.className = "char-card-sub";
    sub.textContent = characterSubtitle(record);
    text.append(name, sub);
    pick.append(canvas, text);
    pick.addEventListener("click", () => {
      // A side card's first click only centres it — entering takes a click on
      // the pilot standing front and centre.
      if (!isCentered(card)) {
        centerCard(card);
        return;
      }
      localStorage.setItem("hl-roster-focus", record.id);
      closeCharacterSelect();
      void enterCharacter(record).catch((error) => {
        console.error("[auth] character restore failed", error);
        showAuthState("error", "COULDN’T LOAD YOUR PILOT");
      });
    });
    portraits.push({ style: record.style, canvas });
    // Prefetch the profile: the portrait upgrades to the character's REAL look
    // (worn armor via the shared WoW rule) as soon as it lands, and the pick
    // handler reuses this exact promise (see profilePrefetch).
    const prefetched = loadCharacterProfile(record);
    profilePrefetch.set(record.id, prefetched);
    prefetched
      .then((profile) => styleWithWornGear(
        profile.style ?? record.style,
        profile.rpg?.inventory.equipped ?? {},
      ))
      .catch(() => record.style) // fetch failed: show the creation style
      .then(async (shown) => {
        await characterPortraits.restyle(canvas, shown);
        canvas.style.visibility = "visible";
      });
    // The location line rides the same prefetch, through the serialized queue
    // (see rosterLocationQueue). A stale card after a roster rebuild is inert:
    // writing textContent to a detached node shows nothing.
    const hometownLine = hometownLocationLine(record);
    prefetched
      .then((profile) => {
        rosterLocationQueue = rosterLocationQueue.then(() => new Promise<void>((done) => {
          window.setTimeout(() => {
            try {
              sub.textContent = lastSeenSubtitle(profile, record) ?? hometownLine;
            } catch {
              sub.textContent = hometownLine;
            }
            done();
          }, 120);
        }));
      })
      .catch(() => {
        if (sub.textContent === "LOCATING…") sub.textContent = hometownLine;
      });

    // Deleting is irreversible, so it takes two deliberate clicks and the
    // confirm names the character being destroyed.
    const del = document.createElement("button");
    del.type = "button";
    del.className = "char-del";
    del.title = `Delete ${record.name}`;
    del.setAttribute("aria-label", `Delete ${record.name}`);
    del.textContent = "✕";
    const confirm = document.createElement("div");
    confirm.className = "char-confirm";
    confirm.hidden = true;
    const ask = document.createElement("span");
    ask.className = "char-confirm-ask";
    ask.textContent = `DELETE ${record.name.toUpperCase()} FOREVER?`;
    const no = document.createElement("button");
    no.type = "button";
    no.className = "char-confirm-no";
    no.textContent = "CANCEL";
    const yes = document.createElement("button");
    yes.type = "button";
    yes.className = "char-confirm-yes";
    yes.textContent = "DELETE";
    confirm.append(ask, no, yes);
    del.addEventListener("click", () => {
      confirm.hidden = false;
      card.classList.add("confirming");
    });
    no.addEventListener("click", () => {
      confirm.hidden = true;
      card.classList.remove("confirming");
    });
    yes.addEventListener("click", () => {
      yes.disabled = true;
      no.disabled = true;
      ask.textContent = "DELETING…";
      void deleteCharacter(record.id)
        .then((remaining) => {
          // The server answers with the roster that survived, so the screen
          // never re-derives it from a local splice that could disagree.
          if (remaining.length === 0) {
            closeCharacterSelect();
            showAuthState("restoring", "OPENING CHARACTER CREATOR…");
            queueMicrotask(() => openCharacterCreator());
            return;
          }
          showCharacterSelect(remaining);
        })
        .catch((error) => {
          console.error("[character] delete failed", error);
          ask.textContent = error instanceof CharacterApiError && error.code === "character-in-session"
            ? "THAT CHARACTER IS PLAYING ELSEWHERE"
            : "COULDN’T DELETE — TRY AGAIN";
          yes.disabled = false;
          no.disabled = false;
        });
    });

    card.append(pick, del, confirm);
    track.appendChild(card);
    cards.push(card);
  }

  if (characters.length < CHARACTER_SLOTS_MAX) {
    const add = document.createElement("button");
    add.type = "button";
    add.className = "char-card char-card-new";
    add.textContent = "+ NEW CHARACTER";
    // Unlike a pilot, the empty slot acts on the FIRST click from anywhere in
    // the line-up — there is nothing to preview by centring it.
    add.addEventListener("click", () => {
      closeCharacterSelect();
      openCharacterCreator();
    });
    track.appendChild(add);
    cards.push(add);
  }

  const nearestIndex = () => {
    const mid = viewport.scrollLeft + viewport.clientWidth / 2;
    let best = 0;
    let bestD = Infinity;
    cards.forEach((card, i) => {
      const d = Math.abs(card.offsetLeft + card.offsetWidth / 2 - mid);
      if (d < bestD) { bestD = d; best = i; }
    });
    return best;
  };
  const step = (dir: -1 | 1) => {
    const next = cards[Math.max(0, Math.min(cards.length - 1, nearestIndex() + dir))];
    if (next) centerCard(next);
  };
  const makeArrow = (dir: -1 | 1): HTMLButtonElement => {
    const arrow = document.createElement("button");
    arrow.type = "button";
    arrow.className = `char-arrow ${dir < 0 ? "char-arrow-left" : "char-arrow-right"}`;
    arrow.setAttribute("aria-label", dir < 0 ? "Previous character" : "Next character");
    arrow.textContent = dir < 0 ? "‹" : "›";
    arrow.addEventListener("click", () => step(dir));
    return arrow;
  };
  carousel.append(makeArrow(-1), viewport, makeArrow(1));
  ui.chars.appendChild(carousel);

  // Keyboard: ←/→ browse the line-up, Enter picks whoever stands at centre.
  const onRosterKey = (event: KeyboardEvent) => {
    if (!rosterOpen) return;
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;
    if (event.key === "ArrowLeft") { step(-1); event.preventDefault(); }
    else if (event.key === "ArrowRight") { step(1); event.preventDefault(); }
    else if (event.key === "Enter" && (!target || target.tagName !== "BUTTON")) {
      const card = cards[nearestIndex()];
      const pickButton = card?.querySelector<HTMLButtonElement>(".char-pick");
      if (pickButton) pickButton.click();
      else if (card instanceof HTMLButtonElement) card.click(); // the NEW slot
    }
  };
  window.addEventListener("keydown", onRosterKey);
  window.addEventListener("resize", layout);
  rosterTeardown = () => {
    window.removeEventListener("keydown", onRosterKey);
    window.removeEventListener("resize", layout);
    if (carouselRaf) cancelAnimationFrame(carouselRaf);
  };

  characterPortraits.build(portraits);
  rosterOpen = true;
  showAuthState("chars");
  // Only now is the panel display:flex — real layout metrics exist. Pad the
  // track so end cards can centre, then open on the last-played pilot.
  requestAnimationFrame(() => {
    layout();
    const focusId = localStorage.getItem("hl-roster-focus");
    const focusIndex = Math.max(0, characters.findIndex((c) => c.id === focusId));
    const focus = cards[focusIndex];
    if (focus) centerCard(focus, false);
    scheduleCarousel();
  });
}

/** Restore one character's world: profile checkpoints, ship, style, resume
 *  pose — then branch into the prologue/homecoming/ready flows exactly as the
 *  single-character boot did. */
async function enterCharacter(record: CharacterRecordV1): Promise<void> {
  const revision = authRevision;
  const loadingUid = authUser?.uid;
  if (!loadingUid) return;
  showAuthState("restoring", "LOADING PILOT…");
  // Every per-character checkpoint from here on files under this slot.
  noteCharacter(record.id);
  activeCharacterRecord = record;
  // Reuse the roster's inflight prefetch when there is one (single-use — a
  // retry after an error refetches rather than trusting a possibly-stale copy).
  const prefetched = profilePrefetch.get(record.id);
  profilePrefetch.delete(record.id);
  const profile = await (prefetched ?? loadCharacterProfile(record));
  if (authStale(revision, loadingUid)) return;
  // NO DEFAULT SHIP (2026-08-10): ownership = a ship was boarded at least once
  // (the profile mirror / hangar pointer). Derived HERE, before any spawn
  // decision below reads it — a shipless character walks.
  shipOwned = profile.ship !== null || profile.activeShipId !== null;
  if (!shipOwned) {
    built.group.visible = false;
    autopilot = null;
  }
  // Newest ship wins, by save timestamp — but ONLY a cache written by
  // THIS account may compete (another account's local ship bled into
  // fresh logins otherwise).
  const cacheOwned = savedShip !== null && savedShip.uid === currentUid();
  const localTime = cacheOwned ? currentLayoutTime : 0;
  const cloudWins = profile.ship && (profile.shipUpdatedAt > localTime || !cacheOwned);
  if (cloudWins) {
    currentLayoutTime = profile.shipUpdatedAt;
    saveLocalLayout(profile.ship!, profile.shipUpdatedAt, currentUid() ?? "guest");
    applyShipLayout(profile.ship!);
  } else if (cacheOwned && currentLayoutTime > profile.shipUpdatedAt) {
    void saveShip(currentLayout, currentLayoutTime).catch(() => { }); // heal the cloud copy
  } else if (!profile.ship && !cacheOwned) {
    applyShipLayout(DEFAULT_LAYOUT); // fresh character: never inherit a stranger's hull
  }
  if (profile.rpg) rpg.applyPersisted(profile.rpg.health, profile.rpg.inventory);
  homeWorld = profile.homeWorld;
  upgrades.boost = profile.upgrades.boost;
  upgrades.pulse = profile.upgrades.pulse;
  upgrades.mining = profile.upgrades.mining;
  applyUpgrades();
  rpg.fuelKit = profile.rpg?.fuelKit === true;
  void listDiscovered().then((entries) => {
    for (const e of entries) discovered.set(e.id, e.at);
    recordDiscovery(SYSTEM_ID, false); // where we woke up counts
  });
  if (countItem(rpg.inventory, "fuel-cell") === 0 && !rpg.fuelKit) {
    addItem(rpg.inventory, "fuel-cell", 3);
    rpg.fuelKit = true;
    rpg.rev++;
  }
  needsHometownSpawn = !profile.pos;
  // Worn armor SHOWS at login (transmog still wins) — profile.rpg was applied
  // above, so the equipped map is populated here; the server echo re-derives.
  playerStyle = styleShowingEquipped(profile.style ?? record.style);
  avatar.setStyle(playerStyle);
  activeOutfitId = profile.activeOutfitId;
  // C4: RETURN must survive a relog inside the home pocket.
  if (profile.lastLattice !== null) lastLattice = profile.lastLattice;
  if (profile.pos && profile.quat) {
    // Resume in whatever galaxy the character logged out in — lattice or
    // their home system (C4).
    let profileSystem = profile.system ?? record.hometown.systemId;
    let profileRef = parseSystemRef(profileSystem);
    if (profileRef?.kind === "sealed" && !hasSystemReveal(profileSystem)) {
      try {
        await loadAuthorizedSystemReveal(profileSystem);
      } catch (error) {
        // A stale/forged pre-v2 profile id must not brick login. Return to
        // this character's always-authorized home pocket and leave a clear log.
        console.warn(`[discover] could not restore ${profileSystem}; returning home`, error);
        profileSystem = record.hometown.systemId;
        profileRef = parseSystemRef(profileSystem);
      }
      if (authStale(revision, loadingUid)) return;
    }
    const sysOk = profileRef !== null;
    if (sysOk && profileSystem !== SYSTEM_ID) switchSystem(profileSystem);
    if (profileSystem === SYSTEM_ID) {
      resume = {
        pos: profile.pos,
        quat: profile.quat,
        posBody: profile.posBody,
        foot: profile.foot,
        shipLand: profile.shipLand,
      };
    }
  } else if (shipOwned) {
    // A character that has never checkpointed wakes up in their own home
    // system, parked over the home planet (planet 0 — placeShipAtSpawn
    // already targets it day-side).
    switchSystem(record.hometown.systemId);
  } else {
    // NO DEFAULT SHIP: never checkpointed and nothing to fly — wake up ON FOOT
    // at the hometown anchor instead of parked-over-the-planet (which assumes
    // a hull the character does not own).
    placeAtHometown(record);
  }
  // Hangar pointer; migrate pre-inventory profiles (ship but no hangar
  // doc) by seeding the collection with the current ship.
  activeShipId = profile.activeShipId;
  if (profile.ship && !profile.activeShipId) {
    void createShipDoc("My Ship", profile.ship, profile.shipUpdatedAt || Date.now())
      .then((id) => {
        activeShipId = id;
        return setActiveShipId(id, "My Ship");
      })
      .catch(() => { });
  }
  nameInput.value = record.name;
  document.getElementById("launch")!.textContent = "CONTINUE";
  if (isPrologueStage(record.stage)) {
    // SOUL kickoff mid-crawl relog: resume at the current beat's site.
    showAuthState("restoring", "REJOINING THE CRAWL…");
    void runPrologueIntro(record, playerStyle).catch((error) => {
      console.error("[prologue] resume failed", error);
      intro.classList.remove("hidden");
      intro.dataset.view = "auth";
      document.body.classList.add("intro-active");
      showAuthState("error", "COULDN’T RESUME THE CRAWL");
    });
  } else if (record.status === "intro" || !record.intro.completed) {
    showAuthState("restoring", "PREPARING YOUR HOMECOMING…");
    void runCharacterIntro(record).catch((error) => {
      console.error("[creation] intro failed", error);
      intro.classList.remove("hidden");
      intro.dataset.view = "auth";
      document.body.classList.add("intro-active");
      showAuthState("error", "COULDN’T BEGIN YOUR HOMECOMING");
    });
  } else {
    showAuthState("ready");
  }
}

if (authAvailable) {
  const formEl = ui.forms as HTMLFormElement;
  const emailEl = document.getElementById("auth-email") as HTMLInputElement;
  const passEl = document.getElementById("auth-password") as HTMLInputElement;
  // The sign-up/in buttons live below the line (outside the form, associated via
  // the `form` attribute), so collect them by id rather than from the form tree.
  const authButtons = ["auth-signin", "auth-signup"]
    .map((id) => document.getElementById(id))
    .filter(Boolean) as HTMLButtonElement[];

  const attempt = async (fn: (email: string, pw: string) => Promise<AuthUser>) => {
    if (!formEl.reportValidity()) return;
    ui.error.textContent = "";
    authButtons.forEach((button) => { button.disabled = true; });
    try {
      await fn(emailEl.value.trim(), passEl.value);
    } catch (err) {
      ui.error.textContent = authErrorMessage(err);
    } finally {
      authButtons.forEach((button) => { button.disabled = false; });
    }
  };
  formEl.addEventListener("submit", (event) => {
    event.preventDefault();
    void attempt(signIn);
  });
  document.getElementById("auth-signup")!.addEventListener("click", () => void attempt(signUp));
  for (const id of ["auth-signout", "verify-signout"]) {
    document.getElementById(id)!.addEventListener("click", () => {
      void signOutUser().catch((err) => { ui.error.textContent = authErrorMessage(err); });
    });
  }
  document.getElementById("verify-check")!.addEventListener("click", () => {
    ui.error.textContent = "";
    void refreshUser()
      .then((user) => {
        if (user?.emailVerified) applyAuthState(user);
        else ui.error.textContent = "not verified yet — click the link in the email first";
      })
      .catch((err) => { ui.error.textContent = authErrorMessage(err); });
  });
  document.getElementById("verify-resend")!.addEventListener("click", () => {
    void resendVerification()
      .then(() => (ui.error.textContent = "verification email re-sent"))
      .catch((err) => (ui.error.textContent = authErrorMessage(err)));
  });
  // ── DEV ONBOARDING KICKSTART (emulator builds only, user call 2026-07-23): one click
  //    mints a fresh ALREADY-VERIFIED user, forces the kickoff flag on (live + persisted),
  //    and hands the session straight to applyAuthState — creator → landfall, no email
  //    dance. Production builds never show the button (firebaseEmulatorMode is Vite-dev-
  //    gated) and the mint itself throws outside the emulator. ──
  const devKick = document.getElementById("auth-dev-kickoff") as HTMLButtonElement | null;
  if (devKick && firebaseEmulatorMode) {
    devKick.style.display = "";
    devKick.addEventListener("click", () => {
      ui.error.textContent = "";
      devKick.disabled = true;
      KICKOFF_ON = true; // this run tests the kickoff — flip the boot flag live…
      localStorage.setItem("hl-kickoff", "1"); // …and persist it for the relog resume
      void devMintVerifiedUser()
        .then((user) => applyAuthState(user))
        .catch((err) => { ui.error.textContent = authErrorMessage(err); })
        .finally(() => { devKick.disabled = false; });
    });
  }

  let authWatchStarted = false;
  const beginAuthWatch = () => {
    if (authWatchStarted) return;
    authWatchStarted = true;
    showAuthState("checking");
    watchAuth(
      applyAuthState,
      (err) => {
        authWatchStarted = false;
        console.error("[auth] session restore failed", err);
        showAuthState("error", "COULDN’T RESTORE YOUR SESSION");
      },
    );
  };
  ui.retry.addEventListener("click", () => {
    if (authUser) applyAuthState(authUser);
    else beginAuthWatch();
  });
  beginAuthWatch();
} else {
  showAuthState("none");
  ui.configMissing.style.display = "block";
}

document.getElementById("launch")!.addEventListener("click", () => {
  void audio.unlock(); // synchronous in the gesture — satisfies the autoplay policy
  const name = nameInput.value.trim();
  if (!name) {
    ui.error.textContent = "pick a pilot name first";
    return;
  }
  void doLaunch(name);
});

// The character's on-foot pose in the stood-on body's LOCAL (orbiting + spinning)
// frame, tagged with the body id. Resume re-anchors here so the boots land on the
// same spot however far the body has since orbited/spun — absolute system-space
// coords go stale the instant the body moves, which is what stranded players on
// moons or out in space after time away.
function footLocalPose(planet: Planet): {
  body: string;
  p: [number, number, number];
  q: [number, number, number, number];
} {
  const lp = new THREE.Vector3()
    .copy(character.pos)
    .sub(planet.pose.center)
    .applyQuaternion(planet.pose.invQuat);
  const lq = new THREE.Quaternion().copy(planet.pose.invQuat).multiply(character.quat);
  return { body: planet.def.id, p: [lp.x, lp.y, lp.z], q: [lq.x, lq.y, lq.z, lq.w] };
}

// The parked ship's pose, anchored to the nearest body's ORBITAL CENTRE when the
// ship is inside that body's gravity well — so resume follows the body around its
// orbit rather than stranding the ship where the body used to be (the same orbital
// drift that put disembarked players on moons). Centre-only, no spin: a ship in
// space isn't glued to the turning surface, so spin-rotating a large offset would
// fling it around the planet. Out past the well, absolute system space is correct.
const SHIP_ANCHOR_RANGE = 60000 * WORLD_SCALE; // matches the ship's gravity-well reach
function shipLocalPose(): { body: string | null; pos: [number, number, number] } {
  const near = system.nearestPlanet(ship.pos);
  if (near && near.surfaceDist < SHIP_ANCHOR_RANGE) {
    const off = new THREE.Vector3().copy(ship.pos).sub(near.planet.pose.center);
    return { body: near.planet.def.id, pos: [off.x, off.y, off.z] };
  }
  return { body: null, pos: [ship.pos.x, ship.pos.y, ship.pos.z] };
}

// Periodically persist position so login resumes where the player left off
// — on foot too: ship parked where it was, boots where they stood.
async function persistPosition(): Promise<void> {
  if (!launched || !authUser?.emailVerified) return;
  const shipAnchor = shipLocalPose();
  // The grounding point (V1): a LANDED ship saves its spin-locked local anchor
  // so it is on the same patch of dirt next login, however far the planet has
  // turned. The autopilot's anchor is already in that frame.
  const landed = footPlanet ? autopilot?.landedAnchor() ?? null : null;
  await savePosition(
    SYSTEM_ID,
    shipAnchor.pos,
    [ship.quat.x, ship.quat.y, ship.quat.z, ship.quat.w],
    shipAnchor.body,
    footPlanet ? footLocalPose(footPlanet) : null,
    lastLattice,
    landed && footPlanet ? { body: footPlanet.def.id, p: [landed.x, landed.y, landed.z] } : null,
  );
}

/** Explicit logout is the one lifecycle transition the app controls, so wait
 * for its checkpoint before revoking Firebase auth. A short cap keeps logout
 * usable offline; the persistent Firestore queue owns delivery after that. */
async function checkpointAndLogout(): Promise<void> {
  let timer = 0;
  const timeout = new Promise<void>((resolve) => {
    timer = window.setTimeout(resolve, 4_000);
  });
  try {
    await Promise.race([persistPosition(), timeout]);
  } catch (err) {
    console.warn("[position] final checkpoint failed", err);
  } finally {
    window.clearTimeout(timer);
    await signOutUser().catch((err) => {
      console.warn("[auth] sign out failed", err);
    });
    window.location.reload();
  }
}

window.setInterval(() => {
  void persistPosition().catch(() => { });
}, 20_000);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") void persistPosition().catch(() => { });
});
// `pagehide` covers browsers/navigation paths that omit visibilitychange and
// remains compatible with the back-forward cache (unlike beforeunload/unload).
window.addEventListener("pagehide", () => {
  void persistPosition().catch(() => { });
});

canvas.addEventListener("click", () => {
  if (mode === "fly" || mode === "foot" || mode === "vehicle") input.requestPointerLock();
});

// --- Chase camera ---
const camOffset = new THREE.Vector3();
const camTarget = new THREE.Vector3();
const smoothPos = new THREE.Vector3().copy(ship.pos);
const smoothQuat = ship.quat.clone();
// Separate, lazier orientation for the camera's up-vector so rolls are
// followed with a deliberate lag (flight-sim style) instead of instantly.
const upQuat = ship.quat.clone();

// Scroll-wheel camera distance: 1 = default chase, up to ZOOM_MAX pulled
// back, and scrolling up past the closest chase distance snaps into
// first-person (zoom 0, ship hidden, rigid cockpit camera).
const ZOOM_MAX = 4.0;
const ZOOM_MIN_CHASE = 0.6;
const ZOOM_STEP = 0.18;
const FP_THRESHOLD = 0.3;
let camZoom = 1;
let camZoomSmooth = 1;

// Title-screen system feed: a slow, continuously moving survey pass around
// the first world in the randomized system. Positions live in system space so
// the shot remains stable across floating-origin rebases and planetary orbit.
const titleEye = new THREE.Vector3();
const titleLook = new THREE.Vector3();
const titleEyeGoal = new THREE.Vector3();
const titleLookGoal = new THREE.Vector3();
const titleRadial = new THREE.Vector3();
const titleSide = new THREE.Vector3();
const titleLift = new THREE.Vector3();
const titleUp = new THREE.Vector3();
const titlePhase = Math.random() * Math.PI * 2;
let titleCameraReady = false;

function updateTitleCamera(dt: number, nowMs: number): void {
  const planet = system.planets[0];
  if (!planet) return;
  const center = planet.pose.center;
  const radius = planet.def.radius;
  const angle = titlePhase + nowMs / 1000 * 0.055;

  // A solar-relative orbital frame plus two slower offsets reads as a piloted
  // flyby instead of a mechanical turntable.
  titleRadial.copy(center).normalize();
  if (titleRadial.lengthSq() < 0.5) titleRadial.set(0, 0, 1);
  titleSide.crossVectors(titleRadial, titleUp.set(0, 1, 0)).normalize();
  if (titleSide.lengthSq() < 0.5) titleSide.set(1, 0, 0);
  titleLift.crossVectors(titleSide, titleRadial).normalize();
  titleEyeGoal
    .copy(center)
    .addScaledVector(titleSide, Math.cos(angle) * radius * 4.6)
    .addScaledVector(titleRadial, Math.sin(angle) * radius * 4.6)
    .addScaledVector(titleLift, radius * (1.05 + Math.sin(angle * 1.7) * 0.42));
  titleLookGoal.copy(center);

  if (!titleCameraReady || titleEye.distanceToSquared(titleEyeGoal) > radius * radius * 400) {
    titleEye.copy(titleEyeGoal);
    titleLook.copy(titleLookGoal);
    titleCameraReady = true;
  } else {
    const ease = 1 - Math.exp(-0.8 * dt);
    titleEye.lerp(titleEyeGoal, ease);
    titleLook.lerp(titleLookGoal, ease);
  }

  built.group.visible = true;
  camera.up.copy(titleLift);
  camera.position.copy(titleEye).sub(origin);
  camTarget.copy(titleLook).sub(origin);
  camera.lookAt(camTarget);
  // The centre of the remaining area is halfway between the panel's right
  // edge and the viewport's right edge. In NDC that horizontal target equals
  // panelRight / viewportWidth; translate it into Three's physical-film skew.
  // Mobile uses a bottom sheet, so its horizontal centre remains unchanged.
  const panelRight = window.innerWidth > 820
    ? document.querySelector<HTMLElement>(".intro-card")?.getBoundingClientRect().right ?? 0
    : 0;
  const targetNdcX = panelRight / window.innerWidth;
  camera.fov += (62 - camera.fov) * Math.min(1, 3 * dt);
  camera.filmOffset = -targetNdcX
    * camera.getFilmWidth()
    * Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5))
    * camera.aspect;
  camera.updateProjectionMatrix();
}

canvas.addEventListener(
  "wheel",
  (e) => {
    if (companion.transition.active) { e.preventDefault(); return; }
    // Shift+wheel reads as HORIZONTAL scroll in browsers — and boost is a
    // held Shift, which silently killed zooming at speed. Take either axis.
    const dy = e.deltaY !== 0 ? e.deltaY : e.deltaX;
    if (mode === "foot") {
      e.preventDefault();
      if (homesteadBuild?.active) return; // build mode owns scroll (ghost rotation), not zoom
      footCam.zoom = Math.max(0.45, Math.min(2.2, footCam.zoom + Math.sign(dy) * 0.15));
      return;
    }
    if (mode !== "fly") return;
    e.preventDefault();
    if (dy < 0) {
      // Scroll up: move closer; from the closest chase distance, go cockpit.
      camZoom = camZoom <= ZOOM_MIN_CHASE ? 0 : Math.max(ZOOM_MIN_CHASE, camZoom - ZOOM_STEP);
    } else if (dy > 0) {
      // Scroll down: out of the cockpit, then pull back up to the cap.
      camZoom = camZoom === 0 ? ZOOM_MIN_CHASE : Math.min(ZOOM_MAX, camZoom + ZOOM_STEP);
    }
  },
  { passive: false },
);

const cliffCameraA = [0, 0, 0], cliffCameraB = [0, 0, 0], cliffCameraHit = cliffHit();
const cliffCameraAnchor = new THREE.Vector3(), cliffCameraLocal = new THREE.Vector3(), cliffCameraInv = new THREE.Quaternion();
const companionCameraAnchor = new THREE.Vector3(), companionCameraOffset = new THREE.Vector3();
const companionCameraProbe = new THREE.Vector3(), companionCameraDirection = new THREE.Vector3(), companionCameraLocal = new THREE.Vector3();
/** The companion's orbit uses the same terrain/building clearance as the foot camera. */
function clampCompanionCamera(planet: Planet): void {
  companionCameraAnchor.copy(avatar.group.position).addScaledVector(character.up, 1.35);
  companionCameraOffset.copy(camera.position).sub(companionCameraAnchor);
  const samples = Math.max(1, Math.ceil(companionCameraOffset.length() / .12));
  for (let i = 1; i <= samples; i++) {
    const k = i / samples;
    companionCameraProbe.copy(character.pos).addScaledVector(character.up, 1.35)
      .addScaledVector(companionCameraOffset, k).sub(planet.pose.center);
    const radius = companionCameraProbe.length();
    companionCameraDirection.copy(companionCameraProbe).normalize();
    companionCameraLocal.copy(companionCameraProbe).applyQuaternion(planet.pose.invQuat);
    if (radius < planet.surfaceRadiusWorld(companionCameraDirection) + .18
      || planet.settlementColliders?.occludes(companionCameraLocal.x, companionCameraLocal.y, companionCameraLocal.z, .18)
      || planet.homesteadOccludes?.(companionCameraLocal.x, companionCameraLocal.y, companionCameraLocal.z, .18)) {
      camera.position.copy(companionCameraAnchor).addScaledVector(companionCameraOffset, (i - 1) / samples);
      break;
    }
  }
}
/** Final camera position uses the same interpolated frame as the visible ledges. */
function clampCameraToCliffs(planet: Planet, anchorWorld: THREE.Vector3): void {
  const field = planet.terrainSolids;
  if (!field?.solids.length) return;
  cliffCameraInv.copy(planet.group.quaternion).invert();
  cliffCameraLocal.copy(anchorWorld).sub(origin).sub(planet.group.position).applyQuaternion(cliffCameraInv).toArray(cliffCameraA);
  cliffCameraLocal.copy(camera.position).sub(planet.group.position).applyQuaternion(cliffCameraInv).toArray(cliffCameraB);
  const hit = field.sphereSweep(cliffCameraA, cliffCameraB, .18, cliffCameraHit);
  if (!hit) return;
  const t = Math.max(0, hit.t - .0001);
  for (let k = 0; k < 3; k++) cliffCameraB[k] = cliffCameraA[k] + (cliffCameraB[k] - cliffCameraA[k]) * t + hit.normal[k] * (hit.penetration + .003);
  camera.position.fromArray(cliffCameraB).applyQuaternion(planet.group.quaternion).add(planet.group.position);
}
function updateCamera(dt: number): void {
  camZoomSmooth += (camZoom - camZoomSmooth) * (1 - Math.exp(-10 * dt));
  const firstPerson = camZoomSmooth < FP_THRESHOLD;
  built.group.visible = !firstPerson || (mode === "jump" && jump !== null);

  if (mode === "jump" && jump) {
    // FIXED tube camera: anchored to the tunnel axis (jump.path), not the
    // drifting hull — the ship visibly flies up/down/left/right within the
    // frame, Star-Fox style. Same boom formula as the chase camera with
    // the ship's lateral offset removed, so when the drift authority hits
    // zero at reveal end the two cameras coincide and control returns
    // without a cut.
    const speedK = Math.min(1, Math.abs(ship.speed) / 32000);
    const zoomK = Math.max(0.6, camZoomSmooth);
    camera.up.set(0, 1, 0).applyQuaternion(jump.quat);
    jumpCamV.copy(jump.path).sub(origin);
    camOffset.set(0, 0, (11 + speedK * 10) * zoomK).applyQuaternion(jump.quat);
    camera.position.copy(jumpCamV).add(camOffset);
    camOffset.set(0, (2.8 + speedK * 1.5) * Math.max(0.55, zoomK * 0.9), 0).applyQuaternion(jump.quat);
    camera.position.add(camOffset);
    camTarget.set(0, 0.6, -30).applyQuaternion(jump.quat).add(jumpCamV);
    camera.lookAt(camTarget);
    // Keep the chase springs warm so flight resumes exactly here.
    smoothQuat.copy(jump.quat);
    upQuat.copy(jump.quat);
    smoothPos.copy(ship.pos);
    const tFov = 72 + speedK * 14;
    if (Math.abs(camera.fov - tFov) > 0.1) {
      camera.fov += (tFov - camera.fov) * Math.min(1, 5 * dt);
      camera.updateProjectionMatrix();
    }
    return;
  }

  smoothQuat.slerp(visQuat, 1 - Math.exp(-8 * dt));
  upQuat.slerp(visQuat, 1 - Math.exp(-3 * dt));
  smoothPos.copy(visPos);
  const speedK = Math.min(1, Math.abs(ship.speed) / 32000);

  if (firstPerson) {
    // Rigid cockpit view: 1:1 with the (interpolated) ship frame.
    camera.up.set(0, 1, 0).applyQuaternion(visQuat);
    camOffset.set(0, 0.55, -0.6).applyQuaternion(visQuat);
    camera.position.copy(smoothPos).sub(origin).add(camOffset);
    camTarget.set(0, 0.45, -40).applyQuaternion(visQuat).add(smoothPos).sub(origin);
    camera.lookAt(camTarget);
  } else {
    camera.up.set(0, 1, 0).applyQuaternion(upQuat);
    // Back-distance lies along the roll axis (snappy pitch/yaw follow is
    // unaffected by roll); the vertical lift uses the lazy roll orientation.
    camOffset.set(0, 0, (11 + speedK * 10) * camZoomSmooth).applyQuaternion(smoothQuat);
    camera.position.copy(smoothPos).sub(origin).add(camOffset);
    camOffset
      .set(0, (2.8 + speedK * 1.5) * Math.max(0.55, camZoomSmooth * 0.9), 0)
      .applyQuaternion(upQuat);
    camera.position.add(camOffset);
    camTarget
      .set(0, 0.6, -30)
      .applyQuaternion(smoothQuat)
      .add(smoothPos)
      .sub(origin);
    camera.lookAt(camTarget);
  }

  const targetFov = 72 + speedK * 14;
  if (Math.abs(camera.fov - targetFov) > 0.1) {
    camera.fov += (targetFov - camera.fov) * Math.min(1, 5 * dt);
    camera.updateProjectionMatrix();
  }
}

// --- Planet arrival banner: same top text as entering a system ---
/** Planet whose banner is currently armed-off (shown; re-arms on leaving). */
let bannerPlanetId: string | null = null;

/** Proximity band that counts as "at" a planet (atmosphere, or near-space). */
function planetBand(p: Planet): number {
  return p.def.atmosphere ? p.def.atmosphere.height * 3 : p.def.radius * 0.15;
}

function updatePlanetBanner(focus: THREE.Vector3): void {
  if (mode === "jump" || !launched) return;
  const near = system.nearestPlanet(focus);
  if (!near) return;
  const band = planetBand(near.planet);
  if (near.surfaceDist < band) {
    if (bannerPlanetId !== near.planet.def.id) {
      bannerPlanetId = near.planet.def.id;
      hud.showSystemBanner(near.planet.def.name);
    }
  } else if (near.surfaceDist > band * 1.5) {
    bannerPlanetId = null; // left the band: re-arm for the next visit
  }
}

// --- Settlement banner: walking (or hovering low) into a town announces it ---
let bannerTownSeed: string | null = null;

function updateTownBanner(focus: THREE.Vector3): void {
  if (mode === "jump" || !launched || !settlementRenderer) return;
  const near = settlementRenderer.nearest(focus, 40);
  if (near) {
    if (bannerTownSeed !== near.settlement.id) {
      bannerTownSeed = near.settlement.id;
      hud.showSystemBanner(`${near.settlement.name} — ${settlementTierName(near.settlement.tier)}`);
    }
  } else {
    bannerTownSeed = null; // left the apron: re-arm for the next visit
  }
}

// --- Atmosphere tint: ease fog + clear color while inside an atmosphere ---
const spaceColor = new THREE.Color(0x000005);
const atmoColor = new THREE.Color();
const _skyHSL = { h: 0, s: 0, l: 0 };
/** Zenith/clear-sky background — the darker base the atmosphere shell thins toward.
 *  Decoupled from fog.color (the brighter horizon haze) so distant ground can fade to
 *  the sky's HORIZON colour, not the zenith — which is what closes the horizon seam. */
const bgColor = new THREE.Color();
/** The atmosphere shell's own day-scaled colour (mirrors its `mix(0.3,1,day)`), used as
 *  the bright endpoint of the horizon-haze fog so ground matches sky at any time of day. */
const hazeColor = new THREE.Color();
const sunsetColor = new THREE.Color(); // scratch: this planet's golden-hour tint

// --- Lead pip: the guns' true firing solution, projected on screen.
// While moving, the barrels lead the reticle to compensate inherited
// velocity — the pip drifts off-center to show it (modern lead indicator).
const pipWorld = new THREE.Vector3();
let pipX = 0;
let pipY = 0;
let pipHasPrev = false;
let pipDtRef = 1 / 120;
function updateLeadPip(dt: number): void {
  pipDtRef = Math.min(dt, 0.05);
  if (
    mode !== "fly" ||
    ship.pulse || // guns locked under pulse: no reticle to promise with
    chargeT > 0 ||
    uiOverlayActive() ||
    menu.visible ||
    built.weaponMounts.length === 0
  ) {
    hud.setLeadPip(null);
    pipHasPrev = false;
    return;
  }
  // The pip marks the convergence point on THE reticle ray — the exact
  // spot every bolt's line passes through. Project with THIS frame's
  // camera matrices (matrixWorldInverse otherwise lags a frame = stutter).
  const targetDistance = shipWeaponAimRay(shipFireCameraOrigin, camDirV);
  pipWorld.copy(shipFireCameraOrigin).addScaledVector(camDirV, targetDistance).sub(origin);
  camera.updateMatrixWorld();
  pipWorld.project(camera);
  if (pipWorld.z > 1) {
    hud.setLeadPip(null); // behind the camera
    pipHasPrev = false;
    return;
  }
  const px = (pipWorld.x * 0.5 + 0.5) * window.innerWidth;
  const py = (-pipWorld.y * 0.5 + 0.5) * window.innerHeight;
  // Screen-space ease: strong enough to track turns, soft enough to glide.
  if (pipHasPrev) {
    const a = 1 - Math.exp(-26 * pipDtRef);
    pipX += (px - pipX) * a;
    pipY += (py - pipY) * a;
  } else {
    pipX = px;
    pipY = py;
    pipHasPrev = true;
  }
  hud.setLeadPip([pipX, pipY]);
}

// --- Point-and-jump: the nose resting on a neighbor star offers a course ---
let aimedStar: NeighborStar | null = null;
const aimV = new THREE.Vector3();

/** True when a planet sits on the ray from the ship toward `dir`. */
function headingOccluded(dir: readonly [number, number, number]): boolean {
  for (const p of system.planets) {
    const c = p.pose.center;
    const rx = c.x - ship.pos.x;
    const ry = c.y - ship.pos.y;
    const rz = c.z - ship.pos.z;
    const proj = rx * dir[0] + ry * dir[1] + rz * dir[2];
    if (proj <= 0) continue; // behind us
    const ox = rx - dir[0] * proj;
    const oy = ry - dir[1] * proj;
    const oz = rz - dir[2] * proj;
    if (ox * ox + oy * oy + oz * oz < p.def.radius * p.def.radius * 1.3) return true;
  }
  return false;
}

function updateStarTarget(): void {
  let found: NeighborStar | null = null;
  let foundIdx: number | null = null;
  if (mode === "fly" && !navTarget && !uiOverlayActive() && !menu.visible) {
    // The player aims with the PIP — the one reticle.
    reticleDir(aimV);
    let best = 0.9994; // ~2 degrees of arc
    for (let i = 0; i < system.neighbors.length; i++) {
      const n = system.neighbors[i];
      const dot = aimV.x * n.dir[0] + aimV.y * n.dir[1] + aimV.z * n.dir[2];
      // Collinear systems (1:0:0 behind 3:0:0) tie on direction — the
      // NEAR one is the offered course; chain jumps to reach the far one.
      if (dot > best + 1e-6 || (dot > best - 1e-6 && found !== null && n.dist < found.dist)) {
        // A star behind a planet is not a heading — fly around first.
        if (headingOccluded(n.dir)) continue;
        best = Math.max(best, dot);
        found = n;
        foundIdx = i;
      }
    }
  }
  aimedStar = found;
  // R belongs to navigation while aiming at a star (no pulse toggles).
  ship.suppressPulse = navTarget !== null || found !== null;
  system.setAimedNeighbor(foundIdx);
  hud.setStarTarget(
    found
      ? `\u25c8 ${found.name.toUpperCase()} \u2014 ${found.sx}:${found.sy}:${found.sz} \u00b7 ${found.dist.toFixed(1)} LY \u00b7 [N] SET COURSE`
      : null,
  );
}

window.addEventListener("keydown", (e) => {
  if (e.ctrlKey || e.metaKey) return; // native browser/editor shortcuts
  if (e.code !== "KeyN" || e.repeat) return;
  if (!aimedStar || mode !== "fly" || uiOverlayActive() || menu.visible) return;
  setNavTarget({
    sx: aimedStar.sx, sy: aimedStar.sy, sz: aimedStar.sz, name: aimedStar.name,
    ...(aimedStar.id.startsWith("sealed:") ? { id: aimedStar.id } : {}),
  });
});

// Double-tap R while aiming at a star: set the course right there;
// holding the second press rides straight into the hyperlight charge.
let lastPulseTap = 0;
window.addEventListener("keydown", (e) => {
  if (e.ctrlKey || e.metaKey) return; // native browser/editor shortcuts
  if (e.code !== "KeyR" || e.repeat) return;
  if (mode !== "fly" || navTarget || !aimedStar || uiOverlayActive() || menu.visible) return;
  const nowMs = performance.now();
  if (nowMs - lastPulseTap < 420) {
    setNavTarget({
      sx: aimedStar.sx, sy: aimedStar.sy, sz: aimedStar.sz, name: aimedStar.name,
      ...(aimedStar.id.startsWith("sealed:") ? { id: aimedStar.id } : {}),
    });
  }
  lastPulseTap = nowMs;
});

// Underwater screen wash: a multiply-blend DOM tint over the canvas, strength
// driven by how far the CAMERA sits below the (flat, deterministic) sea level.
const underwaterOverlay = document.createElement("div");
underwaterOverlay.style.cssText =
  "position:fixed;inset:0;pointer-events:none;z-index:6;opacity:0;mix-blend-mode:multiply;transition:opacity 0.1s linear;";
document.body.appendChild(underwaterOverlay);
const _camWorld = new THREE.Vector3();
const _waterFog = new THREE.Color();
const _cameraWater = {} as WaterColumn;
const _cameraLocal = new THREE.Vector3();

function updateAtmosphere(focus: THREE.Vector3): number {
  atmosphereFrame = undefined;
  // The character studio has a constant backdrop independent of the planet.
  if (mode === "outfit") {
    fog.color.setHex(0x101617);
    fog.density = 0;
    scene.background = fog.color;
    underwaterOverlay.style.opacity = "0";
    return 1;
  }
  const near = system.nearestPlanet(focus);
  let strength = 0;
  // Inside the warp tunnel nothing of the surrounding system shows.
  if (mode !== "jump" && mode !== "builder" && near?.planet.def.atmosphere) {
    const h = near.planet.def.atmosphere.height;
    strength = Math.max(0, Math.min(1, 1 - near.surfaceDist / (h * 3)));
    atmoColor.setRGB(...near.planet.def.atmosphere.color);
    // Bluer by day. The generated atmosphere colours bottom out around 0.35
    // saturation (universe.ts), which at noon reads as overcast rather than as
    // sky. Saturate CLIENT-SIDE: universe.ts is shared with the server and its
    // colours are part of the deterministic world, so the fix belongs here.
    //
    // Saturation only, never hue — a desert's sky is authored orange and a pull
    // toward blue would take it through green on the way. Boosting what is
    // already there makes a blue world bluer and an orange one warmer, which is
    // the same instruction in both cases: less grey.
    if (SKY.saturate !== 1) {
      atmoColor.getHSL(_skyHSL);
      // Scaled by daylight, so dusk and the night side keep their own colour.
      const satK = 1 + (SKY.saturate - 1) * system.dayFactor;
      atmoColor.setHSL(_skyHSL.h, Math.min(1, _skyHSL.s * satK), _skyHSL.l);
    }
  }
  const eased = strength * strength;
  // Golden hour: warm the RAW atmosphere colour toward this planet's sunset tint before
  // the haze/zenith derive from it — keeps the fog, sky background and the shell (which
  // warms by the same strength) all matched as the sun grazes the horizon.
  if (system.sunsetK > 0) {
    sunsetColor.setRGB(
      SUNSET_WARM[0] * 0.7 + atmoColor.r * 0.3,
      SUNSET_WARM[1] * 0.7 + atmoColor.g * 0.3,
      SUNSET_WARM[2] * 0.7 + atmoColor.b * 0.3,
    );
    atmoColor.lerp(sunsetColor, system.sunsetK);
  }
  // Night side: the atmosphere tint dies with the daylight, the sky goes
  // black, and the starfield shows through. A SOLAR ECLIPSE darkens the sky the same way —
  // the moon's shadow drops the effective daylight toward twilight, so the stars come out
  // at totality (never fully to midnight: the corona + the sky beyond the umbra still glow).
  const skyDay = system.dayFactor * (1 - system.eclipse * 0.85);
  const day = 0.04 + 0.96 * skyDay;
  // All three consumers start from the same weather-adjusted raw hue.
  if (eased > 0) weatherSkyTint(atmoColor, playerWeather);
  near?.planet.setWeatherLighting(atmoColor, system.atmosphereLights[1].direction,
    moonWeatherLight.copy(system.atmosphereLights[1].color).add(system.atmosphereLights[2].color), skyDay, 1-system.eclipse*.85);
  // atmoColor still holds the RAW atmosphere colour here. The shell tints with a 0.3
  // floor (mix(0.3,1,day)) while the zenith base uses the 0.04 floor — mirror both so the
  // fog target equals the sky's actual horizon colour at any time of day.
  hazeColor.copy(atmoColor).multiplyScalar(0.3 + 0.7 * skyDay); // = shell colour
  atmoColor.multiplyScalar(day);
  nightSkyTint(atmoColor,skyDay);
  // Background = the zenith colour the shell thins toward (unchanged from before).
  bgColor.copy(spaceColor).lerp(atmoColor, eased);
  scene.background = bgColor;
  // Fog target = the SKY'S HORIZON colour, not the zenith: the shell saturates over the
  // background at the grazing horizon (alpha ≈ 0.85·day), so distant terrain/sea fade to
  // exactly what the sky shows where they meet it. Aerial perspective — closes the seam
  // while the atmosphere stays fully coloured at the horizon (no vanishing band).
  fog.color.copy(bgColor).lerp(hazeColor, 0.85 * day);
  // Far aerial perspective remains in FogExp2; local precipitation extinction
  // belongs to the volume. Keep a stronger, coherent fallback when disabled.
  fog.density = landscapeHazeDensity(eased,skyDay,playerWeather.cloud,playerWeather.precip,
    WEATHER.volumetrics && painterlyPost.enabled && renderer.extensions.has("EXT_color_buffer_float"));
  if (dbgNoFog) fog.density = 0; // DEBUG: __sky.fog(false) isolation

  // Sky-driven ambient/hemi fill: the bounce light takes the sky's own colour (warm at
  // golden hour, blue at noon), fading to cool moonlight off-planet + at night.
  const transmission = system.applySkyLighting(hazeColor, eased, playerWeather.cloudDepth ?? 0);
  precipLight.copy(hazeColor).multiplyScalar(0.14 * skyDay + 0.008);
  for (const light of system.atmosphereLights) {
    precipLight.r += light.color.r * transmission * 0.22;
    precipLight.g += light.color.g * transmission * 0.22;
    precipLight.b += light.color.b * transmission * 0.22;
  }
  precipLight.r += 0.006; precipLight.g += 0.007; precipLight.b += 0.009;

  // The camera uses the same local freshwater column as swimming, including
  // inland basins above sea level; a 1.2 m band eases the submerged transition.
  const sp = near?.planet;
  let underK = 0;
  if (sp && (sp.def.seaRadius !== null || sp.sampler.inlandWetlands().length)) {
    _camWorld.copy(camera.position).add(origin);
    const camR = _camWorld.distanceTo(sp.pose.center);
    underK = Math.max(0, Math.min(1, ((sp.def.seaRadius??-1e9) - camR) / 1.2));
    if (seaSurface(sp.def) !== 'water' || sp.sampler.inlandWetlands().length) {
      _cameraLocal.copy(_camWorld).sub(sp.pose.center).applyQuaternion(sp.pose.invQuat).normalize();
      waterColumn(sp.sampler, sp.def, _cameraLocal.x, _cameraLocal.y, _cameraLocal.z, _cameraWater);
      underK = _cameraWater.surfDepth > 0 && camR > _cameraWater.floorR
        ? Math.max(0, Math.min(1, (_cameraWater.surfR - camR) / 1.2)) : 0;
    }
    if (underK > 0) {
      const c = sp.def.seaColor;
      _waterFog.setRGB(c[0] * 0.35, c[1] * 0.46, c[2] * 0.6).multiplyScalar(0.45 + 0.55 * day);
      fog.color.lerp(_waterFog, underK);
      fog.density = Math.max(fog.density, underK * 0.06); // dense murk underwater
      scene.background = fog.color;
      underwaterOverlay.style.background = `rgb(${(_waterFog.r * 255) | 0},${(_waterFog.g * 255) | 0},${(_waterFog.b * 255) | 0})`;
    }
  }
  underwaterOverlay.style.opacity = String(Math.min(0.6, underK * 0.6));
  if (near?.planet.weatherField && eased > 0.001 && underK < 0.001 && !dbgNoFog) {
    atmosphereAmbient.copy(hazeColor).multiplyScalar(0.02 + skyDay * 0.98 + system.moonGlow * 0.06);
    atmosphereFrame = {
      planet: near.planet, lights: system.atmosphereLights, surface: weatherSurface,
      ambient: atmosphereAmbient, presence: eased * weatherAltitudeK * (1 - near.planet.cloudImmersion),
      mist: weatherMistAmount(playerWeather.mist ?? 0, groundWxWet, skyDay,
        atmosphereUp.copy(focus).sub(near.planet.pose.center).normalize().dot(system.atmosphereLights[0].direction)),
    };
  }
  return transmission;
}

// --- Main loop ---
/** Simulation clock (seconds): advances per fixed step, drift-corrected
 *  toward the wall clock so clients agree without jitter. */
let simTime = Date.now() / 1000;
/** DEV-only: seconds added to the pose clock so probes can scrub orbits to a chosen moment
 *  (e.g. an eclipse alignment) without touching the rest of the world. 0 in normal play. */
let devTimeOffset = 0;
/** DIAG override for the avatar body/clothing lighting floor (window.__hl.devBodyFill); null =
 *  automatic world-tracking. Lets us isolate the "glowy at night" look live. */
let bodyFillOverride: number | null = null;
/** DEV-only: when set, FREEZE all body poses at this absolute time — no orbital drift and (since
 *  the carry reads pose deltas, now zero) no carry drift either. Lets probes hold an eclipse. */
let devFrozenPoseTime: number | null = null;
let last = performance.now();
let accumulator = 0;
let hudTimer = 0;
let frames = 0;
let fpsTime = 0;
let fps = 0;
let lastRpgUiRev = -1;
let lastQuestUiRev = -1;

// RPG saves ride the rev counter: only write when something changed.
let lastRpgSavedRev = 0;
window.setInterval(() => {
  if (!launched || !authUser?.emailVerified || rpg.rev === lastRpgSavedRev) return;
  lastRpgSavedRev = rpg.rev;
  void saveRpg(rpg.health, rpg.inventory).catch(() => { });
}, 5000);

// HMR safety: if this module is hot-swapped in dev, stop the OLD frame loop so it doesn't keep
// running ALONGSIDE the new one. Two live loops double every per-frame action — including firing,
// which shows up as TWO bolts per shot (one from each loop, at slightly different avatar poses).
// The flag is checked at the top of frame() so the stale loop dies at its next tick (no re-request).
let loopAlive = true;
import.meta.hot?.dispose(() => { loopAlive = false; });

function frame(now: number): void {
  if (!loopAlive) return;
  if (!footPlanet && deathPresentation.active) resetPlayerDeath();
  if (mode !== "foot" || uiOverlayActive() || playerDyingT > 0) updateFootReadiness();
  requestAnimationFrame(frame);
  updateWeaponSelector();
  const elapsed = Math.min(0.25, (now - last) / 1000);
  updateCompanion(elapsed);
  last = now;
  dbgFrame++;
  perf.frameStart(now);

  frames++;
  fpsTime += elapsed;
  if (fpsTime >= 1) {
    fps = frames / fpsTime;
    frames = 0;
    fpsTime = 0;
  }

  perf.begin("sim");
  accumulator += elapsed;
  let steps = 0;
  let frameLandingImpact = 0;
  while (accumulator >= FIXED_DT && steps < 5) {
    character.wallRunEnabled=mode==='foot'&&!uiOverlayActive()&&playerDyingT<=0;
    character.wallRunClearance=avatar.wallRunClearance;
    // One clock to rule the frame: poses advance with the SIM, and the
    // carry below uses their exact deltas. (Gently re-synced to the wall
    // clock so all clients still agree on where the worlds are.)
    simTime += FIXED_DT;
    system.tickPoses(devFrozenPoseTime ?? simTime + devTimeOffset);
    bolts.carryBodies(system.planets); // projectiles ride the frames too
    dmgNums.carryBodies(system.planets); // floating damage numbers stay over the wound
    rpg.update(FIXED_DT); // out-of-combat regen runs in any mode
    if (vehicleSpawned && vehicleGround) {
      groundVehicle.prevPos.copy(groundVehicle.pos);
      groundVehicle.prevQuat.copy(groundVehicle.quat);
      carryGroundVehicle();
      if (mode !== "vehicle") groundVehicle.coast(FIXED_DT, vehicleGround);
    }
    if (mode === "fly" && !uiOverlayActive()) {
      ship.prevPos.copy(ship.pos);
      ship.prevQuat.copy(ship.quat);
      carryWithBody(FIXED_DT, ship.pos, ship.vel, ship.quat, shipCarryVel);
      trails.carryFrame(lastCarry.prevCenter, lastCarry.q, lastCarry.delta, lastCarry.k);
      ship.update(FIXED_DT, input, system);
      if (input.isButtonDown(0)) tryFire(1);
      if (input.isButtonDown(2)) tryFire(2);

      // Hyperlight charge: hold R while aligned with the heading.
      // Drifting off-axis drains the charge; steering well away aborts.
      if (navTarget) {
        // Keep the corridor planet-free while not yet charging (the ship
        // moves, so the clearance solution can change).
        if (chargeT === 0) {
          navHeading = clearHeading(navDirOf(navTarget));
        }
        const dir = navHeading ?? navDirOf(navTarget);
        fwdV.set(0, 0, -1).applyQuaternion(ship.quat);
        const align = fwdV.dot(dir);
        if (!headingAligned && ship.headingDir === null) {
          headingAligned = true;
          hud.showToast("ALIGNED — HOLD R TO CHARGE HYPERLIGHT", 3500);
        }
        if (headingAligned && align < ALIGN_ABORT) {
          navTarget = null;
          navHeading = null;
          chargeT = 0;
          ship.suppressPulse = false;
          hud.showToast("JUMP ABORTED — OFF HEADING", 3000);
        } else if (input.isDown("KeyR") && align > ALIGN_CHARGE) {
          // Only UNCHARTED jumps burn a hyperlight cell: pushing the
          // frontier costs fuel; the charted lanes run free.
          const tgtKnown = discovered.has(navTargetId(navTarget));
          if (!tgtKnown && countItem(rpg.inventory, "fuel-cell") < 1) {
            const nowMs = performance.now();
            if (nowMs - lastFuelWarn > 2500) {
              lastFuelWarn = nowMs;
              hud.showToast("NO HYPERLIGHT CELL — MINE VOLATITE, CRAFT IN THE FABRICATOR", 2600);
            }
            chargeT = 0;
          } else {
            chargeT += FIXED_DT;
            if (chargeT >= CHARGE_TIME) beginJump(navTarget);
          }
        } else {
          chargeT = Math.max(0, chargeT - FIXED_DT * 2);
          // Holding R but the cleared heading drifted off the nose:
          // re-slew onto it instead of silently refusing to charge.
          if (input.isDown("KeyR") && headingAligned && align < ALIGN_CHARGE) {
            ship.headingDir = dir.clone();
            headingAligned = false;
          }
        }
      } else {
        chargeT = 0;
      }

      // Docking: highest-priority interact when lined up on a pad.
      const padNear = station.def
        ? station.nearestPad(ship.pos, worldTime())
        : null;
      const canDock =
        padNear !== null && padNear.dist < DOCK_RANGE && Math.abs(ship.speed) < DOCK_SPEED;
      // Contextual F composes with the G-hold mining line.
      const mineTxt = tickMining(FIXED_DT, false);
      const hole = wormholes.nearest(ship.pos);
      // A party member who just warped is followable for a short window.
      const partyFollow = partyModel?.follow(performance.now(), system.def.id) ?? null;
      if (canDock) {
        hud.setInteract(`F (HOLD) — DOCK AT ${(station.def?.name ?? "STATION").toUpperCase()}`);
        if (holdAct.tick("dock", input.isDown("KeyF"), FIXED_DT)) dockAt(padNear!.i);
      } else if (hole) {
        hud.setInteract(`F — JOIN JUMP TO ${hole.destName.toUpperCase()}`);
        if (input.wasPressed("KeyF")) {
          const ref = parseSystemRef(hole.to);
          // Only lattice/sealed destinations carry coords: a home pocket or a
          // cache instance (non-lattice instance id; Phase 4 renderer) can't be
          // joined through a wormhole.
          if (ref && ref.kind !== "home" && ref.kind !== "cache") beginJump({
            sx: ref.c[0], sy: ref.c[1], sz: ref.c[2], name: hole.destName,
            ...(ref.kind === "sealed" ? { id: hole.to } : {}),
          }, hole.pos);
        }
      } else if (canDisembark()) {
        hud.setInteract(mineTxt ? `F (HOLD) — DISEMBARK · ${mineTxt}` : "F (HOLD) — DISEMBARK");
        if (holdAct.tick("disembark", input.isDown("KeyF"), FIXED_DT)) disembark();
      } else if (partyFollow) {
        // Follow-jump: LOWEST priority on the F chain, so docking, wormholes
        // and disembarking always win the key when they're offered.
        hud.setInteract(`F — FOLLOW ${partyFollow.name.toUpperCase()}`);
        if (input.wasPressed("KeyF")) followPartyJump(partyFollow.to);
      } else {
        hud.setInteract(mineTxt);
      }
    } else if (mode === "vehicle" && footPlanet && vehicleGround) {
      // The PARKED ship must keep riding the planet frame while you drive: the
      // autopilot's landed servo holds a planet-LOCAL anchor and derives world
      // pose from the live pose each tick. Without this tick the hull freezes at
      // fixed WORLD coords and the planet's spin visibly carries the ground away
      // from it within seconds of boarding (user-reported as the ship "moving
      // away very quickly").
      autopilot?.update(FIXED_DT, character.pos);
      if (vehicleMountTransition) {
        // DEV: hold an exit at one clip time so it can be inspected. Real time
        // runs far ahead of a headless screenshot loop, so without this a probe
        // only ever photographs the settled result of a 0.44 s leap.
        const scrubbing = devExitScrub !== null && vehicleMountTransition.phase === "dismounting";
        // The driver owns movement from the first boarding frame. During
        // dismount the vehicle only coasts, while its live sockets continue to
        // carry the rider until the detach marker.
        if (vehicleMountTransition.phase === "mounting") {
          groundVehicle.update(FIXED_DT, input, vehicleGround);
        } else if (!scrubbing) {
          // Do not engage the parking brake while stepping off: preserve the
          // chassis trajectory that the rider and eventual foot velocity inherit.
          groundVehicle.coast(FIXED_DT, vehicleGround, false);
        }
        const previousMountTime = vehicleMountTransition.elapsed === 0
          ? -1e-6
          : vehicleMountTransition.elapsed;
        vehicleMountTransition.elapsed = scrubbing
          ? Math.min(vehicleMountTransition.duration, devExitScrub!)
          : Math.min(vehicleMountTransition.duration, vehicleMountTransition.elapsed + FIXED_DT);
        // An exit's detach marker can END the transition here, so nothing below
        // may assume it still exists. A scrubbed exit never detaches — crossing
        // the marker would hand control over and destroy what we are inspecting.
        const ended = !scrubbing
          && dispatchVehicleMountEvents(previousMountTime, vehicleMountTransition.elapsed);
        hud.setInteract(null);
        if (!scrubbing && !ended && vehicleMountTransition.elapsed >= vehicleMountTransition.duration) {
          if (vehicleMountTransition.phase === "dismounting") {
            // No authored detach marker (procedural fallback, or a clip whose
            // marker sits on the last frame): the end of the clip IS the detach.
            completeGroundVehicleExit();
          } else {
            if (!groundVehicle.occupied) groundVehicle.occupyReservedSeat();
            character.pos.copy(groundVehicle.pos);
            character.quat.copy(groundVehicle.quat);
            vehicleMountTransition = null;
            vehicleMountPlayback = null;
            hud.showToast("DRIVE — W/S THROTTLE · RMB DRIFT · SPACE GLIDE · SHIFT BOOST", 4200);
          }
        }
      } else {
        groundVehicle.update(FIXED_DT, companion.transition.active ? limpInput : input, vehicleGround);
        if (!companion.transition.active && input.isButtonDown(0)) tryVehicleFire(1);
        character.pos.copy(groundVehicle.pos);
        character.quat.copy(groundVehicle.quat);
        if (!companion.transition.active && input.wasPressed("KeyF")) exitGroundVehicle();
        // Tell the player which exit the key is about to give them: at speed F
        // stops being a step-off and becomes a leap they have to land.
        else hud.setInteract(groundVehicleWouldBail() ? "F — BAIL OUT" : "F — EXIT SPEEDER");
      }
    } else if (mode === "foot" && footPlanet) {
      character.heldItemId = rpg.heldWeapon()?.id ?? null;
      // The autopilot flies regardless; under overlays the character goes
      // LIMP — physics continues (gravity, momentum, hazards), inputs don't.
      character.prevPos.copy(character.pos);
      character.prevQuat.copy(character.quat);
      carryWithBody(FIXED_DT, character.pos, character.vel, null, charCarryVel);
      autopilot?.update(FIXED_DT, character.pos);
      if (uiOverlayActive() || playerDyingT > 0) {
        character.update(FIXED_DT, limpInput, footPlanet);
        const hzL = hazardOf(footPlanet);
        if (hzL && rpg.shieldLeft(performance.now()) <= 0) {
          takeHazard(hzL.rate * FIXED_DT);
        }
        hud.setInteract(null);
      } else {
        // Real input — a hard landing no longer slows/staggers the player at all. The avatar's
        // anticipated landing ANIMATION (scaled by ground speed + fall impact) is the whole feel; a
        // hard fall just costs a SMALL bit of hp.
        character.update(FIXED_DT, input, footPlanet);
        updateSummon(FIXED_DT);
        maybePromptSummon();
        if (character.impactSpeed > SAFE_LANDING) {
          takeFall((character.impactSpeed - SAFE_LANDING) * FALL_DMG_RATE);
        }
        // The world itself bites: archetype drain unless an Aegis Pack
        // (or your ship's hull) stands between you and it.
        const hz = hazardOf(footPlanet);
        if (hz && rpg.shieldLeft(performance.now()) <= 0) {
          takeHazard(hz.rate * FIXED_DT);
        }
        // BUILD MODE disarms the whole combat layer: no Q/E mode cycling, no
        // construct manifested (unarmed visuals), no fire/swing/guard — RMB stays
        // the camera zoom (character.aiming is mode-independent) and LMB places.
        const building = homesteadBuild?.active === true || hsBeaconMode;
        if(!building)weaponSelector.handleInput(input);
        // Build mode HOLDS the over-the-shoulder aim framing (camera + strafe-
        // lock) without RMB — the precision view for placing pieces.
        character.forceAim = building;
        // ARMED / UNARMED (user call 2026-08-02): ONE equipped weapon — inventory.primary,
        // set on the inventory screen, server-mirrored via the "equip" intent — drawn and
        // stowed with Z. The Q/E construct modes (cannon/shield/blade) are PARKED, and so is
        // the RMB-unarmed system: unarmed RMB is just the over-the-shoulder look (camera +
        // strafe-lock + the plain UAL walk set) — no guard, no parry, no punches.
        // V — the summon key, two tiers on one binding (vehicle-tiers V0+V1):
        //   TAP  = summon / stow the active ground chassis (free, instant-ish)
        //   HOLD = summon the SHIP to come land beside you (5-minute cooldown)
        // Nothing deploys on its own any more. V is otherwise only bound inside
        // the homestead builder, hence the same `!building` guard Z uses.
        if (!building) {
          if (input.wasPressed("KeyV")) {
            keyVHeld = 0;
            keyVShipFired = false;
          }
          if (input.isDown("KeyV")) {
            keyVHeld += FIXED_DT;
            if (!keyVShipFired && keyVHeld >= SHIP_SUMMON_HOLD_S) {
              keyVShipFired = true;
              summonShip();
            }
          } else if (keyVHeld > 0) {
            if (!keyVShipFired) {
              audio.sfx("ui.select");
              toggleSummonActive();
            }
            keyVHeld = 0;
            keyVShipFired = false;
          }
        }
        if (!building && input.wasPressed("KeyZ") && stowSwapTo === null) {
          const eq = rpg.equippedWeapon();
          if (!eq) hud.showToast("NO WEAPON EQUIPPED", 1200);
          else {
            audio.sfx("ui.select");
            actionSys.cancelPendingPunch(); // a draw/stow cancels the swing — no punch goes out
            if (eq.weapon && !eq.weapon.construct) {
              // Real guns play the over-the-shoulder sheathe/draw gesture (the 2H set's
              // gs_sheathe/gs_unsheathe pair); the actual hand↔back swap lands mid-clip.
              stowSwapTo = !rpg.armed;
              const timing=weaponStats(rpg.heldWeapon()?.weapon??{});
              stowSwapT = (stowSwapTo?timing.deployTime:timing.stowTime)*.5;
              avatar.playStowGesture(!stowSwapTo);
            } else {
              rpg.armed = !rpg.armed; // constructs conjure/dissolve — instant
              hud.showToast(rpg.armed ? eq.name.toUpperCase() : "STOWED", 1200);
            }
          }
        }
        if (stowSwapTo !== null && (stowSwapT -= FIXED_DT) <= 0) {
          rpg.armed = stowSwapTo;
          stowSwapTo = null;
          hud.showToast(rpg.armed ? (rpg.equippedWeapon()?.name.toUpperCase() ?? "ARMED") : "STOWED", 1200);
        }
        if (rpg.armed && !rpg.equippedWeapon()) rpg.armed = false; // unequipped from the bag
        // Full flight (glide/dive) shows no weapon — the flight clips can't hold one, and
        // the dissolve-in on landing is the "arm yourself again" beat.
        const inFlight = character.jet === "glide" || character.jet === "dive";
        // The HELD weapon (equipped + drawn) drives everything: a gun aims (RMB) + fires
        // (LMB); a melee weapon focuses (RMB) + swings (LMB); null = unarmed.
        const held = building ? null : rpg.heldWeapon();
        const isGun = !!held?.weapon && held.weapon.class === "main";
        const isMeleeWeapon = !!held?.weapon?.melee;
        const md = character.moveDirLocal();
        // The shield construct is parked with the modes: nothing soaks, nothing guards.
        shieldAbsorbing = false;
        combatBlocking = false;
        character.combatStance = false;
        // ⚠⚠ `evadeReady` is a PER-TICK OUTPUT of the combat layer — reset it here with the other
        // per-tick flags so a stale `true` can never survive a mode/weapon change. Only the action
        // layer and the greatsword layer write it, and neither is guaranteed to run every tick in
        // every mode; the combat lab latched it exactly that way (a directional Space rolled with
        // a rifle for the rest of the session after one aimed blade stint, user 2026-08-03).
        character.evadeReady = false;
        // The controller caps unarmed RMB to walking unless Shift selects sprint.
        // The avatar keeps the directional gait and additive gaze in both gears.
        // Unarmed inherits the shield mode's old role: the safe, fastest-recharging state.
        character.regenMul = held ? 1 : rpg.stats.regenShieldMul;
        // devHeldWeapon: DEV override forcing a catalog weapon regardless of the inventory.
        const handId = building || inFlight ? null : devHeldWeapon ?? held?.id ?? null;
        avatar.setWeapon(handId);
        // ADS scope level rides the held weapon (WeaponDef.adsZoom) — a marksman
        // scope zooms far deeper than a carbine's shoulder-tuck.
        footCam.adsZoom = held?.weapon?.adsZoom ?? 1.3;
        // BACK-STOW (2026-08-02): the equipped gun rides the back whenever it is not in hand
        // (stowed with Z, building, in flight). Constructs conjure/dissolve — nothing to stow.
        const eqW = rpg.equippedWeapon();
        avatar.setStowedWeapon(
          !handId && eqW?.weapon && !eqW.weapon.construct && eqW.weapon.class === "main" ? eqW.id : null,
        );
        avatar.setAim(isGun && footAimAllowed(), character.pitch);
        // The SWORD now drives the armed sd_ locomotion stance (via setWeapon) + RMB strafe-lock (via
        // character.aiming, same as a gun) + the sd_attack swing — so the legacy procedural blade guard
        // is retired (setMelee off). setAim stays gated `&& isGun`, so no gun-aim layer fires on the sword.
        // setSwordFocus relays the RMB hold separately: it flips the sword's forward run between the unarmed
        // run (free movement) and the SSH directional jog (focus strafe) without touching the gun-aim layer.
        // Attack stance and blade formation last only for the right-click hold.
        avatar.setSwordFocus(isMeleeWeapon && input.isButtonDown(2) && character.aiming);
        // Enter immediately; release returns to travel without a parry or delayed attack.
        character.aimDelayS = 0;
        avatar.setCombat(false);
        // Unarmed + RMB rides the OCTAGONAL walk set (om_walk_* 8-way over the neutral
        // idle — the walkMode path with blocking off), like every other octagonal system.
        avatar.setWalkMode(!held && character.aiming);
        avatar.setBlocking(false);      // the shield guard is parked with the modes
        avatar.setJet(character.jet);
        avatar.setFlightPower(character.flightPower);
        avatar.setFlightLaunchRemaining(character.flightLaunchRemaining);avatar.setDivePreparation(character.divePreparation);
        avatar.setFlightApproach(character.flightApproach);
        avatar.setWallRun(character.wallRunState,character.wallRunTime);
        avatar.setLedgeClimb(character.ledgeClimbState,character.ledgeClimbTime,character.ledgeAnchor);
        character.raveFloat = avatar.isAirRave(); // mid-air-string descent falls under eased gravity
        avatar.setBoost(character.boostK); // boost-run: slow the leg cadence as the boost ramps in
        avatar.setJumpChain(character.jumpChain);
        // SUPER-JUMP takeoff: while charging a vertical super-jump (hold Space, NOT moving), hold the SuperHero
        // takeoff windup; the launch bit fires on release. Gated on stillness per the design.
        avatar.setSuperJumpCharge(character.superJumpCharging() && Math.hypot(md.fwd, md.right) < 0.2);
        avatar.setGroundClearance(character.groundClearance); // feet-above-ground → anticipate the big-leap land
        if (character.bigLeapPending) { avatar.flagBigLeap(); character.bigLeapPending = false; } // → sprint-start→loop→sprint-land
        if (character.doubleJumpPending) { avatar.flagDoubleJump(); character.doubleJumpPending = false; } // UAL DoubleJump one-shot
        if (character.diveLandPending) { avatar.flagDiveLand(); character.diveLandPending = false; } // dive touchdown → SuperHero flight-land
        avatar.setReload(null); // no magazines to reload — the modes spend ENERGY
        updateFootReadiness();
        const cycleDef=isGun?rpg.primaryWeapon():null;
        if(cycleWeapon!==cycleDef?.id){weaponCycle.reset();cycleWeapon=cycleDef?.id??null;}
        weaponCycle.update(FIXED_DT,weaponStats(cycleDef?.weapon??{}),input.isButtonDown(0),footAimAllowed(),input.wasButtonPressed(0));
        avatar.setWeaponTrigger(isGun&&input.isButtonDown(0)&&footAimAllowed(),weaponCycle.charge);
        if (isGun && character.aiming && (input.isButtonDown(0)||input.wasButtonPressed(0))) tryFootFire();
        // Hold RMB to form the blade and enable attacks. The shared action layer
        // buffers assembly-time clicks and cancels the entire stint on release.
        // ── THE ACTION LAYER (combat/actionSystem.ts): LMB routing (chain/launcher/
        // aerial/stinger tiers), the stinger machine, High-Time jump-follow, the input
        // buffer, dodge hand-offs, speed/stance feedback and the soft lock — ONE system,
        // shared verbatim with the combat lab. This block used to be ~260 hand-mirrored
        // lines; the history (and the design commentary) lives in the module.
        actionSys.tick(FIXED_DT);
        // F belongs to the LOCK while focused — the interact rungs below stand down.
        const fFree = actionSys.focusFree;
        // F IS THE INTERACT (restored 2026-09-12, user call — the 2026-08-02
        // "RMB look + click" interact is retired): a press opens panels, a hold
        // boards/enters. LMB is a punch everywhere again, so no rung has to
        // cancel a buffered punch. The homestead MANAGE rung still targets via
        // the unarmed RMB look ray (observeMode) but fires on F like the rest.
        const observeMode = !held && character.aiming && !building && !inFlight;
        const iPress = fFree && input.wasPressed("KeyF");
        const iHold = fFree && input.isDown("KeyF");
        const shipDist = character.pos.distanceTo(ship.pos);
        const mineTxtF = tickMining(FIXED_DT, true);
        // STICKY talk focus (2026-08-02 "head-look flickers at the player"):
        // nearest() flapping between two candidates — or across the range edge
        // — retargeted the head glance every frame. Hold the current target
        // until a different pick has persisted ~0.35 s.
        const talkCand = npcLife?.nearest(character.pos, 4.5) ?? null;
        const homesteadInteract = observeMode && footPlanet
          ? homesteadResidentLife?.observeHit(footPlanet.def.id, _obsOrigin, _obsDir, 4.5) ?? null
          : null;
        const homesteadBeacon = homesteadInteract
          ? hsMine.find((beacon) => beacon.id === homesteadInteract.beaconId) ?? null
          : null;
        if (talkCand?.npc.id === talkSticky?.npc.id) {
          talkSticky = talkCand;
          talkStickyMissS = 0;
        } else {
          talkStickyMissS += FIXED_DT;
          if (talkSticky === null || talkStickyMissS > 0.35) {
            talkSticky = talkCand;
            talkStickyMissS = 0;
          }
        }
        const talkable = talkSticky;
        // Talk presence: an open dialog HOLDS its persona (they stop and
        // face the player); otherwise the F-prompt target glances over.
        npcLife?.setFocus(
          dialogPanel.visible ? talkingNpcId : (talkable?.npc.id ?? null),
          dialogPanel.visible,
          character.pos,
        );
        // The plaza quest-board kiosk sits between the ship-board and the
        // talk-NPC rungs: within 3.5 m of a village+ board → open the panel.
        const boardHit = nearestQuestBoard();
        const atBoard = !!boardHit && boardHit.pos.distanceTo(character.pos) < 3.5;
        const vehicleDist = vehicleSpawned ? character.pos.distanceTo(groundVehicle.pos) : Infinity;
        // CACHES take the TOP rung: their range (6 m) is the tightest on the ladder, so
        // standing in it is unambiguous, and a speeder parked at the aperture you rode
        // to must not out-vote the aperture.
        const cacheAct = cacheInteract();
        if (cacheAct) {
          hud.setInteract(cacheAct.text);
          if (!iHold) cacheFGuard = false;
          if (holdAct.tick(cacheAct.key, iHold && !cacheFGuard, FIXED_DT)) {
            cacheFGuard = true;
            cacheAct.run();
          }
        } else if (vehicleDist < vehicleBoardRange()) {
          hud.setInteract(`F (HOLD) — BOARD ${groundVehicle.def.name}`);
          if (holdAct.tick("board-speeder", iHold, FIXED_DT)) boardGroundVehicle();
        } else if (autopilot?.phase === "landed" && shipDist < BOARD_RANGE) {
          hud.setInteract(mineTxtF ? `F (HOLD) — BOARD SHIP · ${mineTxtF}` : "F (HOLD) — BOARD SHIP");
          if (holdAct.tick("board", iHold, FIXED_DT)) boardShip();
        } else if (autopilot?.phase === "callback") {
          hud.setInteract(
            `SHIP INBOUND — ${shipDist > 1000 ? `${(shipDist / 1000).toFixed(1)} KM` : `${Math.round(shipDist)} M`
            }`,
          );
        } else if ((lookPilot = lookedAtPlayer()) !== null) {
          // Face-to-face menu (v49): trade / whisper / party, in person.
          hud.setInteract(`F — ${lookPilot.name.toUpperCase()}`);
          if (iPress) {
            playerPanel.open(lookPilot);
            if (document.pointerLockElement === canvas) document.exitPointerLock();
          }
        } else if (atBoard && boardHit) {
          hud.setInteract("F — QUEST BOARD");
          if (iPress) {
            questBoardPanel.open(boardHit.settlement, boardHit.planet.def, boardHit.roster, questEpoch(net?.serverNow() ?? Date.now() / 1000));
            if (document.pointerLockElement === canvas) document.exitPointerLock();
          }
        } else if (homesteadInteract && homesteadBeacon) {
          hud.setInteract(`F — MANAGE ${homesteadInteract.title.split(" ")[0].toUpperCase()}'S SETTLEMENT`);
          if (iPress) {
            openClaimPanel(homesteadBeacon, true);
            if (document.pointerLockElement === canvas) document.exitPointerLock();
          }
        } else if (talkable) {
          hud.setInteract(`F — TALK TO ${talkable.npc.name.split(" ")[0].toUpperCase()}`);
          if (iPress) {
            talkingNpcId = talkable.npc.id;
            dialogPanel.open(talkable.npc, talkable.settlementName, talkable.dayIndex, storyState);
            dialogPanel.setQuestActions(questDialogActions(talkable.npc.id));
            net?.sendTalk(talkable.npc.id);
            if (document.pointerLockElement === canvas) document.exitPointerLock();
          }
        } else {
          hud.setInteract(mineTxtF);
        }
      }
      // The COMBAT ACTION clock + PvP punch close + High-Time hold + the SWEPT BLADE
      // contact — the action layer's un-gated pass (a swing in flight keeps sweeping even
      // with the map open, exactly as before; see combat/actionSystem.ts contactTick).
      actionSys.contactTick(FIXED_DT);
    } else if (mode === "docked") {
      ship.prevPos.copy(ship.pos);
      ship.prevQuat.copy(ship.quat);
      const w = dockedPad !== null ? station.pad(dockedPad, worldTime()) : null;
      if (w) {
        ship.pos.copy(w.pos).addScaledVector(w.up, 1.6);
        dockLook.lookAt(ZERO_VEL, dockNose.copy(w.nose).negate(), w.up);
        ship.quat.setFromRotationMatrix(dockLook);
      }
      ship.vel.set(0, 0, 0);
      ship.speed = 0;
      shipCarryVel.set(0, 0, 0);
      chargeT = 0;
      hud.setInteract(null);
    } else if (mode === "intro" && returnMode === "foot" && footPlanet) {
      // A surface spawn is established before assets finish loading. Keep it in
      // the body's co-rotating frame while the loader is open; otherwise the
      // planet moves away beneath a character frozen in system space.
      character.prevPos.copy(character.pos);
      character.prevQuat.copy(character.quat);
      carryWithBody(FIXED_DT, character.pos, character.vel, null);
      autopilot?.update(FIXED_DT, character.pos);
      character.update(FIXED_DT, limpInput, footPlanet);
      hud.setInteract(null);
    } else if (mode === "cutscene" && footPlanet) {
      // The homecoming is authored in the HOMETOWN's frame — and that frame keeps
      // moving: the planet spins and orbits while the assets load and the timeline
      // plays. A pilot who doesn't ride the carry stands still in system space while
      // their town sails away, so the shot drifts off the town and the intro hands
      // back control kilometres downrange (far enough, on a slow load, to be left in
      // vacuum). Ride the frame, limp — like any overlay on foot, minus the hazards:
      // nothing may hurt the player during their own cinematic.
      character.prevPos.copy(character.pos);
      character.prevQuat.copy(character.quat);
      carryWithBody(FIXED_DT, character.pos, character.vel, null);
      autopilot?.update(FIXED_DT, character.pos);
      character.update(FIXED_DT, limpInput, footPlanet);
      hud.setInteract(null);
    } else if (mode === "menu" || (mode === "fly" && uiOverlayActive())) {
      // Menus never pause: in flight the ship coasts limp; on foot the body
      // keeps obeying gravity (and the planet keeps being hostile).
      if (returnMode === "fly") {
        ship.prevPos.copy(ship.pos);
        ship.prevQuat.copy(ship.quat);
        carryWithBody(FIXED_DT, ship.pos, ship.vel, ship.quat);
        ship.coast(FIXED_DT, system);
      } else if (returnMode === "foot") {
        character.prevPos.copy(character.pos);
        character.prevQuat.copy(character.quat);
        if (footPlanet) carryWithBody(FIXED_DT, character.pos, character.vel, null);
        autopilot?.update(FIXED_DT, character.pos);
        if (footPlanet) {
          character.update(FIXED_DT, limpInput, footPlanet);
          const hzM = hazardOf(footPlanet);
          if (hzM && rpg.shieldLeft(performance.now()) <= 0) {
            takeHazard(hzM.rate * FIXED_DT);
          }
        }
      } else if (returnMode === "vehicle") {
        // Menu open while driving: the chassis idles via the unconditional
        // parked-coast block above, but the PARKED ship still has to ride the
        // planet frame — same frame bug as the vehicle sim branch, same fix.
        autopilot?.update(FIXED_DT, character.pos);
      }
      chargeT = Math.max(0, chargeT - FIXED_DT * 2);
      hud.setInteract(null);
    } else {
      hud.setInteract(null);
    }
    // One bar, one writer: a running F-hold, else the hyperlight charge.
    // A hold whose prompt vanished this tick (walked out of range, mode
    // change) resets instead of freezing mid-bar.
    if (!holdAct.touched) holdAct.reset();
    holdAct.touched = false;
    hud.setHold(holdAct.frac() ?? (chargeT > 0 ? Math.min(1, chargeT / CHARGE_TIME) : null));
    // Homestead build/abandon consume EDGE inputs (wasPressed/wasButtonPressed),
    // so they must run inside the sim tick BEFORE endTick clears them — reading
    // them from the render section sees only already-cleared state.
    driveHomesteadBuild();
    driveBeaconPlace();
    driveBeaconAbandon(FIXED_DT);
    // impactSpeed is a one-tick pulse; preserve it when multiple physics ticks
    // run before this frame's audio, so a hard landing doesn't become a soft one.
    if (mode === 'foot') frameLandingImpact = Math.max(frameLandingImpact, character.impactSpeed);
    if(mode!=='foot'||!footPlanet||uiOverlayActive()||playerDyingT>0){
      weaponCycle.update(FIXED_DT,weaponStats(rpg.primaryWeapon()?.weapon??{}),false,false);
      avatar.setWeaponTrigger(false,weaponCycle.charge);
    }
    input.endTick();
    accumulator -= FIXED_DT;
    steps++;
  }
  if (steps === 5) accumulator = 0; // spiral-of-death guard
  perf.end("sim");
  // Gentle leash: prefer the SERVER's clock (shared by every client and by
  // server-side pose validation); wall clock is the offline fallback.
  // A LARGE gap (hidden tab paused the loop, or the first server sync)
  // hard-snaps instead — minutes of 0.002/frame catch-up left this client's
  // poses visibly out of sync with the room (and with the server's claim
  // validation). The carry keeps local entities glued through the snap.
  {
    const clockTarget = net?.serverNow() ?? Date.now() / 1000;
    if (Math.abs(clockTarget - simTime) > 0.5) simTime = clockTarget;
    else simTime += (clockTarget - simTime) * 0.002;
  }

  // Warp tunnel ride: the ship barrels through hyperspace, chase camera in
  // tow, with mouse room to drift around the tube. Midway, the destination
  // loads in hidden behind the walls; the tunnel then dissolves around the
  // parked ship to reveal the new system. Duration is theatrical.
  if (mode === "jump" && jump) {
    // CLAMPED cinematic clock (same rule as the reveal): the switchSystem
    // frame is hundreds of ms — unclamped, the ride/exhaust/drift all
    // lurch across it in one step, right as the nameplate appears.
    const jdt = Math.min(elapsed, 1 / 30);
    jump.t += jdt;
    if (!jump.arrived) {
      // Departure: the old system's planets ZIP PAST — swept backward along
      // the travel axis with accelerating speed and a light-speed smear
      // (elongation), instead of blinking off. The heading is pre-cleared
      // of planets, so they streak by beside the ship, never through it.
      if (!jump.departed) {
        const dk = Math.min(1, jump.t / 0.7);
        if (dk >= 1) {
          jump.departed = true;
          system.setBodiesVisible(false); // tunnel is now the loading screen
          station.setVisible(false);
          system.setDepartureWarp(null, 0);
        } else {
          system.setDepartureWarp(jump.dir, dk);
        }
      }
      jump.speed += (9000 - jump.speed) * (1 - Math.exp(-1.4 * jdt));

      if (jump.t >= ARRIVE_T) {
        jump.arrived = true;
        // Orientation is preserved through arrival — the destination planet
        // is placed directly on the nose, so nothing swings as the tunnel
        // dissolves (camera, streak axis, and hull all stay put).
        switchSystem(navTargetId(jump.target), jump.quat);
        // The destination loads INVISIBLE: nothing of the new system — sun
        // included — shows until the walls actually start dissolving.
        system.setBodiesVisible(false);
        station.setVisible(false);
        // Keep the ship's lateral offset inside the tube so the walls don't
        // hop when the tunnel re-anchors around the arrival point.
        jump.path
          .copy(ship.pos)
          .sub(fwdV.set(jump.latX, jump.latY, 0).applyQuaternion(jump.quat));
        // NO speed snap: phase 2 bleeds continuously from ride speed —
        // arrival is a change of scenery, never a jolt.
        system.setArrivalZoom(0.02); // planet warp-zooms in with the reveal
      }
    } else {
      // Hard-brake the superluminal speed to a CRAWL for the loading
      // hold (an open-ended hold at cruise speed could drift PAST the
      // destination planet), then ramp back to cruise across the reveal —
      // the ship still flies OUT of the wormhole.
      const exitRamp = Math.min(1, jump.revealT / REVEAL_T);
      const targetV = 40 + 310 * exitRamp;
      const brakeRate = jump.speed > 600 ? 6 : 2.5;
      jump.speed = targetV + (jump.speed - targetV) * Math.exp(-brakeRate * jdt);
      // LOADING SCREEN: the reveal only advances once the destination's
      // terrain meshes are baked AND a minimum ride has elapsed (hard cap
      // so a stall can't trap us). The walls stay opaque until then.
      if (
        (system.arrivalReady && jump.t > ARRIVE_T + REVEAL_MIN_HOLD) ||
        jump.t > ARRIVE_T + 14
      ) {
        // CLAMPED clock: the first frame the bodies render pays shader
        // compiles / texture uploads — a long frame there used to leap
        // revealT across the whole zoom window and the planet POPPED in
        // at full size. A hitch now pauses the reveal, never skips it.
        jump.revealT += jdt;
      }
      // Warp-zoom: bodies appear the moment the walls start dissolving,
      // then rush in from a point — fully sized BEFORE the walls are gone
      // (ease-out so the planet decelerates into place).
      const holdEnd = REVEAL_T * REVEAL_HOLD_K;
      if (jump.revealT > holdEnd && !jump.shown) {
        jump.shown = true;
        system.setBodiesVisible(true);
        station.setVisible(true);
      }
      const zk = Math.min(
        1,
        Math.max(0, jump.revealT - holdEnd) / ((REVEAL_T - holdEnd) * REVEAL_ZOOM_K),
      );
      system.setArrivalZoom(0.02 + 0.98 * (1 - Math.pow(1 - zk, 3)));
      if (jump.revealT >= REVEAL_T) {
        mode = "fly";
        ship.speed = jump.speed;
        ship.vel.copy(jump.dir).multiplyScalar(jump.speed);
        ship.throttle = 0.45;
        jump = null;
        ship.snapVisual(); // fresh interpolation history for the fly loop
        // The tunnel camera floors its boom at zoom 0.6; start the chase
        // boom there and let it ease back to the player's zoom — never cut.
        camZoomSmooth = Math.max(camZoomSmooth, 0.6);
        system.setBodiesVisible(true);
        station.setVisible(true);
        system.setArrivalZoom(1);
      }
    }

    // SHARED RIDE (both phases): the tube advances with the ship, and the
    // mouse flies a Star-Fox-style drift inside it — a clamped lateral
    // TARGET the ship eases toward, banking into the motion. Control
    // authority fades out across the reveal so flight resumes level.
    if (jump) {
      jump.path.addScaledVector(jump.dir, jump.speed * jdt);
      const { dx, dy } = input.consumeMouse();
      const authority = jump.arrived ? Math.max(0, 1 - jump.revealT / REVEAL_T) : 1;
      jump.latTX = Math.max(
        -TUNNEL_BOUND,
        Math.min(TUNNEL_BOUND, jump.latTX + dx * 0.035 * authority),
      );
      jump.latTY = Math.max(
        -TUNNEL_BOUND,
        Math.min(TUNNEL_BOUND, jump.latTY - dy * (input.invertY ? -1 : 1) * 0.035 * authority),
      );
      // As authority fades through the reveal, the drift TARGET eases back
      // to the tube axis — the ship settles to center, so the fixed tube
      // camera and the returning chase camera coincide exactly at handback.
      const recenter = (1 - authority) * Math.min(1, 4 * jdt);
      jump.latTX *= 1 - recenter;
      jump.latTY *= 1 - recenter;
      const latEase = 1 - Math.exp(-9 * jdt);
      jump.latX += (jump.latTX - jump.latX) * latEase;
      jump.latY += (jump.latTY - jump.latY) * latEase;
      ship.pos
        .copy(jump.path)
        .add(fwdV.set(jump.latX, jump.latY, 0).applyQuaternion(jump.quat));
      ship.speed = jump.speed;
      // Bank/pitch into the drift (the un-eased stick error IS the input),
      // easing the hull toward bearing × tilt — also covers the smooth
      // swing onto the ride bearing when joining a wormhole at an angle.
      const tiltR = Math.max(-0.4, Math.min(0.4, -(jump.latTX - jump.latX) * 0.028)) * authority;
      const tiltP = Math.max(-0.3, Math.min(0.3, (jump.latTY - jump.latY) * 0.02)) * authority;
      jumpTiltE.set(tiltP, 0, tiltR, "YXZ");
      jumpTargetQ.copy(jump.quat).multiply(jumpTiltQ.setFromEuler(jumpTiltE));
      ship.quat.rotateTowards(jumpTargetQ, 3 * jdt);
    }
  }

  // Player focus: the ship in flight, the character on foot (drives the
  // floating origin, LOD refinement, atmosphere, and light placement).
  const drivingVehicle = groundVehicle.occupied && footPlanet !== null;
  const renderingVehicleRider = drivingVehicle || (vehicleMountTransition !== null && groundVehicle.reservedSeat !== null);
  const vehicleCameraActive = renderingVehicleRider && footPlanet !== null;
  const onFoot = footPlanet !== null && !drivingVehicle;
  // Visual pose: interpolate between sim ticks so 60 Hz simulation renders
  // without judder at 120+ Hz (jump/builder poses are frame-driven already).
  const alpha = Math.min(1, accumulator / FIXED_DT);
  if (mode === "jump" || mode === "builder" || mode === "outfit") {
    visPos.copy(ship.pos);
    visQuat.copy(ship.quat);
  } else {
    visPos.lerpVectors(ship.prevPos, ship.pos, alpha);
    visQuat.slerpQuaternions(ship.prevQuat, ship.quat, alpha);
  }
  if (onFoot) {
    charVisPos.lerpVectors(character.prevPos, character.pos, alpha);
    charVisQuat.slerpQuaternions(character.prevQuat, character.quat, alpha);
  }
  if (vehicleSpawned) {
    vehicleVisPos.lerpVectors(groundVehicle.prevPos, groundVehicle.pos, alpha);
    vehicleVisQuat.slerpQuaternions(groundVehicle.prevQuat, groundVehicle.quat, alpha);
  }

  // Focus = the INTERPOLATED render position, NOT the raw 60 Hz sim tick. The origin rebase,
  // the sun/shadow map (StarSystem centres it on this) and the cloth co-rotating frame all
  // key off this — so on a spinning planet they track the smoothly-rendered avatar instead of
  // the sim-tick-stepped sim pose. Keying off the raw pose made the shadow AND the cape jitter
  // together, locked to the sim tick (the render interpolation sawtoothed against it).
  const focusPos = vehicleCameraActive ? vehicleVisPos : onFoot ? charVisPos : visPos;

  // Floating origin rebase. While the screenshot harness camera is pinned, HOLD the
  // origin still — the pin frames a fixed world point by rebasing eye/look against the
  // live origin, so letting the origin snap to the (drifting) ship would swing the frame
  // out to a distant globe. capture.hold() parks origin on the settlement; keep it there.
  if (!devCamPin && focusPos.distanceTo(origin) > ORIGIN_SNAP) origin.copy(focusPos);

  built.group.position.copy(visPos).sub(origin);
  built.group.quaternion.copy(visQuat);
  if (vehicleSpawned) {
    groundVehicleView.group.position.copy(vehicleVisPos).sub(origin);
    groundVehicleView.group.quaternion.copy(vehicleVisQuat);
    groundVehicleView.update(groundVehicle, elapsed);
  }
  updateSummonMarker();

  // Cosmetic bank: the hull leans into left/right mouse turns — an effect
  // of LIFT, so it scales with air density and vanishes in vacuum. Render-
  // only: the flight quat (aim, lift, the wire format) never sees it; the
  // lagging chase camera makes the lean read.
  const bankTarget =
    mode === "fly"
      ? Math.max(-0.35, Math.min(0.35, ship.yawRate * 0.5)) * ship.airDensity
      : 0;
  shipBank += (bankTarget - shipBank) * (1 - Math.exp(-6 * elapsed));
  if (Math.abs(shipBank) > 1e-3) {
    built.group.quaternion.multiply(bankQ.setFromAxisAngle(BANK_AXIS, shipBank));
  }

  // Planets + sky + sun/shadow FIRST, so footPlanet.group carries THIS frame's interpolated
  // pose when the cloth sim (avatar.update, below) reads it as its co-rotating frame — it used
  // to run a frame stale (system.update was below the avatar block) and the cape jittered
  // against the fresh avatar position. The player-centred shadow map is also aimed in here.
  perf.begin("world");
  system.update(elapsed, focusPos, origin, camera.position, footPlanet !== null, alpha);
  perf.end("world");

  perf.begin("actors");
  if (renderingVehicleRider) {
    const seat = groundVehicle.activeSeat ?? groundVehicle.reservedSeat;
    if (seat) vehicleSeatPose(seat, vehicleVisPos, vehicleVisQuat, vehicleRiderPos, vehicleRiderQuat);
    else if (!vehicleRiderPose(
      groundVehicle.def, vehicleVisPos, vehicleVisQuat, vehicleRiderPos, vehicleRiderQuat,
    )) vehicleRiderPos.copy(vehicleVisPos);
    let mountedBlend = 1;
    let transitionSpeed = 0;
    let mountSwing: number | null = null;
    let mountSide = -1;
    let mountSwingReverse = false;
    let mountAnimationTime = 0;
    if (vehicleMountTransition && seat) {
      if (vehicleMountTransition.bail) {
        // A bail has no ground endpoint: the arc leaves the SEAT and gameplay
        // takes over mid-air, so there is no exit socket to resolve.
        vehicleMountEntry.copy(vehicleRiderPos);
      } else if (vehicleMountTransition.phase === "dismounting") {
        vehicleSeatExitPosition(seat, vehicleVisPos, vehicleVisQuat, vehicleMountEntry);
      } else {
        vehicleSeatEntryPosition(vehicleMountEntry);
      }
      vehicleMountUp.set(0, 1, 0).applyQuaternion(vehicleVisQuat).normalize();
      mountAnimationTime = Math.max(0, vehicleMountTransition.elapsed - FIXED_DT * (1 - alpha));
      mountedBlend = vehicleMountTransitionPose(
        vehicleMountTransition,
        vehicleMountEntry,
        vehicleRiderPos,
        vehicleVisQuat,
        vehicleMountUp,
        vehicleMountRoot,
        vehicleMountRootQuat,
        mountAnimationTime,
      );
      vehicleRiderPos.copy(vehicleMountRoot);
      vehicleRiderQuat.copy(vehicleMountRootQuat);
      mountSwingReverse = vehicleMountTransition.phase === "dismounting";
      mountSwing = mountSwingReverse ? 1 - mountedBlend : mountedBlend;
      // A keyed authored MOUNT replaces the procedural leg arc. Constraint/event
      // only starter assets deliberately retain it as a safe sparse fallback.
      //
      // A step-off KEEPS it, and composes: the swing IK is the only thing in the
      // system that produces a real leg-over-the-tail silhouette, and sparse FK
      // cannot fake one (the authored keys are a torso/head/arm performance — put
      // them on their own and the rider strides off the side of the chassis like
      // it was a kerb). The IK runs last, owns one leg, and its weight fades in
      // and out of the FK at both ends. A BAIL opts out: both legs leave together
      // there, which its own clip keys, and a single-leg arc fights that.
      const composesSwing = vehicleMountTransition.phase === "dismounting"
        && !vehicleMountTransition.bail;
      if (!composesSwing && vehicleMountPlayback
        && Object.keys(vehicleMountPlayback.asset.boneTracks).length > 0) {
        mountSwing = null;
      }
      mountSide = vehicleMountTransition.side;
      // Approach is ordinary on-foot locomotion and has already finished. The
      // transition only blends the authored climb/contact pose into the seat.
    }
    avatar.group.visible = true;
    avatar.group.position.copy(vehicleRiderPos).sub(origin);
    avatar.group.quaternion.copy(vehicleRiderQuat);
    // BOTH directions play their authored clip. The exit's used to be loaded and
    // then thrown away here: its duration was honoured, its FK and IK were not,
    // and merely HAVING bone tracks disabled the procedural leg swing above — so
    // the dismount was a bare root slide with the ride pose fading out on a body
    // that never moved. Nothing about the layering is direction-specific.
    const authoredInteraction = vehicleMountTransition ? vehicleMountPlayback : null;
    syncAvatarVehicleMountClip(authoredInteraction);
    avatar.setMountedBlend(mountedBlend, seat?.riderPose);
    avatar.setVehicleMountSwing(mountSwing, mountSide, mountSwingReverse);
    avatar.setMountedSpeed(
      Math.abs(groundVehicle.forwardSpeed) / Math.max(1, groundVehicle.def.handling.maxSpeed),
    );
    // Weight shift comes from what the chassis is actually DOING — see
    // riderLeanSignal for the load model and why its range is as wide as it is.
    avatar.setMountedLean(riderLeanSignal(
      groundVehicle.forwardSpeed,
      groundVehicle.angularVel.dot(groundVehicle.up),
      groundVehicle.driftK,
    ));
    avatar.setMountedBoost(groundVehicle.boostK);
    avatar.setLayeredAuthoringTime(
      authoredInteraction ? Math.min(authoredInteraction.asset.duration, mountAnimationTime) : null,
    );
    avatar.clothFrame = footPlanet?.group ?? null;
    avatar.update(elapsed, transitionSpeed, true, false);
    if (authoredInteraction) {
      for (const constraint of sampleHumanoidConstraints(authoredInteraction.asset, mountAnimationTime)) {
        if (constraint.positionWeight <= 0.001) continue;
        if (!vehicleConstraintWorldTarget(
          groundVehicle.def,
          constraint.target,
          constraint.targetPosition,
          groundVehicleView.group.position,
          vehicleVisQuat,
          vehicleMountConstraintTarget,
        )) continue;
        avatar.applyHumanoidIk(
          constraint.effector,
          vehicleMountConstraintTarget,
          constraint.positionWeight,
          constraint.pole,
        );
      }
    }
  }

  if (onFoot && !renderingVehicleRider) {
    syncAvatarVehicleMountClip(null);
    avatar.setMounted(false);
    avatar.setVehicleMountSwing(null);
    if (playerDyingT > 0 && footPlanet) {
      avatar.group.position.copy(deathRootLocal).applyQuaternion(footPlanet.group.quaternion).add(footPlanet.group.position);
      avatar.group.quaternion.copy(deathRootRotationLocal).premultiply(footPlanet.group.quaternion);
    } else {
      avatar.group.position.copy(charVisPos).sub(origin);
      playerConversationLook.update(avatar,
        mode === "foot" && dialogPanel.visible && talkingNpcId ? npcLife?.avatarOf(talkingNpcId) ?? null : null,
        charVisQuat, character.groundSpeed > 0.1, elapsed);
    }
    companion.prepareAvatar(avatar, playerDyingT <= 0 && character.onGround && character.waterState !== "swim");
    // cloth simulates in the foot planet's CO-ROTATING frame (planet spin + floating-origin
    // rebases otherwise read as phantom wind on the cape)
    avatar.clothFrame = footPlanet?.group ?? null;
    if (playerDyingT > 0) {
      playerDyingT += elapsed;
      if (playerDyingT > PLAYER_DEATH_S) respawnAtTown();
      else if (avatar.ragdollActive()) avatar.stepRagdoll(elapsed);
    }
    if (playerDyingT > 0 && avatar.ragdollActive()) { /* the verlet body owns every bone */ } else
    avatar.update(
      // HITSTOP: a confirmed melee hit crawls the ANIMATION clock for a few frames (the
      // gameplay action clock keeps running — see combat/feedback.ts for why that split).
      feedback.avatarDt(elapsed),
      mode === "foot" ? character.groundSpeed : 0,
      character.onGround,
      character.sprinting,
      mode === "foot" ? character.moveDirLocal() : undefined,
      mode === "foot" && character.crouching,
      mode === "foot" && character.waterState === "swim",
    );
    // (The queued shot fires in flushFootBolt AFTER the camera finalizes — the
    // aim solve needs this frame's reticle ray, not just this frame's muzzle.)
    if (footPlanet) updateFxPose(footPlanet, alpha);
  } else {
    pendingBolt = null; // left foot mode between firing and rendering — drop the orphaned visual
  }
  // actors→fx boundary sits AFTER the avatar if/else (both branches) — inside it,
  // the fly-mode path would skip the bracket and the lap would be lost. The
  // crystal thrusters therefore ride in `actors` with the player pose.
  perf.end("actors");
  perf.begin("fx");
  perf.begin("streams");

  if (drivingVehicle && footPlanet) {
    // Anchor against the INTERPOLATED planet pose, the same frame the terrain and
    // the boost trails render at. Recording against the sim pose instead leaves
    // every particle a frame of planet spin away from the vehicle — on a fast
    // carried world that is metres, and it reads as the dust being off-centre.
    updateFxPose(footPlanet, alpha);
    const contactSpeed = Math.abs(groundVehicle.forwardSpeed);
    vehicleFx.emitContact(footPlanet, vehicleVisPos, vehicleVisQuat, groundVehicle.up, {
      drift: groundVehicle.driftK * groundVehicle.groundedFraction,
      speed: contactSpeed,
      speedFraction: contactSpeed / Math.max(1, groundVehicle.def.handling.maxSpeed),
      contact: groundVehicle.groundedFraction,
      radius: groundVehicle.def.collider.radius,
    }, elapsed, _fxPose);
  }
  // Rear-exhaust boost trails. rearExhaustPoints reads world positions off the
  // view whose transform was set above, so call it here (post system.update, so
  // _fxPose anchors to this frame's interpolated ground). Intensity/length are
  // speed baseline + boost spike, matching the glow and the engine rev.
  if (drivingVehicle && footPlanet) {
    updateFxPose(footPlanet, alpha);
    const nEx = groundVehicleView.rearExhaustPoints(vehicleExhaustSrc);
    const vSpeedK = Math.min(1, Math.abs(groundVehicle.forwardSpeed) / Math.max(1, groundVehicle.def.handling.maxSpeed));
    const vK = Math.min(1, vSpeedK * 0.6 + groundVehicle.boostK * 0.8);
    const vLen = 2.5 + vSpeedK * 6 + groundVehicle.boostK * 4;
    vehicleBoostStreams.update(vehicleExhaustSrc, nEx, _fxPose, origin, camera.position, vK, elapsed, false, vLen);
  } else {
    vehicleBoostStreams.update(vehicleExhaustSrc, 0, null, origin, camera.position, 0, elapsed);
  }
  if (vehicleSpawned && footPlanet) {
    const impact = groundVehicle.drainImpact(vehicleImpactPoint, vehicleImpactNormal);
    if (impact > 0) {
      updateFxPose(footPlanet, alpha);
      vehicleFx.impact(footPlanet, vehicleImpactPoint, vehicleImpactNormal, impact, _fxPose);
      audio.sfx("vehicle.impact", {
        at: vehicleImpactPoint,
        gain: Math.min(1.25, 0.35 + impact / 55),
        rate: 0.88 + Math.min(0.25, impact / 180),
      });
      if (drivingVehicle) {
        groundVehicleCamera.impact(impact);
        input.rumble(Math.min(1, impact / 45), 90 + Math.min(180, impact * 3));
      }
    }
  }
  vehicleFx.update(elapsed);
  perf.end("streams");
  perf.begin("ocean");

  // DEV cloth test bed: step the floating overworld cape (system.update posed footPlanet.group
  // above; refresh its matrixWorld so the child cape + its cloth frame are THIS frame's pose).
  if (devCloak && footPlanet) {
    footPlanet.group.updateWorldMatrix(true, false);
    if (devCloak.follow) placeDevCloak();
    devCloak.avatar.clothFrame = footPlanet.group;
    devCloak.avatar.update(elapsed, 0, true, false);
  }

  // Water VFX: a splash when the player enters water, ripple wakes while moving in
  // it — and the same wakes for remote players (their submersion `sub` is synced).
  const wfxSea = onFoot ? footPlanet?.def.seaRadius : null;
  if (onFoot && footPlanet && (wfxSea != null || footPlanet.sampler.inlandWetlands().length)) {
    const ctr = footPlanet.pose.center;
    if (character.waterState !== "land") {
      _wfxDir.copy(character.pos).sub(ctr).normalize();
      // Waterline = the LOCAL surface (an inland river's ws, not the distant sea).
      const wfxSurf = character.waterSurfR > -1e8 ? character.waterSurfR : (wfxSea??-1e9);
      _wfxPos.copy(ctr).addScaledVector(_wfxDir, wfxSurf); // waterline point
      if (prevWaterState === "land") waterFx.splash(footPlanet, _wfxPos, character.waterState === "swim" ? 1.5 : 1.0);
      wakeTimer -= elapsed;
      if (character.groundSpeed > 1.2 && wakeTimer <= 0) {
        wakeTimer = 0.08; // frequent small foam → a continuous trailing wake
        // Trail the wake BEHIND the swimmer (opposite the tangential velocity).
        _wfxVel.copy(character.vel).addScaledVector(_wfxDir, -character.vel.dot(_wfxDir));
        if (_wfxVel.lengthSq() > 0.5) _wfxPos.addScaledVector(_wfxVel.normalize(), -1.2);
        waterFx.foam(footPlanet, _wfxPos, 0.5 + Math.min(0.4, character.groundSpeed / 12));
      }
    }
    entityWakeTimer -= elapsed;
    if (entityWakeTimer <= 0) {
      entityWakeTimer = 0.1;
      if (net) for (const r of net.remotes.values()) {
        if (r.kind !== "foot" || r.netSubmerge <= 0.05 || (r.groundSpeed ?? 0) < 1.2) continue;
        _wfxDir.copy(r.sysPos).sub(ctr).normalize();
        _cameraLocal.copy(_wfxDir).applyQuaternion(footPlanet.pose.invQuat);
        waterColumn(footPlanet.sampler,footPlanet.def,_cameraLocal.x,_cameraLocal.y,_cameraLocal.z,_cameraWater);
        if(_cameraWater.surfDepth<=0)continue;
        _wfxPos.copy(ctr).addScaledVector(_wfxDir, _cameraWater.surfR);
        waterFx.foam(footPlanet, _wfxPos, 0.5);
      }
      // NPC ground units wading shallow water sit right at the sea line (the AI
      // `surface` clamp); flyers hover well above, land units stand above it.
      for (const d of drones.list()) {
        _wfxDir.copy(d.pos).sub(ctr);
        const dr = _wfxDir.length();
        _wfxDir.divideScalar(dr || 1);
        _cameraLocal.copy(_wfxDir).applyQuaternion(footPlanet.pose.invQuat);
        waterColumn(footPlanet.sampler,footPlanet.def,_cameraLocal.x,_cameraLocal.y,_cameraLocal.z,_cameraWater);
        if(_cameraWater.surfDepth<=0||Math.abs(dr-_cameraWater.surfR)>.8)continue;
        _wfxPos.copy(ctr).addScaledVector(_wfxDir, _cameraWater.surfR);
        waterFx.foam(footPlanet, _wfxPos, 0.45);
      }
    }
  }
  prevWaterState = onFoot && footPlanet ? character.waterState : "land";
  waterFx.update(elapsed);
  perf.end("ocean");
  perf.begin("weather");

  // --- Weather: sample the deterministic field at the VIEWER — on foot, or FLYING/docked inside
  // a planet's atmosphere (you see the rain from the cockpit too) — and drive the near-field
  // precipitation. The same sample feeds the sky/fog response below. ---
  // Interiors P1/P2: indoors when the feet stand inside a hollow settlement
  // building's ground floor. Sampled on the SIM pose (collision frame); the
  // volume index keys the current building's interior lamp pool.
  indoorsNow = false;
  let indoorsVol = -1;
  let homesteadIndoor = false;
  if (onFoot && footPlanet && footPlanet.pose.invQuat) {
    _indoorLocal.copy(character.pos).sub(footPlanet.pose.center).applyQuaternion(footPlanet.pose.invQuat);
    if (footPlanet.settlementColliders) {
      indoorsVol = footPlanet.settlementColliders.indoorsAt(_indoorLocal.x, _indoorLocal.y, _indoorLocal.z);
    }
    homesteadIndoor = homesteadPieces?.indoorsAt(footPlanet.def.id,
      _indoorLocal.x, _indoorLocal.y, _indoorLocal.z) ?? false;
    indoorsNow = indoorsVol >= 0 || homesteadIndoor;
  }
  indoorsK += ((indoorsNow ? 1 : 0) - indoorsK) * (1 - Math.exp(-6 * elapsed));
  system.interiorDim = indoorsK;
  footCam.setIndoors(indoorsNow);
  updateInteriorLights(indoorsNow ? footPlanet : null, indoorsVol, indoorsK);
  // Auto-doors: animate the leaves near the player (same planet-local frame).
  const doorsOn = onFoot && footPlanet !== null && footPlanet.pose.invQuat !== null && footPlanet.settlementColliders !== null;
  doorLeaves.update(doorsOn ? settlementRenderer : null, doorsOn ? footPlanet : null,
    doorsOn ? _indoorLocal : null, elapsed);
  // (OBSERVE moved below the camera update — the reticle ray MUST read THIS
  // frame's camera, same rule as the weather-FX block. See ~line 9930.)
  npcLife?.setPlayerIndoors(indoorsNow); // perf: indoor sampling-radius shrink

  let wxPlanet: import("./world/planet").Planet | null = null;
  let wxSim: THREE.Vector3 | null = null; // sim-space viewer pos (weather sample + ground probe)
  let wxVis: THREE.Vector3 = charVisPos; // interpolated visual pos (box centre)
  let wxDrop = 2.2;
  // Keep ambient weather active indoors; the shared surface cache stops drops
  // at roofs, so rain remains visible through windows and open doorways.
  if (onFoot && footPlanet && footPlanet.def.atmosphere) {
    wxPlanet = footPlanet;
    wxSim = character.pos;
    wxVis = charVisPos;
  } else if (drivingVehicle && footPlanet && footPlanet.def.atmosphere) {
    // In a ground vehicle you're still standing in the weather — sample at the
    // vehicle and centre the precip box on it, same as on foot.
    wxPlanet = footPlanet;
    wxSim = groundVehicle.pos;
    wxVis = vehicleVisPos;
  } else if (!onFoot && (mode === "fly" || mode === "docked")) {
    const near = system.nearestPlanet(ship.pos);
    const atmo = near?.planet.def.atmosphere;
    if (near && atmo && near.surfaceDist < atmo.height * 1.5) {
      wxPlanet = near.planet;
      wxSim = ship.pos;
      wxVis = visPos;
    }
  }
  if (wxPlanet && wxSim) {
    wxPlanet.weatherField?.update(worldTime());
    _wDir.copy(wxSim).sub(wxPlanet.pose.center).normalize(); // world up
    _wLocal.copy(_wDir).applyQuaternion(wxPlanet.pose.invQuat); // planet-local direction
    sampleWeather(wxPlanet.sampler, wxPlanet.def, _wLocal.x, _wLocal.y, _wLocal.z, worldTime(), playerWeather);
    weatherAltitudeK = precipitationAltitude(wxSim.distanceTo(wxPlanet.pose.center) - wxPlanet.def.radius,
      wxPlanet.def.atmosphere!.height * 0.6, wxPlanet.def.atmosphere!.height * 0.4);
    // Feed the flora wind field the SAME weather that slants the rain, so a
    // downpour and the grass under it lean the same way. Everything it needs is
    // planet-local: the player's position in that frame, and weatherAt's own
    // planet-local wind tangent.
    _windLocal.copy(wxSim).sub(wxPlanet.pose.center).applyQuaternion(wxPlanet.pose.invQuat);
    weatherSurface.update(wxPlanet, _windLocal, worldTime(),
      (x, y, z) => homesteadPieces?.standRadiusAt(wxPlanet!.def.id, x, y, z) ?? null);
    _windDir.set(playerWeather.windx, playerWeather.windy, playerWeather.windz);
    floraWind.update(elapsed, _windLocal, _windDir, playerWeather.windSpeed);
    // Grass parts around whatever is actually moving through it. Driving used to
    // switch this OFF entirely, so a speeder tore through a meadow without
    // disturbing a blade — the effect was wired to the character alone. A vehicle
    // parts a swath as wide as it is, and only while its probes are on the ground
    // (a glide passes over the top of the grass, not through it).
    if (drivingVehicle) {
      const partingLocal = _windPart.copy(groundVehicle.pos).sub(wxPlanet.pose.center)
        .applyQuaternion(wxPlanet.pose.invQuat);
      floraWind.setPushCenter(partingLocal);
      floraWind.setPush(
        groundVehicle.groundedFraction > 0.15,
        groundVehicle.def.collider.radius + 1.1,
        1.15,
      );
    } else {
      floraWind.setPushCenter(null);
      floraWind.setPush(onFoot && character.onGround);
    }
    // Deterministic ground aftermath (wet/snow) for the player's column. It changes
    // slowly, so recompute a few times a second and ease the shader uniforms toward it.
    groundWxTimer -= elapsed;
    if (groundWxTimer <= 0) {
      groundWxTimer = 0.25;
      weatherGroundState(wxPlanet.sampler, wxPlanet.def, _wLocal.x, _wLocal.y, _wLocal.z, worldTime(), groundWx);
    }
    const gk = 1 - Math.exp(-1.5 * elapsed);
    groundWxWet += (groundWx.wet - groundWxWet) * gk;
    groundWxSnow += (groundWx.snow - groundWxSnow) * gk;
    updateWeatherGround(_wDir, camera.matrixWorldInverse, groundWxWet, groundWxSnow);
    // Anchor precip to the INTERPOLATED planet pose the terrain renders at (same lerp as
    // system.update, incl. the shortest-angle spin wrap), so drops ride the surface rigidly
    // instead of shearing/jittering against the fast orbit+spin.
    _wxPose.center.lerpVectors(wxPlanet.posePrev.center, wxPlanet.pose.center, alpha);
    let wxAng = wxPlanet.pose.spinAngle - wxPlanet.posePrev.spinAngle;
    wxAng -= Math.round(wxAng / (Math.PI * 2)) * (Math.PI * 2);
    _wxAxis.set(wxPlanet.def.spin.axis[0], wxPlanet.def.spin.axis[1], wxPlanet.def.spin.axis[2]);
    _wxPose.quat.setFromAxisAngle(_wxAxis, wxPlanet.posePrev.spinAngle + wxAng * alpha);
    _wxPose.invQuat.copy(_wxPose.quat).invert();
    // Box floor reaches the GROUND (terrain or sea) below an airborne viewer — jetpack/ship
    // altitude must not leave a dry gap underneath (precipFx caps the depth internally).
    const groundR = Math.max(wxPlanet.surfaceRadiusWorld(_wDir), wxPlanet.def.seaRadius ?? -Infinity);
    wxDrop = wxSim.distanceTo(wxPlanet.pose.center) - groundR + 2.2;

    // Rain dimples the water: spread expanding ripple rings across any water in the box (rain
    // only — snow lands soft). Each spawn samples a random box point and keeps
    // exposed river or ocean water, using the same surface query as gameplay.
    if (playerWeather.precip > 0 && (playerWeather.snowFraction ?? 0) < 1 && !indoorsNow) {
      rainRippleAccum += playerWeather.precip * (1 - (playerWeather.snowFraction ?? 0)) * weatherAltitudeK * 34 * elapsed;
      let budget = Math.min(8, Math.floor(rainRippleAccum));
      rainRippleAccum -= budget;
      _ripA.set(1, 0, 0);
      if (Math.abs(_wDir.x) > 0.9) _ripA.set(0, 1, 0);
      _ripA.crossVectors(_wDir, _ripA).normalize();
      _ripB.crossVectors(_wDir, _ripA).normalize();
      const cen = wxPlanet.pose.center;
      while (budget-- > 0) {
        const rr = Math.sqrt(Math.random()) * 22;
        const aa = Math.random() * 6.283;
        _ripDir.copy(wxSim).addScaledVector(_ripA, Math.cos(aa) * rr).addScaledVector(_ripB, Math.sin(aa) * rr).sub(cen).normalize();
        rainLocalDir.copy(_ripDir).applyQuaternion(wxPlanet.pose.invQuat);
        waterColumn(wxPlanet.sampler, wxPlanet.def, rainLocalDir.x, rainLocalDir.y, rainLocalDir.z, rainWater);
        const roofR = rainWater.surfDepth > 0 ? wxPlanet.settlementColliders?.clearanceRadius(rainLocalDir.x, rainLocalDir.y, rainLocalDir.z, rainWater.surfR) ?? rainWater.surfR : Infinity;
        if (rainWater.surfDepth > 0 && roofR <= rainWater.surfR + 0.1) {
          _ripPt.copy(cen).addScaledVector(_ripDir, rainWater.surfR + 0.05);
          waterFx.ripple(wxPlanet, _ripPt, 0.7 + Math.random() * 0.5);
        }
      }
    }
  } else {
    Object.assign(playerWeather, emptyWeather());
    weatherAltitudeK = 0;
    weatherSurface.disable();
    // Off-planet: no terrain to slick/cover — ease the ground aftermath out.
    const gk = 1 - Math.exp(-1.5 * elapsed);
    groundWxWet += (0 - groundWxWet) * gk;
    groundWxSnow += (0 - groundWxSnow) * gk;
    groundWx.wet = 0;
    groundWx.snow = 0;
    updateWeatherGround(_wDir, camera.matrixWorldInverse, groundWxWet, groundWxSnow);
  }
  perf.end("weather");
  perf.end("fx");
  perf.begin("world");
  station.update(worldTime(), origin);
  perf.end("world");
  perf.begin("ui");
  minimapTimer -= elapsed;
  const minimapOn =
    launched && !inVoid && (mode === "fly" || mode === "foot" || mode === "vehicle" || mode === "docked");
  minimapCanvas.style.display = minimapOn ? "block" : "none";
  if (minimapOn && minimapCtx && minimapTimer <= 0) {
    minimapTimer = 0.08;
    const groundMode = mode === "foot" || mode === "vehicle";
    const groundFocus = mode === "vehicle" ? groundVehicle.pos : character.pos;
    const groundMapPlanet = groundMode
      ? footPlanet ?? system.nearestPlanet(groundFocus)?.planet ?? null
      : null;
    minimapCanvas.dataset.view = groundMapPlanet ? "ground" : "space";
    if (groundMapPlanet) {
      const radiusM = mode === "vehicle" ? 2600 : 1400;
      places.near(groundMapPlanet, groundFocus, radiusM * 1.08, groundMapPlaces);
      groundMapMarkers.length = 0;
      if (net) {
        for (const remote of net.remotes.values()) {
          if ((remote.kind !== "foot" && remote.kind !== "vehicle")
            || remote.sysPos.distanceToSquared(groundFocus) > radiusM * radiusM) continue;
          groundMapLocal.copy(remote.sysPos).sub(groundMapPlanet.pose.center)
            .applyQuaternion(groundMapPlanet.pose.invQuat);
          groundMapMarkers.push({
            local: groundMapLocal.clone(),
            color: "#8ff0d2",
            shape: "dot",
            size: 3,
          });
        }
      }
      // CACHES: every aperture on this planet, themed. Apertures are the one POI a
      // player actively hunts for, so they are drawn unconditionally (not tracked-only).
      if (cacheApertures?.planet === groundMapPlanet.def.id) {
        for (const a of cacheApertures.list()) {
          groundMapMarkers.push({
            local: a.local,
            color: APERTURE_MAP_COLOR[a.site.theme],
            shape: "diamond",
            size: a.site.tier > 0 ? 4.5 : 3.4,
          });
        }
      }
      if (hsTrackedId) {
        const beacon = hsMine.find((b) => b.id === hsTrackedId);
        if (beacon?.planetId === groundMapPlanet.def.id) {
          groundMapMarkers.push({
            local: new THREE.Vector3(beacon.anchor[0], beacon.anchor[1], beacon.anchor[2]),
            color: "#39ddff",
            shape: "diamond",
            size: 4,
          });
        }
      }
      groundMapForward.set(0, 0, -1)
        .applyQuaternion(mode === "vehicle" ? groundVehicle.quat : character.quat);
      groundMinimap.draw(
        minimapCtx,
        158,
        158,
        groundMapPlanet,
        groundFocus,
        groundMapForward,
        radiusM,
        groundMapPlaces,
        groundMapMarkers,
      );
    } else {
      minimapView.draw(minimapCtx, 158, 158, buildSystemNodes(), {
        labels: false,
        sqrtScale: true,
        gridRings: 3,
      });
    }
  }
  if (waypoint && waypointPos(waypointV)) {
    const focusW = footPlanet ? character.pos : ship.pos;
    if (focusW.distanceTo(waypointV) < waypointArriveR()) {
      hud.showToast(`WAYPOINT REACHED — ${waypoint.name.toUpperCase()}`, 2600);
      waypoint = null;
    }
  } else if (waypoint) {
    waypoint = null; // target no longer exists (system switch)
  }
  perf.end("ui");
  net?.update(elapsed, origin, camera.position, worldTime());
  // aimTimeMs = the −INTERP_DELAY render instant — drones sample the same buffered timeline
  // remote players use (and the same instant shots stamp as `ct`), so lag comp is exact.
  perf.begin("actors");
  drones.update(elapsed, origin, camera.position, system, groundForDrones, net?.aimTimeMs() ?? null);
  wildlife.update(elapsed,origin,camera.position,system,net?.aimTimeMs()??Date.now()-150);
  perf.end("actors");

  // --- Bolt collision world: rebuild the collider set every frame ---
  perf.begin("fx");
  perf.begin("bolts");
  bolts.colliders.length = 0;
  if (onFoot) {
    localCharCollider.pos.copy(character.pos).addScaledVector(character.up, 0.95);
    bolts.colliders.push(localCharCollider);
  }
  localShipCollider.pos.copy(ship.pos);
  bolts.colliders.push(localShipCollider);
  if (net) {
    for (const r of net.remotes.values()) {
      if (r.kind === "foot") {
        r.collider.kind = "char";
        r.collider.pos.copy(r.sysPos);
        r.collider.radius = 1.6;
      } else {
        r.collider.kind = "ship";
        r.collider.pos.copy(r.sysPos);
        r.collider.radius = 5;
      }
      r.collider.id = r.id;
      bolts.colliders.push(r.collider);
    }
  }
  drones.collectColliders(bolts.colliders);
  // `alpha` phases the drawn bolts/impacts to the same sub-tick render pose the
  // planets lerp to — carried in full-tick steps they strobe laterally against
  // the drawn world at high refresh rates ("two bolts", 2026-08-17).
  bolts.update(elapsed, origin, system.planets, (planet, pt) => {
    waterFx.splash(planet, pt, 0.7);
    audio.sfx("water.splash", { at: pt });
  }, camera.position, alpha);

  // Contacts: claim drone hits to the server; drone fire damages only the
  // local character (each client owns its own health — relay model). My
  // hits on anything alive float their damage off the impact point.
  for (const h of bolts.drainHits()) {
    audio.impact(h.kind, _audioPt.set(h.point[0], h.point[1], h.point[2]));
    audio.notifyCombat(h.kind === "terrain" ? "act" : "hit");
    // Catalog guns use server-confirmed direct, piercing and blast damage.
    // Predicted contacts still provide immediate impact feedback. The legacy
    // upgraded arm cannon retains its existing validated client-claim path.
    if (h.kind === "drone") {
      if (h.dir) drones.boltHit(h.id, _boltDir.set(h.dir[0], h.dir[1], h.dir[2]),weaponStats(ITEMS[h.wp??""]?.weapon??{}).impact);
      hitSparks?.spawn(_hsPos.set(h.point[0], h.point[1], h.point[2]), false);
      if (h.mine && (!h.predicted || !!ITEMS[h.wp??""]?.weapon?.construct) && !drones.usesServerHits(h.id)) {
        input.rumble(0.25, 60);
        dmgNums.spawn(h.point, h.damage);
        confirmHit();
        net?.sendDroneHit(h.id, h.damage, undefined, undefined, h.wp);
      }
      continue;
    }
    // Predicted (server-authoritative) bolts show their impact FX above, but the SERVER
    // owns their damage — no local claim, no local damage number. `bhit` drives those.
    if (h.predicted) continue;
    if (h.mine && h.kind !== "terrain") {
      dmgNums.spawn(h.point, h.damage);
      confirmHit();
    } else if (!h.mine && h.kind === "char" && h.id === LOCAL_CHAR) {
      // Under SERVER_AUTHORITATIVE_HP this is a PREDICTION (instant bar/feel; the `hp` stream
      // confirms); legacy mode it's the real application. Evade i-frames negate entirely; a
      // PARRY (well-timed MMB) negates too (no knockback target on a bolt); a held block soaks
      // it LOW + a block-hit reaction; else take it in full.
      if (character.iframeT > 0) {
        /* dodged — no damage */
      } else if (parryActive()) {
        avatar.parry(); // bolt deflected — no damage
        audio.sfx("combat.parry");
      } else {
        // The shield (if any) soaks it and flares toward the SHOOTER; whatever
        // gets through still hurts — and snaps the body from that side (flinch).
        // Direction comes from the BOLT'S TRAVEL, not the contact point: a fast
        // bolt steps metres per frame and is caught inside the 1.6 m hit sphere
        // often PAST the centre — the contact lands on the EXIT side and the
        // flare/flinch read "behind the player" (user report). The attacker is
        // always opposite the bolt's motion, whatever the planet carried.
        const hitFrom: readonly [number, number, number] = h.dir
          ? [character.pos.x - h.dir[0] * 6,
             character.pos.y + character.up.y - h.dir[1] * 6,
             character.pos.z - h.dir[2] * 6]
          : h.point;
        applyIncoming(shieldSoak(h.damage, hitFrom));
        takeHitReaction(hitFrom, h.damage);
        audio.notifyCombat("hurt");
      }
    }
  }
  // Authoritative bolt impacts (the server's hit truth): float the damage number + combat audio
  // at the confirmed point. Drone hp arrives via the `drones` snapshot; a k:"char" hit ON US is
  // already predicted at contact (hurt cue included) — the `hp` stream reconciles the bar.
  for (const b of net?.drainBoltHits() ?? []) {
    dmgNums.spawn(b.point, b.dmg);
    if (b.by === net?.playerId) confirmHit(); // bhit is room-wide — confirm only the SHOOTER's reticle
    audio.notifyCombat(b.by === net?.playerId ? "hit" : "act"); // someone else's fight only sustains ours
  }
  // Drain BOTH relay queues every frame regardless of mode — queues must
  // never accumulate while in-flight (stale-damage burst on next landing).
  const incomingPunches = net?.drainPunches() ?? [];
  const incomingMelee = net?.drainDroneMelee() ?? [];
  // Incoming MELEE SWINGS (relayed): predict the damage the server is about to apply to us, so the
  // HP bar reacts on contact instead of one round-trip later.
  //
  // A PREDICTION IS ONLY WORTH MAKING IF IT MATCHES WHAT THE SERVER WILL DO. This used to cone-test
  // every relayed swing with the FISTS cone (2.4 + 0.6 m, against the attacker's CAMERA aim) and
  // predict `punch.dmg` — the attacker's own CLAIMED number. Both halves are now false: the server
  // sweeps the action's BAKED BLADE ARC (~2.6 m of blade, in the attacker's body frame, not their
  // camera's) and reads the damage FROM THE DEF. So the old prediction contradicted the authoritative
  // `hp` stream on essentially every exchange — damage appearing on a victim 3.5 m away that the
  // blade never reaches, then healing back up: visible HP rubber-banding.
  //
  // We now run the SERVER'S OWN GEOMETRY: the same HIT_ARCS entry, the same feet-to-head capsule
  // (MELEE_HIT_RADIUS/PLAYER_HIT_HEIGHT, shared so there is one copy of the numbers), swept over the
  // whole active window in one call — sweepArcVsCapsule is subdivision-invariant, so (0, 1) here is
  // the same verdict as applyMelee's (0, 1) there. What we CANNOT reproduce is lag comp: the server
  // rewinds US to the attacker's view time, and we only know where we are now. That is a sub-frame
  // positional difference against a 2.6 m arc, not a different answer, so it stays a prediction and
  // the `hp` stream remains the truth.
  if (onFoot) {
    const cosFist = Math.cos(FISTS_MELEE.arc);
    const coneReach = FISTS_MELEE.reach + PLAYER_HIT_RADIUS; // matches applyMelee's cone exactly (was 0.6)
    const capsuleR = MELEE_HIT_RADIUS + PLAYER_HIT_MARGIN;   // ...and its melee hurt capsule
    for (const punch of incomingPunches) {
      const def = punch.a ? COMBAT_ACTIONS[punch.a] : null;
      if (def && !def.arc && def.damage <= 0) continue; // a dodge/roll carries no hit volume at all
      const arc = SWEPT_MELEE && def?.arc ? HIT_ARCS[def.arc] : undefined;
      // DAMAGE FROM THE DEF, never the attacker's claim — that is what the server will apply. (No
      // action id under the flag → the server treats it as a FIST; so do we. `dmg` survives only
      // for a flag-off build, where the server really does read it.)
      const dmg = SWEPT_MELEE
        ? (def ? def.damage : FISTS_MELEE.damage)
        : (punch.dmg ?? FISTS_MELEE.damage);
      const ox = character.pos.x - punch.origin[0];
      const oy = character.pos.y - punch.origin[1];
      const oz = character.pos.z - punch.origin[2];
      let hit: boolean;
      if (arc) {
        // Into the ATTACKER's character-local frame — the space the arc is baked in — with the body
        // rotation they streamed (net.ts snapshots it at receipt). Our hurt volume is the same
        // feet-to-head capsule the server tests, so both sides ask the identical question.
        _atkInvQ.set(punch.q[0], punch.q[1], punch.q[2], punch.q[3]).conjugate();
        _selfLocal.set(ox, oy, oz).applyQuaternion(_atkInvQ);
        hit = sweepArcVsCapsule(arc, 0, 1,
          _selfLocal.x, _selfLocal.y, _selfLocal.z,
          _selfLocal.x, _selfLocal.y + PLAYER_HIT_HEIGHT, _selfLocal.z,
          capsuleR) >= 0;
      } else {
        // The LEGACY CONE — the fists, and sword_air (arc-less by design). Same test applyMelee runs.
        const dist = Math.hypot(ox, oy, oz);
        const dot = dist > 1e-3 ? (ox * punch.dir[0] + oy * punch.dir[1] + oz * punch.dir[2]) / dist : 1;
        hit = dist <= coneReach && dot >= cosFist;
      }
      if (!hit) continue;
      // Under server HP this is a PREDICTION (the `hp` stream confirms). Evade i-frames negate; a PARRY
      // negates (PvP knockback not relayed — just no damage); a held block soaks LOW; else take it.
      if (character.iframeT > 0) {
        /* dodged — no damage */
      } else if (parryActive()) {
        avatar.parry();
        audio.sfx("combat.parry");
      } else {
        applyIncoming(shieldSoak(dmg, punch.origin));
        takeHitReaction(punch.origin, dmg);
        audio.notifyCombat("hurt");
      }
    }
    // Incoming ENEMY melee (relayed): TELEGRAPHED strikes (wu) defer to their impact time —
    // matching the server, which schedules damage at emit+wu and re-checks reach at impact.
    // Predicting at receipt (= windup start) billed hits the windup lets you escape.
    const nowMs = performance.now();
    for (const m of incomingMelee) {
      pendingEnemyMelee.push({ m, at: nowMs + (m.wu ?? 0) });
    }
    let pw = 0;
    for (const pe of pendingEnemyMelee) {
      if (pe.at > nowMs) { pendingEnemyMelee[pw++] = pe; continue; }
      if (nowMs - pe.at > 600) continue; // stale (boarded a ship mid-windup) — never burst later
      const m = pe.m;
      // IMPACT-TIME reach test (the client mirror of the server's meleeStillConnects): the
      // strike origin is the windup-start pose — allow the attacker a step of pursuit, same
      // slack the server grants itself.
      const ox = character.pos.x - m.origin[0];
      const oy = character.pos.y - m.origin[1];
      const oz = character.pos.z - m.origin[2];
      if (Math.hypot(ox, oy, oz) > m.reach + 1.0) continue; // escaped during the windup
      // Under server HP: PREDICTION (the server applies drone melee for real; this also
      // restores the parry knockback + block/parry reactions that authority had muted).
      // Evade i-frames negate; a PARRY negates AND knocks the attacker back (away along its strike
      // axis, -m.dir); a held block soaks LOW; else take it in full. All judged AT IMPACT — you
      // parry the blow, not the windup.
      if (character.iframeT > 0) {
        /* dodged — no damage */
      } else if (parryActive()) {
        // The SERVER staggers the attacker (it saw our `py` at the landing tick — the
        // stun posture arrives in the snapshots); the old knockback shove is retired:
        // it fought the stagger's ground seat. Client side = the deflect feel only.
        avatar.parry();
        audio.sfx("combat.parry");
        feedback.meleeHit("heavy"); // the clang lands like a heavy — the moment IS the reward
      } else {
        applyIncoming(shieldSoak(m.dmg, m.origin));
        takeHitReaction(m.origin, m.dmg);
        audio.notifyCombat("hurt");
      }
    }
    pendingEnemyMelee.length = pw;
  }
  perf.end("bolts");
  shieldHex.update(elapsed, _hexPlayer.copy(charVisPos).sub(origin), camera);
  // SOFT-LOCK MARKER: a small soft-glow orb floating above the locked target (see
  // combat/lockMarker.ts). Lazy — built on first lock, hidden whenever the lock is empty.
  if (softLock.targetId !== null) {
    if (!lockMarker) {
      lockMarker = makeLockMarker();
      scene.add(lockMarker);
    }
    const t = drones.list().find((x) => x.id === softLock.targetId);
    if (t) {
      lockMarker.visible = true;
      // RADIAL UP AT THE TARGET — from the PLANET's centre, not the system's (user report
      // 2026-07-24: "the circle drifts off the enemy"). `t.pos` is SYSTEM space with the STAR
      // at the origin, so the old `t.pos.normalize()` lifted the orb along the star-radial —
      // ~99° off the real up, i.e. almost horizontally, in a direction that swings as the
      // planet spins and orbits. The combat lab never showed it: its planet IS at the system
      // origin, where the two directions coincide.
      const lockNear = footPlanet ?? system.nearestPlanet(t.pos)?.planet ?? null;
      if (lockNear) _lockUpN.copy(t.pos).sub(lockNear.pose.center).normalize();
      else _lockUpN.copy(t.pos).normalize(); // deep space: the system radial is all there is
      lockMarker.position.copy(t.pos).sub(origin).addScaledVector(_lockUpN, LOCK_MARKER_LIFT);
      lockMarker.scale.setScalar(0.55 + 0.07 * Math.sin(now * 0.006)); // breathing glow
    } else lockMarker.visible = false;
  } else if (lockMarker) lockMarker.visible = false;
  // LIGHTSABER SWING STREAK — sample the blade segment while an ATTACK is live (arced
  // actions + the stinger's dash/slash; rolls and flinches ride the strike channel too but
  // must not streak). World-space samples, re-based per frame (fx/bladeTrail.ts).
  if (!bladeTrail) {
    bladeTrail = new BladeTrail();
    scene.add(bladeTrail.mesh);
  }
  if (!hitSparks) {
    hitSparks = new HitSparks();
    scene.add(hitSparks.group);
  }
  hitSparks.update(elapsed, origin);
  {
    // PLANET-CARRY anchor (user report 2026-07-22: overworld streaks smeared at carry
    // speed): samples store planet-local via the same interpolated _fxPose the boost
    // streams ride (updateFxPose ran in the on-foot avatar block above).
    bladeTrail.setFrame(onFoot && footPlanet ? _fxPose : null);
    if (actionSys.attacking() && rpg.heldMelee() && avatar.bladeWorld(_btBase, _btTip)) {
      bladeTrail.setColor(avatar.bladeAccent());
      bladeTrail.push(_btBase.add(origin), _btTip.add(origin));
    }
    bladeTrail.update(elapsed, origin);
  }

  // Reticle bloom: decay, then size the ring to the live spread angle.

  if (onFoot && character.aiming) {
    const halfFov = ((camera.fov / 2) * Math.PI) / 180;
    const px = ((2 * Math.tan(currentSpread())) / Math.tan(halfFov)) * (window.innerHeight / 2);
    hud.setReticle(Math.max(10, px));
  } else {
    hud.setReticle(8);
  }
  let trailSpeed = mode === "builder" ? 0 : ship.speed;
  if (mode === "jump" && jump) {
    // Tube-frame exhaust: carry the ribbon history forward with the ride,
    // leaving a 600 m/s aft flow — the exhaust streams hard backward like
    // a ship at speed, through ride and hold alike, plus the lateral drift
    // sweep. ACROSS THE REVEAL both the aft flow and the virtual emission
    // speed ramp to the ship's REAL values, so the exhaust the player exits
    // with IS the tunnel exhaust — no state jump at handback. (Full
    // cancellation froze the exhaust at the nozzles; no cancellation
    // path-compressed kilometers of travel into a wrong-reading streak.)
    const settle = jump.arrived ? Math.min(1, jump.revealT / REVEAL_T) : 0;
    const aft = 600 + (jump.speed - 600) * settle; // → jump.speed: carry → 0
    jumpTrailV.copy(jump.dir).multiplyScalar((jump.speed - aft) * Math.min(elapsed, 1 / 30));
    trails.carryFrame(ZERO_VEL, IDENTITY_Q, jumpTrailV, 1);
    trailSpeed = 6000 + (jump.speed - 6000) * settle; // → real speed at exit
  }
  // Anchor the carried history to the near planet's INTERPOLATED pose (system.update ran
  // above, so planet.group is current) — keeps the streak welded to the ground in atmosphere
  // instead of jittering by the spin sub-frame. Null in deep space / warp ⇒ plain render.
  const trailNear = mode !== "jump" ? system.nearestPlanet(ship.pos) : null;
  let trailAnchor: TrailAnchor | null = null;
  if (trailNear) {
    _trailAnchor.gpos = trailNear.planet.group.position;
    _trailAnchor.gquat = trailNear.planet.group.quaternion;
    _trailAnchor.k = lastCarry.k;
    trailAnchor = _trailAnchor;
  }
  perf.begin("trails");
  trails.update(
    visPos,
    // The RENDERED orientation (bank included): exhaust must anchor to the
    // thruster tips of the hull the player sees, not the unbanked sim quat.
    built.group.quaternion,
    trailSpeed,
    origin,
    camera.position,
    elapsed,
    trailAnchor,
  );
  wormholes.update(now / 1000, origin);
  perf.end("trails");
  // The settlement/citizen block is world+actors work that happens to live
  // between fx systems — pause fx so city cost stops masquerading as effects.
  perf.end("fx");

  // Resource beacons pulse; the mining beam tracks the worked vein.
  resourceNodes?.update(elapsed);
  homesteadMarkers?.update(elapsed); // orb pulse (boundary fade runs post-camera below)
  // CACHES: apertures follow the FOOT planet (the catalog is a per-planet sampler
  // sweep — cheap once, wasteful for a whole system) and pulse on the frame clock.
  // Created lazily as well as in switchSystem: a player whose saved system IS the
  // boot system never switches, and would otherwise see no apertures at all.
  if (footPlanet && !cacheApertures) cacheApertures = new CacheApertures();
  if (cacheApertures) {
    cacheApertures.setPlanet(
      footPlanet && !inCacheInstance() ? footPlanet : null,
      footPlanet ? system.planets.indexOf(footPlanet) : -1,
    );
    cacheApertures.update(elapsed, footLocal(cacheProbeV));
  }
  // Grown settlements build/dispose by proximity and glow at night. Focus on the player's ACTUAL position
  // (footPlanet-derived `focusPos`), not `mode` — in the ESC menu `mode` is
  // "menu", which would stream around the far-away ship and dispose the city
  // the player is standing in.
  // Anchored on the player's ACTUAL position (see the note above on focusPos).
  perf.begin("world");
  perf.begin("settlements");
  settlementRenderer?.update(focusPos, devFrozenPoseTime ?? simTime + devTimeOffset);
  // Pre-warm freshly-built settlements OFF the render thread: compile their
  // shaders in parallel (KHR_parallel_shader_compile) while they stay invisible,
  // then reveal — so a first-visit town never stalls the reflection + draw passes
  // with a synchronous first-render shader compile. Reveal on reject too (never
  // leave a settlement permanently hidden); skip if it was disposed meanwhile.
  const _freshGroups = settlementRenderer?.takePendingWarm();
  if (_freshGroups && _freshGroups.length > 0) {
    for (const _g of _freshGroups) {
      const reveal = (): void => { if (_g.parent) _g.visible = true; };
      compileObjectAsync(renderer, _g, camera, scene).then(reveal, reveal);
    }
  }
  perf.end("settlements");
  perf.end("world");
  // The settlements' people — same clock as tickPoses so they stand on the
  // spinning ground. An OPEN dialog holds its persona from here: the foot
  // interact ladder (which normally drives setFocus) is gated off while an
  // overlay owns the input, and the partner must keep facing the player.
  perf.begin("actors");
  perf.begin("npcs");
  if (dialogPanel.visible && talkingNpcId) {
    const partner = npcLife?.avatarOf(talkingNpcId);
    if (!partner || mode !== "foot" || partner.group.position.clone().add(origin).distanceTo(character.pos) > 9) dialogPanel.close();
    else npcLife?.setFocus(talkingNpcId, true, character.pos);
  }
  npcLife?.update(elapsed, origin, camera.position, system, devFrozenPoseTime ?? simTime + devTimeOffset);
  homesteadResidentLife?.update(
    elapsed, camera.position, system, homesteadPieces, devFrozenPoseTime ?? simTime + devTimeOffset,
  );
  perf.end("npcs");
  perf.end("actors");
  perf.begin("fx");
  {
    const beamTo =
      mineNodeId !== null &&
        input.isDown("KeyG") &&
        (mode === "fly" || mode === "foot") &&
        !uiOverlayActive()
        ? resourceNodes?.posOf(mineNodeId) ?? null
        : null;
    if (beamTo) {
      beamFromV.copy(onFoot ? charVisPos : visPos).sub(origin);
      if (onFoot) beamFromV.addScaledVector(character.up, 1.1); // hand height
      beamToV.copy(beamTo).sub(origin);
      miningBeam.update(elapsed, beamFromV, beamToV);
      audio.setMining(true, beamTo);
    } else {
      miningBeam.update(elapsed, null, null);
      audio.setMining(false);
    }
  }

  // Hover ground-effect: air pushed onto the terrain under the ship.
  {
    const nearShip = system.nearestPlanet(ship.pos);
    const ringOn =
      nearShip !== null && !inVoid && mode !== "jump" && ship.surfaceDist < 20;
    hoverRing.update(
      elapsed,
      ringOn,
      ringOn ? nearShip!.planet : null,
      visPos, // INTERPOLATED ship pose (matches the rendered ship + terrain — see HoverRing)
      origin,
      ship.surfaceDist,
      ship.speed,
    );
  }

  // The hull elongates with the charge, snapping back in the tunnel.
  const stretchTarget = mode === "jump" ? 1 : 1 + (chargeT / CHARGE_TIME) * 1.1;
  shipStretch += (stretchTarget - shipStretch) * Math.min(1, 8 * elapsed);
  built.group.scale.set(1, 1, shipStretch);

  // Warp tunnel envelope: fade in, full ride, then DISSOLVE over the parked
  // arrival so the new system's planet emerges through the fading walls.
  let tunnelK = 0;
  if (mode === "jump" && jump) {
    const dis = Math.max(0, Math.min(1, (jump.revealT / REVEAL_T - REVEAL_HOLD_K) / (1 - REVEAL_HOLD_K)));
    tunnelK =
      jump.t < 0.45
        ? jump.t / 0.45
        : jump.t < ARRIVE_T
          ? 1
          : 1 - dis * dis * (3 - 2 * dis); // smoothstep dissolve, no kink
    tunnel.update(
      tunnelK,
      Math.min(elapsed, 1 / 30),
      jumpCamV.copy(jump.path).sub(origin),
      jump.quat,
      Math.max(0.35, jump.speed / 9000), // walls decelerate with the ride
    );
  } else {
    tunnel.update(0, elapsed, built.group.position, visQuat);
  }

  // Red-shift: the background stars themselves stretch and flow toward the
  // camera. The skew starts within the first second of charging, saturates
  // in the tunnel, and unwinds as the new system fades in.
  // Streaks ride the FULL tunnel — through the post-arrival loading hold —
  // and only unwind once the walls start dissolving and the destination
  // bodies appear. Unwinding at mere arrival left a dead window: no
  // streaks, no planets, just walls.
  const streakTarget =
    mode === "jump"
      ? jump && jump.arrived && jump.revealT > REVEAL_T * REVEAL_HOLD_K
        ? 0
        : 1
      : chargeT > 0
        ? Math.min(1, chargeT / 1.2) * 0.5
        : 0;
  starStreakAmt += (streakTarget - starStreakAmt) * Math.min(1, 3 * elapsed);
  fwdV.set(0, 0, -1).applyQuaternion(visQuat);
  system.setStarStreak(starStreakAmt, fwdV, elapsed);
  perf.end("fx");

  // Roster portraits render to their own target off the title screen, so this
  // rides outside the camera-owner chain below (the title camera keeps the
  // world behind the overlay; these only fill the cards).
  if (rosterOpen) characterPortraits.update(elapsed, renderer);

  if (mode === "builder") {
    shipyard.update();
  } else if (mode === "outfit") {
    characterCreator.update(elapsed); // shared appearance / outfit editor
  } else if (mode === "creator") {
    characterCreator.update(elapsed);
  } else if (mode === "cutscene") {
    cutscenePlayer.update(elapsed);
  } else if (mode === "cutscene-editor") {
    cutsceneEditor?.update(elapsed);
  } else if (vehicleCameraActive) {
    // On dismount the camera tracks the animated body, not the chassis centre,
    // so its motion happens throughout the step-off instead of after release.
    const cameraAnchor = vehicleMountTransition?.phase === "dismounting"
      ? vehicleRiderPos
      : vehicleVisPos;
    if (groundVehicle.boostStarted) groundVehicleCamera.boostEngaged();
    groundVehicleCamera.update(camera, groundVehicle, cameraAnchor, origin, elapsed);
  } else if (onFoot && footPlanet) {
    // Foot camera also covers the ESC menu while disembarked. (devFreezeCam lets
    // a headless harness pin the camera to an arbitrary diagnostic angle.)
    if (!devFreezeCam) footCam.update(camera, charVisPos, character, footPlanet, origin, elapsed);
    // Scope sensitivity: the wrist covers world angle in proportion to the LIVE
    // fov (eases with the ADS zoom transition). Clamped ≤1 — unzoomed = normal.
    character.lookScale = Math.min(1, camera.fov / 70);
  } else if (!launched && !launching) {
    updateTitleCamera(elapsed, now);
  } else {
    updateCamera(elapsed); // jump included: the chase camera rides the tunnel
  }
  companion.frame(camera, onFoot && !renderingVehicleRider && playerDyingT <= 0 ? avatar : null);
  if (companion.transition.active && onFoot && footPlanet && !renderingVehicleRider) clampCompanionCamera(footPlanet);
  applyCameraHandoff(elapsed, origin, onFoot ? charVisPos : null);
  // Combat feedback camera kick (+ its timer decay) — after every rig has posed the camera,
  // before devCamPin so pinned harness screenshots stay deterministic.
  feedback.applyCamera(camera, elapsed);
  if (vehicleCameraActive && footPlanet) clampCameraToCliffs(footPlanet, vehicleVisPos);
  else if (onFoot && footPlanet && !devFreezeCam) {
    cliffCameraAnchor.copy(charVisPos).addScaledVector(character.up, character.crouching ? 1.05 : 1.55);
    clampCameraToCliffs(footPlanet, cliffCameraAnchor);
  } else if (mode === 'fly') {
    const nearest = system.nearestPlanet(ship.pos);
    if (nearest && nearest.surfaceDist < 1500) clampCameraToCliffs(nearest.planet, visPos);
  }
  // After every clamp: the aiming crystal wings keep out of this camera's reticle.
  avatar.setSightCamera(onFoot && footPlanet && !vehicleCameraActive && !devFreezeCam ? camera : null);
  // Apply after every ordinary rig, handoff, kick and terrain clamp: the death
  // viewpoint never chases the controller or gets nudged out of its held pose.
  if (deathPresentation.active) {
    avatar.torsoCenterInto(deathTarget);
    deathPresentation.update(camera, deathTarget, elapsed, origin);
    deathFlash.style.opacity = String(deathPresentation.flashAlpha);
  }
  // R1 D5 screenshot harness: if a world-space camera pin is set, override whatever
  // the chase/foot camera just computed. Rebased by the live floating origin so the
  // eye/look stay glued to the world point across origin snaps (render = world − origin).
  if (devCamPin) {
    // Planet-anchored pin: derive this frame's world eye/look/up from the LIVE
    // planet pose (see the devCamPin type note — world-space pins go stale).
    const anchor = devCamPin.anchor;
    if (anchor) {
      const p = system.planets[anchor.pi];
      if (p) {
        devCamPin.eye.copy(anchor.eyeL).applyQuaternion(p.pose.quat).add(p.pose.center);
        devCamPin.look.copy(anchor.lookL).applyQuaternion(p.pose.quat).add(p.pose.center);
        devCamPin.up.copy(anchor.upL).applyQuaternion(p.pose.quat).normalize();
      }
    }
    camera.up.copy(devCamPin.up);
    camera.position.copy(devCamPin.eye).sub(origin);
    _camPinTarget.copy(devCamPin.look).sub(origin);
    camera.lookAt(_camPinTarget);
  }
  deathCameraOrigin.copy(origin);

  // OBSERVE: the unarmed look reads whatever the reticle faces. MUST run after
  // this frame's camera update (2026-08-02 "can't select NPCs"): the block used
  // to sit ~700 lines up, feeding the PREVIOUS frame's camera through the ray —
  // while the mouse moves at all, that lag walks the ray off citizen-sized
  // targets every frame (buildings are big enough to absorb it).
  {
    const observing = doorsOn && character.aiming && rpg.heldWeapon() === null
      && homesteadBuild?.active !== true && !hsBeaconMode;
    if (observing && footPlanet) {
      // RENDER frame, not sim: the planet spins, and pose.invQuat is the latest
      // sim tick — metres of lateral skew against the smoothly-rendered world.
      // Same rule as probeGround.
      _obsQ.copy(footPlanet.group.quaternion).invert();
      _obsOrigin.copy(camera.position).sub(footPlanet.group.position).applyQuaternion(_obsQ);
      camera.getWorldDirection(_obsDir).applyQuaternion(_obsQ);
      observe.update(true, footPlanet, _obsOrigin, _obsDir, elapsed);
    } else {
      observe.update(false, null, null, null, elapsed);
    }
  }

  // Camera-dependent weather FX MUST run after this frame's camera update. Running them in the
  // weather-sampling block above fed a previous-frame render-space camera through the current
  // interpolated planet pose. On a fast orbit/spin that error can be metres per frame, forcing the
  // toroidal precipitation box to wrap most particles repeatedly — visible as field-wide jitter.
  perf.begin("fx");
  perf.begin("weather");
  if (wxPlanet && wxSim) {
    _wxCam.copy(camera.position).add(origin);
    Object.assign(precipWeather, playerWeather, { precip: playerWeather.precip * weatherAltitudeK });
    precipFx.update(elapsed, wxVis, origin, _wxPose, precipWeather, wxDrop, _wxCam);
    weatherVeil.update(playerWeather.kind, playerWeather.precip, camera.position, _wDir, _wxPose.quat, elapsed);
  } else {
    precipFx.update(elapsed, charVisPos, origin, null, playerWeather);
    weatherVeil.update(0, 0, camera.position, null, null, elapsed);
  }
  perf.end("weather");
  perf.end("fx");

  setFloraTime(now / 1000); // one breeze clock for every planet's flora
  updateGrassUniforms();
  setFloraEye(camera.position); // scene-space eye the flora distance fades measure from
  setWeatherTime(worldTime()); // one drift clock for every planet's clouds (shared epoch → all clients agree)
  const weatherLightTransmission = updateAtmosphere(focusPos);
  if (wxPlanet?.weatherField) {
    precipFx.setEnvironment(weatherSurface, wxPlanet.weatherField, wxPlanet.def.atmosphere!.height * 0.6, precipLight);
    weatherVeil.setEnvironment(weatherSurface, wxPlanet.weatherField, wxPlanet.def.atmosphere!.height * 0.6, precipLight, playerWeather);
  }
  // Water and custom material highlights need this frame's weather-adjusted
  // sunlight too. Update them after the weather sample and sky lighting.
  perf.begin("ocean");
  {
    seaPlanetThisFrame =
      mode === "outfit" || dbgNoWater ? null : footPlanet ?? (system.nearestPlanet(ship.pos)?.planet ?? null);
    const viewerVis = drivingVehicle ? vehicleVisPos : onFoot ? charVisPos : visPos;
    oceanSunDir.copy(system.sunLight.position).sub(system.sunLight.target.position).normalize();
    oceanSunCol.copy(system.sunLight.color).multiplyScalar((0.1 + 0.9 * system.dayFactor) * weatherLightTransmission);
    oceanPatch.update(viewerVis, seaPlanetThisFrame, origin, worldTime(), oceanSunDir, oceanSunCol, system.dayFactor);
    seaPlanetThisFrame?.updateWater(worldTime(), oceanSunDir, oceanSunCol, system.dayFactor, viewerVis);
  }
  perf.end("ocean");
  // Stylized rim + sun sheen share the same dimmed sunlight and sky-horizon tint.
  updateStylized(oceanSunDir, camera.matrixWorldInverse, oceanSunCol, fog.color, system.dayFactor,
    system.directionalLights);
  // Speed warp bends the world only while a vehicle is genuinely flying; every
  // other mode holds it at zero so nothing else in the game is ever distorted.
  updateSpeedWarp(mode === "vehicle" ? groundVehicleCamera.warpAmount : 0);
  // Avatar body + clothing lighting FLOOR (addBodyLightingFloor) is a self-lit fill that keeps
  // downward planes readable in daylight — but at night, with the scene lights dim, that same
  // self-lit term reads as the avatar GLOWING against the dark world. So fade it out toward
  // night: hold the full daylight fill through the day, ramp to 0 across dusk (smoothstep on the
  // day/night factor). The outfit editor + character creator light the avatar with their own
  // studio rig (own scene), so they always keep the full fill. bodyFillOverride
  // (window.__hl.devBodyFill) forces the value for diagnosis when set.
  BODY_FILL.value =
    bodyFillOverride ??
    (mode === "outfit" || mode === "creator" ? 1 : THREE.MathUtils.smoothstep(system.dayFactor, 0.05, 0.45));
  updateStarTarget();
  updateLeadPip(elapsed);
  // Fresh projection matrices: the pip's early-return path (on foot) left
  // them one frame stale, which dragged every marker off its subject.
  camera.updateMatrixWorld();
  // THE shot fired in this frame's sim ticks leaves the barrel HERE — camera
  // finalized (the reticle ray is THIS frame's), avatar posed (the muzzle is the
  // one being drawn), colliders rebuilt. See flushFootBolt for why the whole
  // solve must live on one side of the frame.
  flushFootBolt(alpha);
  // Damage numbers project HERE — with THIS frame's finalized camera. They used to update
  // near the top of the frame, one camera-move behind the world: every over-shoulder sway
  // showed as per-frame jitter on the numbers (user report ×2, 2026-08-02). `alpha` lerps
  // them between sim ticks exactly like the planet render pose — carried to the FULL tick
  // they led the lerped ground by a varying sub-step, the residual jitter (2026-08-13).
  dmgNums.update(elapsed, camera, origin, alpha);
  // THREAT RING: age the arcs, re-bear them against THIS frame's finalized
  // camera (0° = ahead, clockwise positive — screen convention), and paint.
  if (threatArcs.length) {
    for (let i = threatArcs.length - 1; i >= 0; i--) {
      threatArcs[i].t -= elapsed;
      if (threatArcs[i].t <= 0) threatArcs.splice(i, 1);
    }
    camera.getWorldDirection(_thF);
    _thR.setFromMatrixColumn(camera.matrixWorld, 0); // camera right, world space
    hud.setThreatArcs(threatArcs.map((a) => ({
      deg: (Math.atan2(a.dir.dot(_thR), a.dir.dot(_thF)) * 180) / Math.PI,
      k: Math.min(1, a.t / THREAT_FADE),
    })));
    threatShown = true;
  } else if (threatShown) {
    hud.setThreatArcs([]); // one clearing paint, then idle
    threatShown = false;
  }
  // Homestead build aim MUST see THIS frame's camera too — computed any earlier,
  // the ghost rides a one-frame-stale ray and jitters against the crosshair
  // whenever the planet carry moves the camera (i.e. always, on foot).
  updateHomesteadVisual();
  updateBeaconGhostVisual();
  // Staged homestead baking: holograms promote to real geometry near the
  // camera (1 build/frame), demote + free when far. Fresh focus cache per frame.
  perf.begin("world");
  if (homesteadPieces) {
    _hsFocusCache.clear();
    homesteadPieces.update(hsFocusFor);
    homesteadMarkers?.update(0, hsFocusFor); // boundary proximity fade (post-camera)
  }
  perf.end("world");
  // Charge/countdown arc HUD, screen-positioned under the player (projected with THIS frame's camera):
  // boost-run wind-up (cyan) while sprinting straight, or super-jump charge (amber) while holding jump.
  {
    const foot = onFoot && mode === "foot";
    // OUTER ring = boost-run: fills during the wind-up, then holds FULL while engaged so it stays
    // visible BEHIND a charging jump. INNER ring = the jump charge (super-jump OR the boost big-leap).
    // Both can show at once — a boost big-leap is charged WHILE boost-running.
    const showBoost = foot && (character.boosting || character.boostRampFrac > 0.02);
    const boostFrac = character.boosting ? 1 : character.boostRampFrac;
    const showJump = foot && character.jumpChargeFrac > 0.001;
    if (showBoost || showJump) {
      arcWorld.copy(charVisPos).sub(origin); // the feet, render space
      arcWorld.project(camera);
      if (arcWorld.z < 1) {
        const sx = (arcWorld.x * 0.5 + 0.5) * window.innerWidth;
        const sy = (-arcWorld.y * 0.5 + 0.5) * window.innerHeight + 30; // nudge below the feet
        hud.setChargeArc([sx, sy], showBoost ? boostFrac : null, showJump ? character.jumpChargeFrac : null);
      } else hud.setChargeArc(null, null, null);
    } else hud.setChargeArc(null, null, null);
  }
  // Body tags project HERE — after the camera is positioned for this
  // frame's origin. Projecting before it made every floating-origin rebase
  // (once a second at hyperlight) a one-frame tag jump: the "flash".
  // A cutscene is a CAMERA, not a cockpit: no world tags float over the shot.
  const cinematic = mode === "cutscene" || mode === "cutscene-editor" || companion.transition.active;
  perf.begin("ui");
  bodyMarkers.update(
    buildBodyTargets(),
    origin,
    camera,
    !inVoid && !cinematic && mode !== "jump" && mode !== "builder" && mode !== "outfit",
    focusPos, // distances are measured from the PLAYER, not from the floating origin
    elapsed,
  );
  // Marker anchors offset along LOCAL up — world +Y points sideways on a
  // planet, which skewed every tag off its subject.
  markerUp.set(0, 1, 0);
  if (drivingVehicle) markerUp.copy(groundVehicle.up);
  else if (onFoot) markerUp.copy(character.up);
  else {
    const mnear = system.nearestPlanet(focusPos);
    if (mnear && mnear.surfaceDist < mnear.planet.def.radius * 2) {
      markerUp.copy(focusPos).sub(mnear.planet.pose.center).normalize();
    }
  }
  playerMarkers.update(
    net ? net.remotes : null,
    focusPos,
    origin,
    camera,
    onFoot,
    (mode === "fly" || mode === "foot" || mode === "vehicle") && !inVoid,
    now,
    markerUp,
    partyModel?.idsHere() ?? null,
  );
  // Party frames sit under the location panel; they no-op unless the model
  // moved (rev) or we changed system.
  partyFrames?.update(partyModel, system.def.id);
  // Your own ship: tag in sight, persistent edge arrow when it isn't.
  // Anchored on the INTERPOLATED hull pose (visPos), not the sim pose.
  shipMarker.update(visPos, focusPos, origin, camera, footPlanet !== null && !inVoid && !cinematic, markerUp);

  hud.setCrosshair(((onFoot && character.aiming) || drivingVehicle) && !cinematic);
  hud.weaponCharge.update(weaponCycle.charge,input.isButtonDown(0)&&footAimAllowed(),
    onFoot&&!cinematic&&!uiOverlayActive()&&playerDyingT<=0
    &&weaponStats(rpg.heldWeapon()?.weapon??{}).chargeTime>0&&(character.aiming||weaponCycle.charge>0));
  hud.setVehicleStatus(drivingVehicle && !cinematic ? {
    boost: groundVehicle.def.boost
      ? groundVehicle.boostEnergy / groundVehicle.def.boost.capacity
      : 0,
    boosting: groundVehicle.boostK,
    drift: groundVehicle.driftK,
    gliding: groundVehicle.gliding,
    stalled: groundVehicle.stalled,
    airspeed: groundVehicle.airspeed,
  } : null);

  hudTimer -= elapsed;
  if (hudTimer <= 0) {
    hudTimer = 0.1;
    updatePlanetBanner(focusPos);
    updateTownBanner(focusPos);
    hud.update({
      systemName: system.def.name,
      // On foot the nearest body is the planet underfoot (footPlanet) — ship.nearestPlanetName is only
      // computed by ship.update() in fly mode, so it's stale/uninitialized ("—") on a grounded login.
      planetName: footPlanet ? footPlanet.def.name : ship.nearestPlanetName,
      // Biome under the player: always on foot; from the ship only when low
      // enough that "what's below" is a meaningful answer.
      biome: (() => {
        // INSIDE AN INSTANCE THE BIOME ROW NAMES THE DUNGEON, not the pad it is
        // staged on. A cache pad is a real barren planet (see `isCachePadDef`), so
        // the sampler happily answered "BARRENS / ROCKY WASTE" underneath a vault.
        if (liveCache) return CACHE_BIOME_LABEL[liveCache.info.theme];
        if (drivingVehicle && footPlanet) return biomeLabel(footPlanet, groundVehicle.pos);
        if (onFoot && footPlanet) return biomeLabel(footPlanet, character.pos);
        if (!footPlanet && ship.altitude < 3000) {
          const near = system.nearestPlanet(ship.pos);
          if (near) return biomeLabel(near.planet, ship.pos);
        }
        return null;
      })(),
      speed: drivingVehicle ? groundVehicle.forwardSpeed : onFoot ? character.groundSpeed : ship.speed,
      altitude: footPlanet ? 0 : ship.altitude,
      mode: mode === "fly" ? ship.mode : mode === "foot" ? "on foot" : mode === "vehicle" ? `speeder · boost ${Math.round(groundVehicle.boostEnergy)}%` : mode,
      fps,
      net: netStatus.s,
      players: netStatus.players,
      cells: countItem(rpg.inventory, "fuel-cell"),
      foot: onFoot
        ? {
          hp: rpg.health.hp,
          max: rpg.health.max,
          shield: rpg.shield,
          shieldMax: rpg.shieldMax,
          combat: rpg.heldWeapon()?.name ?? (rpg.equippedWeapon() ? "STOWED" : "UNARMED"),
          energy: character.energy / character.energyMax,
          flight:character.jet==='none'?undefined:{mode:character.flightStalled?'STALL · HOVER':character.jet==='dive'?'DIVE':character.flightPower>.1?'BOOST':character.jet.toUpperCase(),speed:character.flightSpeed,altitude:character.groundClearance},
          hazard: footPlanet
            ? (() => {
              const hz = hazardOf(footPlanet);
              if (!hz) return null;
              const sh = rpg.shieldLeft(performance.now());
              return sh > 0 ? `${hz.label} — SHIELDED ${Math.ceil(sh)}S` : hz.label;
            })()
            : null,
        }
        : undefined,
    });
    // The panel re-renders off the same dirty counter the saves use.
    if (invPanel.visible && rpg.rev !== lastRpgUiRev) {
      lastRpgUiRev = rpg.rev;
      invPanel.render();
    }
    garagePanel.sync(); // same idea, off the collection model's own rev
    refreshArmorProfile(); // v49: the worn kit's numbers ride the same counter
    // Tracked-quest tracker (fly + foot); journal / board re-render + dialog
    // choices refresh off the quest log's own dirty counter.
    // Inside a run the tracker belongs to the run: keys held, gates open, boss state.
    // Outside one it is the tracked quest, exactly as before.
    hud.setQuest(
      foundingObjectiveLine()
      ?? cacheHudLine()
      ?? (mode === "fly" || mode === "foot" || mode === "vehicle" ? quests.trackedLine() : null),
    );
    if (quests.rev !== lastQuestUiRev) {
      lastQuestUiRev = quests.rev;
      if (journalPanel.visible) journalPanel.render();
      if (questBoardPanel.visible) questBoardPanel.refresh();
      refreshDialogQuestActions();
    }
  }

  // Audio: drive the listener-relative mix from this frame's state. Placed AFTER the
  // camera is positioned (footCam/updateCamera above) so the listener pose is current,
  // and BEFORE render so positional one-shots fired this frame are already spatialised.
  // Gather other ships/players that emit world sound (system space) so distance applies.
  perf.end("ui");
  perf.begin("audio");
  _audioShips.length = 0;
  _audioJetters.length = 0;
  if (net) for (const [rid, r] of net.remotes) {
    if (r.kind === "ship") _audioShips.push({ id: rid, pos: r.sysPos, speed: r.speed });
    else if (r.netJet && r.netJet !== "none") _audioJetters.push({ id: rid, pos: r.sysPos, power:r.netFlightPower, jet: r.netJet === "dive" ? "glide" : r.netJet }); // a dive sounds like the glide (same full-flight kit)
  }
  if (launched && !inVoid && footPlanet && (mode === 'foot' || mode === 'vehicle')) {
    if (musicPlace?.seed !== footPlanet.def.seed || now >= musicPlaceQueryAt) {
      _musicLocal.copy(mode === 'vehicle' ? groundVehicle.pos : character.pos)
        .sub(footPlanet.pose.center).applyQuaternion(footPlanet.pose.invQuat).normalize();
      musicPlace = {
        seed: footPlanet.def.seed,
        biome: footPlanet.def.archetype,
        daylight: system.dayFactor,
        settlement: settlementInfluenceAt(footPlanet.sampler, _musicLocal.x, _musicLocal.y, _musicLocal.z, 35),
      };
      musicPlaceQueryAt = now + 1000;
    }
    musicPlace.daylight = system.dayFactor;
  } else musicPlace = undefined;
  audio.update({
    dt: elapsed,
    active: launched && !inVoid,
    mode,
    onFoot,
    speed: character.groundSpeed,
    grounded: character.onGround,
    waterState: character.waterState,
    submerge: character.submergeK,
    jet: character.jet === "dive" ? "glide" : character.jet, // a dive sounds like the glide (same full-flight kit)
    boostK: character.boostK,
    flightPower:character.flightPower,
    crouching: character.crouching,
    impactSpeed: frameLandingImpact,
    jumpChain: character.jumpChain,
    iframeT: character.iframeT,
    footstepSurface: playerFootstepSurface,
    footfalls: avatar.takeFootfalls(),
    atmosphere: !!wxPlanet?.def.atmosphere,
    hasSea: footPlanet?.def.seaRadius != null && seaSurface(footPlanet.def) === 'water',
    biome: wxPlanet?.def.archetype ?? "",
    place: musicPlace,
    precip: playerWeather.precip * (1 - (playerWeather.snowFraction ?? 0)) * weatherAltitudeK * (indoorsNow ? 0.22 : 1),
    precipKind: playerWeather.precip * (1 - (playerWeather.snowFraction ?? 0)) > 0.001 ? 1 : playerWeather.kind,
    windSpeed: playerWeather.windSpeed,
    volcanoes: wxPlanet?.def.atmosphere && wxPlanet.volcanicEffects?.group.visible
      ? wxPlanet.volcanicEffects.audioSources(wxPlanet.pose.center,wxPlanet.pose.quat) : undefined,
    shipMode: ship.mode,
    shipThrottle: ship.throttle,
    shipSpeed: ship.speed,
    shipPos: ship.pos,
    vehicle: drivingVehicle ? {
      speed: groundVehicle.forwardSpeed,
      maxSpeed: groundVehicle.def.handling.maxSpeed,
      boost: groundVehicle.boostK,
    } : undefined,
    ships: _audioShips,
    jetters: _audioJetters,
  });
  perf.end("audio");

  // Weather: re-bake the cloud fields into their offscreen targets (the near planet
  // billows + tracks the sun, others bake once). Before the reflection so freshly-baked
  // clouds also show in the water. `now/1000` is a small session clock — the epoch would
  // overflow the noise. Cheap: a few low-res offscreen passes, not the per-pixel march.
  perf.begin("render");
  perf.begin("wx-bake");
  perf.gpuBegin("wx-bake");
  system.bakeWeather(renderer, focusPos, now / 1000);
  perf.end("wx-bake");

  // Planar water reflection: mirror the scene across the sea surface into the
  // ocean's half-res reflection target (no-op when the patch is off screen or the
  // camera is underwater). Done LAST — after every object's final pose for the
  // frame — so players, drones and terrain reflect at their drawn positions.
  // The orbit reveals surfaces and water reflections behind the player. Prepare
  // both views once on arrival, with the loaded body and live light layout.
  // The menu never waits for this job; leaving cancels its outstanding polls.
  if (companionWarmSystem !== system || companionWarmPlanet !== footPlanet) {
    companionWarmSystem = system; companionWarmPlanet = footPlanet;
    companionWarmAbort?.abort();
    const warm = companionWarmAbort = new AbortController();
    const orbit = footPlanet !== null;
    void avatar.styleSettled().then(async () => {
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      if (warm.signal.aborted) return;
      if (orbit) await warmSceneViews(renderer, scene, camera, warm.signal);
      else await companion.warmup(renderer, camera);
    }).catch(() => { /* render can retry after cancellation or context restoration */ });
  }
  worldRenderPose.begin(scene);
  try {
    perf.begin("reflection");
    perf.gpuBegin("reflection");
    const _reflCallsBefore = renderer.info.render.calls;
    const riverMirror = riverReflection.render(renderer, scene, camera, seaPlanetThisFrame, oceanPatch.mesh, fog.color);
    if (riverMirror) oceanPatch.clearReflection();
    else oceanPatch.renderReflection(
      renderer,
      scene,
      camera,
      [seaPlanetThisFrame?.sea, seaPlanetThisFrame?.riverWaterGroup, seaPlanetThisFrame?.inlandWetlandWaterGroup].filter((o): o is NonNullable<typeof o> => !!o),
      fog.color,
    );
    if (!riverMirror) oceanPatch.shareRiverReflection(seaPlanetThisFrame?.riverWaterMaterial ?? null, camera);
    perf.end("reflection");
    // Each render() resets info at its start, so an UNCHANGED counter means the
    // reflection no-oped (patch off screen / underwater) — report null, not the
    // weather bake's leftover count.
    // While the GPU timer holds autoReset off the counters accumulate over the
    // frame, so the pass's own count is the delta.
    perf.noteReflectionDraws(
      renderer.info.render.calls === _reflCallsBefore ? null
        : renderer.info.render.calls - (renderer.info.autoReset ? 0 : _reflCallsBefore),
    );
    perf.begin("draw");
    // The painted print, with the vehicle speed lens as one of its terms. The fog
    // density goes in so the wet-in-wet softening tracks the atmosphere itself —
    // thick in valley air, exactly zero in space.
    painterlyPost.render(
      renderer,
      scene,
      camera,
      mode === "vehicle" ? groundVehicleCamera.warpAmount : 0,
      fog.density,
      // The depth buffer that survives partitioning belongs to the NEAREST slice
      // only — the rest were cleared between passes — so the print has to
      // linearise against that, not against the camera's full range. The ink
      // frame anchors the line-breakup noise to the FOOT planet, which spins —
      // anchored to render space instead, every line's gaps race at surface
      // speed (see the anchor note in the ink block).
      {
        ...(partitionedDraw ? { drawScene: partitionedDraw, depth: depthPartition.nearSlice } : {}),
        ...(footPlanet
          ? { inkFrame: { center: footPlanet.pose.center, invQuat: footPlanet.pose.invQuat, origin } }
          : {}),
        // Day gates the ink's shadow suppression; at night the borders stay
        // drawn, full black (user direction).
        day: system.dayFactor,
        atmosphere: atmosphereFrame,
      },
    );
    const wallRunning=Math.abs(character.wallRunState)>0&&Math.abs(character.wallRunState)<3;
    flightSpeedLines.update(elapsed,mode==='foot'&&!character.onGround&&(character.jet==='glide'||wallRunning),wallRunning?character.groundSpeed:character.flightSpeed,character.flightPower,camera,character.vel,wallRunning);
    flightSpeedLines.render(renderer);
    perf.gpuEnd();
    perf.end("draw");
  } finally {
    worldRenderPose.end(scene);
  }
  perf.end("render");

  // LIVE dialog portrait: the persona's talking head (visemes, blinks, head-look all
  // rendered live) into the dialog's canvas — throttled, and only while the panel is open.
  if (dialogPanel.visible && talkingNpcId && npcLife && now - lastPortraitMs > 42) {
    lastPortraitMs = now;
    const portraitCtx = dialogPanel.portraitContext();
    if (portraitCtx) dialogPanel.setPortraitActive(npcLife.livePortraitInto(talkingNpcId, renderer, portraitCtx));
  }

  // Keep the canvas black while the randomized title world is still filling
  // its terrain/atmosphere caches. Reveal only after a real rendered frame and
  // a settled LOD window (with a cap so a slow worker cannot strand the UI).
  if (
    document.body.classList.contains("intro-active")
    && !document.body.classList.contains("intro-background-ready")
  ) {
    introBackgroundFrames++;
    const waitedMs = performance.now() - introBackgroundStartedAt;
    if (introBackgroundFrames >= 12 && (system.terrainSettled(focusPos) || waitedMs >= 5000)) {
      document.body.classList.add("intro-background-ready");
    }
  }

  // DEBUG: trace the nearest planet's atmosphere as the camera moves/tilts. Toggle with
  // __sky.log(true). `onBeforeRender` stamps the frame the shell actually DREW, so we can
  // tell rendered-vs-culled; the frustum test cross-checks. All else (uniforms, pose)
  // should be constant under a pure tilt — if it vanishes anyway, this says why.
  dbgLogT -= elapsed;
  if (dbgAtmoLog && dbgLogT <= 0) {
    dbgLogT = 0.2;
    const nearD = system.nearestPlanet(focusPos);
    const atmo = nearD?.planet.atmo as (THREE.Mesh & { userData: { drew?: number; probed?: boolean } }) | undefined;
    if (atmo) {
      if (!atmo.userData.probed) {
        atmo.userData.probed = true;
        atmo.onBeforeRender = () => (atmo.userData.drew = dbgFrame);
      }
      _dbgMat.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
      _dbgFrustum.setFromProjectionMatrix(_dbgMat);
      const sm = atmo.material as THREE.ShaderMaterial;
      const u = sm.uniforms;
      const ctr = nearD!.planet.pose.center;
      _dbgUp.copy(camera.position).add(origin).sub(ctr).normalize();
      camera.getWorldDirection(_dbgFwd);
      const camR = camera.position.clone().add(origin).distanceTo(ctr);
      const outer = u.uRo.value as number;
      // eslint-disable-next-line no-console
      console.log("[atmo]", {
        drewThisFrame: atmo.userData.drew === dbgFrame, // false ⇒ culled / not drawn
        inFrustum: _dbgFrustum.intersectsObject(atmo),
        visible: atmo.visible,
        camInsideShell: camR < outer, // shell can't be frustum-culled while this is true
        altitude: +nearD!.surfaceDist.toFixed(1),
        pitchDeg: +((Math.asin(Math.max(-1, Math.min(1, _dbgFwd.dot(_dbgUp)))) * 180) / Math.PI).toFixed(1),
        uDay: +(u.uDay.value as number).toFixed(3),
        uFade: +(u.uFade.value as number).toFixed(3),
        uFog: (u.uFog.value as THREE.Vector3).toArray().map((x) => +x.toFixed(3)),
        camLocalLen: +(u.uCam.value as THREE.Vector3).length().toFixed(0),
        transparentSorted: renderer.info.render.calls,
      });
    }
  }
  perf.frameDone(renderer.info);
}

requestAnimationFrame(frame);

// Service worker: cache assets for instant, offline-capable repeat visits.
// PROD-only — in `vite dev` a worker would intercept HMR and serve stale modules.
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").catch((e) => console.warn("[sw] register failed", e));
  });
}
